import React, { useState, useEffect, useRef } from "react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceArea , BarChart, Bar, Legend } from "recharts";
import axios from "axios";
import QRCode from "qrcode";
// Correctif B6.b : `toast` etait appele dans saveClinicalEvent() sans jamais
// avoir ete importe. La note clinique etait bien enregistree cote serveur, puis
// toast.success levait un ReferenceError : la modale ne se fermait pas et le
// soignant ressaisissait sa note (doublons dans le dossier).
import { toast } from "sonner";
import { clearLoggedIn } from "../lib/authState";
import { formatDoctorName } from "@/lib/utils";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Securite : echappement HTML pour toute valeur patient/soignant injectee
// dans les fenetres d'impression construites par concatenation de chaines
// (document.write). Sans cela, un champ libre (nom, diagnostic, note...)
// contenant du HTML/JS est execute dans le contexte de l'appli (XSS stockee).
const esc = (v) => String(v ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

// Correctif B7 : la session est portee par le cookie httpOnly, plus par un
// jeton lisible en JavaScript. Aucun en-tete Authorization a construire.
const getAuthConfig = () => ({
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

// Pour les uploads multipart : NE PAS forcer Content-Type (le navigateur
// pose lui-meme la boundary), sinon le fichier est rejete (422).
const getUploadConfig = () => ({ withCredentials: true });

// Correctif (nettoyage post-reconstruction labo) : ce fichier ne sert plus
// aucun compte laboratoire (systeme dedie /laboratoire, LaboDashboard.js) —
// le seul compte labo qui tournait encore sur cet espace hopital partage a
// ete supprime. `roleLabel` traduisait auparavant les roles hopital en
// libelles labo (LAB_ROLE_LABELS) quand establishment_type === "laboratory" ;
// cette branche n'a plus aucun compte pour la declencher, retiree ici.
// L'etiquette generique ROLE_LABELS (plus bas dans ce fichier) reste la
// seule valeur utilisee.
const roleLabel = (role) => ROLE_LABELS[role] || role;

// Modules de l'espace hopital. "active: false" = a venir (phases suivantes).
const MODULES = [
  { id: "overview", label: "Vue d'ensemble", active: true },
  { id: "onboarding", label: "Mise en route", active: true },
  { id: "stats", label: "Tableau de bord", active: true },
  { id: "services", label: "Services", active: true },
  { id: "actes", label: "Types d'actes", active: true },
  { id: "agenda", label: "Agenda", active: true },
  { id: "staff", label: "Personnel", active: true },
  { id: "doctors", label: "Medecins", active: true },
  { id: "consultations", label: "Consultations", active: true },
  { id: "ordonnances", label: "Ordonnances", active: true },
  { id: "facturation", label: "Facturation", active: true },
  { id: "vitrine", label: "Ma vitrine", active: true },
  { id: "reception", label: "Accueil", active: true },
  { id: "preleveur", label: "Ecran preleveur", active: true },
  { id: "patients", label: "Patients", active: true },
  { id: "urgences", label: "Urgences", active: true },
  { id: "bloc", label: "Bloc operatoire", active: true },
  { id: "beds", label: "Lits & hospitalisation", active: true },
  { id: "lab", label: "Laboratoire", active: true },
  { id: "blood", label: "Banque de sang", active: true },
  { id: "phlebotomy", label: "Prelevements", active: true },
  { id: "vitals", label: "Pancarte", active: true },
  { id: "pathology", label: "Anatomopathologie", active: true },
  { id: "imaging", label: "Imagerie", active: true },
  { id: "pharmacy", label: "Pharmacie", active: true },
  { id: "care", label: "Soins infirmiers", active: true },
  { id: "audit", label: "Tracabilite", active: true },
  { id: "planning", label: "Planning", active: true },
  { id: "integration", label: "Integration SIL", active: true },
  { id: "labstats", label: "Statistiques", active: true },
  { id: "support", label: "Aide & Support", active: true },
];

function HopitalDashboard() {
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState("");
  const [ctx, setCtx] = useState(null); // { clinic_id, establishment_role, establishment_type, establishment }
  const [activeTab, setActiveTab] = useState("overview");
  // Recherche globale + raccourci clavier (cahier des charges, section 40 —
  // "recherche globale" et "raccourcis clavier" font partie des principes
  // UX du back-office). Ctrl/Cmd+K ouvre la modale depuis n'importe quel
  // onglet, "Echap" la ferme ; voir GlobalSearchModal plus bas.
  const [showGlobalSearch, setShowGlobalSearch] = useState(false);
  useEffect(() => {
    const onKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setShowGlobalSearch(true);
      } else if (e.key === "Escape") {
        setShowGlobalSearch(false);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  useEffect(() => {
    axios
      .get(`${API_URL}/api/sih/me`, getAuthConfig())
      .then((r) => {
        setCtx(r.data);
        // Le nouveau systeme labo dedie (/laboratoire, LaboDashboard.js) est
        // maintenant valide en conditions reelles : un compte laboratoire qui
        // atterrit ici (favori, lien externe, ancienne habitude) est renvoye
        // automatiquement plutot que de devoir cliquer sur le bouton "Essayer
        // le nouveau tableau de bord" ci-dessous, qui reste affiche le temps
        // de la redirection par securite (ex. si establishment_type change de
        // forme un jour) mais ne devrait plus jamais etre necessaire en
        // pratique. Aucune ligne du reste de ce fichier n'est modifiee.
        if (r.data?.establishment_type === "laboratory") {
          window.location.replace("/laboratoire");
        }
      })
      .catch((e) => {
        const code = e.response?.status;
        if (code === 401) setPageError("Vous n'etes pas connecte. Connectez-vous d'abord.");
        else if (code === 403) setPageError("Votre compte n'est rattache a aucun etablissement.");
        else setPageError("Impossible de charger votre etablissement.");
      })
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => {
    const goRx = () => setActiveTab("ordonnances");
    window.addEventListener("tabbibak:goToPrescription", goRx);
    return () => window.removeEventListener("tabbibak:goToPrescription", goRx);
  }, []);

  if (loading)
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", color: "#64748B", fontFamily: "system-ui, sans-serif" }}>
        Chargement de votre espace...
      </div>
    );

  if (pageError)
    return (
      <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 16, fontFamily: "system-ui, sans-serif", padding: 24, textAlign: "center" }}>
        <div style={{ fontSize: 18, fontWeight: 600, color: "#0F172A" }}>Espace professionnel</div>
        <div style={{ fontSize: 14, color: "#A32D2D", maxWidth: 420 }}>{pageError}</div>
        <a href="/login" style={{ padding: "10px 20px", background: "#185FA5", color: "#fff", borderRadius: 8, textDecoration: "none", fontSize: 14 }}>Se connecter</a>
      </div>
    );

  const est = ctx?.establishment || {};
  const can = (p) => (ctx?.permissions || []).includes(p);

  // Correctifs M1 et M2 :
  //  M1 — l'ancienne cascade commencait par `if (!m.active) return true;`, soit
  //       l'inverse de l'intention documentee ligne 29 (« active: false = a
  //       venir »). Un module desactive se serait affiche pour tout le monde,
  //       sans aucun controle de permission.
  //  M2 — 8 modules (stats, blood, phlebotomy, vitals, pathology, care, audit,
  //       planning) n'avaient AUCUN cas dans la cascade et tombaient sur le
  //       `return true` final : ils apparaissaient dans le menu de TOUS les
  //       roles. Une secretaire voyait « Tracabilite » et « Banque de sang »,
  //       et le clic ouvrait une page blanche (le rendu, lui, verifiait bien
  //       la permission). Une table explicite rend l'oubli impossible.
  const MODULE_PERMS = {
    overview: "services:read",
    onboarding: "services:read",
    stats: "stats:read",
    services: "services:read",
    actes: "acts:read",
    agenda: "agenda:read",
    staff: "staff:read",
    doctors: "staff:read",
    consultations: "consultation:read",
    ordonnances: "prescription:read",
    facturation: "billing:read",
    vitrine: "staff:write",
    reception: "reception:read",
    preleveur: "reception:read",
    patients: "patients:read",
    urgences: "emergency:read",
    bloc: "or:read",
    beds: "beds:read",
    lab: "lab:read",
    blood: "blood:read",
    phlebotomy: "phlebotomy:read",
    vitals: "vitals:read",
    pathology: "pathology:read",
    imaging: "imaging:read",
    pharmacy: "pharmacy:read",
    care: "care:read",
    audit: "audit:read",
    planning: "planning:read",
    integration: "integration:read",
    labstats: "stats:read",
    support: "support:read",
  };

  // Les etablissements de type "laboratory" independant sont desormais servis
  // par leur propre tableau de bord dedie (frontend/src/pages/LaboDashboard.js,
  // route /laboratoire) — le filtre de modules specifique aux labos qui vivait
  // ici a ete retire (plus aucun compte reel de ce type n'utilise ce tableau
  // de bord hopital).
  const moduleVisible = (m) => {
    if (!m.active) return false;
    const perm = MODULE_PERMS[m.id];
    // Un module sans permission declaree est masque par defaut : en cas d'ajout
    // futur, l'oubli echoue du cote sur (module invisible) plutot que du cote
    // dangereux (module visible par tous).
    return perm ? can(perm) : false;
  };

  return (
    <div style={{ display: "flex", minHeight: "100vh", fontFamily: "system-ui, sans-serif", background: "#F8FAFC" }}>
      {/* Sidebar */}
      <aside style={{ width: 240, background: "#fff", borderRight: "1px solid #E2E8F0", padding: "20px 0", flexShrink: 0 }}>
        <div style={{ padding: "0 20px 16px", borderBottom: "1px solid #F1F5F9" }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: "#0F172A" }}>{est.name || "Etablissement"}</div>
          <div style={{ fontSize: 11, color: "#185FA5", marginTop: 4, background: "#E6F1FB", display: "inline-block", padding: "2px 8px", borderRadius: 12 }}>
            Hopital · {roleLabel(ctx.establishment_role)}
          </div>
        </div>
        <nav style={{ marginTop: 12 }}>
          {MODULES.filter(moduleVisible).map((m) => (
            <button
              key={m.id}
              disabled={!m.active}
              onClick={() => m.active && setActiveTab(m.id)}
              style={{
                display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%",
                padding: "10px 20px", border: "none", background: activeTab === m.id ? "#E6F1FB" : "transparent",
                color: !m.active ? "#94A3B8" : activeTab === m.id ? "#185FA5" : "#334155",
                fontSize: 14, cursor: m.active ? "pointer" : "default", textAlign: "left",
                fontWeight: activeTab === m.id ? 600 : 400,
              }}
            >
              {m.label}
              {!m.active && <span style={{ fontSize: 10, color: "#94A3B8", background: "#F1F5F9", padding: "1px 6px", borderRadius: 8 }}>bientot</span>}
            </button>
          ))}
        </nav>
        <div style={{ padding: "16px 20px", marginTop: 12, borderTop: "1px solid #F1F5F9", display: "flex", flexDirection: "column", gap: 10 }}>
          <a href="/settings" style={{ fontSize: 13, color: "#185FA5", textDecoration: "none" }}>🔒 Changer mon mot de passe</a>
          <a href="/" style={{ fontSize: 13, color: "#64748B", textDecoration: "none" }}>← Retour a l'accueil</a>
          <button
            onClick={async () => {
              // Correctif B7 : la deconnexion doit faire supprimer le cookie
              // httpOnly PAR LE SERVEUR. L'ancien code se contentait de vider
              // localStorage : le cookie survivait et la session restait ouverte.
              try { await axios.post(`${API_URL}/api/auth/logout`, {}, getAuthConfig()); } catch (e) { /* on deconnecte quand meme */ }
              clearLoggedIn();
              window.location.href = "/login";
            }}
            style={{ fontSize: 13, color: "#A32D2D", background: "none", border: "none", padding: 0, textAlign: "left", cursor: "pointer" }}
          >⎋ Se deconnecter</button>
        </div>
      </aside>

      {/* Contenu */}
      <main style={{ flex: 1, padding: "28px 32px", overflow: "auto" }}>
        <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 12, marginBottom: 16 }}>
          <button
            onClick={() => setShowGlobalSearch(true)}
            style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 14px", background: "#fff", border: "1px solid #E2E8F0", borderRadius: 8, color: "#64748B", fontSize: 13, cursor: "pointer" }}
          >
            🔍 Rechercher un patient
            <span style={{ fontSize: 11, color: "#94A3B8", border: "1px solid #E2E8F0", borderRadius: 4, padding: "1px 5px" }}>Ctrl+K</span>
          </button>
          <HospitalNotifBell />
        </div>
        {showGlobalSearch && can("patients:read") && (
          <GlobalSearchModal
            onClose={() => setShowGlobalSearch(false)}
            onSelectPatient={(patient) => {
              setActiveTab("patients");
              window.dispatchEvent(new CustomEvent("tabbibak:searchPatient", { detail: { query: patient.patient_number || `${patient.first_name} ${patient.last_name}` } }));
              setShowGlobalSearch(false);
            }}
          />
        )}
        {activeTab === "overview" && <Overview ctx={ctx} est={est} />}
        {activeTab === "onboarding" && can("services:read") && <OnboardingModule can={can} clinicId={ctx.clinic_id} setActiveTab={setActiveTab} />}
        {activeTab === "stats" && can("stats:read") && <StatsModule can={can} />}
        {activeTab === "services" && can("services:read") && <ServicesModule can={can} />}
        {activeTab === "actes" && can("acts:read") && <ActTypesModule can={can} ctx={ctx} />}
        {activeTab === "agenda" && can("agenda:read") && <AgendaModule can={can} />}
        {activeTab === "staff" && can("staff:read") && <StaffModule can={can} ctx={ctx} />}
        {activeTab === "reception" && can("reception:read") && <ReceptionModule can={can} />}
        {activeTab === "preleveur" && can("reception:read") && <PreleveurModule can={can} />}
        {activeTab === "patients" && can("patients:read") && <PatientsModule can={can} />}
        {activeTab === "beds" && can("beds:read") && <BedsModule can={can} />}
        {activeTab === "lab" && can("lab:read") && <LabModule can={can} />}
        {activeTab === "blood" && can("blood:read") && <BloodModule can={can} />}
        {activeTab === "phlebotomy" && can("phlebotomy:read") && <PhlebotomyModule can={can} />}
        {activeTab === "vitals" && can("vitals:read") && <VitalsModule can={can} />}
        {activeTab === "pathology" && can("pathology:read") && <PathologyModule can={can} />}
        {activeTab === "imaging" && can("imaging:read") && <ImagingModule can={can} />}
        {activeTab === "pharmacy" && can("pharmacy:read") && <PharmacyModule can={can} />}
        {activeTab === "care" && can("care:read") && <CareModule can={can} />}
        {activeTab === "audit" && can("audit:read") && <AuditModule can={can} />}
        {activeTab === "planning" && can("planning:read") && <PlanningModule can={can} />}
        {activeTab === "integration" && can("integration:read") && <SILIntegrationModule can={can} />}
        {activeTab === "labstats" && can("stats:read") && <LabStatsModule can={can} />}
        {activeTab === "support" && can("support:read") && <SupportModule can={can} />}
        {activeTab === "urgences" && can("emergency:read") && <UrgencesModule can={can} />}
        {activeTab === "bloc" && can("or:read") && <BlocModule can={can} />}
        {activeTab === "doctors" && can("staff:read") && <DoctorsModule can={can} />}
        {activeTab === "vitrine" && can("staff:write") && <VitrineModule can={can} ctx={ctx} />}
        {activeTab === "consultations" && can("consultation:read") && <ConsultationsModule can={can} />}
        {activeTab === "ordonnances" && can("prescription:read") && <OrdonnancesModule can={can} />}
        {activeTab === "facturation" && can("billing:read") && <BillingModule can={can} />}
      </main>
    </div>
  );
}

// Recherche globale (cahier des charges, section 40). Cherche parmi les
// patients de l'etablissement (l'entite la plus universellement pertinente,
// que l'on soit sur l'Agenda, l'Accueil ou les Patients) et renvoie la main
// au module Patients pour l'ouverture du dossier complet, plutot que de
// dupliquer ici l'affichage d'une fiche patient.
function GlobalSearchModal({ onClose, onSelectPatient }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  useEffect(() => {
    if (q.trim().length < 2) { setResults([]); return; }
    setLoading(true);
    const t = setTimeout(() => {
      axios.get(`${API_URL}/api/sih/patients`, { ...getAuthConfig(), params: { search: q.trim(), limit: 8 } })
        .then((r) => setResults(r.data.patients || []))
        .catch(() => setResults([]))
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.45)", display: "flex", alignItems: "flex-start", justifyContent: "center", zIndex: 2000, paddingTop: "12vh" }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 14, width: "94%", maxWidth: 520, boxShadow: "0 20px 60px rgba(0,0,0,0.2)", overflow: "hidden" }}>
        <input
          ref={inputRef}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Escape") onClose(); }}
          placeholder="Rechercher un patient (nom, numero de dossier, telephone...)"
          style={{ width: "100%", padding: "16px 20px", border: "none", borderBottom: "1px solid #F1F5F9", fontSize: 16, boxSizing: "border-box", outline: "none" }}
        />
        <div style={{ maxHeight: 360, overflowY: "auto" }}>
          {loading && <div style={{ padding: "16px 20px", fontSize: 13, color: "#94A3B8" }}>Recherche...</div>}
          {!loading && q.trim().length >= 2 && results.length === 0 && (
            <div style={{ padding: "16px 20px", fontSize: 13, color: "#94A3B8" }}>Aucun patient trouve.</div>
          )}
          {results.map((p) => (
            <div key={p.patient_id} onClick={() => onSelectPatient(p)}
              style={{ padding: "12px 20px", borderTop: "1px solid #F8FAFC", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <div style={{ fontWeight: 500, fontSize: 14, color: "#0F172A" }}>{p.first_name} {p.last_name}</div>
                <div style={{ fontSize: 12, color: "#94A3B8" }}>{p.patient_number}{p.phone ? ` · ${p.phone}` : ""}</div>
              </div>
              <span style={{ fontSize: 12, color: "#185FA5" }}>Ouvrir →</span>
            </div>
          ))}
        </div>
        <div style={{ padding: "8px 20px", borderTop: "1px solid #F1F5F9", fontSize: 11, color: "#94A3B8" }}>Echap pour fermer</div>
      </div>
    </div>
  );
}

// Assistant d'onboarding (cahier des charges, section 38) : enchaine les
// briques deja existantes (Utilisateurs, Horaires, Actes, Notifications,
// Integration SIL...) au lieu de les laisser eparpillees entre plusieurs
// onglets sans fil conducteur. "Sites" (etape 2 du §38) est omise : ce
// module reste mono-etablissement par choix explicite (voir
// docs/CAHIER_DES_CHARGES_STATUT.md, section multi-site) — l'"Organisation"
// couvre deja le seul site existant. Deux etapes sont detectees
// automatiquement a partir de donnees reelles (Utilisateurs, Actes) ; les
// autres se cochent a la main une fois verifiees, et sont memorisees par
// etablissement (pas seulement par navigateur) via un cookie applicatif
// (pas de nouvelle route backend pour un simple etat "fait/pas fait").
function OnboardingModule({ can, clinicId, setActiveTab }) {
  const storageKey = `tabbibak_onboarding_${clinicId || "default"}`;
  const [checked, setChecked] = useState(() => {
    try { return JSON.parse(localStorage.getItem(storageKey)) || {}; } catch { return {}; }
  });
  const [staffCount, setStaffCount] = useState(null);
  const [actCount, setActCount] = useState(null);

  useEffect(() => {
    axios.get(`${API_URL}/api/sih/staff`, getAuthConfig()).then((r) => setStaffCount((r.data.staff || []).length)).catch(() => setStaffCount(0));
    axios.get(`${API_URL}/api/sih/act-types/`, getAuthConfig()).then((r) => setActCount((r.data.act_types || []).length)).catch(() => setActCount(0));
  }, []);

  const toggle = (id) => {
    setChecked((c) => {
      const next = { ...c, [id]: !c[id] };
      try { localStorage.setItem(storageKey, JSON.stringify(next)); } catch { /* stockage indisponible : perte silencieuse acceptable ici */ }
      return next;
    });
  };

  const STEPS = [
    { id: "organisation", label: "Organisation", desc: "Nom, wilaya, adresse, telephone — deja renseignes a l'inscription.", tab: "overview", done: true },
    { id: "utilisateurs", label: "Utilisateurs", desc: "Ajoutez les membres de votre equipe (secretariat, prevelement...).", tab: "staff", done: staffCount === null ? null : staffCount > 1 },
    { id: "horaires", label: "Horaires", desc: "Configurez vos horaires d'ouverture, pauses et capacite simultanee.", tab: "agenda", done: !!checked.horaires, manual: true },
    { id: "actes", label: "Types d'actes", desc: "Creez vos types de prelevements/analyses (duree, personnel, preparation).", tab: "actes", done: actCount === null ? null : actCount > 0 },
    { id: "ressources", label: "Ressources", desc: "Salles et materiel necessaires, si applicable a votre organisation.", tab: "staff", done: !!checked.ressources, manual: true },
    { id: "notifications", label: "Notifications", desc: "Email et notifications internes sont actives automatiquement, rien a configurer.", tab: null, done: true },
    { id: "integration", label: "Integration SIL", desc: "Optionnel : connectez votre systeme metier (webhooks signes).", tab: "integration", done: !!checked.integration, manual: true, optional: true },
    { id: "test", label: "Test", desc: "Creez un rendez-vous, faites arriver un patient fictif, testez la file d'attente et l'ecran preleveur.", tab: "reception", done: !!checked.test, manual: true },
    { id: "production", label: "Mise en production", desc: "Vous etes pret : informez votre equipe et commencez a recevoir de vrais patients.", tab: null, done: !!checked.production, manual: true },
  ];
  const doneCount = STEPS.filter((s) => s.done).length;

  return (
    <div>
      <h1 style={{ fontSize: 22, fontWeight: 700, color: "#0F172A", margin: 0 }}>Mise en route</h1>
      <p style={{ fontSize: 13, color: "#64748B", marginTop: 6 }}>{doneCount} / {STEPS.length} etapes completees</p>
      <div style={{ background: "#E2E8F0", borderRadius: 8, height: 8, marginTop: 8, overflow: "hidden" }}>
        <div style={{ width: `${(doneCount / STEPS.length) * 100}%`, background: "#185FA5", height: "100%", transition: "width 0.2s" }} />
      </div>

      <div style={{ marginTop: 20, display: "flex", flexDirection: "column", gap: 10 }}>
        {STEPS.map((s, i) => (
          <div key={s.id} style={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 10, padding: 14, display: "flex", alignItems: "flex-start", gap: 12 }}>
            <div style={{
              width: 26, height: 26, borderRadius: "50%", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center",
              background: s.done ? "#166534" : "#F1F5F9", color: s.done ? "#fff" : "#94A3B8", fontSize: 13, fontWeight: 700,
            }}>{s.done ? "✓" : i + 1}</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: "#0F172A" }}>
                {s.label}{s.optional && <span style={{ marginLeft: 8, fontSize: 11, color: "#94A3B8", fontWeight: 400 }}>(optionnel)</span>}
              </div>
              <div style={{ fontSize: 13, color: "#64748B", marginTop: 2 }}>{s.desc}</div>
            </div>
            <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
              {s.tab && can && (
                <button onClick={() => setActiveTab(s.tab)} style={{ padding: "6px 12px", background: "#F1F5F9", color: "#334155", border: "none", borderRadius: 8, fontSize: 12, cursor: "pointer" }}>Ouvrir</button>
              )}
              {s.manual && (
                <button onClick={() => toggle(s.id)} style={{ padding: "6px 12px", background: s.done ? "#DCFCE7" : "#185FA5", color: s.done ? "#166534" : "#fff", border: "none", borderRadius: 8, fontSize: 12, cursor: "pointer" }}>
                  {s.done ? "Fait" : "Marquer fait"}
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function HospitalNotifBell() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);

  const load = () => {
    axios.get(`${API_URL}/api/sih/notifications`, getAuthConfig())
      .then((r) => setList(r.data || []))
      .catch(() => {});
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 20000);
    return () => clearInterval(id);
  }, []);

  const unread = list.filter((n) => !n.read).length;

  const toggle = async () => {
    const willOpen = !open;
    setOpen(willOpen);
    if (willOpen && unread > 0) {
      setList((prev) => prev.map((n) => ({ ...n, read: true })));
      try { await axios.put(`${API_URL}/api/sih/notifications/read-all`, {}, getAuthConfig()); } catch (_) {}
    }
  };

  return (
    <div style={{ position: "relative" }}>
      <button onClick={toggle} title="Notifications" style={{ position: "relative", width: 42, height: 42, borderRadius: 21, border: "1px solid #E2E8F0", background: "#fff", cursor: "pointer", fontSize: 18, lineHeight: 1 }}>
        🔔
        {unread > 0 && (
          <span style={{ position: "absolute", top: -4, right: -4, minWidth: 18, height: 18, padding: "0 4px", borderRadius: 9, background: "#EF4444", color: "#fff", fontSize: 11, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center" }}>
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>
      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 40 }} />
          <div style={{ position: "absolute", right: 0, marginTop: 8, width: 330, maxWidth: "90vw", background: "#fff", border: "1px solid #E2E8F0", borderRadius: 14, boxShadow: "0 12px 32px rgba(0,0,0,0.12)", zIndex: 50, overflow: "hidden" }}>
            <div style={{ padding: "12px 16px", borderBottom: "1px solid #F1F5F9", fontWeight: 700, color: "#0F172A" }}>Notifications</div>
            <div style={{ maxHeight: 360, overflowY: "auto" }}>
              {list.length === 0 ? (
                <div style={{ padding: "20px 16px", textAlign: "center", color: "#94A3B8", fontSize: 13 }}>Aucune notification</div>
              ) : list.map((n) => (
                <div key={n.notification_id} style={{ padding: "10px 16px", borderBottom: "1px solid #F8FAFC", background: n.read ? "#fff" : "#F4FAFF" }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "#0F172A" }}>{n.title || "Notification"}</div>
                  {n.message && <div style={{ fontSize: 12, color: "#64748B", marginTop: 2 }}>{n.message}</div>}
                  {n.created_at && <div style={{ fontSize: 11, color: "#94A3B8", marginTop: 3 }}>{new Date(n.created_at).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</div>}
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function StatCard({ label, value, sub, color, accent }) {
  return (
    <div style={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 12, padding: "16px 18px", borderLeft: `4px solid ${accent || "#185FA5"}` }}>
      <div style={{ fontSize: 28, fontWeight: 700, color: color || "#0F172A", lineHeight: 1.1 }}>{value}</div>
      <div style={{ fontSize: 13, color: "#475569", marginTop: 4 }}>{label}</div>
      {sub != null && <div style={{ fontSize: 12, color: "#94A3B8", marginTop: 2 }}>{sub}</div>}
    </div>
  );
}
const SHIFT_TYPES = { matin: ["Matin", "#DBEAFE", "#1E40AF", "M"], apres_midi: ["Après-midi", "#FEF3C7", "#854F0B", "AM"], nuit: ["Nuit", "#E0E7FF", "#3730A3", "N"], jour: ["Jour", "#EAF3DE", "#3B6D11", "J"], garde: ["Garde", "#FCEBEB", "#A32D2D", "G"], astreinte: ["Astreinte", "#F1F5F9", "#475569", "A"] };
const LEAVE_TYPES = { conge_annuel: "Congé annuel", maladie: "Maladie", maternite: "Maternité", recuperation: "Récupération", sans_solde: "Sans solde", formation: "Formation", absence: "Absence", autre: "Autre" };
const LEAVE_STATUS = { en_attente: ["En attente", "#FEF3C7", "#854F0B"], approuve: ["Approuvé", "#EAF3DE", "#3B6D11"], refuse: ["Refusé", "#FCEBEB", "#A32D2D"] };
function mondayOf(d) { const x = new Date(d); const day = (x.getDay() + 6) % 7; x.setDate(x.getDate() - day); x.setHours(0, 0, 0, 0); return x; }
function isoDay(d) { const x = new Date(d); const y = x.getFullYear(); const m = String(x.getMonth() + 1).padStart(2, "0"); const j = String(x.getDate()).padStart(2, "0"); return y + "-" + m + "-" + j; }
// Jours feries algeriens.
// 1) Feries a DATE FIXE (grégorienne) : identiques chaque annee -> calcul auto (MM-DD).
const FERIES_FIXES = {
  "01-01": "Nouvel an", "01-12": "Yennayer", "05-01": "Fete du Travail",
  "07-05": "Fete de l'Independance", "11-01": "Fete de la Revolution",
};
// 2) Feries RELIGIEUX : calendrier lunaire, non calculables -> a completer chaque
// annee (dates officielles, susceptibles d'un decalage d'un a deux jours).
const FERIES_RELIGIEUX = {
  "2026-03-19": "Aid el-Fitr", "2026-03-20": "Aid el-Fitr", "2026-03-21": "Aid el-Fitr",
  "2026-05-27": "Aid el-Adha", "2026-05-28": "Aid el-Adha", "2026-05-29": "Aid el-Adha",
  "2026-06-16": "Awal Mouharram", "2026-06-25": "Achoura", "2026-08-25": "Mawlid Ennabaoui",
  "2027-03-09": "Aid el-Fitr", "2027-03-10": "Aid el-Fitr", "2027-03-11": "Aid el-Fitr",
  "2027-05-16": "Aid el-Adha", "2027-05-17": "Aid el-Adha", "2027-05-18": "Aid el-Adha",
  "2027-06-06": "Awal Mouharram", "2027-06-15": "Achoura", "2027-08-15": "Mawlid Ennabaoui",
};
// day = "YYYY-MM-DD". Les fixes marchent pour TOUTE annee ; les religieux si renseignes.
const ferieAt = (day) => FERIES_RELIGIEUX[day] || FERIES_FIXES[(day || "").slice(5)] || null;
function PlanningModule({ can }) {
  const manage = can("planning:manage");
  const [tab, setTab] = useState(manage ? "grid" : "myplanning");
  const [shifts, setShifts] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [staff, setStaff] = useState([]);
  const [services, setServices] = useState([]);
  const [beds, setBeds] = useState([]);
  const [coverage, setCoverage] = useState(null);   // couverture requis/present du jour affiche
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [weekStart, setWeekStart] = useState(mondayOf(new Date()));
  const [viewMode, setViewMode] = useState("semaine"); // jour | semaine | mois
  const [cell, setCell] = useState(null);
  const [showLeave, setShowLeave] = useState(false);
  const today = isoDay(new Date());
  const [shForm, setShForm] = useState({ shift_type: "jour", start_time: "", end_time: "", department: "", role_post: "", room: "", status: "confirme", note: "" });
  const [lvForm, setLvForm] = useState({ staff_id: "", leave_type: "conge_annuel", date_from: today, date_to: today, reason: "" });
  const weekDays = Array.from({ length: 7 }, (_, i) => { const d = new Date(weekStart); d.setDate(d.getDate() + i); return d; });
  const rangeFrom = isoDay(weekStart);
  const rangeTo = isoDay(weekDays[6]);
  const load = async () => {
    setLoading(true);
    try {
      const calls = [
        axios.get(`${API_URL}/api/sih/planning/shifts?date_from=${rangeFrom}&date_to=${rangeTo}`, getAuthConfig()).then((r) => setShifts(r.data.shifts || [])).catch(() => {}),
        axios.get(`${API_URL}/api/sih/planning/leaves`, getAuthConfig()).then((r) => setLeaves(r.data.leaves || [])).catch(() => {}),
      ];
      if (manage) {
        // Personnel + medecins (les medecins vivent dans une collection separee).
        calls.push(Promise.all([
          axios.get(`${API_URL}/api/sih/staff`, getAuthConfig()).then((r) => r.data.staff || []).catch(() => []),
          axios.get(`${API_URL}/api/sih/doctors`, getAuthConfig()).then((r) => r.data.doctors || []).catch(() => []),
        ]).then(([st, docs]) => {
          const medecins = docs.map((d) => ({ staff_id: d.doctor_id, name: d.name, role: "medecin", service_id: d.service_id }));
          setStaff([...medecins, ...st]);
        }));
        calls.push(axios.get(`${API_URL}/api/sih/services`, getAuthConfig()).then((r) => setServices(r.data.services || [])).catch(() => {}));
        calls.push(axios.get(`${API_URL}/api/sih/beds`, getAuthConfig()).then((r) => setBeds(r.data.beds || [])).catch(() => {}));
      }
      await Promise.all(calls);
    } catch (e) { setError("Erreur de chargement"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [rangeFrom]);
  const iS = { padding: "9px 11px", border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 14, fontFamily: "inherit", boxSizing: "border-box", width: "100%" };
  const lab = { fontSize: 12, color: "#64748B", marginBottom: 4, display: "block" };
  const btn = (bg) => ({ padding: "8px 16px", background: bg, color: "#fff", border: "none", borderRadius: 8, fontSize: 14, cursor: "pointer" });
  const staffName = (s) => (s.name || s.full_name || "").trim() || s.email || "—";
  const shiftAt = (sid, day) => shifts.find((s) => s.staff_id === sid && s.date === day);
  const leaveAt = (sid, day) => leaves.find((l) => l.staff_id === sid && l.status === "approuve" && l.date_from <= day && l.date_to >= day);
  // Duree d'un creneau en heures (gere la garde de nuit qui passe minuit).
  const shiftHours = (sh) => {
    if (!sh || !sh.start_time || !sh.end_time) return 0;
    const [h1, m1] = sh.start_time.split(":").map(Number);
    const [h2, m2] = sh.end_time.split(":").map(Number);
    if ([h1, m1, h2, m2].some((n) => Number.isNaN(n))) return 0;
    let mins = (h2 * 60 + m2) - (h1 * 60 + m1);
    if (mins <= 0) mins += 24 * 60; // franchit minuit
    return mins / 60;
  };
  // Total d'heures planifiees d'un agent sur la semaine affichee.
  const weekHours = (sid) => weekDays.reduce((tot, d) => tot + shiftHours(shiftAt(sid, isoDay(d))), 0);
  const fmtH = (h) => (h % 1 === 0 ? String(h) : h.toFixed(1)) + "h";
  const openCell = (s, day) => { setError(""); const ex = shiftAt(s.staff_id, day); setShForm({ shift_type: ex ? ex.shift_type : "jour", start_time: ex ? (ex.start_time || "") : "", end_time: ex ? (ex.end_time || "") : "", department: ex ? (ex.department || "") : "", role_post: ex ? (ex.role_post || "") : "", room: ex ? (ex.room || "") : "", status: ex ? (ex.status || "confirme") : "confirme", note: ex ? (ex.note || "") : "" }); setCell({ staff: s, day, existing: ex }); };
  const saveCell = async () => {
    // Heures toujours obligatoires ; service et salle en plus pour un medecin.
    if (!shForm.start_time || !shForm.end_time) { setError("Heure de début et de fin obligatoires."); return; }
    if (cell && cell.staff && cell.staff.role === "medecin") {
      if (!shForm.department) { setError("Service obligatoire pour un médecin."); return; }
      if (!shForm.room) { setError("Salle obligatoire pour un médecin."); return; }
    }
    if (saving) return; setSaving(true); setError("");
    try {
      if (cell.existing) {
        // Statut automatique : un creneau deja publie qu'on edite passe "modifie".
        await axios.put(`${API_URL}/api/sih/planning/shifts/${cell.existing.shift_id}`, { ...shForm, status: "modifie" }, getAuthConfig());
      } else {
        await axios.post(`${API_URL}/api/sih/planning/shifts`, { ...shForm, staff_id: cell.staff.staff_id, date: cell.day, status: "confirme" }, getAuthConfig());
      }
      setCell(null); load();
    } catch (e) { setError(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Erreur"); }
    finally { setSaving(false); }
  };
  const delCell = async () => { if (!cell.existing) { setCell(null); return; } try { await axios.delete(`${API_URL}/api/sih/planning/shifts/${cell.existing.shift_id}`, getAuthConfig()); setCell(null); load(); } catch (e) { setError("Erreur"); } };
  // --- Remplacement d'un agent sur un creneau (absence imprevue) ---
  const [showReplace, setShowReplace] = useState(false);
  const [rpForm, setRpForm] = useState({ replacement_staff_id: "", reason: "" });
  const doReplace = async () => {
    if (!rpForm.replacement_staff_id) { setError("Sélectionnez un remplaçant."); return; }
    if (saving) return; setSaving(true); setError("");
    try {
      await axios.post(`${API_URL}/api/sih/planning/shifts/${cell.existing.shift_id}/replace`, rpForm, getAuthConfig());
      setShowReplace(false); setRpForm({ replacement_staff_id: "", reason: "" }); setCell(null); load();
    } catch (e) { setError(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Erreur"); }
    finally { setSaving(false); }
  };
  const createLeave = async () => {
    if (!lvForm.date_from || !lvForm.date_to) { setError("Dates requises"); return; }
    if (lvForm.date_to < lvForm.date_from) { setError("La date de fin ne peut pas être antérieure à la date de début."); return; }
    if (manage && !lvForm.staff_id) { setError("Sélectionnez un membre du personnel"); return; }
    if (saving) return; setSaving(true); setError("");
    try {
      const payload = { ...lvForm };
      if (!manage) delete payload.staff_id;
      await axios.post(`${API_URL}/api/sih/planning/leaves`, payload, getAuthConfig());
      setShowLeave(false); setLvForm({ staff_id: "", leave_type: "conge_annuel", date_from: today, date_to: today, reason: "" }); load();
    } catch (e) { setError(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Erreur"); }
    finally { setSaving(false); }
  };
  const decide = async (id, decision) => { try { await axios.put(`${API_URL}/api/sih/planning/leaves/${id}/decision`, { decision }, getAuthConfig()); load(); } catch (e) { setError("Erreur"); } };
  const [publishMsg, setPublishMsg] = useState("");
  const publishWeek = async () => {
    if (saving) return; setSaving(true); setError(""); setPublishMsg("");
    try {
      const r = await axios.post(`${API_URL}/api/sih/planning/shifts/publish?date_from=${rangeFrom}&date_to=${rangeTo}`, {}, getAuthConfig());
      setPublishMsg(r.data.message || "Planning publie.");
      load();
    } catch (e) { setError(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Erreur de publication"); }
    finally { setSaving(false); }
  };
  const shiftWeek = (n) => { const d = new Date(weekStart); d.setDate(d.getDate() + n * 7); setWeekStart(mondayOf(d)); };
  // --- Navigation jour / mois ---
  const [dayDate, setDayDate] = useState(isoDay(new Date()));
  // Couverture du jour affiche : effectif requis vs planifie (recalcule a chaque changement).
  useEffect(() => {
    if (!manage) return;
    axios.get(`${API_URL}/api/sih/planning/coverage?date=${dayDate}`, getAuthConfig())
      .then((r) => setCoverage(r.data))
      .catch(() => setCoverage(null));
    /* eslint-disable-next-line */
  }, [dayDate, shifts.length, manage]);
  const shiftDay = (n) => { const d = new Date(dayDate); d.setDate(d.getDate() + n); setDayDate(isoDay(d)); };
  const [monthAnchor, setMonthAnchor] = useState(new Date());
  const shiftMonth = (n) => { const d = new Date(monthAnchor); d.setMonth(d.getMonth() + n); setMonthAnchor(d); };
  // Grille du mois : cases du lundi de la 1re semaine au dimanche de la derniere.
  const monthGrid = () => {
    const y = monthAnchor.getFullYear(), m = monthAnchor.getMonth();
    const first = new Date(y, m, 1);
    const start = mondayOf(first);
    const cells = [];
    for (let i = 0; i < 42; i++) { const d = new Date(start); d.setDate(d.getDate() + i); cells.push(d); }
    return cells;
  };
  // Tous les creneaux d'un jour donne (tous agents), pour la vue jour.
  const dayShiftsFor = (day) => shifts.filter((s) => s.date === day);
  const shiftsOn = (day) => shifts.filter((s) => s.date === day);
  const dLabel = (d) => d.toLocaleDateString("fr-FR", { weekday: "short", day: "2-digit", month: "2-digit" });
  const fmtDate = (d) => { try { return new Date(d).toLocaleDateString("fr-FR", { weekday: "short", day: "2-digit", month: "short" }); } catch (e) { return d; } };
  const pendingLeaves = leaves.filter((l) => l.status === "en_attente");
  // Manager : toutes les lignes du personnel. Agent : sa propre ligne (reconstituee
  // depuis ses shifts, car il ne charge pas la liste complete du personnel).
  const rowStaff = manage ? staff : (() => {
    const mine = shifts.find((x) => x.staff_id);
    if (mine) return [{ staff_id: mine.staff_id, name: mine.staff_name, role: mine.staff_role }];
    return [{ staff_id: "__me__", name: "Mon planning", role: "" }];
  })();
  const tabBtn = (id, label) => (<button onClick={() => setTab(id)} style={{ padding: "8px 16px", background: tab === id ? "#185FA5" : "#fff", color: tab === id ? "#fff" : "#475569", border: "1px solid " + (tab === id ? "#185FA5" : "#CBD5E1"), borderRadius: 8, fontSize: 13, cursor: "pointer", fontWeight: 600 }}>{label}</button>);
  return (
    <div>
      <h1 style={{ fontSize: 22, fontWeight: 700, color: "#0F172A", margin: 0 }}>Planning du personnel</h1>
      <div style={{ fontSize: 13, color: "#94A3B8", marginTop: 4 }}>{manage ? "Affectations, gardes et validation des congés" : "Mon planning et mes demandes de congés"}</div>
      <div style={{ display: "flex", gap: 8, marginTop: 18, flexWrap: "wrap" }}>
        {manage && tabBtn("grid", "Planning")}
        {!manage && tabBtn("myplanning", "Mon planning")}
        {tabBtn("leaves", manage ? `Congés${pendingLeaves.length ? " (" + pendingLeaves.length + ")" : ""}` : "Mes congés")}
      </div>
      {error && <div style={{ background: "#FCEBEB", border: "1px solid #F7C1C1", color: "#A32D2D", borderRadius: 8, padding: "10px 14px", marginTop: 14, fontSize: 13 }}>{error}</div>}
      {publishMsg && <div style={{ background: "#EAF3DE", border: "1px solid #C3D9A0", color: "#3B6D11", borderRadius: 8, padding: "10px 14px", marginTop: 14, fontSize: 13 }}>{publishMsg}</div>}
      {(tab === "grid" || tab === "myplanning") && (
        <div style={{ marginTop: 16 }}>
          <div style={{ display: "flex", gap: 6, marginBottom: 14, background: "#F1F5F9", padding: 4, borderRadius: 10, width: "fit-content" }}>
            {[["jour", "Jour"], ["semaine", "Semaine"], ["mois", "Mois"]].map(([v, l]) => (
              <button key={v} onClick={() => setViewMode(v)} style={{ padding: "6px 16px", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer", background: viewMode === v ? "#fff" : "transparent", color: viewMode === v ? "#185FA5" : "#64748B", boxShadow: viewMode === v ? "0 1px 3px rgba(0,0,0,0.1)" : "none" }}>{l}</button>
            ))}
          </div>
          {viewMode === "semaine" && (<>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
            <button onClick={() => shiftWeek(-1)} style={{ padding: "6px 12px", border: "1px solid #CBD5E1", background: "#fff", borderRadius: 8, cursor: "pointer", fontSize: 13 }}>← Semaine préc.</button>
            <div style={{ fontSize: 14, fontWeight: 600, color: "#334155" }}>Semaine du {weekStart.toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" })}</div>
            <button onClick={() => shiftWeek(1)} style={{ padding: "6px 12px", border: "1px solid #CBD5E1", background: "#fff", borderRadius: 8, cursor: "pointer", fontSize: 13 }}>Semaine suiv. →</button>
            <button onClick={() => setWeekStart(mondayOf(new Date()))} style={{ padding: "6px 12px", border: "1px solid #CBD5E1", background: "#fff", borderRadius: 8, cursor: "pointer", fontSize: 13, color: "#185FA5" }}>Aujourd'hui</button>
            {manage && <button onClick={publishWeek} disabled={saving} style={{ padding: "6px 14px", border: "none", background: saving ? "#94A3B8" : "#3B6D11", color: "#fff", borderRadius: 8, cursor: saving ? "not-allowed" : "pointer", fontSize: 13, fontWeight: 600, marginLeft: "auto" }}>{saving ? "…" : "Publier la semaine"}</button>}
          </div>
          <div style={{ display: "flex", gap: 10, marginBottom: 12, flexWrap: "wrap" }}>
            {Object.keys(SHIFT_TYPES).map((k) => <span key={k} style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12, color: "#64748B" }}><span style={{ width: 14, height: 14, borderRadius: 4, background: SHIFT_TYPES[k][1], border: "1px solid " + SHIFT_TYPES[k][2] }}></span>{SHIFT_TYPES[k][0]}</span>)}
            <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12, color: "#64748B" }}><span style={{ width: 14, height: 14, borderRadius: 4, background: "#F8FAFC", border: "1px dashed #CBD5E1" }}></span>Congé</span>
          </div>
          <div style={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 12, overflow: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 820 }}>
              <thead><tr style={{ background: "#F8FAFC" }}>
                <th style={{ padding: "10px 12px", fontSize: 12, color: "#64748B", textAlign: "left", position: "sticky", left: 0, background: "#F8FAFC", minWidth: 160 }}>Personnel</th>
                {weekDays.map((d, i) => { const isToday = isoDay(d) === today; return (<th key={i} style={{ padding: "8px 6px", fontSize: 11, color: isToday ? "#185FA5" : "#64748B", textAlign: "center", fontWeight: isToday ? 700 : 600, minWidth: 88 }}>{dLabel(d)}</th>); })}
                <th style={{ padding: "8px 10px", fontSize: 11, color: "#334155", textAlign: "center", fontWeight: 700, minWidth: 64, borderLeft: "2px solid #E2E8F0" }}>Total</th>
              </tr></thead>
              <tbody>
                {loading && <tr><td colSpan={8} style={{ padding: 16, textAlign: "center", color: "#94A3B8", fontSize: 13 }}>Chargement...</td></tr>}
                {!loading && rowStaff.length === 0 && <tr><td colSpan={8} style={{ padding: 16, textAlign: "center", color: "#94A3B8", fontSize: 13 }}>Aucun membre du personnel.</td></tr>}
                {!loading && rowStaff.map((s) => (
                  <tr key={s.staff_id} style={{ borderTop: "1px solid #F1F5F9" }}>
                    <td style={{ padding: "8px 12px", fontSize: 13, fontWeight: 500, position: "sticky", left: 0, background: "#fff" }}>{staffName(s)}<div style={{ fontSize: 11, color: "#94A3B8" }}>{ROLE_LABELS[s.role] || s.role || ""}</div></td>
                    {weekDays.map((d, i) => { const day = isoDay(d); const sh = shiftAt(s.staff_id, day); const lv = leaveAt(s.staff_id, day); const st = sh ? SHIFT_TYPES[sh.shift_type] : null;
                      return (<td key={i} onClick={() => manage && !lv && openCell(s, day)} style={{ padding: 4, textAlign: "center", cursor: manage && !lv ? "pointer" : "default", borderLeft: "1px solid #F8FAFC" }}>
                        {lv ? <div style={{ fontSize: 10, color: "#94A3B8", background: "#F8FAFC", border: "1px dashed #CBD5E1", borderRadius: 6, padding: "8px 4px" }}>Congé</div>
                          : sh ? <div style={{ fontSize: 11, fontWeight: 600, color: st[2], background: st[1], borderRadius: 6, padding: "6px 4px", position: "relative", opacity: sh.status === "annule" ? 0.45 : 1, border: sh.shift_type === "nuit" ? "2px solid #3730A3" : "none", boxShadow: sh.shift_type === "nuit" ? "0 0 0 1px #3730A3 inset" : "none" }}>{sh.shift_type === "nuit" ? "\uD83C\uDF19 " : ""}{st[0]}{sh.start_time ? <div style={{ fontSize: 9, fontWeight: 400 }}>{sh.start_time}-{sh.end_time}</div> : null}{sh.room ? <div style={{ fontSize: 8, fontWeight: 500, marginTop: 2, opacity: 0.85 }}>{sh.room}</div> : null}{sh.status && sh.status !== "confirme" ? <div style={{ fontSize: 7, fontWeight: 700, marginTop: 2, textTransform: "uppercase", letterSpacing: 0.3, color: sh.status === "remplace" ? "#A32D2D" : sh.status === "modifie" ? "#854F0B" : "#64748B" }}>{sh.status === "remplace" ? "Remplace" : sh.status === "modifie" ? "Modifie" : "Annule"}</div> : null}{manage && !sh.published ? <div style={{ fontSize: 7, fontWeight: 700, marginTop: 2, color: "#94A3B8", fontStyle: "italic" }}>brouillon</div> : null}</div>
                          : <div style={{ fontSize: 16, color: "#E2E8F0", padding: "6px 4px" }}>{manage ? "+" : "·"}</div>}
                      </td>); })}
                    <td style={{ padding: "8px 10px", textAlign: "center", fontSize: 13, fontWeight: 700, color: weekHours(s.staff_id) > 48 ? "#A32D2D" : "#334155", borderLeft: "2px solid #E2E8F0" }}>{fmtH(weekHours(s.staff_id))}</td>
                  </tr>))}
              </tbody>
            </table>
          </div>
          </>)}
          {/* fin vue semaine */}
          {viewMode === "jour" && (
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
                <button onClick={() => shiftDay(-1)} style={{ padding: "6px 12px", border: "1px solid #CBD5E1", background: "#fff", borderRadius: 8, cursor: "pointer", fontSize: 13 }}>← Jour préc.</button>
                <div style={{ fontSize: 14, fontWeight: 600, color: "#334155" }}>{new Date(dayDate).toLocaleDateString("fr-FR", { weekday: "long", day: "2-digit", month: "long", year: "numeric" })}</div>
                <button onClick={() => shiftDay(1)} style={{ padding: "6px 12px", border: "1px solid #CBD5E1", background: "#fff", borderRadius: 8, cursor: "pointer", fontSize: 13 }}>Jour suiv. →</button>
                <button onClick={() => setDayDate(isoDay(new Date()))} style={{ padding: "6px 12px", border: "1px solid #CBD5E1", background: "#fff", borderRadius: 8, cursor: "pointer", fontSize: 13, color: "#185FA5" }}>Aujourd'hui</button>
              </div>
              {manage && services.length > 0 && (
                <div style={{ marginBottom: 14 }}>
                  <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 8 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: "#64748B" }}>Couverture des services</div>
                    {coverage && coverage.summary && coverage.summary.coverage_rate != null && (
                      <div style={{ fontSize: 12, fontWeight: 700, color: coverage.summary.missing > 0 ? "#A32D2D" : "#3B6D11" }}>
                        Taux de couverture {coverage.summary.coverage_rate}%{coverage.summary.missing > 0 ? ` \u2014 ${coverage.summary.missing} poste(s) non pourvu(s)` : ""}
                      </div>
                    )}
                  </div>
                  <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                    {(coverage ? coverage.services : []).map((sv) => {
                      const planifies = sv.planned_total;
                      if (!sv.has_requirements) {
                        return (
                          <div key={sv.service_id} style={{ padding: "8px 12px", borderRadius: 8, fontSize: 12, background: "#F8FAFC", color: "#64748B", border: "1px solid #E2E8F0", minWidth: 150 }}>
                            <div style={{ fontWeight: 700, marginBottom: 2 }}>{sv.name}</div>
                            <div>{planifies} agent{planifies > 1 ? "s" : ""} planifie{planifies > 1 ? "s" : ""}</div>
                            <div style={{ fontSize: 10, color: "#94A3B8", marginTop: 2 }}>aucune exigence definie</div>
                          </div>);
                      }
                      const manque = sv.requirements.filter((r) => r.gap < 0);
                      const ko = manque.length > 0;
                      return (
                        <div key={sv.service_id} style={{ padding: "8px 12px", borderRadius: 8, fontSize: 12, minWidth: 170, background: ko ? "#FCEBEB" : "#EAF3DE", border: "1px solid " + (ko ? "#F7C1C1" : "#C3D9A0") }}>
                          <div style={{ fontWeight: 700, marginBottom: 4, color: ko ? "#A32D2D" : "#3B6D11" }}>{ko ? "\uD83D\uDD34 " : ""}{sv.name}</div>
                          {sv.requirements.map((r, i) => (
                            <div key={i} style={{ display: "flex", justifyContent: "space-between", gap: 10, color: r.gap < 0 ? "#A32D2D" : "#3B6D11", fontWeight: r.gap < 0 ? 700 : 400 }}>
                              <span>{(SHIFT_TYPES[r.shift_type] || [r.shift_type])[0]}{r.role ? " \u00b7 " + (ROLE_LABELS[r.role] || r.role) : ""}{r.skill ? " (" + r.skill + ")" : ""}</span>
                              <span>{r.present}/{r.required}</span>
                            </div>
                          ))}
                        </div>);
                    })}
                  </div>
                </div>
              )}
              <div style={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 12, overflow: "hidden" }}>
                {shiftsOn(dayDate).length === 0 ? <div style={{ padding: 24, textAlign: "center", color: "#94A3B8", fontSize: 14 }}>Aucune affectation ce jour.</div>
                  : shiftsOn(dayDate).sort((a, b) => (a.start_time || "").localeCompare(b.start_time || "")).map((sh) => { const st = SHIFT_TYPES[sh.shift_type] || ["?", "#F1F5F9", "#64748B"]; return (
                    <div key={sh.shift_id} style={{ display: "flex", alignItems: "center", gap: 14, padding: "12px 16px", borderTop: "1px solid #F1F5F9" }}>
                      <div style={{ minWidth: 90, fontSize: 13, fontWeight: 600, color: "#334155" }}>{sh.start_time || "—"}{sh.end_time ? " - " + sh.end_time : ""}</div>
                      <div style={{ fontSize: 11, fontWeight: 600, color: st[2], background: st[1], borderRadius: 6, padding: "4px 10px", border: sh.shift_type === "nuit" ? "2px solid #3730A3" : "none" }}>{sh.shift_type === "nuit" ? "\uD83C\uDF19 " : ""}{st[0]}</div>
                      <div style={{ flex: 1, fontSize: 13, color: "#0F172A", fontWeight: 500 }}>{sh.staff_name}{sh.staff_role ? <span style={{ color: "#94A3B8", fontWeight: 400 }}> · {ROLE_LABELS[sh.staff_role] || sh.staff_role}</span> : null}</div>
                      {sh.room ? <div style={{ fontSize: 12, color: "#64748B" }}>{sh.room}</div> : null}
                      {sh.status && sh.status !== "confirme" ? <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", color: sh.status === "remplace" ? "#A32D2D" : sh.status === "modifie" ? "#854F0B" : "#64748B" }}>{sh.status}</div> : null}
                    </div>); })}
              </div>
            </div>
          )}
          {viewMode === "mois" && (
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
                <button onClick={() => shiftMonth(-1)} style={{ padding: "6px 12px", border: "1px solid #CBD5E1", background: "#fff", borderRadius: 8, cursor: "pointer", fontSize: 13 }}>← Mois préc.</button>
                <div style={{ fontSize: 14, fontWeight: 600, color: "#334155", textTransform: "capitalize" }}>{monthAnchor.toLocaleDateString("fr-FR", { month: "long", year: "numeric" })}</div>
                <button onClick={() => shiftMonth(1)} style={{ padding: "6px 12px", border: "1px solid #CBD5E1", background: "#fff", borderRadius: 8, cursor: "pointer", fontSize: 13 }}>Mois suiv. →</button>
                <button onClick={() => setMonthAnchor(new Date())} style={{ padding: "6px 12px", border: "1px solid #CBD5E1", background: "#fff", borderRadius: 8, cursor: "pointer", fontSize: 13, color: "#185FA5" }}>Ce mois</button>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 6 }}>
                {["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"].map((j) => <div key={j} style={{ textAlign: "center", fontSize: 11, fontWeight: 700, color: "#64748B", padding: "4px 0" }}>{j}</div>)}
                {monthGrid().map((d, i) => { const day = isoDay(d); const inMonth = d.getMonth() === monthAnchor.getMonth(); const dayShifts = shiftsOn(day); const isToday = day === today; const ferie = ferieAt(day); return (
                  <div key={i} onClick={() => { setDayDate(day); setViewMode("jour"); }} style={{ minHeight: 84, background: ferie ? "#FEF2F2" : (inMonth ? "#fff" : "#F8FAFC"), border: "1px solid " + (isToday ? "#185FA5" : ferie ? "#FCA5A5" : "#E2E8F0"), borderRadius: 8, padding: 6, cursor: "pointer", opacity: inMonth ? 1 : 0.5 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 4 }}><span style={{ fontSize: 11, fontWeight: isToday ? 700 : 500, color: isToday ? "#185FA5" : "#334155" }}>{d.getDate()}</span></div>
                    {ferie ? <div style={{ fontSize: 8, fontWeight: 700, color: "#B91C1C", marginBottom: 3, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{ferie}</div> : null}
                    {dayShifts.slice(0, 3).map((sh) => { const st = SHIFT_TYPES[sh.shift_type] || ["?", "#F1F5F9", "#64748B"]; return (
                      <div key={sh.shift_id} style={{ fontSize: 9, fontWeight: 600, color: st[2], background: st[1], borderRadius: 4, padding: "1px 4px", marginBottom: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", border: sh.shift_type === "nuit" ? "1px solid #3730A3" : "none" }}>{sh.shift_type === "nuit" ? "\uD83C\uDF19" : st[3]} {sh.staff_name}</div>); })}
                    {dayShifts.length > 3 ? <div style={{ fontSize: 9, color: "#94A3B8" }}>+{dayShifts.length - 3}</div> : null}
                  </div>); })}
              </div>
            </div>
          )}
        </div>
      )}
      {tab === "leaves" && (
        <div style={{ marginTop: 16 }}>
          <button onClick={() => { setError(""); setShowLeave(true); }} style={{ ...btn("#3B6D11"), marginBottom: 14 }}>+ {manage ? "Poser un congé (pour un agent)" : "Poser un congé"}</button>
          <div style={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 12, overflow: "hidden" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr style={{ background: "#F8FAFC", textAlign: "left" }}>
                {manage && <th style={{ padding: "10px 14px", fontSize: 12, color: "#64748B" }}>Personnel</th>}
                <th style={{ padding: "10px 14px", fontSize: 12, color: "#64748B" }}>Type</th>
                <th style={{ padding: "10px 14px", fontSize: 12, color: "#64748B" }}>Du</th>
                <th style={{ padding: "10px 14px", fontSize: 12, color: "#64748B" }}>Au</th>
                <th style={{ padding: "10px 14px", fontSize: 12, color: "#64748B" }}>Statut</th>
                {manage && <th style={{ padding: "10px 14px", fontSize: 12, color: "#64748B" }}></th>}
              </tr></thead>
              <tbody>
                {loading && <tr><td colSpan={6} style={{ padding: 16, textAlign: "center", color: "#94A3B8", fontSize: 13 }}>Chargement...</td></tr>}
                {!loading && leaves.length === 0 && <tr><td colSpan={6} style={{ padding: 16, textAlign: "center", color: "#94A3B8", fontSize: 13 }}>Aucune demande de congé.</td></tr>}
                {!loading && leaves.map((l) => { const ls = LEAVE_STATUS[l.status] || ["?", "#F1F5F9", "#64748B"]; return (
                  <tr key={l.leave_id} style={{ borderTop: "1px solid #F1F5F9" }}>
                    {manage && <td style={{ padding: "10px 14px", fontSize: 13, fontWeight: 500 }}>{l.staff_name}</td>}
                    <td style={{ padding: "10px 14px", fontSize: 13, color: "#475569" }}>{LEAVE_TYPES[l.leave_type] || l.leave_type}</td>
                    <td style={{ padding: "10px 14px", fontSize: 13, color: "#334155" }}>{fmtDate(l.date_from)}</td>
                    <td style={{ padding: "10px 14px", fontSize: 13, color: "#334155" }}>{fmtDate(l.date_to)}</td>
                    <td style={{ padding: "10px 14px" }}><span style={{ fontSize: 12, padding: "3px 10px", borderRadius: 12, background: ls[1], color: ls[2] }}>{ls[0]}</span></td>
                    {manage && <td style={{ padding: "10px 14px", textAlign: "right" }}>{l.status === "en_attente" && (<span style={{ display: "inline-flex", gap: 8 }}><button onClick={() => decide(l.leave_id, "approuve")} style={{ fontSize: 12, color: "#3B6D11", background: "none", border: "none", cursor: "pointer", fontWeight: 600 }}>Approuver</button><button onClick={() => decide(l.leave_id, "refuse")} style={{ fontSize: 12, color: "#A32D2D", background: "none", border: "none", cursor: "pointer" }}>Refuser</button></span>)}</td>}
                  </tr>); })}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {cell && (
        <div onClick={() => setCell(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: 20, overflow: "auto" }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 14, padding: 24, maxWidth: 420, width: "100%", marginTop: 40 }}>
            <h2 style={{ fontSize: 18, fontWeight: 700, margin: "0 0 4px" }}>{cell.existing ? "Modifier l'affectation" : "Nouvelle affectation"}</h2>
            <div style={{ fontSize: 13, color: "#64748B", marginBottom: 16 }}>{staffName(cell.staff)} · {new Date(cell.day).toLocaleDateString("fr-FR", { weekday: "long", day: "2-digit", month: "long" })}</div>
            <div style={{ marginBottom: 12 }}><label style={lab}>Type de garde</label><select style={iS} value={shForm.shift_type} onChange={(e) => setShForm({ ...shForm, shift_type: e.target.value })}>{Object.keys(SHIFT_TYPES).map((k) => <option key={k} value={k}>{SHIFT_TYPES[k][0]}</option>)}</select></div>
            <div style={{ display: "flex", gap: 10, marginBottom: 16 }}>
              <div style={{ flex: 1 }}><label style={lab}>Début</label><input type="time" style={iS} value={shForm.start_time} onChange={(e) => setShForm({ ...shForm, start_time: e.target.value })} /></div>
              <div style={{ flex: 1 }}><label style={lab}>Fin</label><input type="time" style={iS} value={shForm.end_time} onChange={(e) => setShForm({ ...shForm, end_time: e.target.value })} /></div>
            </div>
            <div style={{ marginBottom: 12 }}>
              <div><label style={lab}>Service / departement</label><select style={iS} value={shForm.department} onChange={(e) => setShForm({ ...shForm, department: e.target.value })}><option value="">—</option>{services.map((sv) => <option key={sv.service_id} value={sv.name}>{sv.name}</option>)}</select></div>
            </div>
            <div style={{ marginBottom: 16 }}>
              <div><label style={lab}>Salle / unite</label><select style={iS} value={shForm.room} onChange={(e) => setShForm({ ...shForm, room: e.target.value })}><option value="">—</option>{beds.map((b) => <option key={b.bed_id} value={(b.room ? b.room + " - " : "") + b.bed_number}>{(b.room ? b.room + " - " : "") + b.bed_number}</option>)}</select></div>
            </div>
            {error && <div style={{ background: "#FCEBEB", border: "1px solid #F7C1C1", color: "#A32D2D", borderRadius: 8, padding: "10px 14px", marginBottom: 16, fontSize: 13 }}>{error}</div>}
            <div style={{ display: "flex", gap: 8, justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ display: "flex", gap: 14 }}>{cell.existing && <><button onClick={delCell} style={{ fontSize: 13, color: "#A32D2D", background: "none", border: "none", cursor: "pointer" }}>Supprimer</button><button onClick={() => { setError(""); setShowReplace(true); }} style={{ fontSize: 13, color: "#854F0B", background: "none", border: "none", cursor: "pointer", fontWeight: 600 }}>Remplacer</button></>}</div>
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={() => setCell(null)} style={{ padding: "8px 16px", background: "#fff", color: "#64748B", border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 14, cursor: "pointer" }}>Annuler</button>
                <button onClick={saveCell} disabled={saving} style={{ ...btn(saving ? "#94A3B8" : "#185FA5"), cursor: saving ? "not-allowed" : "pointer" }}>{saving ? "…" : "Enregistrer"}</button>
              </div>
            </div>
          </div>
        </div>
      )}
      {showReplace && cell && cell.existing && (
        <div onClick={() => setShowReplace(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1100, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: 20, overflow: "auto" }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 14, padding: 24, maxWidth: 440, width: "100%", marginTop: 40 }}>
            <h2 style={{ fontSize: 18, fontWeight: 700, margin: "0 0 4px" }}>Remplacer sur ce poste</h2>
            <div style={{ fontSize: 13, color: "#64748B", marginBottom: 16 }}>{cell.existing.staff_name} · {new Date(cell.existing.date).toLocaleDateString("fr-FR", { weekday: "long", day: "2-digit", month: "long" })}{cell.existing.start_time ? " · " + cell.existing.start_time + "-" + cell.existing.end_time : ""}</div>
            <div style={{ marginBottom: 12 }}><label style={lab}>Remplaçant *</label><select style={iS} value={rpForm.replacement_staff_id} onChange={(e) => setRpForm({ ...rpForm, replacement_staff_id: e.target.value })}><option value="">Choisir…</option>{staff.filter((s) => s.staff_id !== cell.existing.staff_id).map((s) => <option key={s.staff_id} value={s.staff_id}>{staffName(s)} — {ROLE_LABELS[s.role] || s.role}</option>)}</select></div>
            <div style={{ marginBottom: 16 }}><label style={lab}>Motif</label><input style={iS} value={rpForm.reason} onChange={(e) => setRpForm({ ...rpForm, reason: e.target.value })} placeholder="ex : arrêt maladie" /></div>
            {error && <div style={{ background: "#FCEBEB", border: "1px solid #F7C1C1", color: "#A32D2D", borderRadius: 8, padding: "10px 14px", marginBottom: 16, fontSize: 13 }}>{error}</div>}
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button onClick={() => setShowReplace(false)} style={{ padding: "8px 16px", background: "#fff", color: "#64748B", border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 14, cursor: "pointer" }}>Annuler</button>
              <button onClick={doReplace} disabled={saving} style={{ ...btn(saving ? "#94A3B8" : "#854F0B"), cursor: saving ? "not-allowed" : "pointer" }}>{saving ? "…" : "Confirmer le remplacement"}</button>
            </div>
          </div>
        </div>
      )}
      {showLeave && (
        <div onClick={() => setShowLeave(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: 20, overflow: "auto" }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 14, padding: 24, maxWidth: 460, width: "100%", marginTop: 40 }}>
            <h2 style={{ fontSize: 18, fontWeight: 700, margin: "0 0 16px" }}>Poser un congé</h2>
            {manage && <div style={{ marginBottom: 12 }}><label style={lab}>Membre du personnel *</label><select style={iS} value={lvForm.staff_id} onChange={(e) => setLvForm({ ...lvForm, staff_id: e.target.value })}><option value="">Choisir…</option>{staff.map((s) => <option key={s.staff_id} value={s.staff_id}>{staffName(s)} — {ROLE_LABELS[s.role] || s.role}</option>)}</select></div>}
            <div style={{ marginBottom: 12 }}><label style={lab}>Type</label><select style={iS} value={lvForm.leave_type} onChange={(e) => setLvForm({ ...lvForm, leave_type: e.target.value })}>{Object.keys(LEAVE_TYPES).map((k) => <option key={k} value={k}>{LEAVE_TYPES[k]}</option>)}</select></div>
            <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
              <div style={{ flex: 1 }}><label style={lab}>Du *</label><input type="date" style={iS} value={lvForm.date_from} onChange={(e) => setLvForm({ ...lvForm, date_from: e.target.value })} /></div>
              <div style={{ flex: 1 }}><label style={lab}>Au *</label><input type="date" style={iS} value={lvForm.date_to} onChange={(e) => setLvForm({ ...lvForm, date_to: e.target.value })} /></div>
            </div>
            <div style={{ marginBottom: 16 }}><label style={lab}>Motif</label><input style={iS} value={lvForm.reason} onChange={(e) => setLvForm({ ...lvForm, reason: e.target.value })} /></div>
            {error && <div style={{ background: "#FCEBEB", border: "1px solid #F7C1C1", color: "#A32D2D", borderRadius: 8, padding: "10px 14px", marginBottom: 16, fontSize: 13 }}>{error}</div>}
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button onClick={() => setShowLeave(false)} style={{ padding: "8px 16px", background: "#fff", color: "#64748B", border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 14, cursor: "pointer" }}>Annuler</button>
              <button onClick={createLeave} disabled={saving} style={{ ...btn(saving ? "#94A3B8" : "#3B6D11"), cursor: saving ? "not-allowed" : "pointer" }}>{saving ? "Enregistrement…" : "Envoyer la demande"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
// Correctif B6.a : cette constante avait disparu lors du refactoring « grille »
// (elle existe encore dans HopitalDashboard.js.bak-avant-grille:259). Son
// absence faisait planter tout le module Tracabilite — exige par la loi 18-07 —
// des qu'un evenement d'audit existait, avec un ReferenceError.
const AUDIT_ACTION_COLORS = {
  create: ["#EAF3DE", "#3B6D11"],
  update: ["#E0E7FF", "#3730A3"],
  delete: ["#FCEBEB", "#A32D2D"],
  read:   ["#F1F5F9", "#64748B"],
  login:  ["#DBEAFE", "#1E40AF"],
  assign: ["#FEF3C7", "#854F0B"],
  denied: ["#FCEBEB", "#A32D2D"],
  upload: ["#E0E7FF", "#3730A3"],
};

function AuditModule({ can }) {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [actions, setActions] = useState([]);
  const [entityTypes, setEntityTypes] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [fAction, setFAction] = useState("");
  const [fEntity, setFEntity] = useState("");
  const [fFrom, setFFrom] = useState("");
  const [fTo, setFTo] = useState("");
  const [page, setPage] = useState(0);
  const pageSize = 100;
  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (fAction) params.append("action", fAction);
      if (fEntity) params.append("entity_type", fEntity);
      if (fFrom) params.append("date_from", fFrom);
      if (fTo) params.append("date_to", fTo);
      params.append("limit", pageSize);
      params.append("skip", page * pageSize);
      const r = await axios.get(`${API_URL}/api/sih/audit/logs?${params.toString()}`, getAuthConfig());
      setLogs(r.data.logs || []); setTotal(r.data.total || 0);
      setActions(r.data.actions || []); setEntityTypes(r.data.entity_types || []);
    } catch (e) { setError("Impossible de charger le journal d'audit"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [fAction, fEntity, fFrom, fTo, page]);
  const iS = { padding: "8px 12px", border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 13, background: "#fff" };
  const lab = { fontSize: 12, color: "#64748B", marginBottom: 4, display: "block" };
  const fmt = (iso) => { try { return new Date(iso).toLocaleString("fr-FR"); } catch (e) { return iso; } };
  const resetFilters = () => { setFAction(""); setFEntity(""); setFFrom(""); setFTo(""); setPage(0); };
  const totalPages = Math.ceil(total / pageSize);
  return (
    <div>
      <h1 style={{ fontSize: 22, fontWeight: 700, color: "#0F172A", margin: 0 }}>Tracabilite</h1>
      <div style={{ fontSize: 13, color: "#94A3B8", marginTop: 4 }}>Journal d'audit — lecture seule (conforme loi 18-07 / 25-11)</div>
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end", marginTop: 18, background: "#fff", border: "1px solid #E2E8F0", borderRadius: 12, padding: 16 }}>
        <div><label style={lab}>Action</label><select value={fAction} onChange={(e) => { setFAction(e.target.value); setPage(0); }} style={iS}><option value="">Toutes</option>{actions.map((a) => <option key={a} value={a}>{a}</option>)}</select></div>
        <div><label style={lab}>Type d'objet</label><select value={fEntity} onChange={(e) => { setFEntity(e.target.value); setPage(0); }} style={iS}><option value="">Tous</option>{entityTypes.map((t) => <option key={t} value={t}>{t}</option>)}</select></div>
        <div><label style={lab}>Du</label><input type="date" value={fFrom} onChange={(e) => { setFFrom(e.target.value); setPage(0); }} style={iS} /></div>
        <div><label style={lab}>Au</label><input type="date" value={fTo} onChange={(e) => { setFTo(e.target.value); setPage(0); }} style={iS} /></div>
        {(fAction || fEntity || fFrom || fTo) && <button onClick={resetFilters} style={{ padding: "8px 14px", background: "#fff", color: "#475569", border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 13, cursor: "pointer" }}>Reinitialiser</button>}
        <div style={{ marginLeft: "auto", fontSize: 13, color: "#64748B", alignSelf: "center" }}>{total} evenement(s)</div>
      </div>
      {error && <div style={{ background: "#FCEBEB", border: "1px solid #F7C1C1", color: "#A32D2D", borderRadius: 8, padding: "10px 14px", marginTop: 14, fontSize: 13 }}>{error}</div>}
      <div style={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 12, marginTop: 16, overflow: "hidden" }}>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 800 }}>
            <thead><tr style={{ background: "#F8FAFC", textAlign: "left" }}>
              <th style={{ padding: "10px 14px", fontSize: 12, color: "#64748B", fontWeight: 600 }}>Date/heure</th>
              <th style={{ padding: "10px 14px", fontSize: 12, color: "#64748B", fontWeight: 600 }}>Utilisateur</th>
              <th style={{ padding: "10px 14px", fontSize: 12, color: "#64748B", fontWeight: 600 }}>Role</th>
              <th style={{ padding: "10px 14px", fontSize: 12, color: "#64748B", fontWeight: 600 }}>Action</th>
              <th style={{ padding: "10px 14px", fontSize: 12, color: "#64748B", fontWeight: 600 }}>Objet</th>
              <th style={{ padding: "10px 14px", fontSize: 12, color: "#64748B", fontWeight: 600 }}>Reference</th>
              <th style={{ padding: "10px 14px", fontSize: 12, color: "#64748B", fontWeight: 600 }}>IP</th>
            </tr></thead>
            <tbody>
              {loading && <tr><td colSpan={7} style={{ padding: "16px 14px", fontSize: 13, color: "#94A3B8", textAlign: "center" }}>Chargement...</td></tr>}
              {!loading && logs.length === 0 && <tr><td colSpan={7} style={{ padding: "16px 14px", fontSize: 13, color: "#94A3B8", textAlign: "center" }}>Aucun evenement.</td></tr>}
              {!loading && logs.map((l, i) => { const ac = AUDIT_ACTION_COLORS[l.action] || ["#F1F5F9", "#64748B"]; return (
                <tr key={i} style={{ borderTop: "1px solid #F1F5F9" }}>
                  <td style={{ padding: "10px 14px", fontSize: 13, color: "#334155", whiteSpace: "nowrap" }}>{fmt(l.ts)}</td>
                  <td style={{ padding: "10px 14px", fontSize: 13, fontWeight: 500 }}>{l.user_name || "—"}</td>
                  <td style={{ padding: "10px 14px", fontSize: 12, color: "#64748B" }}>{l.user_role || "—"}</td>
                  <td style={{ padding: "10px 14px" }}><span style={{ fontSize: 12, padding: "3px 10px", borderRadius: 12, background: ac[0], color: ac[1] }}>{l.action}</span></td>
                  <td style={{ padding: "10px 14px", fontSize: 13, color: "#475569" }}>{l.entity_type || "—"}</td>
                  <td style={{ padding: "10px 14px", fontSize: 12, color: "#94A3B8", fontFamily: "monospace" }}>{l.entity_id || "—"}</td>
                  <td style={{ padding: "10px 14px", fontSize: 12, color: "#94A3B8" }}>{l.ip || "—"}</td>
                </tr>); })}
            </tbody>
          </table>
        </div>
      </div>
      {totalPages > 1 && (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 12, marginTop: 16 }}>
          <button onClick={() => setPage(Math.max(0, page - 1))} disabled={page === 0} style={{ padding: "8px 14px", background: "#fff", color: page === 0 ? "#CBD5E1" : "#475569", border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 13, cursor: page === 0 ? "not-allowed" : "pointer" }}>Precedent</button>
          <span style={{ fontSize: 13, color: "#64748B" }}>Page {page + 1} / {totalPages}</span>
          <button onClick={() => setPage(Math.min(totalPages - 1, page + 1))} disabled={page >= totalPages - 1} style={{ padding: "8px 14px", background: "#fff", color: page >= totalPages - 1 ? "#CBD5E1" : "#475569", border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 13, cursor: page >= totalPages - 1 ? "not-allowed" : "pointer" }}>Suivant</button>
        </div>
      )}
    </div>
  );
}
function StatsModule({ can }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try { const r = await axios.get(`${API_URL}/api/sih/stats/dashboard`, getAuthConfig()); setData(r.data); }
      catch (e) { setError("Impossible de charger le tableau de bord"); }
      finally { setLoading(false); }
    })();
  }, []);
  const Kpi = ({ label, value, sub, color }) => (
    <div style={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 12, padding: "16px 18px" }}>
      <div style={{ fontSize: 12, color: "#64748B", marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 28, fontWeight: 700, color: color || "#0F172A", lineHeight: 1 }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: "#94A3B8", marginTop: 6 }}>{sub}</div>}
    </div>
  );
  const Panel = ({ title, children }) => (
    <div style={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 12, padding: 18 }}>
      <div style={{ fontSize: 14, fontWeight: 600, color: "#334155", marginBottom: 14 }}>{title}</div>
      {children}
    </div>
  );
  if (loading) return <div><h1 style={{ fontSize: 22, fontWeight: 700, color: "#0F172A" }}>Tableau de bord</h1><div style={{ marginTop: 20, color: "#94A3B8", fontSize: 14 }}>Chargement des indicateurs...</div></div>;
  if (error || !data) return <div><h1 style={{ fontSize: 22, fontWeight: 700, color: "#0F172A" }}>Tableau de bord</h1><div style={{ background: "#FCEBEB", border: "1px solid #F7C1C1", color: "#A32D2D", borderRadius: 8, padding: "10px 14px", marginTop: 14, fontSize: 13 }}>{error || "Aucune donnee"}</div></div>;
  const b = data.beds, a = data.admissions, t = data.technical, bl = data.blood;
  const occColor = b.occupancy_rate >= 90 ? "#A32D2D" : b.occupancy_rate >= 75 ? "#854F0B" : "#3B6D11";
  return (
    <div>
      <h1 style={{ fontSize: 22, fontWeight: 700, color: "#0F172A", margin: 0 }}>Tableau de bord</h1>
      <div style={{ fontSize: 13, color: "#94A3B8", marginTop: 4 }}>Indicateurs de pilotage en temps reel</div>

      <div style={{ fontSize: 13, fontWeight: 600, color: "#64748B", marginTop: 24, marginBottom: 10, textTransform: "uppercase", letterSpacing: 0.5 }}>Activite hospitaliere</div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 14 }}>
        <Kpi label="Patients hospitalises" value={a.active} color="#185FA5" />
        <Kpi label="Taux d'occupation" value={`${b.occupancy_rate}%`} sub={`${b.occupied} / ${b.total} lits`} color={occColor} />
        <Kpi label="Duree moyenne de sejour" value={`${a.dms} j`} sub="sorties du mois" />
        <Kpi label="Entrees aujourd'hui" value={a.today} sub={`${a.month} ce mois`} color="#3B6D11" />
        <Kpi label="Sorties aujourd'hui" value={a.discharges_today} sub={`${a.discharges_month} ce mois`} color="#854F0B" />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginTop: 24 }}>
        <Panel title="Entrees / sorties (7 derniers jours)">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={data.trend} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
              <XAxis dataKey="day" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="entrees" name="Entrees" fill="#185FA5" radius={[3, 3, 0, 0]} />
              <Bar dataKey="sorties" name="Sorties" fill="#94A3B8" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Panel>
        <Panel title="Patients hospitalises par service">
          {data.by_service.length === 0 ? <div style={{ fontSize: 13, color: "#94A3B8", padding: "40px 0", textAlign: "center" }}>Aucun patient hospitalise.</div> : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={data.by_service} layout="vertical" margin={{ top: 5, right: 20, left: 20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                <YAxis type="category" dataKey="service" tick={{ fontSize: 11 }} width={100} />
                <Tooltip />
                <Bar dataKey="count" name="Patients" fill="#185FA5" radius={[0, 3, 3, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Panel>
      </div>

      <div style={{ fontSize: 13, fontWeight: 600, color: "#64748B", marginTop: 28, marginBottom: 10, textTransform: "uppercase", letterSpacing: 0.5 }}>Plateaux techniques</div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 14 }}>
        <Kpi label="Consultations aujourd'hui" value={t.consult_today} sub={`${t.consult_month} ce mois`} />
        <Kpi label="Interventions programmees" value={t.or_scheduled} sub={`${t.or_done_month} realisees ce mois`} />
        <Kpi label="Passages urgences actifs" value={t.er_active} color={t.er_active > 0 ? "#854F0B" : "#0F172A"} />
        <Kpi label="Labo en attente" value={t.lab_pending} />
        <Kpi label="Imagerie en attente" value={t.imaging_pending} />
        <Kpi label="Anatomopath en cours" value={t.path_pending} />
      </div>

      <div style={{ fontSize: 13, fontWeight: 600, color: "#64748B", marginTop: 28, marginBottom: 10, textTransform: "uppercase", letterSpacing: 0.5 }}>Banque de sang</div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 14 }}>
        <Kpi label="Poches disponibles" value={bl.available} color={bl.available < 5 ? "#A32D2D" : "#3B6D11"} />
        <Kpi label="Demandes de transfusion en attente" value={bl.transfusion_pending} color={bl.transfusion_pending > 0 ? "#854F0B" : "#0F172A"} />
      </div>
    </div>
  );
}
function Overview({ ctx, est }) {
  const [s, setS] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    axios.get(`${API_URL}/api/sih/overview`, getAuthConfig()).then((r) => setS(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);
  const occRate = s && s.beds_total ? Math.round((s.beds_occupied / s.beds_total) * 100) : 0;
  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: 8 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: "#0F172A", margin: 0 }}>Vue d'ensemble</h1>
        {s && <span style={{ fontSize: 13, color: "#94A3B8" }}>{s.scoped ? "Périmètre : votre service" : "Périmètre : tout l'établissement"} · {s.date}</span>}
      </div>

      {loading ? <div style={{ padding: 24, color: "#94A3B8" }}>Chargement des chiffres…</div> : s && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(190px, 1fr))", gap: 14, marginTop: 18 }}>
          <StatCard label="Patients (dossiers)" value={s.patients_total} accent="#185FA5" />
          <StatCard label="Patients hospitalisés" value={s.hospitalized} accent="#3B6D11" />
          <StatCard label="Lits occupés" value={`${s.beds_occupied}/${s.beds_total}`} sub={`${s.beds_free} libres · ${occRate}% d'occupation`} accent="#7C3AED" />
          <StatCard label="RDV aujourd'hui" value={s.appointments_today} accent="#0596DE" />
          <StatCard label="Consultations à documenter" value={s.consultations_todo} color={s.consultations_todo ? "#B45309" : "#0F172A"} accent="#F59E0B" />
          <StatCard label="Urgences en attente" value={s.er_waiting} color={s.er_waiting ? "#A32D2D" : "#0F172A"} accent="#DC2626" />
          <StatCard label="Interventions aujourd'hui" value={s.or_today} accent="#0E7490" />
        </div>
      )}

      <div style={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 12, padding: 20, marginTop: 22, maxWidth: 560 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: "#185FA5", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>Établissement</div>
        <Row label="Nom" value={est.name} />
        <Row label="Type" value={ctx.establishment_type === "hospital" ? "Hôpital" : ctx.establishment_type === "laboratory" ? "Laboratoire" : ctx.establishment_type} />
        <Row label="Votre rôle" value={roleLabel(ctx.establishment_role, ctx.establishment_type)} />
        {est.wilaya_code && <Row label="Wilaya" value={est.wilaya_code} />}
        {est.phone && <Row label="Téléphone" value={est.phone} />}
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid #F1F5F9" }}>
      <span style={{ fontSize: 13, color: "#64748B" }}>{label}</span>
      <span style={{ fontSize: 14, color: "#0F172A", fontWeight: 500 }}>{value || "—"}</span>
    </div>
  );
}

// Tableau de bord du jour pour un laboratoire (cahier des charges, section 5).
// Remplace Overview (pensé pour un hôpital : lits, bloc, urgences) — ces
// notions n'existent pas pour un labo. Calculé a partir des arrivees reelles
// (reception_queue), pas des rendez-vous : beaucoup de patients de labo
// n'ont pas de compte ni de RDV en ligne.
function LabOverview({ ctx, est }) {
  const [s, setS] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    axios.get(`${API_URL}/api/sih/reception/stats`, getAuthConfig()).then((r) => setS(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);
  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: 8 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: "#0F172A", margin: 0 }}>Vue d'ensemble</h1>
        {s && <span style={{ fontSize: 13, color: "#94A3B8" }}>Aujourd'hui · {s.date}</span>}
      </div>

      {loading ? <div style={{ padding: 24, color: "#94A3B8" }}>Chargement des chiffres…</div> : s && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(190px, 1fr))", gap: 14, marginTop: 18 }}>
          <StatCard label="Patients arrivés" value={s.patients_arrives} accent="#185FA5" />
          <StatCard label="En attente" value={s.en_attente} color={s.en_attente ? "#B45309" : "#0F172A"} accent="#F59E0B" />
          <StatCard label="Pris en charge" value={s.pris_en_charge} accent="#3730A3" />
          <StatCard label="Terminés" value={s.termines} accent="#3B6D11" />
          <StatCard label="Avec RDV" value={s.avec_rdv} sub={`${s.sans_rdv} sans RDV`} accent="#0596DE" />
          <StatCard label="Dossiers incomplets" value={s.dossiers_incomplets} color={s.dossiers_incomplets ? "#A32D2D" : "#0F172A"} accent="#DC2626" />
          <StatCard label="Attente moyenne" value={`${s.temps_attente_moyen_min} min`} accent="#7C3AED" />
          <StatCard label="Prélèvements terminés" value={s.prelevements_termines} accent="#0E7490" />
        </div>
      )}

      <div style={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 12, padding: 20, marginTop: 22, maxWidth: 560 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: "#185FA5", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>Établissement</div>
        <Row label="Nom" value={est.name} />
        <Row label="Type" value="Laboratoire" />
        <Row label="Votre rôle" value={roleLabel(ctx.establishment_role, ctx.establishment_type)} />
        {est.wilaya_code && <Row label="Wilaya" value={est.wilaya_code} />}
        {est.phone && <Row label="Téléphone" value={est.phone} />}
      </div>
    </div>
  );
}

function ServicesModule({ can }) {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [form, setForm] = useState({ name: "", code: "", head_name: "", capacity: "", description: "", staffing: [] });

  const load = () => {
    setLoading(true);
    axios
      .get(`${API_URL}/api/sih/services`, getAuthConfig())
      .then((r) => setServices(r.data.services || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const resetForm = () => { setForm({ name: "", code: "", head_name: "", capacity: "", description: "", staffing: [] }); setEditing(null); setError(""); };

  const save = async () => {
    if (!form.name.trim()) { setError("Le nom du service est obligatoire"); return; }
    setSaving(true); setError("");
    const payload = { ...form, capacity: form.capacity ? parseInt(form.capacity, 10) : null };
    try {
      if (editing) await axios.put(`${API_URL}/api/sih/services/${editing}`, payload, getAuthConfig());
      else await axios.post(`${API_URL}/api/sih/services`, payload, getAuthConfig());
      setShowForm(false); resetForm(); load();
    } catch (e) {
      setError(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Erreur lors de l'enregistrement");
    } finally { setSaving(false); }
  };

  const startEdit = (s) => {
    setEditing(s.service_id);
    setForm({ name: s.name || "", code: s.code || "", head_name: s.head_name || "", capacity: s.capacity != null ? String(s.capacity) : "", description: s.description || "", staffing: s.staffing || [] });
    setError(""); setShowForm(true);
  };

  const doDelete = async () => {
    if (!toDelete) return;
    try { await axios.delete(`${API_URL}/api/sih/services/${toDelete.service_id}`, getAuthConfig()); setToDelete(null); load(); }
    catch (e) { setError("Erreur lors de la suppression"); setToDelete(null); }
    finally { setSaving(false); }
  };

  const [photoUploading, setPhotoUploading] = useState("");
  const uploadPhoto = async (serviceId, file) => {
    if (!file) return;
    setPhotoUploading(serviceId); setError("");
    const fd = new FormData(); fd.append("file", file);
    try { await axios.post(`${API_URL}/api/sih/services/${serviceId}/photo`, fd, getUploadConfig()); load(); }
    catch (e) { setError(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Echec de l'envoi de la photo"); }
    finally { setPhotoUploading(""); }
  };

  const inputStyle = { width: "100%", padding: "9px 12px", border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 14, boxSizing: "border-box", background: "#fff" };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: "#0F172A", margin: 0 }}>Services</h1>
        {can("services:write") && <button onClick={() => { resetForm(); setShowForm(true); }} style={{ padding: "9px 18px", background: "#185FA5", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, cursor: "pointer" }}>+ Nouveau service</button>}
      </div>

      {error && !showForm && (
        <div style={{ background: "#FCEBEB", border: "1px solid #F7C1C1", color: "#A32D2D", borderRadius: 8, padding: "10px 14px", marginTop: 14, fontSize: 13 }}>{error}</div>
      )}

      {showForm && (
        <div style={{ background: "#fff", border: "1px solid #B5D4F4", borderRadius: 12, padding: 20, marginTop: 16 }}>
          <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 14, color: "#0F172A" }}>{editing ? "Modifier le service" : "Nouveau service"}</div>
          {error && <div style={{ background: "#FCEBEB", border: "1px solid #F7C1C1", color: "#A32D2D", borderRadius: 8, padding: "10px 14px", marginBottom: 12, fontSize: 13 }}>{error}</div>}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <input style={inputStyle} placeholder="Nom du service * (ex: Oncologie)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <input style={inputStyle} placeholder="Code (ex: ONCO)" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} />
            <input style={inputStyle} placeholder="Chef de service" value={form.head_name} onChange={(e) => setForm({ ...form, head_name: e.target.value })} />
            <input style={inputStyle} type="number" placeholder="Capacite (nb de lits)" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} />
          </div>
          <textarea style={{ ...inputStyle, marginTop: 12, minHeight: 60, resize: "vertical" }} placeholder="Description (optionnel)" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          <div style={{ marginTop: 16, borderTop: "1px solid #E2E8F0", paddingTop: 12 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#334155", marginBottom: 4 }}>Effectif minimum requis</div>
            <div style={{ fontSize: 12, color: "#94A3B8", marginBottom: 10 }}>Definit, pour chaque type de poste, le nombre d'agents necessaires. Sert a detecter le sous-effectif.</div>
            {(form.staffing || []).map((r, i) => (
              <div key={i} style={{ display: "flex", gap: 8, marginBottom: 8, alignItems: "center" }}>
                <select style={{ ...inputStyle, flex: 1 }} value={r.shift_type} onChange={(e) => { const c = [...form.staffing]; c[i] = { ...c[i], shift_type: e.target.value }; setForm({ ...form, staffing: c }); }}>
                  {[["matin", "Matin"], ["journee", "Journee"], ["apres_midi", "Apres-midi"], ["soir", "Soir"], ["nuit", "Nuit"], ["garde", "Garde"], ["astreinte", "Astreinte"]].map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
                <select style={{ ...inputStyle, flex: 1 }} value={r.role || ""} onChange={(e) => { const c = [...form.staffing]; c[i] = { ...c[i], role: e.target.value || null }; setForm({ ...form, staffing: c }); }}>
                  <option value="">Tout role</option>
                  {Object.keys(ROLE_LABELS).map((k) => <option key={k} value={k}>{ROLE_LABELS[k]}</option>)}
                </select>
                <input type="number" min="1" style={{ ...inputStyle, width: 70 }} value={r.min_count} onChange={(e) => { const c = [...form.staffing]; c[i] = { ...c[i], min_count: parseInt(e.target.value, 10) || 1 }; setForm({ ...form, staffing: c }); }} />
                <input style={{ ...inputStyle, flex: 1 }} placeholder="Competence (option.)" value={r.skill || ""} onChange={(e) => { const c = [...form.staffing]; c[i] = { ...c[i], skill: e.target.value || null }; setForm({ ...form, staffing: c }); }} />
                <button onClick={() => setForm({ ...form, staffing: form.staffing.filter((_, j) => j !== i) })} style={{ background: "none", border: "none", color: "#A32D2D", cursor: "pointer", fontSize: 18, padding: "0 4px" }}>×</button>
              </div>
            ))}
            <button onClick={() => setForm({ ...form, staffing: [...(form.staffing || []), { shift_type: "journee", role: null, min_count: 1, skill: null }] })} style={{ padding: "6px 12px", background: "#fff", border: "1px dashed #CBD5E1", borderRadius: 8, fontSize: 13, cursor: "pointer", color: "#185FA5" }}>+ Ajouter une exigence</button>
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
            <button onClick={save} disabled={saving} style={{ padding: "8px 18px", background: "#185FA5", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, cursor: "pointer" }}>{saving ? "Enregistrement..." : "Enregistrer"}</button>
            <button onClick={() => { setShowForm(false); resetForm(); }} style={{ padding: "8px 18px", background: "transparent", border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 14, cursor: "pointer" }}>Annuler</button>
          </div>
        </div>
      )}

      <div style={{ marginTop: 20, background: "#fff", border: "1px solid #E2E8F0", borderRadius: 12, overflow: "hidden" }}>
        {loading ? (
          <div style={{ padding: 30, textAlign: "center", color: "#94A3B8", fontSize: 14 }}>Chargement...</div>
        ) : services.length === 0 ? (
          <div style={{ padding: 30, textAlign: "center", color: "#94A3B8", fontSize: 14 }}>Aucun service. Cliquez sur « Nouveau service ».</div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#F8FAFC", textAlign: "left" }}>
                {["Service", "Code", "Chef de service", "Capacite", "Actions"].map((h) => (
                  <th key={h} style={{ padding: "10px 16px", fontSize: 12, color: "#64748B", fontWeight: 600 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {services.map((s) => (
                <tr key={s.service_id} style={{ borderTop: "1px solid #F1F5F9" }}>
                  <td style={{ padding: "12px 16px", fontSize: 14, fontWeight: 500, color: "#0F172A" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      {s.photo_url ? <img src={s.photo_url} alt={s.name} style={{ width: 40, height: 40, borderRadius: 8, objectFit: "cover", border: "1px solid #E2E8F0" }} /> : <div style={{ width: 40, height: 40, borderRadius: 8, background: "#F1F5F9", display: "flex", alignItems: "center", justifyContent: "center", color: "#94A3B8", fontSize: 16 }}>+</div>}
                      <div>{s.name}{s.description && <div style={{ fontSize: 12, color: "#94A3B8", fontWeight: 400 }}>{s.description}</div>}</div>
                    </div>
                  </td>
                  <td style={{ padding: "12px 16px", fontSize: 13, color: "#475569" }}>{s.code || "—"}</td>
                  <td style={{ padding: "12px 16px", fontSize: 13, color: "#475569" }}>{s.head_name || "—"}</td>
                  <td style={{ padding: "12px 16px", fontSize: 13, color: "#475569" }}>{s.capacity != null ? `${s.capacity} lits` : "—"}</td>
                  <td style={{ padding: "12px 16px" }}>
                    <span style={{ display: "flex", gap: 12, alignItems: "center" }}>
                      {can("services:write") && <>
                        <label style={{ fontSize: 13, color: "#185FA5", cursor: "pointer" }}>{photoUploading === s.service_id ? "Envoi..." : "Photo"}<input type="file" accept="image/*" style={{ display: "none" }} onChange={(e) => uploadPhoto(s.service_id, e.target.files[0])} /></label>
                        <button onClick={() => startEdit(s)} style={{ fontSize: 13, color: "#185FA5", background: "none", border: "none", cursor: "pointer" }}>Modifier</button>
                        <button onClick={() => setToDelete(s)} style={{ fontSize: 13, color: "#A32D2D", background: "none", border: "none", cursor: "pointer" }}>Supprimer</button></>}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {toDelete && (
        <div onClick={() => setToDelete(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 12, width: "90%", maxWidth: 400, padding: 24 }}>
            <div style={{ fontSize: 16, fontWeight: 600, color: "#0F172A", marginBottom: 8 }}>Supprimer ce service ?</div>
            <div style={{ fontSize: 13, color: "#64748B", marginBottom: 20, lineHeight: 1.5 }}><b>{toDelete.name}</b> sera supprime. Cette action est irreversible.</div>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button onClick={() => setToDelete(null)} style={{ fontSize: 13, padding: "8px 16px", borderRadius: 8, border: "1px solid #CBD5E1", background: "#fff", color: "#64748B", cursor: "pointer" }}>Annuler</button>
              <button onClick={doDelete} style={{ fontSize: 13, padding: "8px 16px", borderRadius: 8, border: "none", background: "#A32D2D", color: "#fff", cursor: "pointer" }}>Supprimer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


const ROLE_LABELS = {
  gestionnaire_hopital: "Gestionnaire",
  chef_service: "Chef de service",
  cadre_sante: "Cadre de sante",
  medecin: "Medecin",
  interne: "Interne",
  infirmier: "Infirmier",
  aide_soignant: "Aide-soignant",
  secretaire: "Secretaire",
  agent_laboratoire: "Agent laboratoire",
  agent_imagerie: "Agent imagerie",
  pharmacien: "Pharmacien",
  caissier: "Caissier",
  biologiste: "Biologiste",
};
const STAFF_ROLES = ["chef_service", "cadre_sante", "medecin", "interne", "infirmier", "aide_soignant", "secretaire", "agent_laboratoire", "agent_imagerie", "pharmacien", "caissier"];
const SCOPED_STAFF_ROLES = ["chef_service", "cadre_sante", "medecin", "interne", "infirmier", "aide_soignant"];
function StaffModule({ can, ctx }) {
  const [staff, setStaff] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [createdInfo, setCreatedInfo] = useState(null);
  const [editHasAccount, setEditHasAccount] = useState(false);
  const [form, setForm] = useState({ full_name: "", role: "infirmier", service_id: "", phone: "", email: "", create_account: false,
    matricule: "", skills: [], contract_type: "", weekly_hours: "", work_rate: "", availability: [], shift_preferences: [], restrictions: "" });
  const [showPlanif, setShowPlanif] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([
      axios.get(`${API_URL}/api/sih/staff`, getAuthConfig()),
      axios.get(`${API_URL}/api/sih/services`, getAuthConfig()),
    ]).then(([st, sv]) => {
      setStaff(st.data.staff || []);
      setServices(sv.data.services || []);
    }).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const resetForm = () => { setForm({ full_name: "", role: "infirmier", service_id: "", phone: "", email: "", create_account: false, matricule: "", skills: [], contract_type: "", weekly_hours: "", work_rate: "", availability: [], shift_preferences: [], restrictions: "" }); setEditing(null); setEditHasAccount(false); setShowPlanif(false); setError(""); };

  const save = async () => {
    if (!form.full_name.trim()) { setError("Le nom est obligatoire"); return; }
    if (SCOPED_STAFF_ROLES.includes(form.role) && !form.service_id) { setError("L'affectation à un service est obligatoire pour ce rôle"); return; }
    if (form.create_account && !form.email.trim()) { setError("Un email est requis pour creer un compte connectable"); return; }
    setSaving(true); setError(""); setCreatedInfo(null);
    try {
      if (editing) {
        const r = await axios.put(`${API_URL}/api/sih/staff/${editing}`, {
          full_name: form.full_name, role: form.role, service_id: form.service_id, phone: form.phone,
          create_account: form.create_account, email: form.email,
          // Donnees de planification : sans elles, une simple edition les effacerait.
          matricule: form.matricule, skills: form.skills, contract_type: form.contract_type,
          weekly_hours: form.weekly_hours === "" ? null : Number(form.weekly_hours),
          work_rate: form.work_rate === "" ? null : Number(form.work_rate),
          availability: form.availability, shift_preferences: form.shift_preferences,
          restrictions: form.restrictions,
        }, getAuthConfig());
        if (r.data.temp_password) setCreatedInfo({ email: form.email, password: r.data.temp_password });
      } else {
        // Correctif : "form" etait envoye brut, avec weekly_hours/work_rate a
        // "" quand la section "Donnees de planification" reste fermee (cas le
        // plus courant a la creation) — Pydantic rejette "" pour un float
        // (422), la modification (PUT ci-dessus) avait deja le bon correctif.
        const r = await axios.post(`${API_URL}/api/sih/staff`, {
          ...form,
          weekly_hours: form.weekly_hours === "" ? null : Number(form.weekly_hours),
          work_rate: form.work_rate === "" ? null : Number(form.work_rate),
        }, getAuthConfig());
        if (r.data.temp_password) setCreatedInfo({ email: form.email, password: r.data.temp_password });
      }
      setShowForm(false); resetForm(); load();
    } catch (e) {
      setError(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Erreur lors de l'enregistrement");
    } finally { setSaving(false); }
  };

  const startEdit = (m) => {
    setEditing(m.staff_id);
    setEditHasAccount(!!m.user_id);
    setForm({ full_name: m.full_name || "", role: m.role || "infirmier", service_id: m.service_id || "", phone: m.phone || "", email: m.email || "", create_account: false,
      matricule: m.matricule || "", skills: m.skills || [], contract_type: m.contract_type || "", weekly_hours: m.weekly_hours || "", work_rate: m.work_rate || "", availability: m.availability || [], shift_preferences: m.shift_preferences || [], restrictions: m.restrictions || "" });
    setError(""); setShowForm(true);
  };

  const doDelete = async () => {
    if (!toDelete) return;
    try { await axios.delete(`${API_URL}/api/sih/staff/${toDelete.staff_id}`, getAuthConfig()); setToDelete(null); load(); }
    catch (e) { setError("Erreur lors de la suppression"); setToDelete(null); }
    finally { setSaving(false); }
  };

  const inputStyle = { width: "100%", padding: "9px 12px", border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 14, boxSizing: "border-box", background: "#fff" };
  const noService = services.length === 0;

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: "#0F172A", margin: 0 }}>Personnel</h1>
        {can("staff:write") && <button onClick={() => { resetForm(); setCreatedInfo(null); setShowForm(true); }} disabled={noService}
          title={noService ? "Creez d'abord un service" : ""}
          style={{ padding: "9px 18px", background: noService ? "#94A3B8" : "#185FA5", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, cursor: noService ? "not-allowed" : "pointer" }}>+ Ajouter du personnel</button>}
      </div>

      {noService && (
        <div style={{ background: "#FAEEDA", border: "1px solid #F0D8A8", color: "#854F0B", borderRadius: 8, padding: "10px 14px", marginTop: 14, fontSize: 13 }}>
          Vous devez d'abord creer au moins un <b>service</b> avant d'ajouter du personnel.
        </div>
      )}

      {createdInfo && (
        <div style={{ background: "#EAF3DE", border: "1px solid #C5E0A5", color: "#3B6D11", borderRadius: 8, padding: "12px 14px", marginTop: 14, fontSize: 13 }}>
          Compte connectable cree. Identifiants a transmettre : <b>{createdInfo.email}</b> / mot de passe temporaire : <b>{createdInfo.password}</b>
        </div>
      )}

      {error && !showForm && (
        <div style={{ background: "#FCEBEB", border: "1px solid #F7C1C1", color: "#A32D2D", borderRadius: 8, padding: "10px 14px", marginTop: 14, fontSize: 13 }}>{error}</div>
      )}

      {showForm && (
        <div style={{ background: "#fff", border: "1px solid #B5D4F4", borderRadius: 12, padding: 20, marginTop: 16 }}>
          <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 14, color: "#0F172A" }}>{editing ? "Modifier le personnel" : "Nouveau personnel"}</div>
          {error && <div style={{ background: "#FCEBEB", border: "1px solid #F7C1C1", color: "#A32D2D", borderRadius: 8, padding: "10px 14px", marginBottom: 12, fontSize: 13 }}>{error}</div>}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <input style={inputStyle} placeholder="Nom complet *" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} />
            <select style={inputStyle} value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
              {STAFF_ROLES.map((r) => <option key={r} value={r}>{roleLabel(r)}</option>)}
            </select>
            <select style={inputStyle} value={form.service_id} onChange={(e) => setForm({ ...form, service_id: e.target.value })}>
              <option value="">{SCOPED_STAFF_ROLES.includes(form.role) ? "Service * ..." : "Service (optionnel) ..."}</option>
              {services.map((s) => <option key={s.service_id} value={s.service_id}>{s.name}</option>)}
            </select>
            <input style={inputStyle} placeholder="Telephone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          </div>
          {editing && editHasAccount ? (
            <div style={{ marginTop: 12, fontSize: 13, color: "#3B6D11", background: "#F0F7E6", border: "1px solid #D6E8BF", borderRadius: 8, padding: "8px 12px" }}>
              ✓ Ce membre a déjà un compte connectable.
            </div>
          ) : (
            <div style={{ marginTop: 12 }}>
              <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#334155", cursor: "pointer" }}>
                <input type="checkbox" checked={form.create_account} onChange={(e) => setForm({ ...form, create_account: e.target.checked })} />
                {editing ? "Activer un compte connectable (l'employé pourra se connecter)" : "Creer un compte connectable (l'employe pourra se connecter a l'espace)"}
              </label>
              {form.create_account && (
                <input style={{ ...inputStyle, marginTop: 10 }} placeholder="Email * (pour le compte)" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              )}
            </div>
          )}
          <div style={{ marginTop: 16, borderTop: "1px solid #E2E8F0", paddingTop: 12 }}>
            <button onClick={() => setShowPlanif(!showPlanif)} style={{ background: "none", border: "none", padding: 0, cursor: "pointer", fontSize: 13, fontWeight: 600, color: "#185FA5" }}>
              {showPlanif ? "\u25BE" : "\u25B8"} Donnees de planification {showPlanif ? "" : "(matricule, competences, contrat, disponibilites)"}
            </button>
            {showPlanif && (
              <div style={{ marginTop: 12 }}>
                <div style={{ display: "flex", gap: 10, marginBottom: 10 }}>
                  <input style={{ ...inputStyle, flex: 1 }} placeholder="Matricule" value={form.matricule} onChange={(e) => setForm({ ...form, matricule: e.target.value })} />
                  <select style={{ ...inputStyle, flex: 1 }} value={form.contract_type} onChange={(e) => setForm({ ...form, contract_type: e.target.value })}>
                    <option value="">Type de contrat…</option>
                    {["CDI", "CDD", "Vacataire", "Stagiaire", "Contractuel"].map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div style={{ display: "flex", gap: 10, marginBottom: 10 }}>
                  <input type="number" style={{ ...inputStyle, flex: 1 }} placeholder="Heures/semaine (ex: 40)" value={form.weekly_hours} onChange={(e) => setForm({ ...form, weekly_hours: e.target.value })} />
                  <input type="number" style={{ ...inputStyle, flex: 1 }} placeholder="Quotite % (ex: 100)" value={form.work_rate} onChange={(e) => setForm({ ...form, work_rate: e.target.value })} />
                </div>
                <div style={{ marginBottom: 10 }}>
                  <div style={{ fontSize: 12, color: "#64748B", marginBottom: 5 }}>Competences</div>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    {["Reanimation", "Bloc operatoire", "Urgences", "Tri", "Pediatrie", "Maternite", "Dialyse", "Oncologie", "Prelevements", "Radiologie", "Encadrement"].map((sk) => {
                      const on = (form.skills || []).includes(sk);
                      return <button key={sk} onClick={() => setForm({ ...form, skills: on ? form.skills.filter((x) => x !== sk) : [...(form.skills || []), sk] })} style={{ padding: "4px 10px", borderRadius: 14, fontSize: 12, cursor: "pointer", border: "1px solid " + (on ? "#185FA5" : "#CBD5E1"), background: on ? "#E6F1FB" : "#fff", color: on ? "#185FA5" : "#64748B" }}>{sk}</button>;
                    })}
                  </div>
                </div>
                <div style={{ marginBottom: 10 }}>
                  <div style={{ fontSize: 12, color: "#64748B", marginBottom: 5 }}>Jours de disponibilite</div>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    {[["monday", "Lun"], ["tuesday", "Mar"], ["wednesday", "Mer"], ["thursday", "Jeu"], ["friday", "Ven"], ["saturday", "Sam"], ["sunday", "Dim"]].map(([k, l]) => {
                      const on = (form.availability || []).includes(k);
                      return <button key={k} onClick={() => setForm({ ...form, availability: on ? form.availability.filter((x) => x !== k) : [...(form.availability || []), k] })} style={{ padding: "4px 12px", borderRadius: 14, fontSize: 12, cursor: "pointer", border: "1px solid " + (on ? "#3B6D11" : "#CBD5E1"), background: on ? "#EAF3DE" : "#fff", color: on ? "#3B6D11" : "#64748B" }}>{l}</button>;
                    })}
                  </div>
                </div>
                <div style={{ marginBottom: 10 }}>
                  <div style={{ fontSize: 12, color: "#64748B", marginBottom: 5 }}>Postes preferes</div>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    {[["matin", "Matin"], ["journee", "Journee"], ["apres_midi", "Apres-midi"], ["soir", "Soir"], ["nuit", "Nuit"], ["garde", "Garde"], ["astreinte", "Astreinte"]].map(([k, l]) => {
                      const on = (form.shift_preferences || []).includes(k);
                      return <button key={k} onClick={() => setForm({ ...form, shift_preferences: on ? form.shift_preferences.filter((x) => x !== k) : [...(form.shift_preferences || []), k] })} style={{ padding: "4px 12px", borderRadius: 14, fontSize: 12, cursor: "pointer", border: "1px solid " + (on ? "#854F0B" : "#CBD5E1"), background: on ? "#FEF3C7" : "#fff", color: on ? "#854F0B" : "#64748B" }}>{l}</button>;
                    })}
                  </div>
                </div>
                <input style={inputStyle} placeholder="Restrictions (ex: pas de nuit, poste amenage)" value={form.restrictions} onChange={(e) => setForm({ ...form, restrictions: e.target.value })} />
              </div>
            )}
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
            <button onClick={save} disabled={saving} style={{ padding: "8px 18px", background: "#185FA5", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, cursor: "pointer" }}>{saving ? "Enregistrement..." : "Enregistrer"}</button>
            <button onClick={() => { setShowForm(false); resetForm(); }} style={{ padding: "8px 18px", background: "transparent", border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 14, cursor: "pointer" }}>Annuler</button>
          </div>
        </div>
      )}

      <div style={{ marginTop: 20, background: "#fff", border: "1px solid #E2E8F0", borderRadius: 12, overflow: "hidden" }}>
        {loading ? (
          <div style={{ padding: 30, textAlign: "center", color: "#94A3B8", fontSize: 14 }}>Chargement...</div>
        ) : staff.length === 0 ? (
          <div style={{ padding: 30, textAlign: "center", color: "#94A3B8", fontSize: 14 }}>Aucun personnel. Cliquez sur « Ajouter du personnel ».</div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#F8FAFC", textAlign: "left" }}>
                {["Nom", "Role", "Service", "Telephone", "Compte", "Actions"].map((h) => (
                  <th key={h} style={{ padding: "10px 16px", fontSize: 12, color: "#64748B", fontWeight: 600 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {staff.map((m) => (
                <tr key={m.staff_id} style={{ borderTop: "1px solid #F1F5F9" }}>
                  <td style={{ padding: "12px 16px", fontSize: 14, fontWeight: 500, color: "#0F172A" }}>{m.full_name}</td>
                  <td style={{ padding: "12px 16px", fontSize: 13, color: "#475569" }}>{roleLabel(m.role)}</td>
                  <td style={{ padding: "12px 16px", fontSize: 13, color: "#475569" }}>{m.service_name || "—"}</td>
                  <td style={{ padding: "12px 16px", fontSize: 13, color: "#475569" }}>{m.phone || "—"}</td>
                  <td style={{ padding: "12px 16px", fontSize: 13 }}>{m.user_id ? <span style={{ color: "#3B6D11" }}>Oui</span> : <span style={{ color: "#94A3B8" }}>—</span>}</td>
                  <td style={{ padding: "12px 16px" }}>
                    <span style={{ display: "flex", gap: 12 }}>
                      {can("staff:write") && <><button onClick={() => startEdit(m)} style={{ fontSize: 13, color: "#185FA5", background: "none", border: "none", cursor: "pointer" }}>Modifier</button>
                      <button onClick={() => setToDelete(m)} style={{ fontSize: 13, color: "#A32D2D", background: "none", border: "none", cursor: "pointer" }}>Supprimer</button></>}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {toDelete && (
        <div onClick={() => setToDelete(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 12, width: "90%", maxWidth: 400, padding: 24 }}>
            <div style={{ fontSize: 16, fontWeight: 600, color: "#0F172A", marginBottom: 8 }}>Supprimer ce membre ?</div>
            <div style={{ fontSize: 13, color: "#64748B", marginBottom: 20, lineHeight: 1.5 }}><b>{toDelete.full_name}</b> sera retire de l'annuaire et perdra immediatement tout acces a cet etablissement. Son compte de connexion eventuel (utilisable ailleurs) n'est pas supprime, mais son acces ici est bien revoque.</div>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button onClick={() => setToDelete(null)} style={{ fontSize: 13, padding: "8px 16px", borderRadius: 8, border: "1px solid #CBD5E1", background: "#fff", color: "#64748B", cursor: "pointer" }}>Annuler</button>
              <button onClick={doDelete} style={{ fontSize: 13, padding: "8px 16px", borderRadius: 8, border: "none", background: "#A32D2D", color: "#fff", cursor: "pointer" }}>Supprimer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


function ageFrom(dob) {
  if (!dob) return null;
  const d = new Date(dob);
  if (isNaN(d)) return null;
  const diff = Date.now() - d.getTime();
  return Math.floor(diff / (365.25 * 24 * 3600 * 1000));
}

const MEDICAL_OPTIONS = {
  medical_history: ["Diabete", "Hypertension arterielle", "Asthme", "Cardiopathie", "Insuffisance renale", "Hepatite", "Cancer", "Tuberculose", "Epilepsie", "AVC", "Hypothyroidie"],
  surgical_history: ["Appendicectomie", "Cesarienne", "Cholecystectomie", "Hernie", "Amygdalectomie", "Chirurgie cardiaque", "Chirurgie orthopedique"],
  family_history: ["Diabete", "Hypertension", "Cancer", "Cardiopathie", "Maladie genetique", "AVC"],
  allergies: ["Penicilline", "Aspirine", "Iode", "Latex", "Arachides", "Fruits de mer", "Pollen"],
  current_treatments: ["Antihypertenseur", "Antidiabetique", "Anticoagulant", "Corticoides", "Chimiotherapie", "Insuline", "Antalgiques"],
};

function MultiSelectField({ label, options, value, onChange }) {
  const tokens = (value || "").split(",").map((t) => t.trim()).filter(Boolean);
  const selected = tokens.filter((t) => options.includes(t));
  const autreText = tokens.filter((t) => !options.includes(t)).join(", ");
  const rebuild = (sel, autre) => {
    const ordered = options.filter((o) => sel.includes(o));
    const parts = [...ordered];
    if (autre.trim()) parts.push(autre.trim());
    onChange(parts.join(", "));
  };
  const toggle = (opt) => {
    const next = selected.includes(opt) ? selected.filter((x) => x !== opt) : [...selected, opt];
    rebuild(next, autreText);
  };
  return (
    <div>
      <label style={{ fontSize: 12, color: "#64748B", marginBottom: 6, display: "block" }}>{label}</label>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
        {options.map((opt) => {
          const on = selected.includes(opt);
          return (
            <button type="button" key={opt} onClick={() => toggle(opt)}
              style={{ fontSize: 12, padding: "5px 10px", borderRadius: 16, cursor: "pointer",
                border: on ? "1px solid #185FA5" : "1px solid #CBD5E1",
                background: on ? "#E6F1FB" : "#fff", color: on ? "#185FA5" : "#475569" }}>
              {on ? "\u2713 " : ""}{opt}
            </button>
          );
        })}
      </div>
      <input placeholder="Autre (preciser, separer par des virgules)" value={autreText}
        onChange={(e) => rebuild(selected, e.target.value)}
        style={{ width: "100%", padding: "8px 12px", border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 13, boxSizing: "border-box" }} />
    </div>
  );
}

// Gestion documentaire (cahier des charges labo, section 15).
const DOCUMENT_CATEGORY_LABELS = {
  ordonnance: "Ordonnance", administratif: "Document administratif", patient: "Transmis par le patient",
};

function PatientsModule({ can }) {
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailAdms, setDetailAdms] = useState([]);
  const [dossier, setDossier] = useState(null);
  const [dInvoices, setDInvoices] = useState(null);
  const [dDocuments, setDDocuments] = useState(null);
  const [docUploading, setDocUploading] = useState(false);
  const [docCategory, setDocCategory] = useState("administratif");
  const [docLabel, setDocLabel] = useState("");
  const [docError, setDocError] = useState("");
  const [dMessages, setDMessages] = useState(null);
  const [msgBody, setMsgBody] = useState("");
  const [msgFile, setMsgFile] = useState(null);
  const [msgSending, setMsgSending] = useState(false);
  const [msgError, setMsgError] = useState("");
  // RGPD (cahier des charges labo, section 33) : export et anonymisation.
  const [rgpdBusy, setRgpdBusy] = useState(false);
  const [rgpdError, setRgpdError] = useState("");
  const [rgpdDone, setRgpdDone] = useState("");
  const [anonymizeText, setAnonymizeText] = useState("");
  // Import de patients par CSV (cahier des charges labo, section 39).
  const [showImport, setShowImport] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importPreview, setImportPreview] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [importBusy, setImportBusy] = useState(false);
  const [importError, setImportError] = useState("");
  const [dTab, setDTab] = useState("synthese");
  const [openConsId, setOpenConsId] = useState(null);
  const [openRxId, setOpenRxId] = useState(null);
  const [hospitalName, setHospitalName] = useState("");
  const [search, setSearch] = useState("");
  // Recherche cote SERVEUR : la liste est paginee, un filtrage local ne verrait
  // que la page courante. `debounced` evite une requete a chaque frappe.
  const [debounced, setDebounced] = useState("");
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const PAGE_SIZE = 50;
  const [fSex, setFSex] = useState("");
  const [fBlood, setFBlood] = useState("");
  const [fStatus, setFStatus] = useState("");
  const empty = { first_name: "", last_name: "", date_of_birth: "", sex: "", national_id: "", phone: "", email: "", address: "", wilaya_code: "", blood_group: "", emergency_contact_name: "", emergency_contact_phone: "", profession: "", marital_status: "", insurance: "", medical_history: "", surgical_history: "", family_history: "", allergies: "", current_treatments: "", notes: "" };
  const [form, setForm] = useState(empty);

  const load = () => {
    setLoading(true);
    const params = new URLSearchParams({ limit: PAGE_SIZE, skip: page * PAGE_SIZE });
    if (debounced) params.set("search", debounced);
    axios.get(`${API_URL}/api/sih/patients?${params}`, getAuthConfig())
      .then((r) => { setPatients(r.data.patients || []); setTotal(r.data.total ?? (r.data.patients || []).length); })
      .catch(() => {}).finally(() => setLoading(false));
  };
  // Debounce de la saisie : 350 ms sans frappe avant d'interroger le serveur.
  useEffect(() => {
    const t = setTimeout(() => { setDebounced(search.trim()); setPage(0); }, 350);
    return () => clearTimeout(t);
  }, [search]);
  useEffect(() => { load(); }, [debounced, page]);
  // Recherche globale (cahier des charges, section 40 : "recherche globale"
  // parmi les principes UX) : la modale de recherche (GlobalSearchModal,
  // niveau HopitalDashboard) bascule sur cet onglet et transmet la requete
  // par ce meme mecanisme d'evenement deja utilise pour "aller a
  // l'ordonnance" (voir tabbibak:goToPrescription plus haut).
  useEffect(() => {
    const onGlobalSearch = (e) => setSearch(e.detail?.query || "");
    window.addEventListener("tabbibak:searchPatient", onGlobalSearch);
    return () => window.removeEventListener("tabbibak:searchPatient", onGlobalSearch);
  }, []);

  const resetForm = () => { setForm(empty); setEditing(null); setError(""); };

  const save = async () => {
    if (!form.first_name.trim() || !form.last_name.trim()) { setError("Nom et prenom obligatoires"); return; }
    setSaving(true); setError("");
    try {
      if (editing) await axios.put(`${API_URL}/api/sih/patients/${editing}`, form, getAuthConfig());
      else await axios.post(`${API_URL}/api/sih/patients`, form, getAuthConfig());
      setShowForm(false); resetForm();
      // Retour a la premiere page apres creation : la liste est triee par nom,
      // un nouveau dossier peut atterrir sur n'importe quelle page. Sans ce
      // retour, l'utilisateur ne verrait pas le patient qu'il vient de saisir.
      if (!editing && page !== 0) setPage(0); else load();
    } catch (e) { setError(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Erreur lors de l'enregistrement"); }
    finally { setSaving(false); }
  };

  const fetchFull = async (id) => {
    const r = await axios.get(`${API_URL}/api/sih/patients/${id}`, getAuthConfig());
    return r.data.patient;
  };
  const startEdit = async (p) => {
    try { const full = await fetchFull(p.patient_id); setEditing(p.patient_id); setForm({ ...empty, ...full }); setError(""); setShowForm(true); }
    catch (e) { setError("Impossible de charger le dossier"); }
  };
  const loadDocuments = (patientId) => {
    axios.get(`${API_URL}/api/sih/patients/${patientId}/documents`, getAuthConfig())
      .then((rd) => setDDocuments(rd.data.documents || [])).catch(() => setDDocuments([]));
  };
  // Messagerie labo <-> patient (cahier des charges labo, section 18).
  const loadMessages = (patientId) => {
    axios.get(`${API_URL}/api/sih/patients/${patientId}/messages`, getAuthConfig())
      .then((rm) => setDMessages(rm.data.messages || [])).catch(() => setDMessages([]));
  };
  const openDetail = async (p) => {
    setDTab("synthese"); setDossier(null); setDInvoices(null); setDDocuments(null); setDocError("");
    setDMessages(null); setMsgBody(""); setMsgFile(null); setMsgError("");
    setRgpdError(""); setRgpdDone(""); setAnonymizeText("");
    if (!hospitalName) { try { const me = await axios.get(`${API_URL}/api/sih/me`, getAuthConfig()); setHospitalName(me.data.establishment?.name || ""); } catch (e) {} }
    try {
      const r = await axios.get(`${API_URL}/api/sih/patients/${p.patient_id}/dossier`, getAuthConfig());
      setDossier(r.data);
      setDetail(r.data.patient);
      setDetailAdms(r.data.admissions || []);
      if (can("billing:read")) {
        axios.get(`${API_URL}/api/sih/patients/${p.patient_id}/invoices`, getAuthConfig()).then((ri) => setDInvoices(ri.data.invoices || [])).catch(() => setDInvoices([]));
      }
      if (can("documents:read")) loadDocuments(p.patient_id);
      if (can("messaging:read")) loadMessages(p.patient_id);
    } catch (e) {
      // repli : au moins la fiche
      try { const full = await fetchFull(p.patient_id); setDetail(full); setDetailAdms([]); }
      catch (e2) { setError("Impossible de charger le dossier"); }
    }
  };
  const doDelete = async () => {
    if (!toDelete) return;
    try { await axios.delete(`${API_URL}/api/sih/patients/${toDelete.patient_id}`, getAuthConfig()); setToDelete(null); load(); }
    catch (e) { setError("Erreur lors de la suppression"); setToDelete(null); }
  };

  // Gestion documentaire (cahier des charges labo, section 15).
  const uploadDocument = async (e) => {
    const file = e.target.files && e.target.files[0]; if (!file || !detail) return;
    setDocError(""); setDocUploading(true);
    const fd = new FormData();
    fd.append("file", file); fd.append("category", docCategory); fd.append("label", docLabel);
    try {
      await axios.post(`${API_URL}/api/sih/patients/${detail.patient_id}/documents`, fd, getUploadConfig());
      setDocLabel(""); loadDocuments(detail.patient_id);
    } catch (er) { setDocError(er.response?.data?.detail || "Erreur lors de l'envoi"); }
    finally { setDocUploading(false); e.target.value = ""; }
  };
  const viewDocument = async (doc) => {
    try {
      const r = await axios.get(`${API_URL}/api/sih/patients/${detail.patient_id}/documents/${doc.document_id}`, getAuthConfig());
      window.open(r.data.url, "_blank");
    } catch (e) { setDocError("Impossible d'ouvrir le document"); }
  };
  const deleteDocument = async (doc) => {
    try {
      await axios.delete(`${API_URL}/api/sih/patients/${detail.patient_id}/documents/${doc.document_id}`, getAuthConfig());
      loadDocuments(detail.patient_id);
    } catch (e) { setDocError("Erreur lors de la suppression"); }
  };

  // Messagerie labo <-> patient (cahier des charges labo, section 18).
  const sendMessage = async () => {
    if (!detail || (!msgBody.trim() && !msgFile)) return;
    setMsgError(""); setMsgSending(true);
    const fd = new FormData();
    fd.append("body", msgBody.trim());
    if (msgFile) fd.append("file", msgFile);
    try {
      await axios.post(`${API_URL}/api/sih/patients/${detail.patient_id}/messages`, fd, getUploadConfig());
      setMsgBody(""); setMsgFile(null); loadMessages(detail.patient_id);
    } catch (er) { setMsgError(er.response?.data?.detail || "Erreur lors de l'envoi du message"); }
    finally { setMsgSending(false); }
  };
  const viewMessageAttachment = async (m) => {
    try {
      const r = await axios.get(`${API_URL}/api/sih/patients/${detail.patient_id}/messages/${m.message_id}/attachment`, getAuthConfig());
      window.open(r.data.url, "_blank");
    } catch (e) { setMsgError("Impossible d'ouvrir la piece jointe"); }
  };

  // RGPD (cahier des charges labo, section 33).
  const exportPatientRgpd = async () => {
    if (!detail) return;
    setRgpdError(""); setRgpdDone(""); setRgpdBusy(true);
    try {
      const r = await axios.get(`${API_URL}/api/sih/patients/${detail.patient_id}/rgpd/export`, getAuthConfig());
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `donnees_${detail.patient_id}.json`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      setRgpdDone("Export telecharge.");
    } catch (e) { setRgpdError(e.response?.data?.detail || "Erreur lors de l'export"); }
    finally { setRgpdBusy(false); }
  };
  const anonymizePatientRgpd = async () => {
    if (!detail || anonymizeText.trim() !== "ANONYMISER") return;
    setRgpdError(""); setRgpdDone(""); setRgpdBusy(true);
    try {
      await axios.post(`${API_URL}/api/sih/patients/${detail.patient_id}/rgpd/anonymize`,
        { confirm: anonymizeText.trim() }, getAuthConfig());
      setAnonymizeText(""); setRgpdDone("Patient anonymise.");
      const full = await fetchFull(detail.patient_id);
      setDetail(full); load();
    } catch (e) { setRgpdError(e.response?.data?.detail || "Erreur lors de l'anonymisation"); }
    finally { setRgpdBusy(false); }
  };

  // Import de patients par CSV (cahier des charges labo, section 39).
  const openImport = () => { setShowImport(true); setImportFile(null); setImportPreview(null); setImportResult(null); setImportError(""); };
  const downloadImportTemplate = async () => {
    try {
      const r = await axios.get(`${API_URL}/api/sih/patients/import/template`, { ...getAuthConfig(), responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = "modele_import_patients.csv";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { setImportError("Impossible de telecharger le modele"); }
  };
  const previewImport = async () => {
    if (!importFile) return;
    setImportBusy(true); setImportError(""); setImportPreview(null); setImportResult(null);
    const fd = new FormData();
    fd.append("file", importFile);
    try {
      const r = await axios.post(`${API_URL}/api/sih/patients/import/preview`, fd, getUploadConfig());
      setImportPreview(r.data);
    } catch (e) { setImportError(e.response?.data?.detail || "Erreur lors de l'analyse du fichier"); }
    finally { setImportBusy(false); }
  };
  const commitImport = async () => {
    if (!importFile) return;
    setImportBusy(true); setImportError("");
    const fd = new FormData();
    fd.append("file", importFile);
    try {
      const r = await axios.post(`${API_URL}/api/sih/patients/import/commit`, fd, getUploadConfig());
      setImportResult(r.data); setImportPreview(null); load();
    } catch (e) { setImportError(e.response?.data?.detail || "Erreur lors de l'import"); }
    finally { setImportBusy(false); }
  };

  const iS = { width: "100%", padding: "9px 12px", border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 14, boxSizing: "border-box", background: "#fff" };
  const taS = { ...iS, minHeight: 56, resize: "vertical" };
  const lab = { fontSize: 12, color: "#64748B", marginBottom: 4, display: "block" };

  // La recherche texte est traitee par le serveur (sur toute la base) ; les
  // filtres ci-dessous restent locaux et ne portent donc que sur la page
  // affichee — c'est signale explicitement dans l'interface.
  const filtered = patients.filter((p) => {
    if (fSex && p.sex !== fSex) return false;
    if (fBlood && p.blood_group !== fBlood) return false;
    if (fStatus === "admitted" && !p.admitted) return false;
    if (fStatus === "not" && p.admitted) return false;
    return true;
  });

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: "#0F172A", margin: 0 }}>Patients</h1>
        <span style={{ display: "flex", gap: 10 }}>
          {can("patients:import") && <button onClick={openImport} style={{ padding: "9px 18px", background: "#fff", color: "#185FA5", border: "1px solid #B5D4F4", borderRadius: 8, fontSize: 14, cursor: "pointer" }}>Importer (CSV)</button>}
          {can("patients:create") && <button onClick={() => { resetForm(); setShowForm(true); }} style={{ padding: "9px 18px", background: "#185FA5", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, cursor: "pointer" }}>+ Nouveau patient</button>}
        </span>
      </div>

      {showImport && (
        <div onClick={() => setShowImport(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "flex-start", justifyContent: "center", zIndex: 1000, overflowY: "auto", paddingBottom: 40 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 12, width: "94%", maxWidth: 720, padding: 24, marginTop: 40 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
              <h3 style={{ fontSize: 17, fontWeight: 700, color: "#0F172A", margin: 0 }}>Importer des patients (CSV)</h3>
              <button onClick={() => setShowImport(false)} style={{ background: "none", border: "none", fontSize: 20, color: "#94A3B8", cursor: "pointer" }}>×</button>
            </div>
            <div style={{ fontSize: 13, color: "#64748B", marginTop: 8, marginBottom: 14, lineHeight: 1.5 }}>
              Colonnes reconnues : Nom et Prenom (obligatoires), Date de naissance (AAAA-MM-JJ), Sexe, Telephone, Email, Adresse, Wilaya, Groupe sanguin, Identifiant national. Aucun patient n'est cree tant que vous n'avez pas verifie l'aperçu et confirme.
            </div>
            <button onClick={downloadImportTemplate} style={{ fontSize: 13, color: "#185FA5", background: "none", border: "none", padding: 0, cursor: "pointer", marginBottom: 14, display: "block" }}>Telecharger le modele CSV</button>

            {importError && <div style={{ background: "#FCEBEB", border: "1px solid #F7C1C1", color: "#A32D2D", borderRadius: 8, padding: "10px 14px", marginBottom: 14, fontSize: 13 }}>{importError}</div>}

            {!importResult && (
              <>
                <label style={{ display: "inline-block", fontSize: 13, color: "#185FA5", cursor: "pointer", border: "1px dashed #B5D4F4", borderRadius: 8, padding: "10px 16px", marginBottom: 14 }}>
                  {importFile ? `📄 ${importFile.name}` : "+ Choisir un fichier CSV"}
                  <input type="file" accept=".csv,text/csv" onChange={(e) => { setImportFile(e.target.files?.[0] || null); setImportPreview(null); }} style={{ display: "none" }} />
                </label>

                {importPreview && (
                  <div style={{ marginBottom: 14 }}>
                    <div style={{ display: "flex", gap: 14, flexWrap: "wrap", fontSize: 13, marginBottom: 10 }}>
                      <span style={{ color: "#3B6D11" }}>{importPreview.summary.ok} pret(s)</span>
                      <span style={{ color: "#854F0B" }}>{importPreview.summary.a_verifier} a verifier</span>
                      <span style={{ color: "#64748B" }}>{importPreview.summary.doublon} doublon(s) ignore(s)</span>
                      <span style={{ color: "#A32D2D" }}>{importPreview.summary.erreur} erreur(s) ignoree(s)</span>
                    </div>
                    <div style={{ maxHeight: 260, overflowY: "auto", border: "1px solid #E2E8F0", borderRadius: 8 }}>
                      {importPreview.rows.map((r) => (
                        <div key={r.line} style={{ padding: "8px 12px", borderBottom: "1px solid #F1F5F9", fontSize: 12, display: "flex", justifyContent: "space-between", gap: 10 }}>
                          <span>L{r.line} · {r.row.first_name} {r.row.last_name}</span>
                          <span style={{
                            color: r.status === "ok" ? "#3B6D11" : r.status === "a_verifier" ? "#854F0B" : r.status === "doublon" ? "#64748B" : "#A32D2D",
                            textAlign: "right", maxWidth: "60%",
                          }}>{r.reason || (r.issues || []).join(" · ") || "OK"}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
                  {!importPreview && <button onClick={previewImport} disabled={!importFile || importBusy} style={{ padding: "9px 18px", background: "#185FA5", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, cursor: "pointer", opacity: (!importFile || importBusy) ? 0.6 : 1 }}>{importBusy ? "Analyse..." : "Analyser (aperçu)"}</button>}
                  {importPreview && (
                    <button onClick={commitImport} disabled={importBusy || (importPreview.summary.ok + importPreview.summary.a_verifier === 0)}
                      style={{ padding: "9px 18px", background: "#185FA5", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, cursor: "pointer", opacity: (importBusy || (importPreview.summary.ok + importPreview.summary.a_verifier === 0)) ? 0.6 : 1 }}>
                      {importBusy ? "Import..." : `Confirmer l'import de ${importPreview.summary.ok + importPreview.summary.a_verifier} patient(s)`}
                    </button>
                  )}
                </div>
              </>
            )}

            {importResult && (
              <div>
                <div style={{ background: "#EAF7EC", border: "1px solid #BFE6C4", color: "#276B32", borderRadius: 8, padding: "10px 14px", marginBottom: 14, fontSize: 13 }}>{importResult.message}</div>
                {importResult.skipped.length > 0 && (
                  <div style={{ fontSize: 12, color: "#64748B", marginBottom: 14 }}>
                    Lignes ignorees : {importResult.skipped.map((s) => `L${s.line} (${s.reason})`).join(" · ")}
                  </div>
                )}
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                  <button onClick={() => setShowImport(false)} style={{ padding: "9px 18px", background: "#185FA5", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, cursor: "pointer" }}>Fermer</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {error && !showForm && <div style={{ background: "#FCEBEB", border: "1px solid #F7C1C1", color: "#A32D2D", borderRadius: 8, padding: "10px 14px", marginTop: 14, fontSize: 13 }}>{error}</div>}

      {showForm && (
        <div style={{ background: "#fff", border: "1px solid #B5D4F4", borderRadius: 12, padding: 20, marginTop: 16 }}>
          <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 14, color: "#0F172A" }}>{editing ? "Modifier le dossier" : "Nouveau patient"}</div>
          {error && <div style={{ background: "#FCEBEB", border: "1px solid #F7C1C1", color: "#A32D2D", borderRadius: 8, padding: "10px 14px", marginBottom: 12, fontSize: 13 }}>{error}</div>}
          <div style={{ fontSize: 12, fontWeight: 700, color: "#185FA5", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 10 }}>Identite</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
            <div><label style={lab}>Prenom *</label><input style={iS} value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} /></div>
            <div><label style={lab}>Nom *</label><input style={iS} value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} /></div>
            <div><label style={lab}>Date de naissance</label><input type="date" style={iS} value={form.date_of_birth || ""} onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })} /></div>
            <div><label style={lab}>Sexe</label><select style={iS} value={form.sex || ""} onChange={(e) => setForm({ ...form, sex: e.target.value })}><option value="">—</option><option value="M">Masculin</option><option value="F">Feminin</option></select></div>
            <div><label style={lab}>Groupe sanguin</label><select style={iS} value={form.blood_group || ""} onChange={(e) => setForm({ ...form, blood_group: e.target.value })}><option value="">—</option>{["A+","A-","B+","B-","AB+","AB-","O+","O-"].map(g=> <option key={g} value={g}>{g}</option>)}</select></div>
            <div><label style={lab}>Identifiant national</label><input style={iS} value={form.national_id || ""} onChange={(e) => setForm({ ...form, national_id: e.target.value })} /></div>
            <div><label style={lab}>Telephone</label><input style={iS} value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
            <div><label style={lab}>Email</label><input type="email" style={iS} placeholder="Pour les notifications de RDV" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div style={{ gridColumn: "span 2" }}><label style={lab}>Adresse</label><input style={iS} value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
            <div><label style={lab}>Contact d'urgence (nom)</label><input style={iS} value={form.emergency_contact_name || ""} onChange={(e) => setForm({ ...form, emergency_contact_name: e.target.value })} /></div>
            <div><label style={lab}>Contact d'urgence (tel)</label><input style={iS} value={form.emergency_contact_phone || ""} onChange={(e) => setForm({ ...form, emergency_contact_phone: e.target.value })} /></div>
          </div>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#185FA5", textTransform: "uppercase", letterSpacing: 0.5, margin: "18px 0 10px" }}>Administratif</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
            <div><label style={lab}>Profession</label><input style={iS} value={form.profession || ""} onChange={(e) => setForm({ ...form, profession: e.target.value })} /></div>
            <div><label style={lab}>Situation familiale</label><select style={iS} value={form.marital_status || ""} onChange={(e) => setForm({ ...form, marital_status: e.target.value })}><option value="">—</option><option value="Celibataire">Celibataire</option><option value="Marie(e)">Marie(e)</option><option value="Divorce(e)">Divorce(e)</option><option value="Veuf(ve)">Veuf(ve)</option></select></div>
            <div><label style={lab}>Mutuelle / assurance</label><input style={iS} value={form.insurance || ""} onChange={(e) => setForm({ ...form, insurance: e.target.value })} /></div>
          </div>
          {can("patients:medical") && (<><div style={{ fontSize: 12, fontWeight: 700, color: "#185FA5", textTransform: "uppercase", letterSpacing: 0.5, margin: "18px 0 10px" }}>Dossier medical</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <MultiSelectField label="Antecedents medicaux" options={MEDICAL_OPTIONS.medical_history} value={form.medical_history} onChange={(v) => setForm({ ...form, medical_history: v })} />
            <MultiSelectField label="Antecedents chirurgicaux" options={MEDICAL_OPTIONS.surgical_history} value={form.surgical_history} onChange={(v) => setForm({ ...form, surgical_history: v })} />
            <MultiSelectField label="Antecedents familiaux" options={MEDICAL_OPTIONS.family_history} value={form.family_history} onChange={(v) => setForm({ ...form, family_history: v })} />
            <MultiSelectField label="Allergies" options={MEDICAL_OPTIONS.allergies} value={form.allergies} onChange={(v) => setForm({ ...form, allergies: v })} />
            <MultiSelectField label="Traitements en cours" options={MEDICAL_OPTIONS.current_treatments} value={form.current_treatments} onChange={(v) => setForm({ ...form, current_treatments: v })} />
            <div><label style={lab}>Notes / observations</label><textarea style={taS} value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
          </div>
          </>)}
          <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
            <button onClick={save} disabled={saving} style={{ padding: "8px 18px", background: "#185FA5", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, cursor: "pointer" }}>{saving ? "Enregistrement..." : "Enregistrer"}</button>
            <button onClick={() => { setShowForm(false); resetForm(); }} style={{ padding: "8px 18px", background: "transparent", border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 14, cursor: "pointer" }}>Annuler</button>
          </div>
        </div>
      )}

      <div style={{ marginTop: 18, marginBottom: 14, display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center" }}>
        <input style={{ ...iS, maxWidth: 320 }} placeholder="Rechercher (nom, N° dossier, NIN, telephone)..." value={search} onChange={(e) => setSearch(e.target.value)} />
        <select style={{ ...iS, maxWidth: 150 }} value={fSex} onChange={(e) => setFSex(e.target.value)}><option value="">Sexe : tous</option><option value="M">Masculin</option><option value="F">Feminin</option></select>
        <select style={{ ...iS, maxWidth: 160 }} value={fBlood} onChange={(e) => setFBlood(e.target.value)}><option value="">Groupe : tous</option>{["A+","A-","B+","B-","AB+","AB-","O+","O-"].map(g => <option key={g} value={g}>{g}</option>)}</select>
        {can("admissions:read") && <select style={{ ...iS, maxWidth: 190 }} value={fStatus} onChange={(e) => setFStatus(e.target.value)}><option value="">Statut : tous</option><option value="admitted">Hospitalises</option><option value="not">Non hospitalises</option></select>}
        {(search || fSex || fBlood || fStatus) && <button onClick={() => { setSearch(""); setFSex(""); setFBlood(""); setFStatus(""); setPage(0); }} style={{ fontSize: 13, padding: "8px 14px", borderRadius: 8, border: "1px solid #CBD5E1", background: "#fff", color: "#64748B", cursor: "pointer" }}>Reinitialiser</button>}
        {/* Le compteur distingue le total serveur du sous-ensemble affiche :
            annoncer "12 patients" alors que la base en contient 8000 serait
            trompeur, surtout sur un ecran ou l'on cherche un dossier precis. */}
        <span style={{ fontSize: 13, color: "#94A3B8", marginLeft: "auto" }}>
          {total > 0
            ? <>{total} patient{total > 1 ? "s" : ""}{search ? " trouve" + (total > 1 ? "s" : "") : ""}
                {(fSex || fBlood || fStatus) && filtered.length !== patients.length
                  ? ` · ${filtered.length} affiche${filtered.length > 1 ? "s" : ""} sur cette page`
                  : total > PAGE_SIZE ? ` · page ${page + 1} sur ${Math.ceil(total / PAGE_SIZE)}` : ""}
              </>
            : "Aucun patient"}
        </span>
      </div>

      <div style={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 12, overflow: "hidden" }}>
        {loading ? (
          <div style={{ padding: 30, textAlign: "center", color: "#94A3B8", fontSize: 14 }}>Chargement...</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 30, textAlign: "center", color: "#94A3B8", fontSize: 14 }}>
            {search ? `Aucun patient ne correspond a « ${search} ».`
              : patients.length === 0 ? "Aucun patient. Cliquez sur « Nouveau patient »."
              : "Aucun resultat avec ces filtres."}
          </div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr style={{ background: "#F8FAFC", textAlign: "left" }}>
              {["N patient", "Nom", "Naissance", "Sexe", "Telephone", "Statut", "Actions"].map((h) => <th key={h} style={{ padding: "10px 16px", fontSize: 12, color: "#64748B", fontWeight: 600 }}>{h}</th>)}
            </tr></thead>
            <tbody>
              {filtered.map((p) => {
                const age = ageFrom(p.date_of_birth);
                return (
                  <tr key={p.patient_id} style={{ borderTop: "1px solid #F1F5F9" }}>
                    <td style={{ padding: "12px 16px", fontSize: 12, color: "#64748B", fontFamily: "monospace" }}>{p.patient_number}</td>
                    <td style={{ padding: "12px 16px", fontSize: 14, fontWeight: 500, color: "#0F172A" }}>{p.first_name} {p.last_name}</td>
                    <td style={{ padding: "12px 16px", fontSize: 13, color: "#475569" }}>{p.date_of_birth || "—"}{age != null ? ` (${age} ans)` : ""}</td>
                    <td style={{ padding: "12px 16px", fontSize: 13, color: "#475569" }}>{p.sex === "M" ? "M" : p.sex === "F" ? "F" : "—"}</td>
                    <td style={{ padding: "12px 16px", fontSize: 13, color: "#475569" }}>{p.phone || "—"}</td>
                    <td style={{ padding: "12px 16px" }}>{p.admitted ? <span style={{ fontSize: 12, padding: "3px 10px", borderRadius: 12, background: "#EAF3DE", color: "#3B6D11" }}>Hospitalise{p.current_bed ? ` · ${p.current_bed}` : ""}</span> : <span style={{ color: "#94A3B8" }}>—</span>}</td>
                    <td style={{ padding: "12px 16px" }}>
                      <span style={{ display: "flex", gap: 12 }}>
                        <button onClick={() => openDetail(p)} style={{ fontSize: 13, color: "#185FA5", background: "none", border: "none", cursor: "pointer" }}>Dossier</button>
                        {can("patients:update") && <button onClick={() => startEdit(p)} style={{ fontSize: 13, color: "#185FA5", background: "none", border: "none", cursor: "pointer" }}>Modifier</button>}
                        {can("patients:delete") && <button onClick={() => setToDelete(p)} style={{ fontSize: 13, color: "#A32D2D", background: "none", border: "none", cursor: "pointer" }}>Supprimer</button>}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination serveur. Sans elle, l'ecran ne montrerait que les 50
          premiers dossiers sans aucun moyen d'atteindre les suivants. */}
      {!loading && total > PAGE_SIZE && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 12, marginTop: 16 }}>
          <button
            onClick={() => setPage((n) => Math.max(0, n - 1))}
            disabled={page === 0}
            style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid #CBD5E1",
                     background: page === 0 ? "#F1F5F9" : "#fff",
                     color: page === 0 ? "#CBD5E1" : "#334155", fontSize: 13,
                     cursor: page === 0 ? "default" : "pointer" }}
          >← Precedent</button>
          <span style={{ fontSize: 13, color: "#64748B" }}>
            {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} sur {total}
          </span>
          <button
            onClick={() => setPage((n) => n + 1)}
            disabled={(page + 1) * PAGE_SIZE >= total}
            style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid #CBD5E1",
                     background: (page + 1) * PAGE_SIZE >= total ? "#F1F5F9" : "#fff",
                     color: (page + 1) * PAGE_SIZE >= total ? "#CBD5E1" : "#334155", fontSize: 13,
                     cursor: (page + 1) * PAGE_SIZE >= total ? "default" : "pointer" }}
          >Suivant →</button>
        </div>
      )}


      {detail && (
        <div onClick={() => { setDetail(null); setDossier(null); }} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: 24, overflow: "auto" }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 12, width: "94%", maxWidth: 780, padding: 24, marginTop: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                  <div style={{ fontSize: 19, fontWeight: 700, color: "#0F172A" }}>{detail.first_name} {detail.last_name}</div>
                  {detail.current_status === "hospitalise"
                    ? <span style={{ fontSize: 12, padding: "3px 10px", borderRadius: 12, background: "#EAF3DE", color: "#3B6D11" }}>Hospitalisé{detail.current_bed ? ` · Lit ${detail.current_bed}` : ""}{detail.current_service ? ` · ${detail.current_service}` : ""}</span>
                    : <span style={{ fontSize: 12, padding: "3px 10px", borderRadius: 12, background: "#F1F5F9", color: "#64748B" }}>Ambulatoire</span>}
                </div>
                <div style={{ fontSize: 12, color: "#64748B", marginTop: 3 }}>
                  <span style={{ fontFamily: "monospace" }}>{detail.patient_number}</span>
                  {ageFrom(detail.date_of_birth) != null ? ` · ${ageFrom(detail.date_of_birth)} ans` : ""}
                  {detail.sex ? ` · ${detail.sex === "M" ? "Masculin" : "Féminin"}` : ""}
                  {detail.blood_group ? ` · ${detail.blood_group}` : ""}
                </div>
                {detail.allergies && (Array.isArray(detail.allergies) ? detail.allergies.length : detail.allergies) ? <div style={{ fontSize: 12, color: "#A32D2D", marginTop: 4 }}>⚠️ Allergies : {Array.isArray(detail.allergies) ? detail.allergies.join(", ") : detail.allergies}</div> : null}
              </div>
              <button onClick={() => { setDetail(null); setDossier(null); }} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#94A3B8" }}>×</button>
            </div>

            <div style={{ display: "flex", gap: 6, marginTop: 16, marginBottom: 16, flexWrap: "wrap", borderBottom: "1px solid #E2E8F0", paddingBottom: 10 }}>
              {[["synthese", "Synthèse"], ["parcours", `Parcours${parcoursRows(dossier)?.length ? ` (${parcoursRows(dossier).length})` : ""}`], ["consultations", `Consultations${dossier?.consultations?.length ? ` (${dossier.consultations.length})` : ""}`], ["ordonnances", `Ordonnances${dossier?.prescriptions?.length ? ` (${dossier.prescriptions.length})` : ""}`], ["labo", `Labo${dossier?.labs?.length ? ` (${dossier.labs.length})` : ""}`], ["imagerie", `Imagerie${dossier?.imaging?.length ? ` (${dossier.imaging.length})` : ""}`], ["hospit", `Hospitalisations${dossier?.admissions?.length ? ` (${dossier.admissions.length})` : ""}`], ...(can("billing:read") ? [["factures", `Factures${dInvoices?.length ? ` (${dInvoices.length})` : ""}`]] : []), ...(can("documents:read") ? [["documents", `Documents${dDocuments?.length ? ` (${dDocuments.length})` : ""}`]] : []), ...(can("messaging:read") ? [["messages", `Messages${dMessages?.length ? ` (${dMessages.length})` : ""}`]] : []), ...(can("rgpd:export") ? [["rgpd", "RGPD"]] : [])].map(([id, lbl]) => (
                <button key={id} onClick={() => setDTab(id)} style={{ fontSize: 13, padding: "6px 12px", borderRadius: 8, cursor: "pointer", border: dTab === id ? "1px solid #185FA5" : "1px solid #E2E8F0", background: dTab === id ? "#185FA5" : "#fff", color: dTab === id ? "#fff" : "#475569" }}>{lbl}</button>
              ))}
            </div>

            {!dossier && <div style={{ padding: 14, color: "#94A3B8", fontSize: 13 }}>Chargement du dossier…</div>}

            {dTab === "synthese" && (
              <div>
                <DetailSection title="Identité" rows={[["Date de naissance", detail.date_of_birth ? `${detail.date_of_birth}${ageFrom(detail.date_of_birth)!=null?` (${ageFrom(detail.date_of_birth)} ans)`:""}` : null], ["Téléphone", detail.phone], ["Email", detail.email], ["Adresse", detail.address], ["Contact urgence", detail.emergency_contact_name ? `${detail.emergency_contact_name} ${detail.emergency_contact_phone||""}` : null], ["Profession", detail.profession], ["Mutuelle / assurance", detail.insurance]]} />
                {can("patients:medical") && <DetailSection title="Dossier médical" rows={[["Antécédents médicaux", detail.medical_history], ["Antécédents chirurgicaux", detail.surgical_history], ["Antécédents familiaux", detail.family_history], ["Allergies", detail.allergies], ["Traitements en cours", detail.current_treatments], ["Notes", detail.notes]]} />}
              </div>
            )}

            {dTab === "parcours" && (
              <DossierList rows={parcoursRows(dossier)} empty="Aucun evenement de parcours (rendez-vous, accueil, prelevement)"
                render={(e) => (
                  <div key={e.key} style={dossierRow}>
                    <div><div style={{ fontWeight: 500, color: "#0F172A" }}>{e.label}</div>
                      <div style={{ fontSize: 12, color: "#94A3B8" }}>{e.when}</div></div>
                    <span style={{ fontSize: 12, padding: "3px 10px", borderRadius: 12, background: e.color[0], color: e.color[1], whiteSpace: "nowrap" }}>{e.statusLabel}</span>
                  </div>
                )} />
            )}

            {dTab === "consultations" && (
              <DossierList rows={dossier?.consultations} empty="Aucune consultation"
                render={(c) => (
                  <div key={c.consultation_id} style={dossierRow}>
                    <div><div style={{ fontWeight: 500, color: "#0F172A" }}>{c.diagnosis || c.motif || "Consultation"}</div>
                      <div style={{ fontSize: 12, color: "#94A3B8" }}>{fmtDate(c.created_at)}{c.doctor_name ? ` · Dr. ${c.doctor_name}` : ""}{c.service_name ? ` · ${c.service_name}` : ""}</div></div>
                    <button onClick={() => setOpenConsId(c.consultation_id)} style={dossierLink}>Voir</button>
                  </div>
                )} />
            )}

            {dTab === "ordonnances" && (
              <DossierList rows={dossier?.prescriptions} empty="Aucune ordonnance"
                render={(rx) => (
                  <div key={rx.prescription_id} style={dossierRow}>
                    <div><div style={{ fontWeight: 500, color: "#0F172A" }}>{(rx.lines || []).length} médicament(s){rx.diagnosis ? ` · ${rx.diagnosis}` : ""}</div>
                      <div style={{ fontSize: 12, color: "#94A3B8" }}>{fmtDate(rx.created_at)}{rx.doctor_name ? ` · Dr. ${rx.doctor_name}` : ""} · {rx.prescription_number}</div></div>
                    <span style={{ display: "flex", gap: 12 }}>
                      <button onClick={() => setOpenRxId(rx.prescription_id)} style={dossierLink}>Voir</button>
                      <button onClick={() => printPrescription(rx, hospitalName)} style={dossierLink}>PDF</button>
                    </span>
                  </div>
                )} />
            )}

            {dTab === "labo" && (
              <DossierList rows={dossier?.labs} empty="Aucun examen biologique"
                render={(l) => (
                  <div key={l.request_id} style={dossierRow}>
                    <div><div style={{ fontWeight: 500, color: "#0F172A" }}>{l.exam || l.type || l.label || l.category || "Bilan biologique"}</div>
                      <div style={{ fontSize: 12, color: "#94A3B8" }}>{fmtDate(l.created_at || l.requested_at)}{l.service_name ? ` · ${l.service_name}` : ""}</div></div>
                    <span style={{ fontSize: 12, color: (l.status === "completed" || l.status === "resulted" || l.status === "valide") ? "#3B6D11" : "#B45309" }}>{l.status || "—"}</span>
                  </div>
                )} />
            )}

            {dTab === "imagerie" && (
              <DossierList rows={dossier?.imaging} empty="Aucun examen d'imagerie"
                render={(im) => (
                  <div key={im.request_id} style={dossierRow}>
                    <div><div style={{ fontWeight: 500, color: "#0F172A" }}>{im.modality || im.exam || im.type || im.label || "Imagerie"}</div>
                      <div style={{ fontSize: 12, color: "#94A3B8" }}>{fmtDate(im.created_at || im.requested_at)}{im.service_name ? ` · ${im.service_name}` : ""}</div></div>
                    <span style={{ fontSize: 12, color: (im.status === "completed" || im.status === "reported" || im.status === "valide") ? "#3B6D11" : "#B45309" }}>{im.status || "—"}</span>
                  </div>
                )} />
            )}

            {dTab === "hospit" && (
              <div>
                <DossierList rows={dossier?.admissions} empty="Aucune hospitalisation"
                  render={(a) => (
                    <div key={a.admission_id} style={dossierRow}>
                      <div><div style={{ fontWeight: 500, color: "#0F172A" }}>{a.service_name || "—"} · Lit {a.bed_number || "—"}</div>
                        <div style={{ fontSize: 12, color: "#94A3B8" }}>{a.reason || "—"}</div></div>
                      <span style={{ fontSize: 12, color: a.status === "active" ? "#3B6D11" : "#64748B", whiteSpace: "nowrap" }}>{a.status === "active" ? `Depuis ${fmtDate(a.admitted_at)}` : `${fmtDate(a.admitted_at)} → ${fmtDate(a.discharged_at)}`}</span>
                    </div>
                  )} />
                {dossier?.er?.length ? (
                  <div style={{ marginTop: 16 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: "#185FA5", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>Passages aux urgences</div>
                    {dossier.er.map((v) => (
                      <div key={v.visit_id} style={dossierRow}>
                        <div><div style={{ fontWeight: 500, color: "#0F172A" }}>{v.complaint || "Urgence"}</div>
                          <div style={{ fontSize: 12, color: "#94A3B8" }}>{fmtDate(v.arrival_at)}{v.triage ? ` · Tri ${v.triage}` : ""}</div></div>
                        <span style={{ fontSize: 12, color: "#64748B" }}>{v.outcome || v.status || "—"}</span>
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            )}

            {dTab === "factures" && can("billing:read") && (
              <DossierList rows={dInvoices} empty="Aucune facture"
                render={(inv) => {
                  const stt = (typeof INV_STATUS !== "undefined" && INV_STATUS[inv.status]) || [inv.status, "#64748B", "#F1F5F9"];
                  const bal = (inv.total_amount || 0) - (inv.paid_amount || 0);
                  return (
                    <div key={inv.invoice_id} style={dossierRow}>
                      <div><div style={{ fontWeight: 500, color: "#0F172A" }}>{dzd(inv.total_amount)} <span style={{ fontSize: 12, fontWeight: 400, color: "#94A3B8" }}>· {inv.invoice_number}</span></div>
                        <div style={{ fontSize: 12, color: "#94A3B8" }}>{fmtDate(inv.created_at)}{bal > 0 ? ` · reste ${dzd(bal)}` : " · soldée"}</div></div>
                      <span style={{ fontSize: 12, padding: "3px 10px", borderRadius: 12, background: stt[2], color: stt[1] }}>{stt[0]}</span>
                    </div>
                  );
                }} />
            )}

            {dTab === "documents" && can("documents:read") && (
              <div>
                {docError && <div style={{ background: "#FCEBEB", border: "1px solid #F7C1C1", color: "#A32D2D", borderRadius: 8, padding: "8px 12px", marginBottom: 12, fontSize: 12 }}>{docError}</div>}
                {can("documents:write") && (
                  <div style={{ background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 10, padding: 14, marginBottom: 16 }}>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
                      <select value={docCategory} onChange={(e) => setDocCategory(e.target.value)} style={{ padding: "8px 10px", border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 13 }}>
                        <option value="ordonnance">Ordonnance</option>
                        <option value="administratif">Document administratif</option>
                        <option value="patient">Transmis par le patient</option>
                      </select>
                      <input placeholder="Libelle (optionnel)" value={docLabel} onChange={(e) => setDocLabel(e.target.value)} style={{ padding: "8px 10px", border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 13 }} />
                    </div>
                    <label style={{ display: "inline-block", fontSize: 13, color: "#185FA5", cursor: "pointer", border: "1px dashed #B5D4F4", borderRadius: 8, padding: "8px 14px" }}>
                      {docUploading ? "Envoi en cours..." : "+ Ajouter un document (JPEG, PNG, PDF - max 10 Mo)"}
                      <input type="file" accept="image/jpeg,image/png,application/pdf" onChange={uploadDocument} style={{ display: "none" }} disabled={docUploading} />
                    </label>
                  </div>
                )}
                <DossierList rows={dDocuments} empty="Aucun document"
                  render={(doc) => (
                    <div key={doc.document_id} style={dossierRow}>
                      <div>
                        <div style={{ fontWeight: 500, color: "#0F172A" }}>{doc.label || doc.filename}</div>
                        <div style={{ fontSize: 12, color: "#94A3B8" }}>
                          {DOCUMENT_CATEGORY_LABELS[doc.category] || doc.category} · {fmtDate(doc.uploaded_at)}
                          {doc.uploaded_by ? ` · ${doc.uploaded_by}` : ""} · {(doc.size / 1024 / 1024).toFixed(2)} Mo
                        </div>
                      </div>
                      <span style={{ display: "flex", gap: 12 }}>
                        <button onClick={() => viewDocument(doc)} style={dossierLink}>Voir</button>
                        {can("documents:write") && <button onClick={() => deleteDocument(doc)} style={{ ...dossierLink, color: "#A32D2D" }}>Supprimer</button>}
                      </span>
                    </div>
                  )} />
              </div>
            )}

            {dTab === "messages" && can("messaging:read") && (
              <div>
                {msgError && <div style={{ background: "#FCEBEB", border: "1px solid #F7C1C1", color: "#A32D2D", borderRadius: 8, padding: "8px 12px", marginBottom: 12, fontSize: 12 }}>{msgError}</div>}
                <DossierList rows={dMessages} empty="Aucun message"
                  render={(m) => (
                    <div key={m.message_id} style={{ ...dossierRow, alignItems: "flex-start", flexDirection: m.sender === "staff" ? "row-reverse" : "row" }}>
                      <div style={{ maxWidth: "78%" }}>
                        <div style={{ display: "flex", gap: 8, alignItems: "baseline", justifyContent: m.sender === "staff" ? "flex-end" : "flex-start" }}>
                          <span style={{ fontSize: 12, fontWeight: 600, color: m.sender === "staff" ? "#185FA5" : "#0F172A" }}>{m.sender === "staff" ? (m.sender_name || "Etablissement") : (m.sender_name || "Patient")}</span>
                          <span style={{ fontSize: 11, color: "#94A3B8" }}>{fmtDate(m.created_at)}</span>
                        </div>
                        {m.body && <div style={{ fontSize: 13, color: "#334155", marginTop: 4, whiteSpace: "pre-wrap", textAlign: m.sender === "staff" ? "right" : "left" }}>{m.body}</div>}
                        {m.attachment && <div style={{ marginTop: 4, textAlign: m.sender === "staff" ? "right" : "left" }}><button onClick={() => viewMessageAttachment(m)} style={dossierLink}>📎 {m.attachment.filename}</button></div>}
                      </div>
                    </div>
                  )} />
                {can("messaging:write") && (
                  <div style={{ background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 10, padding: 14, marginTop: 16 }}>
                    <textarea placeholder="Ecrire un message au patient..." value={msgBody} onChange={(e) => setMsgBody(e.target.value)}
                      style={{ width: "100%", minHeight: 64, padding: "9px 12px", border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 13, boxSizing: "border-box", resize: "vertical", fontFamily: "inherit" }} />
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 10 }}>
                      <label style={{ fontSize: 12, color: "#185FA5", cursor: "pointer" }}>
                        {msgFile ? `📎 ${msgFile.name}` : "+ Piece jointe (JPEG, PNG, PDF)"}
                        <input type="file" accept="image/jpeg,image/png,application/pdf" onChange={(e) => setMsgFile(e.target.files?.[0] || null)} style={{ display: "none" }} />
                      </label>
                      <button onClick={sendMessage} disabled={msgSending || (!msgBody.trim() && !msgFile)}
                        style={{ padding: "8px 18px", background: "#185FA5", color: "#fff", border: "none", borderRadius: 8, fontSize: 13, cursor: "pointer", opacity: (msgSending || (!msgBody.trim() && !msgFile)) ? 0.6 : 1 }}>
                        {msgSending ? "Envoi..." : "Envoyer"}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {dTab === "rgpd" && can("rgpd:export") && (
              <div>
                {rgpdError && <div style={{ background: "#FCEBEB", border: "1px solid #F7C1C1", color: "#A32D2D", borderRadius: 8, padding: "8px 12px", marginBottom: 12, fontSize: 12 }}>{rgpdError}</div>}
                {rgpdDone && <div style={{ background: "#EAF7EC", border: "1px solid #BFE6C4", color: "#276B32", borderRadius: 8, padding: "8px 12px", marginBottom: 12, fontSize: 12 }}>{rgpdDone}</div>}
                <div style={{ background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 10, padding: 14, marginBottom: 16 }}>
                  <div style={{ fontWeight: 600, color: "#0F172A", marginBottom: 4 }}>Exporter les données du patient</div>
                  <div style={{ fontSize: 12, color: "#64748B", marginBottom: 10 }}>Télécharge un fichier contenant l'identité, les rendez-vous, l'historique d'accueil, les messages et la liste des documents de ce patient (droit d'accès, section 33).</div>
                  <button onClick={exportPatientRgpd} disabled={rgpdBusy} style={{ padding: "8px 18px", background: "#185FA5", color: "#fff", border: "none", borderRadius: 8, fontSize: 13, cursor: "pointer", opacity: rgpdBusy ? 0.6 : 1 }}>
                    {rgpdBusy ? "..." : "Exporter (JSON)"}
                  </button>
                </div>
                {can("rgpd:anonymize") && !detail.anonymized && (
                  <div style={{ background: "#FEF6F6", border: "1px solid #F3C9C9", borderRadius: 10, padding: 14 }}>
                    <div style={{ fontWeight: 600, color: "#A32D2D", marginBottom: 4 }}>Anonymiser ce patient</div>
                    <div style={{ fontSize: 12, color: "#7A3131", marginBottom: 10 }}>
                      Action irréversible. Le nom, les coordonnées et le lien du portail patient seront définitivement effacés et remplacés dans toutes les données du laboratoire (rendez-vous, accueil, messagerie). Le dossier médical et l'historique (dates, statuts, statistiques) sont conservés. Aucun rendez-vous n'est annulé.
                    </div>
                    <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                      <input placeholder='Taper "ANONYMISER" pour confirmer' value={anonymizeText} onChange={(e) => setAnonymizeText(e.target.value)}
                        style={{ padding: "8px 10px", border: "1px solid #F3C9C9", borderRadius: 8, fontSize: 13, minWidth: 220 }} />
                      <button onClick={anonymizePatientRgpd} disabled={rgpdBusy || anonymizeText.trim() !== "ANONYMISER"}
                        style={{ padding: "8px 18px", background: "#A32D2D", color: "#fff", border: "none", borderRadius: 8, fontSize: 13, cursor: "pointer", opacity: (rgpdBusy || anonymizeText.trim() !== "ANONYMISER") ? 0.5 : 1 }}>
                        {rgpdBusy ? "..." : "Anonymiser définitivement"}
                      </button>
                    </div>
                  </div>
                )}
                {detail.anonymized && (
                  <div style={{ fontSize: 12, color: "#64748B" }}>Ce patient a été anonymisé{detail.anonymized_at ? ` le ${fmtDate(detail.anonymized_at)}` : ""}.</div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {openConsId && <ConsultationModal consultationId={openConsId} canWrite={can("consultation:write")} can={can} onClose={() => setOpenConsId(null)} onDone={() => setOpenConsId(null)} />}
      {openRxId && <PrescriptionModal prescriptionId={openRxId} hospitalName={hospitalName} can={can} onClose={() => setOpenRxId(null)} onDone={() => setOpenRxId(null)} />}

      {toDelete && (
        <div onClick={() => setToDelete(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 12, width: "90%", maxWidth: 400, padding: 24 }}>
            <div style={{ fontSize: 16, fontWeight: 600, color: "#0F172A", marginBottom: 8 }}>Supprimer ce patient ?</div>
            <div style={{ fontSize: 13, color: "#64748B", marginBottom: 20, lineHeight: 1.5 }}>Le dossier de <b>{toDelete.first_name} {toDelete.last_name}</b> sera supprime. Action irreversible.</div>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button onClick={() => setToDelete(null)} style={{ fontSize: 13, padding: "8px 16px", borderRadius: 8, border: "1px solid #CBD5E1", background: "#fff", color: "#64748B", cursor: "pointer" }}>Annuler</button>
              <button onClick={doDelete} style={{ fontSize: 13, padding: "8px 16px", borderRadius: 8, border: "none", background: "#A32D2D", color: "#fff", cursor: "pointer" }}>Supprimer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const dossierRow = { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: "1px solid #F1F5F9" };
const dossierLink = { fontSize: 13, color: "#185FA5", background: "none", border: "none", cursor: "pointer" };

// Libelles/couleurs complets de la file d'attente (section 13) : le tableau
// "Accueil" n'affiche que les statuts en cours (RECEPTION_STATUS_LABELS, plus
// bas dans ce fichier), mais le Parcours du dossier patient doit aussi savoir
// afficher un passage deja termine ou annule.
const PARCOURS_RECEPTION_LABELS = { attente: "En attente", appelee: "Appelée", en_cours: "Prise en charge", terminee: "Terminée", annulee: "Annulée" };
const PARCOURS_RECEPTION_COLORS = {
  attente: ["#FEF3C7", "#854F0B"], appelee: ["#E0E7FF", "#3730A3"], en_cours: ["#DBEAFE", "#1E40AF"],
  terminee: ["#DCFCE7", "#166534"], annulee: ["#F1F5F9", "#94A3B8"],
};

// Parcours patient unifie (cahier des charges, section 9-10) : fusionne les
// trois etapes du circuit labo — rendez-vous (agenda), passage a l'accueil /
// file d'attente (reception), prelevement (phlebotomy) — en une seule
// chronologie, la plus recente en tete. Chaque etape garde son propre statut,
// comme demande section 10 ("Chaque etape possede un statut.").
function parcoursRows(dossier) {
  if (!dossier) return null;
  const rows = [];
  (dossier.appointments || []).forEach((a) => rows.push({
    key: `rdv_${a.appointment_id}`,
    at: a.date ? `${a.date}T${a.time || "00:00"}` : a.created_at,
    label: `Rendez-vous${a.act_type_name ? ` · ${a.act_type_name}` : ""}`,
    when: a.date ? `${fmtDate(a.date)}${a.time ? ` · ${a.time}` : ""}` : fmtDate(a.created_at),
    statusLabel: AGENDA_STATUS_LABELS[a.status] || a.status,
    color: AGENDA_STATUS_COLORS[a.status] || ["#F1F5F9", "#64748B"],
  }));
  (dossier.reception || []).forEach((r) => rows.push({
    key: `acc_${r.queue_id}`,
    at: r.arrived_at,
    label: `Accueil${r.act_type ? ` · ${r.act_type}` : ""}${r.priority ? " · Prioritaire" : ""}`,
    when: fmtDate(r.arrived_at),
    statusLabel: PARCOURS_RECEPTION_LABELS[r.status] || r.status,
    color: PARCOURS_RECEPTION_COLORS[r.status] || ["#F1F5F9", "#64748B"],
  }));
  (dossier.phlebotomy || []).forEach((s) => rows.push({
    key: `prel_${s.sample_id}`,
    at: s.created_at,
    label: `Prélèvement${s.sample_type ? ` · ${s.sample_type}` : ""}`,
    when: fmtDate(s.created_at),
    statusLabel: PHLEB_STATUS_LABELS[s.status] || s.status,
    color: PHLEB_STATUS_COLORS[s.status] || ["#F1F5F9", "#64748B"],
  }));
  rows.sort((a, b) => (b.at || "").localeCompare(a.at || ""));
  return rows;
}

function DossierList({ rows, empty, render }) {
  if (rows == null) return <div style={{ padding: 14, color: "#94A3B8", fontSize: 13 }}>Chargement…</div>;
  if (rows.length === 0) return <div style={{ padding: 14, color: "#94A3B8", fontSize: 13 }}>{empty}</div>;
  return <div>{rows.map(render)}</div>;
}

function DetailSection({ title, rows }) {
  const filled = rows.filter(([, v]) => v);
  return (
    <div style={{ marginBottom: 18 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: "#185FA5", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>{title}</div>
      {filled.length === 0 ? <div style={{ fontSize: 13, color: "#94A3B8" }}>Non renseigne</div> : filled.map(([k, v]) => (
        <div key={k} style={{ display: "flex", padding: "6px 0", borderBottom: "1px solid #F1F5F9", fontSize: 13 }}>
          <span style={{ color: "#64748B", width: 180, flexShrink: 0 }}>{k}</span>
          <span style={{ color: "#0F172A", whiteSpace: "pre-wrap" }}>{v}</span>
        </div>
      ))}
    </div>
  );
}


function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d) ? iso : d.toLocaleDateString("fr-FR");
}

function BedsModule({ can }) {
  const [sub, setSub] = useState("beds"); // "beds" | "adm"
  return (
    <div>
      <div style={{ display: "flex", gap: 8, marginBottom: 18 }}>
        {[["beds", "Lits"], ["adm", "Hospitalisations"]].map(([id, label]) => (
          <button key={id} onClick={() => setSub(id)}
            style={{ fontSize: 14, padding: "7px 16px", borderRadius: 8, cursor: "pointer",
              border: sub === id ? "1px solid #185FA5" : "1px solid #CBD5E1",
              background: sub === id ? "#185FA5" : "#fff", color: sub === id ? "#fff" : "#475569" }}>{label}</button>
        ))}
      </div>
      {sub === "beds" ? <BedsList can={can} /> : <AdmissionsList can={can} />}
    </div>
  );
}

function BedsList({ can }) {
  const [beds, setBeds] = useState([]);
  const [saving, setSaving] = useState(false);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [form, setForm] = useState({ service_id: "", bed_number: "", room: "", status: "available" });

  const load = () => {
    setLoading(true);
    Promise.all([
      axios.get(`${API_URL}/api/sih/beds`, getAuthConfig()),
      axios.get(`${API_URL}/api/sih/services`, getAuthConfig()),
    ]).then(([b, sv]) => { setBeds(b.data.beds || []); setServices(sv.data.services || []); })
      .catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);
  const reset = () => { setForm({ service_id: "", bed_number: "", room: "", status: "available" }); setEditing(null); setError(""); };
  const save = async () => {
    if (!form.service_id) { setError("Service obligatoire"); return; }
    if (!form.bed_number.trim()) { setError("Numero de lit obligatoire"); return; }
    setError("");
    if (saving) return;
    setSaving(true);
    try {
      if (editing) await axios.put(`${API_URL}/api/sih/beds/${editing}`, form, getAuthConfig());
      else await axios.post(`${API_URL}/api/sih/beds`, form, getAuthConfig());
      setShowForm(false); reset(); load();
    } catch (e) { setError(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Erreur"); }
    finally { setSaving(false); }
  };
  const startEdit = (b) => { setEditing(b.bed_id); setForm({ service_id: b.service_id || "", bed_number: b.bed_number || "", room: b.room || "", status: b.status || "available" }); setError(""); setShowForm(true); };
  const doDelete = async () => { if (!toDelete) return; try { await axios.delete(`${API_URL}/api/sih/beds/${toDelete.bed_id}`, getAuthConfig()); setToDelete(null); load(); } catch (e) { setError(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Erreur"); setToDelete(null); } };

  const iS = { width: "100%", padding: "9px 12px", border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 14, boxSizing: "border-box", background: "#fff" };
  const stColor = { available: ["#EAF3DE", "#3B6D11", "Disponible"], occupied: ["#FCEBEB", "#A32D2D", "Occupe"], maintenance: ["#FAEEDA", "#854F0B", "Maintenance"] };
  const noService = services.length === 0;

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h2 style={{ fontSize: 18, fontWeight: 700, color: "#0F172A", margin: 0 }}>Lits</h2>
        {can("beds:write") && <button onClick={() => { reset(); setShowForm(true); }} disabled={noService} title={noService ? "Creez d'abord un service" : ""}
          style={{ padding: "9px 18px", background: noService ? "#94A3B8" : "#185FA5", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, cursor: noService ? "not-allowed" : "pointer" }}>+ Nouveau lit</button>}
      </div>
      {noService && <div style={{ background: "#FAEEDA", border: "1px solid #F0D8A8", color: "#854F0B", borderRadius: 8, padding: "10px 14px", marginTop: 14, fontSize: 13 }}>Creez d'abord un <b>service</b> avant d'ajouter des lits.</div>}
      {error && !showForm && <div style={{ background: "#FCEBEB", border: "1px solid #F7C1C1", color: "#A32D2D", borderRadius: 8, padding: "10px 14px", marginTop: 14, fontSize: 13 }}>{error}</div>}
      {showForm && (
        <div style={{ background: "#fff", border: "1px solid #B5D4F4", borderRadius: 12, padding: 20, marginTop: 16 }}>
          <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 14 }}>{editing ? "Modifier le lit" : "Nouveau lit"}</div>
          {error && <div style={{ background: "#FCEBEB", border: "1px solid #F7C1C1", color: "#A32D2D", borderRadius: 8, padding: "10px 14px", marginBottom: 12, fontSize: 13 }}>{error}</div>}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <select style={iS} value={form.service_id} onChange={(e) => setForm({ ...form, service_id: e.target.value })}><option value="">Service * ...</option>{services.map((s) => <option key={s.service_id} value={s.service_id}>{s.name}</option>)}</select>
            <input style={iS} placeholder="Numero de lit * (ex: A-12)" value={form.bed_number} onChange={(e) => setForm({ ...form, bed_number: e.target.value })} />
            <input style={iS} placeholder="Chambre / salle" value={form.room} onChange={(e) => setForm({ ...form, room: e.target.value })} />
            <select style={iS} value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}><option value="available">Disponible</option><option value="maintenance">Maintenance</option></select>
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
            <button onClick={save} style={{ padding: "8px 18px", background: "#185FA5", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, cursor: "pointer" }}>Enregistrer</button>
            <button onClick={() => { setShowForm(false); reset(); }} style={{ padding: "8px 18px", background: "transparent", border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 14, cursor: "pointer" }}>Annuler</button>
          </div>
        </div>
      )}
      <div style={{ marginTop: 18, background: "#fff", border: "1px solid #E2E8F0", borderRadius: 12, overflow: "hidden" }}>
        {loading ? <div style={{ padding: 30, textAlign: "center", color: "#94A3B8" }}>Chargement...</div>
        : beds.length === 0 ? <div style={{ padding: 30, textAlign: "center", color: "#94A3B8", fontSize: 14 }}>Aucun lit.</div>
        : (
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr style={{ background: "#F8FAFC", textAlign: "left" }}>{["Lit", "Service", "Chambre", "Statut", "Occupant", "Actions"].map((h) => <th key={h} style={{ padding: "10px 16px", fontSize: 12, color: "#64748B", fontWeight: 600 }}>{h}</th>)}</tr></thead>
            <tbody>
              {beds.map((b) => { const c = stColor[b.status] || stColor.available; return (
                <tr key={b.bed_id} style={{ borderTop: "1px solid #F1F5F9" }}>
                  <td style={{ padding: "12px 16px", fontSize: 14, fontWeight: 500 }}>{b.bed_number}</td>
                  <td style={{ padding: "12px 16px", fontSize: 13, color: "#475569" }}>{b.service_name || "—"}</td>
                  <td style={{ padding: "12px 16px", fontSize: 13, color: "#475569" }}>{b.room || "—"}</td>
                  <td style={{ padding: "12px 16px" }}><span style={{ fontSize: 12, padding: "3px 10px", borderRadius: 12, background: c[0], color: c[1] }}>{c[2]}</span></td>
                  <td style={{ padding: "12px 16px", fontSize: 13, color: "#0F172A" }}>{b.occupant || "—"}</td>
                  <td style={{ padding: "12px 16px" }}>{can("beds:write") && <span style={{ display: "flex", gap: 12 }}>
                    <button onClick={() => startEdit(b)} style={{ fontSize: 13, color: "#185FA5", background: "none", border: "none", cursor: "pointer" }}>Modifier</button>
                    <button onClick={() => setToDelete(b)} style={{ fontSize: 13, color: "#A32D2D", background: "none", border: "none", cursor: "pointer" }}>Supprimer</button>
                  </span>}</td>
                </tr>); })}
            </tbody>
          </table>
        )}
      </div>
      {toDelete && (
        <div onClick={() => setToDelete(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 12, width: "90%", maxWidth: 400, padding: 24 }}>
            <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>Supprimer ce lit ?</div>
            <div style={{ fontSize: 13, color: "#64748B", marginBottom: 20 }}>Lit <b>{toDelete.bed_number}</b>. Action irreversible.</div>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button onClick={() => setToDelete(null)} style={{ fontSize: 13, padding: "8px 16px", borderRadius: 8, border: "1px solid #CBD5E1", background: "#fff", color: "#64748B", cursor: "pointer" }}>Annuler</button>
              <button onClick={doDelete} style={{ fontSize: 13, padding: "8px 16px", borderRadius: 8, border: "none", background: "#A32D2D", color: "#fff", cursor: "pointer" }}>Supprimer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function AdmissionsList({ can }) {
  const [adms, setAdms] = useState([]);
  const [saving, setSaving] = useState(false);
  const [patients, setPatients] = useState([]);
  const [services, setServices] = useState([]);
  const [beds, setBeds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState("");
  const [toDischarge, setToDischarge] = useState(null);
  const [noteAdm, setNoteAdm] = useState(null);
  const [noteType, setNoteType] = useState("visite");
  const [noteText, setNoteText] = useState("");
  const saveClinicalEvent = async () => { if (!noteText.trim()) return; try { await axios.post(`${API_URL}/api/sih/clinical-events`, { patient_id: noteAdm.patient_id, event_type: noteType, text: noteText }, getAuthConfig()); setNoteAdm(null); setNoteText(""); setNoteType("visite"); toast.success("Note enregistree"); } catch (e) { toast.error("Erreur enregistrement"); } };
  const [staySum, setStaySum] = useState(null);
  const [patientFile, setPatientFile] = useState(null);
  const loadStaySum = async (adm) => { setStaySum(null); setPatientFile(null); try { const r = await axios.get(`${API_URL}/api/sih/admissions/${adm.admission_id}/summary`, getAuthConfig()); setStaySum(r.data.summary); } catch (e) { setStaySum(null); } try { const p = await axios.get(`${API_URL}/api/sih/patients/${adm.patient_id}/dossier`, getAuthConfig()); setPatientFile(p.data.patient); } catch (e) { setPatientFile(null); } };
  const [dxQuery, setDxQuery] = useState(""); const [dxResults, setDxResults] = useState([]); const searchDx = async (q) => { setDxQuery(q); if (!q || q.length < 2) { setDxResults([]); return; } try { const r = await axios.get(`${API_URL}/api/sih/icd10/search?q=${encodeURIComponent(q)}`, getAuthConfig()); setDxResults(r.data.results || []); } catch (e) { setDxResults([]); } };
  const [toDeleteAdm, setToDeleteAdm] = useState(null);
  const delAdmission = async () => {
    if (!toDeleteAdm) return;
    try { await axios.delete(`${API_URL}/api/sih/admissions/${toDeleteAdm.admission_id}`, getAuthConfig()); setToDeleteAdm(null); load(); }
    catch (e) { setError(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Erreur"); setToDeleteAdm(null); }
  };
  const [dForm, setDForm] = useState({ discharge_diagnosis: "", stay_summary: "", discharge_instructions: "", entry_mode: "", discharge_mode: "", history: "", evolution: "", secondary_diagnoses: "", discharge_treatment: "", exams_to_plan: "", followup: "" });
  const [form, setForm] = useState({ patient_id: "", service_id: "", bed_id: "", reason: "", doctor_name: "" });

  const load = () => {
    setLoading(true);
    Promise.all([
      axios.get(`${API_URL}/api/sih/admissions`, getAuthConfig()),
      axios.get(`${API_URL}/api/sih/patients`, getAuthConfig()),
      axios.get(`${API_URL}/api/sih/services`, getAuthConfig()),
      axios.get(`${API_URL}/api/sih/beds`, getAuthConfig()),
    ]).then(([a, p, sv, b]) => { setAdms(a.data.admissions || []); setPatients(p.data.patients || []); setServices(sv.data.services || []); setBeds(b.data.beds || []); })
      .catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);
  const reset = () => { setForm({ patient_id: "", service_id: "", bed_id: "", reason: "", doctor_name: "" }); setError(""); };
  const save = async () => {
    if (!form.patient_id || !form.service_id || !form.bed_id) { setError("Patient, service et lit obligatoires"); return; }
    setError("");
    if (saving) return;
    setSaving(true);
    try { await axios.post(`${API_URL}/api/sih/admissions`, form, getAuthConfig()); setShowForm(false); reset(); load(); }
    catch (e) { setError(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Erreur"); }
    finally { setSaving(false); }
  };
  const doDischarge = async (andPrint) => {
    if (!toDischarge) return;
    try {
      await axios.put(`${API_URL}/api/sih/admissions/${toDischarge.admission_id}/discharge`, dForm, getAuthConfig());
      const adm = { ...toDischarge, ...dForm, status: "discharged", discharged_at: new Date().toISOString(), stay_snapshot: staySum };
      setToDischarge(null); setDForm({ discharge_diagnosis: "", stay_summary: "", discharge_instructions: "" }); load();
      if (andPrint) printDischarge(adm, undefined, patientFile);
    } catch (e) { setError(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Erreur"); setToDischarge(null); }
  };

  const iS = { width: "100%", padding: "9px 12px", border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 14, boxSizing: "border-box", background: "#fff" };
  // lits disponibles du service choisi
  const availBeds = beds.filter((b) => b.service_id === form.service_id && b.status === "available");


  // --- Transfert inter-services ---
  const [toTransfer, setToTransfer] = useState(null);
  const [tForm, setTForm] = useState({ service_id: "", bed_id: "", reason: "", doctor_name: "" });
  const [transferring, setTransferring] = useState(false);
  const openTransfer = (a) => { setError(""); setTForm({ service_id: "", bed_id: "", reason: "", doctor_name: "" }); setToTransfer(a); };
  const availBedsTransfer = beds.filter((b) => b.service_id === tForm.service_id && b.status === "available");
  const doTransfer = async () => {
    if (!toTransfer) return;
    if (!tForm.service_id || !tForm.bed_id) { setError("Service et lit de destination obligatoires"); return; }
    if (transferring) return;
    setTransferring(true); setError("");
    try {
      await axios.post(`${API_URL}/api/sih/admissions/${toTransfer.admission_id}/transfer`, tForm, getAuthConfig());
      setToTransfer(null); load();
    } catch (e) { setError(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Erreur"); }
    finally { setTransferring(false); }
  };
  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h2 style={{ fontSize: 18, fontWeight: 700, color: "#0F172A", margin: 0 }}>Hospitalisations</h2>
        {can("admissions:write") && <button onClick={() => { reset(); setShowForm(true); }}
          style={{ padding: "9px 18px", background: "#185FA5", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, cursor: "pointer" }}>+ Hospitaliser un patient</button>}
      </div>
      {error && !showForm && <div style={{ background: "#FCEBEB", border: "1px solid #F7C1C1", color: "#A32D2D", borderRadius: 8, padding: "10px 14px", marginTop: 14, fontSize: 13 }}>{error}</div>}
      {showForm && (
        <div style={{ background: "#fff", border: "1px solid #B5D4F4", borderRadius: 12, padding: 20, marginTop: 16 }}>
          <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 14 }}>Nouvelle hospitalisation</div>
          {error && <div style={{ background: "#FCEBEB", border: "1px solid #F7C1C1", color: "#A32D2D", borderRadius: 8, padding: "10px 14px", marginBottom: 12, fontSize: 13 }}>{error}</div>}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <select style={iS} value={form.patient_id} onChange={(e) => setForm({ ...form, patient_id: e.target.value })}><option value="">Patient * ...</option>{patients.map((p) => <option key={p.patient_id} value={p.patient_id}>{p.first_name} {p.last_name}</option>)}</select>
            <select style={iS} value={form.service_id} onChange={(e) => setForm({ ...form, service_id: e.target.value, bed_id: "" })}><option value="">Service * ...</option>{services.map((s) => <option key={s.service_id} value={s.service_id}>{s.name}</option>)}</select>
            <select style={iS} value={form.bed_id} onChange={(e) => setForm({ ...form, bed_id: e.target.value })} disabled={!form.service_id}><option value="">{form.service_id ? (availBeds.length ? "Lit disponible * ..." : "Aucun lit disponible") : "Choisir un service d'abord"}</option>{availBeds.map((b) => <option key={b.bed_id} value={b.bed_id}>{b.bed_number}{b.room ? ` (${b.room})` : ""}</option>)}</select>
            <input style={iS} placeholder="Medecin responsable" value={form.doctor_name} onChange={(e) => setForm({ ...form, doctor_name: e.target.value })} />
          </div>
          <textarea style={{ ...iS, marginTop: 12, minHeight: 56, resize: "vertical" }} placeholder="Motif d'hospitalisation" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} />
          <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
            <button onClick={save} style={{ padding: "8px 18px", background: "#185FA5", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, cursor: "pointer" }}>Hospitaliser</button>
            <button onClick={() => { setShowForm(false); reset(); }} style={{ padding: "8px 18px", background: "transparent", border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 14, cursor: "pointer" }}>Annuler</button>
          </div>
        </div>
      )}
      <div style={{ marginTop: 18, background: "#fff", border: "1px solid #E2E8F0", borderRadius: 12, overflow: "hidden" }}>
        {loading ? <div style={{ padding: 30, textAlign: "center", color: "#94A3B8" }}>Chargement...</div>
        : adms.length === 0 ? <div style={{ padding: 30, textAlign: "center", color: "#94A3B8", fontSize: 14 }}>Aucune hospitalisation.</div>
        : (
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr style={{ background: "#F8FAFC", textAlign: "left" }}>{["Patient", "Service", "Lit", "Entree", "Statut", "Actions"].map((h) => <th key={h} style={{ padding: "10px 16px", fontSize: 12, color: "#64748B", fontWeight: 600 }}>{h}</th>)}</tr></thead>
            <tbody>
              {adms.map((a) => (
                <tr key={a.admission_id} style={{ borderTop: "1px solid #F1F5F9" }}>
                  <td style={{ padding: "12px 16px", fontSize: 14, fontWeight: 500 }}>{a.patient_name || "—"}</td>
                  <td style={{ padding: "12px 16px", fontSize: 13, color: "#475569" }}>{a.service_name || "—"}</td>
                  <td style={{ padding: "12px 16px", fontSize: 13, color: "#475569" }}>{a.bed_number || "—"}</td>
                  <td style={{ padding: "12px 16px", fontSize: 13, color: "#475569" }}>{fmtDate(a.admitted_at)}</td>
                  <td style={{ padding: "12px 16px" }}>{a.status === "active"
                    ? <span style={{ fontSize: 12, padding: "3px 10px", borderRadius: 12, background: "#EAF3DE", color: "#3B6D11" }}>Hospitalise</span>
                    : <span style={{ fontSize: 12, padding: "3px 10px", borderRadius: 12, background: "#F1F5F9", color: "#64748B" }}>Sorti {fmtDate(a.discharged_at)}</span>}</td>
                  <td style={{ padding: "12px 16px" }}>
                    <span style={{ display: "flex", gap: 12 }}>
                      {a.status === "active" && can("admissions:write")
                        ? <button onClick={() => { setDForm({ discharge_diagnosis: "", stay_summary: "", discharge_instructions: "" }); setToDischarge(a); loadStaySum(a); }} style={{ fontSize: 13, color: "#185FA5", background: "none", border: "none", cursor: "pointer" }}>Faire sortir</button>
                        : a.status !== "active" ? <button onClick={() => printDischarge(a)} style={{ fontSize: 13, color: "#185FA5", background: "none", border: "none", cursor: "pointer" }}>Compte-rendu</button> : null}
                      {a.status === "active" && can("admissions:write") && <button onClick={() => openTransfer(a)} style={{ fontSize: 13, color: "#7C3AED", background: "none", border: "none", cursor: "pointer" }}>Transférer</button>}
                      {a.status === "active" && <button onClick={() => { setNoteAdm(a); setNoteType("visite"); setNoteText(""); }} style={{ fontSize: 13, color: "#0F766E", background: "none", border: "none", cursor: "pointer" }}>+ Note</button>}
                      {can("admissions:write") && <button onClick={() => setToDeleteAdm(a)} style={{ fontSize: 13, color: "#A32D2D", background: "none", border: "none", cursor: "pointer" }}>Supprimer</button>}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {toDischarge && (
        <div onClick={() => setToDischarge(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: 20, overflow: "auto" }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 12, width: "94%", maxWidth: 520, padding: 24, marginTop: 16 }}>
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 2 }}>Sortie & compte-rendu</div>
            <div style={{ fontSize: 13, color: "#64748B", marginBottom: 16 }}><b>{toDischarge.patient_name}</b> · {toDischarge.service_name || "—"} · Lit {toDischarge.bed_number || "—"}</div>
            {staySum && (
              <div style={{ background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 8, padding: 12, marginBottom: 14, fontSize: 12, maxHeight: 200, overflowY: "auto" }}>
                <div style={{ fontWeight: 700, color: "#334155", marginBottom: 8 }}>Récapitulatif du séjour (auto)</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
                  <span style={{ padding: "2px 8px", borderRadius: 10, background: "#E6F1FB", color: "#185FA5" }}>{staySum.counts.consultations} consult.</span>
                  <span style={{ padding: "2px 8px", borderRadius: 10, background: "#E6F1FB", color: "#185FA5" }}>{staySum.counts.cares} soins</span>
                  <span style={{ padding: "2px 8px", borderRadius: 10, background: "#E6F1FB", color: "#185FA5" }}>{staySum.counts.labs} labo</span>
                  <span style={{ padding: "2px 8px", borderRadius: 10, background: "#E6F1FB", color: "#185FA5" }}>{staySum.counts.imaging} imagerie</span>
                  <span style={{ padding: "2px 8px", borderRadius: 10, background: "#E6F1FB", color: "#185FA5" }}>{staySum.counts.prescriptions} ordo.</span>
                </div>
                {staySum.consultations.map((c, i) => (
                  <div key={"c" + i} style={{ padding: "4px 0", borderTop: "1px solid #E2E8F0", color: "#475569" }}>
                    <b>Consult.</b> {c.motif || "—"}{c.diagnostic ? ` · Dx: ${c.diagnostic}` : ""}
                  </div>
                ))}
                {staySum.cares.map((c, i) => (
                  <div key={"s" + i} style={{ padding: "4px 0", borderTop: "1px solid #E2E8F0", color: "#475569" }}>
                    <b>Soin</b> {c.description || "—"} ({c.status})
                  </div>
                ))}
                {staySum.labs.map((l, i) => (
                  <div key={"l" + i} style={{ padding: "4px 0", borderTop: "1px solid #E2E8F0", color: "#475569" }}>
                    <b>Labo</b> ({l.status}){l.reason ? ` · ${l.reason}` : ""}
                  </div>
                ))}
                {staySum.imaging.map((im, i) => (
                  <div key={"i" + i} style={{ padding: "4px 0", borderTop: "1px solid #E2E8F0", color: "#475569" }}>
                    <b>Imagerie</b> {im.modality || "—"} ({im.status})
                  </div>
                ))}
                {staySum.prescriptions.map((p, i) => (
                  <div key={"p" + i} style={{ padding: "4px 0", borderTop: "1px solid #E2E8F0", color: "#475569" }}>
                    <b>Ordonnance</b> {p.date ? p.date.slice(0, 10) : ""}
                  </div>
                ))}
              </div>
            )}
            <div style={{ marginBottom: 10, position: "relative" }}>
              <label style={{ fontSize: 12, color: "#64748B", display: "block", marginBottom: 4 }}>Diagnostic de sortie (CIM-11)</label>
              <input style={iS} value={dForm.discharge_diagnosis} placeholder="Rechercher une maladie (ex: pneumonie)..." onChange={(e) => { setDForm({ ...dForm, discharge_diagnosis: e.target.value }); searchDx(e.target.value); }} />
              {dxResults.length > 0 && (
                <div style={{ position: "absolute", zIndex: 50, left: 0, right: 0, background: "#fff", border: "1px solid #CBD5E1", borderRadius: 8, marginTop: 2, maxHeight: 220, overflowY: "auto", boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }}>
                  {dxResults.map((d, i) => (
                    <div key={i} onClick={() => { setDForm({ ...dForm, discharge_diagnosis: (d.code ? d.code + " — " : "") + d.label }); setDxResults([]); }} style={{ padding: "8px 12px", borderBottom: "1px solid #F1F5F9", cursor: "pointer", fontSize: 13, display: "flex", justifyContent: "space-between", gap: 8 }}>
                      <span style={{ color: "#0F172A" }}>{d.label}</span>
                      {d.code && <span style={{ color: "#185FA5", fontWeight: 600, whiteSpace: "nowrap" }}>{d.code}</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div style={{ display: "flex", gap: 10, marginBottom: 10 }}>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: 12, color: "#64748B", display: "block", marginBottom: 4 }}>Mode d'entrée</label>
                <select style={iS} value={dForm.entry_mode} onChange={(e) => setDForm({ ...dForm, entry_mode: e.target.value })}>
                  <option value="">—</option>
                  <option value="Programmée">Programmée</option>
                  <option value="Urgence">Urgence</option>
                  <option value="Transfert">Transfert d'un autre établissement</option>
                  <option value="Mutation interne">Mutation interne</option>
                </select>
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: 12, color: "#64748B", display: "block", marginBottom: 4 }}>Mode de sortie</label>
                <select style={iS} value={dForm.discharge_mode} onChange={(e) => setDForm({ ...dForm, discharge_mode: e.target.value })}>
                  <option value="">—</option>
                  <option value="Domicile">Retour à domicile</option>
                  <option value="Transfert">Transfert</option>
                  <option value="Contre avis médical">Sortie contre avis médical</option>
                  <option value="Décès">Décès</option>
                </select>
              </div>
            </div>
            <div style={{ marginBottom: 10 }}><label style={{ fontSize: 12, color: "#64748B", display: "block", marginBottom: 4 }}>Histoire de la maladie</label><textarea style={{ ...iS, minHeight: 60, resize: "vertical" }} value={dForm.history} onChange={(e) => setDForm({ ...dForm, history: e.target.value })} /></div>
            <div style={{ marginBottom: 10 }}><label style={{ fontSize: 12, color: "#64748B", display: "block", marginBottom: 4 }}>Évolution durant le séjour</label><textarea style={{ ...iS, minHeight: 60, resize: "vertical" }} value={dForm.evolution} onChange={(e) => setDForm({ ...dForm, evolution: e.target.value })} /></div>
            <div style={{ marginBottom: 10 }}><label style={{ fontSize: 12, color: "#64748B", display: "block", marginBottom: 4 }}>Diagnostics associés</label><textarea style={{ ...iS, minHeight: 50, resize: "vertical" }} value={dForm.secondary_diagnoses} onChange={(e) => setDForm({ ...dForm, secondary_diagnoses: e.target.value })} /></div>
            <div style={{ marginBottom: 10 }}><label style={{ fontSize: 12, color: "#64748B", display: "block", marginBottom: 4 }}>Traitement de sortie</label><textarea style={{ ...iS, minHeight: 60, resize: "vertical" }} value={dForm.discharge_treatment} onChange={(e) => setDForm({ ...dForm, discharge_treatment: e.target.value })} placeholder="Médicament, posologie, durée..." /></div>
            <div style={{ marginBottom: 10 }}><label style={{ fontSize: 12, color: "#64748B", display: "block", marginBottom: 4 }}>Examens à prévoir</label><textarea style={{ ...iS, minHeight: 50, resize: "vertical" }} value={dForm.exams_to_plan} onChange={(e) => setDForm({ ...dForm, exams_to_plan: e.target.value })} /></div>
            <div style={{ marginBottom: 10 }}><label style={{ fontSize: 12, color: "#64748B", display: "block", marginBottom: 4 }}>Rendez-vous de suivi</label><textarea style={{ ...iS, minHeight: 50, resize: "vertical" }} value={dForm.followup} onChange={(e) => setDForm({ ...dForm, followup: e.target.value })} /></div>
            <div style={{ marginBottom: 10 }}><label style={{ fontSize: 12, color: "#64748B", display: "block", marginBottom: 4 }}>Résumé du séjour</label><textarea style={{ ...iS, minHeight: 70, resize: "vertical" }} value={dForm.stay_summary} onChange={(e) => setDForm({ ...dForm, stay_summary: e.target.value })} /></div>
            <div style={{ marginBottom: 4 }}><label style={{ fontSize: 12, color: "#64748B", display: "block", marginBottom: 4 }}>Consignes & traitement de sortie</label><textarea style={{ ...iS, minHeight: 60, resize: "vertical" }} value={dForm.discharge_instructions} onChange={(e) => setDForm({ ...dForm, discharge_instructions: e.target.value })} /></div>
            <div style={{ fontSize: 12, color: "#94A3B8", margin: "10px 0 16px" }}>Le lit <b>{toDischarge.bed_number}</b> redeviendra disponible.</div>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", flexWrap: "wrap" }}>
              <button onClick={() => setToDischarge(null)} style={{ fontSize: 13, padding: "8px 16px", borderRadius: 8, border: "1px solid #CBD5E1", background: "#fff", color: "#64748B", cursor: "pointer" }}>Annuler</button>
              <button onClick={() => { const pid = toDischarge.patient_id; const pname = toDischarge.patient_name; window.__rxPending = { patientId: pid, patientName: pname }; setToDischarge(null); window.dispatchEvent(new CustomEvent("tabbibak:goToPrescription", { detail: { patientId: pid, patientName: pname } })); }} style={{ fontSize: 13, padding: "8px 16px", borderRadius: 8, border: "1px solid #3B6D11", background: "#fff", color: "#3B6D11", cursor: "pointer" }}>Ordonnance de sortie</button>
              <button onClick={() => doDischarge(false)} style={{ fontSize: 13, padding: "8px 16px", borderRadius: 8, border: "1px solid #185FA5", background: "#fff", color: "#185FA5", cursor: "pointer" }}>Confirmer la sortie</button>
              <button onClick={() => doDischarge(true)} style={{ fontSize: 13, padding: "8px 16px", borderRadius: 8, border: "none", background: "#185FA5", color: "#fff", cursor: "pointer" }}>Sortie + imprimer le CR</button>
            </div>
          </div>
        </div>
      )}
      {toTransfer && (
        <div onClick={() => setToTransfer(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: 20, overflow: "auto" }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 12, width: "94%", maxWidth: 480, padding: 24, marginTop: 40 }}>
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 2 }}>Transfert inter-services</div>
            <div style={{ fontSize: 13, color: "#64748B", marginBottom: 16 }}><b>{toTransfer.patient_name}</b> &middot; actuellement {toTransfer.service_name || "-"} &middot; Lit {toTransfer.bed_number || "-"}</div>
            {error && <div style={{ background: "#FCEBEB", border: "1px solid #F7C1C1", color: "#A32D2D", borderRadius: 8, padding: "10px 14px", marginBottom: 12, fontSize: 13 }}>{error}</div>}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <select style={iS} value={tForm.service_id} onChange={(e) => setTForm({ ...tForm, service_id: e.target.value, bed_id: "" })}><option value="">Service destination * ...</option>{services.map((sv) => <option key={sv.service_id} value={sv.service_id}>{sv.name}</option>)}</select>
              <select style={iS} value={tForm.bed_id} onChange={(e) => setTForm({ ...tForm, bed_id: e.target.value })} disabled={!tForm.service_id}><option value="">{tForm.service_id ? (availBedsTransfer.length ? "Lit disponible * ..." : "Aucun lit disponible") : "Choisir un service d'abord"}</option>{availBedsTransfer.map((b) => <option key={b.bed_id} value={b.bed_id}>{b.bed_number}{b.room ? ` (${b.room})` : ""}</option>)}</select>
            </div>
            <input style={{ ...iS, marginTop: 12 }} placeholder="Medecin (transfert)" value={tForm.doctor_name} onChange={(e) => setTForm({ ...tForm, doctor_name: e.target.value })} />
            <textarea style={{ ...iS, marginTop: 12, minHeight: 56, resize: "vertical" }} placeholder="Motif du transfert" value={tForm.reason} onChange={(e) => setTForm({ ...tForm, reason: e.target.value })} />
            <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
              <button onClick={doTransfer} disabled={transferring} style={{ padding: "9px 20px", background: "#7C3AED", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, cursor: transferring ? "default" : "pointer", opacity: transferring ? 0.6 : 1 }}>{transferring ? "Transfert..." : "Confirmer le transfert"}</button>
              <button onClick={() => setToTransfer(null)} style={{ padding: "9px 20px", background: "transparent", border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 14, cursor: "pointer" }}>Annuler</button>
            </div>
          </div>
        </div>
      )}
      {toDeleteAdm && (
        <div onClick={() => setToDeleteAdm(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 12, width: "90%", maxWidth: 420, padding: 24 }}>
            <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>Supprimer cette hospitalisation ?</div>
            <div style={{ fontSize: 13, color: "#64748B", marginBottom: 20 }}>L'enregistrement de <b>{toDeleteAdm.patient_name}</b> sera définitivement supprimé.{toDeleteAdm.status === "active" ? ` Le lit ${toDeleteAdm.bed_number || ""} sera libéré.` : ""}</div>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button onClick={() => setToDeleteAdm(null)} style={{ fontSize: 13, padding: "8px 16px", borderRadius: 8, border: "1px solid #CBD5E1", background: "#fff", color: "#64748B", cursor: "pointer" }}>Annuler</button>
              <button onClick={delAdmission} style={{ fontSize: 13, padding: "8px 16px", borderRadius: 8, border: "none", background: "#A32D2D", color: "#fff", cursor: "pointer" }}>Supprimer</button>
            </div>
          </div>
        </div>
      )}
      {noteAdm && (
        <div onClick={() => setNoteAdm(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 12, width: "90%", maxWidth: 480, padding: 24 }}>
            <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}>Ajouter au dossier — {noteAdm.patient_name}</div>
            <div style={{ fontSize: 12, color: "#64748B", marginBottom: 16 }}>Cette note alimentera automatiquement le compte-rendu de sortie.</div>
            <label style={{ fontSize: 12, color: "#64748B", display: "block", marginBottom: 4 }}>Type</label>
            <select style={iS} value={noteType} onChange={(e) => setNoteType(e.target.value)}>
              <option value="observation">Observation médicale initiale</option>
              <option value="visite">Visite médicale</option>
              <option value="transmission">Transmission infirmière</option>
              <option value="obstetrique">Note obstétricale</option>
              <option value="autre">Autre</option>
            </select>
            <label style={{ fontSize: 12, color: "#64748B", display: "block", margin: "12px 0 4px" }}>Contenu</label>
            <textarea style={{ ...iS, minHeight: 120, resize: "vertical" }} value={noteText} onChange={(e) => setNoteText(e.target.value)} placeholder="Décrivez l'observation, la visite, la transmission..." />
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 16 }}>
              <button onClick={() => setNoteAdm(null)} style={{ fontSize: 13, padding: "8px 16px", borderRadius: 8, border: "1px solid #CBD5E1", background: "#fff", color: "#64748B", cursor: "pointer" }}>Annuler</button>
              <button onClick={saveClinicalEvent} style={{ fontSize: 13, padding: "8px 16px", borderRadius: 8, border: "none", background: "#0F766E", color: "#fff", cursor: "pointer" }}>Enregistrer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


const LAB_CATEGORIES = [
  "Hematologie", "Coagulation", "Glucides / Diabete", "Lipides", "Fonction renale",
  "Foie & enzymes", "Ionogramme & mineraux", "Inflammation", "Thyroide",
  "Marqueurs cardiaques", "Bilan martial (fer)", "Hormones & marqueurs",
  "Vitamines", "Serologies & infectieux", "Urines",
];

// Valeurs de reference : standard adulte indicatif (a adapter par labo).
const LAB_CATALOG = [
  // Hematologie
  { cat: "Hematologie", name: "Hemogramme (NFS)", unit: "", reference: "Voir GR, GB, Hb, plaquettes", low: null, high: null },
  { cat: "Hematologie", name: "Globules blancs (Leucocytes)", unit: "10^3/uL", reference: "4 - 10", low: 4, high: 10 },
  { cat: "Hematologie", name: "Globules rouges (Hematies)", unit: "10^6/uL", reference: "H 4.5-5.9 / F 4.0-5.2", low: 4.0, high: 5.9 },
  { cat: "Hematologie", name: "Hemoglobine", unit: "g/dL", reference: "H 13-17 / F 12-16", low: 12, high: 17 },
  { cat: "Hematologie", name: "Hematocrite", unit: "%", reference: "H 40-52 / F 37-47", low: 37, high: 52 },
  { cat: "Hematologie", name: "VGM", unit: "fL", reference: "80 - 100", low: 80, high: 100 },
  { cat: "Hematologie", name: "Plaquettes", unit: "10^3/uL", reference: "150 - 400", low: 150, high: 400 },
  { cat: "Hematologie", name: "Reticulocytes", unit: "%", reference: "0.5 - 2.5", low: 0.5, high: 2.5 },
  { cat: "Hematologie", name: "VS (vitesse de sedimentation)", unit: "mm/h", reference: "< 20", low: null, high: 20 },
  { cat: "Hematologie", name: "Groupe sanguin", unit: "", reference: "A / B / AB / O - Rh +/-", low: null, high: null },
  // Coagulation
  { cat: "Coagulation", name: "TP (Taux de prothrombine)", unit: "%", reference: "70 - 100", low: 70, high: null },
  { cat: "Coagulation", name: "INR", unit: "", reference: "0.8 - 1.2 (hors traitement)", low: 0.8, high: 1.2 },
  { cat: "Coagulation", name: "TCA", unit: "s", reference: "25 - 35", low: 25, high: 35 },
  { cat: "Coagulation", name: "Fibrinogene", unit: "g/L", reference: "2 - 4", low: 2, high: 4 },
  { cat: "Coagulation", name: "D-dimeres", unit: "ug/L", reference: "< 500", low: null, high: 500 },
  // Glucides / Diabete
  { cat: "Glucides / Diabete", name: "Glycemie a jeun", unit: "g/L", reference: "0.7 - 1.1", low: 0.7, high: 1.1 },
  { cat: "Glucides / Diabete", name: "Glycemie post-prandiale", unit: "g/L", reference: "< 1.4", low: null, high: 1.4 },
  { cat: "Glucides / Diabete", name: "HbA1c", unit: "%", reference: "< 6.0 (diabete < 7)", low: null, high: 6.0 },
  // Lipides
  { cat: "Lipides", name: "Cholesterol total", unit: "g/L", reference: "< 2.0", low: null, high: 2.0 },
  { cat: "Lipides", name: "HDL cholesterol", unit: "g/L", reference: "> 0.4", low: 0.4, high: null },
  { cat: "Lipides", name: "LDL cholesterol", unit: "g/L", reference: "< 1.6", low: null, high: 1.6 },
  { cat: "Lipides", name: "Triglycerides", unit: "g/L", reference: "< 1.5", low: null, high: 1.5 },
  // Fonction renale
  { cat: "Fonction renale", name: "Uree", unit: "g/L", reference: "0.15 - 0.45", low: 0.15, high: 0.45 },
  { cat: "Fonction renale", name: "Creatinine", unit: "mg/L", reference: "H 7-13 / F 6-11", low: 6, high: 13 },
  { cat: "Fonction renale", name: "Acide urique", unit: "mg/L", reference: "H 35-70 / F 25-60", low: 25, high: 70 },
  { cat: "Fonction renale", name: "DFG (clairance)", unit: "mL/min", reference: "> 90", low: 90, high: null },
  { cat: "Fonction renale", name: "Microalbuminurie", unit: "mg/24h", reference: "< 30", low: null, high: 30 },
  // Foie & enzymes
  { cat: "Foie & enzymes", name: "ASAT (TGO)", unit: "UI/L", reference: "< 35", low: null, high: 35 },
  { cat: "Foie & enzymes", name: "ALAT (TGP)", unit: "UI/L", reference: "< 45", low: null, high: 45 },
  { cat: "Foie & enzymes", name: "GGT", unit: "UI/L", reference: "H < 55 / F < 38", low: null, high: 55 },
  { cat: "Foie & enzymes", name: "Phosphatases alcalines (PAL)", unit: "UI/L", reference: "40 - 130", low: 40, high: 130 },
  { cat: "Foie & enzymes", name: "Bilirubine totale", unit: "mg/L", reference: "< 12", low: null, high: 12 },
  { cat: "Foie & enzymes", name: "Bilirubine conjuguee", unit: "mg/L", reference: "< 3", low: null, high: 3 },
  { cat: "Foie & enzymes", name: "Albumine", unit: "g/L", reference: "35 - 50", low: 35, high: 50 },
  { cat: "Foie & enzymes", name: "Proteines totales", unit: "g/L", reference: "65 - 80", low: 65, high: 80 },
  { cat: "Foie & enzymes", name: "Amylase", unit: "UI/L", reference: "28 - 100", low: 28, high: 100 },
  { cat: "Foie & enzymes", name: "Lipase", unit: "UI/L", reference: "< 60", low: null, high: 60 },
  { cat: "Foie & enzymes", name: "LDH", unit: "UI/L", reference: "240 - 480", low: 240, high: 480 },
  // Ionogramme & mineraux
  { cat: "Ionogramme & mineraux", name: "Sodium (Na)", unit: "mmol/L", reference: "135 - 145", low: 135, high: 145 },
  { cat: "Ionogramme & mineraux", name: "Potassium (K)", unit: "mmol/L", reference: "3.5 - 5.0", low: 3.5, high: 5.0 },
  { cat: "Ionogramme & mineraux", name: "Chlore (Cl)", unit: "mmol/L", reference: "98 - 107", low: 98, high: 107 },
  { cat: "Ionogramme & mineraux", name: "Calcium", unit: "mg/L", reference: "88 - 105", low: 88, high: 105 },
  { cat: "Ionogramme & mineraux", name: "Phosphore", unit: "mg/L", reference: "25 - 45", low: 25, high: 45 },
  { cat: "Ionogramme & mineraux", name: "Magnesium", unit: "mg/L", reference: "18 - 24", low: 18, high: 24 },
  { cat: "Ionogramme & mineraux", name: "Bicarbonates (CO2)", unit: "mmol/L", reference: "22 - 29", low: 22, high: 29 },
  // Inflammation
  { cat: "Inflammation", name: "CRP", unit: "mg/L", reference: "< 6", low: null, high: 6 },
  { cat: "Inflammation", name: "Procalcitonine", unit: "ug/L", reference: "< 0.5", low: null, high: 0.5 },
  // Thyroide
  { cat: "Thyroide", name: "TSH", unit: "mUI/L", reference: "0.4 - 4.0", low: 0.4, high: 4.0 },
  { cat: "Thyroide", name: "T4 libre (FT4)", unit: "pmol/L", reference: "12 - 22", low: 12, high: 22 },
  { cat: "Thyroide", name: "T3 libre (FT3)", unit: "pmol/L", reference: "3.1 - 6.8", low: 3.1, high: 6.8 },
  // Marqueurs cardiaques
  { cat: "Marqueurs cardiaques", name: "Troponine", unit: "ng/L", reference: "< 14", low: null, high: 14 },
  { cat: "Marqueurs cardiaques", name: "CPK", unit: "UI/L", reference: "H < 190 / F < 170", low: null, high: 190 },
  { cat: "Marqueurs cardiaques", name: "CK-MB", unit: "UI/L", reference: "< 25", low: null, high: 25 },
  { cat: "Marqueurs cardiaques", name: "BNP", unit: "ng/L", reference: "< 100", low: null, high: 100 },
  // Bilan martial
  { cat: "Bilan martial (fer)", name: "Fer serique", unit: "ug/dL", reference: "60 - 170", low: 60, high: 170 },
  { cat: "Bilan martial (fer)", name: "Ferritine", unit: "ug/L", reference: "H 30-400 / F 15-150", low: 15, high: 400 },
  { cat: "Bilan martial (fer)", name: "Transferrine", unit: "g/L", reference: "2 - 3.6", low: 2, high: 3.6 },
  { cat: "Bilan martial (fer)", name: "Coeff. saturation transferrine", unit: "%", reference: "20 - 40", low: 20, high: 40 },
  // Hormones & marqueurs
  { cat: "Hormones & marqueurs", name: "Beta-HCG", unit: "UI/L", reference: "< 5 (non enceinte)", low: null, high: 5 },
  { cat: "Hormones & marqueurs", name: "Cortisol (matin)", unit: "ug/dL", reference: "5 - 25", low: 5, high: 25 },
  { cat: "Hormones & marqueurs", name: "PSA", unit: "ng/mL", reference: "< 4", low: null, high: 4 },
  { cat: "Hormones & marqueurs", name: "Prolactine", unit: "ng/mL", reference: "H < 15 / F < 25", low: null, high: 25 },
  // Vitamines
  { cat: "Vitamines", name: "Vitamine D (25-OH)", unit: "ng/mL", reference: "30 - 60", low: 30, high: 60 },
  { cat: "Vitamines", name: "Vitamine B12", unit: "pg/mL", reference: "200 - 900", low: 200, high: 900 },
  { cat: "Vitamines", name: "Folates (B9)", unit: "ng/mL", reference: "3 - 17", low: 3, high: 17 },
  // Serologies & infectieux (qualitatif)
  { cat: "Serologies & infectieux", name: "Serologie VIH", unit: "", reference: "Negatif", low: null, high: null },
  { cat: "Serologies & infectieux", name: "Ag HBs (Hepatite B)", unit: "", reference: "Negatif", low: null, high: null },
  { cat: "Serologies & infectieux", name: "Serologie Hepatite C", unit: "", reference: "Negatif", low: null, high: null },
  { cat: "Serologies & infectieux", name: "Widal (typhoide)", unit: "", reference: "Negatif", low: null, high: null },
  { cat: "Serologies & infectieux", name: "ASLO", unit: "UI/mL", reference: "< 200", low: null, high: 200 },
  { cat: "Serologies & infectieux", name: "Serologie syphilis (TPHA/VDRL)", unit: "", reference: "Negatif", low: null, high: null },
  // Urines
  { cat: "Urines", name: "ECBU", unit: "", reference: "Sterile", low: null, high: null },
  { cat: "Urines", name: "Proteinurie 24h", unit: "g/24h", reference: "< 0.15", low: null, high: 0.15 },
  { cat: "Urines", name: "Glycosurie", unit: "", reference: "Negatif", low: null, high: null },
  { cat: "Urines", name: "Bandelette urinaire", unit: "", reference: "Normal", low: null, high: null },
  // Coagulation / hemostase (complement)
  { cat: "Coagulation", name: "Fibrinogene", specimen: "sang", unit: "g/L", reference: "2 - 4", low: 2, high: 4 },
  { cat: "Coagulation", name: "D-dimeres", specimen: "sang", unit: "ug/L", reference: "< 500", low: null, high: 500 },
  { cat: "Coagulation", name: "Temps de thrombine (TT)", specimen: "sang", unit: "s", reference: "14 - 21", low: 14, high: 21 },
  { cat: "Coagulation", name: "Facteur V", specimen: "sang", unit: "%", reference: "70 - 120", low: 70, high: 120 },
  { cat: "Coagulation", name: "Facteur VIII", specimen: "sang", unit: "%", reference: "50 - 150", low: 50, high: 150 },
  { cat: "Coagulation", name: "Antithrombine III", specimen: "sang", unit: "%", reference: "80 - 120", low: 80, high: 120 },
  { cat: "Coagulation", name: "Proteine C", specimen: "sang", unit: "%", reference: "70 - 140", low: 70, high: 140 },
  { cat: "Coagulation", name: "Proteine S", specimen: "sang", unit: "%", reference: "60 - 140", low: 60, high: 140 },
  // Biochimie / enzymes (complement)
  { cat: "Foie & enzymes", name: "LDH", specimen: "sang", unit: "UI/L", reference: "135 - 225", low: 135, high: 225 },
  { cat: "Foie & enzymes", name: "Lipase", specimen: "sang", unit: "UI/L", reference: "13 - 60", low: 13, high: 60 },
  { cat: "Foie & enzymes", name: "Amylase", specimen: "sang", unit: "UI/L", reference: "28 - 100", low: 28, high: 100 },
  { cat: "Foie & enzymes", name: "Bilirubine totale", specimen: "sang", unit: "mg/L", reference: "< 12", low: null, high: 12 },
  { cat: "Foie & enzymes", name: "Bilirubine conjuguee", specimen: "sang", unit: "mg/L", reference: "< 3", low: null, high: 3 },
  { cat: "Foie & enzymes", name: "Albumine", specimen: "sang", unit: "g/L", reference: "35 - 50", low: 35, high: 50 },
  { cat: "Foie & enzymes", name: "Proteines totales", specimen: "sang", unit: "g/L", reference: "64 - 83", low: 64, high: 83 },
  // Ionogramme / mineraux (complement)
  { cat: "Ionogramme & mineraux", name: "Magnesium", specimen: "sang", unit: "mg/L", reference: "17 - 25", low: 17, high: 25 },
  { cat: "Ionogramme & mineraux", name: "Phosphore", specimen: "sang", unit: "mg/L", reference: "25 - 45", low: 25, high: 45 },
  { cat: "Ionogramme & mineraux", name: "Calcium ionise", specimen: "sang", unit: "mmol/L", reference: "1.15 - 1.35", low: 1.15, high: 1.35 },
  { cat: "Ionogramme & mineraux", name: "Lactates", specimen: "sang", unit: "mmol/L", reference: "0.5 - 2.2", low: 0.5, high: 2.2 },
  { cat: "Ionogramme & mineraux", name: "Ammoniemie", specimen: "sang", unit: "umol/L", reference: "11 - 35", low: 11, high: 35 },
  // Immunologie / auto-immunite
  { cat: "Immunologie / auto-immunite", name: "Anticorps antinucleaires (AAN)", specimen: "sang", unit: "", reference: "Negatif (< 1/160)", low: null, high: null },
  { cat: "Immunologie / auto-immunite", name: "Anti-DNA natif", specimen: "sang", unit: "UI/mL", reference: "< 10", low: null, high: 10 },
  { cat: "Immunologie / auto-immunite", name: "Anticorps anti-ENA", specimen: "sang", unit: "", reference: "Negatif", low: null, high: null },
  { cat: "Immunologie / auto-immunite", name: "ANCA", specimen: "sang", unit: "", reference: "Negatif", low: null, high: null },
  { cat: "Immunologie / auto-immunite", name: "Facteur rhumatoide", specimen: "sang", unit: "UI/mL", reference: "< 20", low: null, high: 20 },
  { cat: "Immunologie / auto-immunite", name: "Anticorps anti-CCP", specimen: "sang", unit: "U/mL", reference: "< 20", low: null, high: 20 },
  { cat: "Immunologie / auto-immunite", name: "Complement C3", specimen: "sang", unit: "g/L", reference: "0.9 - 1.8", low: 0.9, high: 1.8 },
  { cat: "Immunologie / auto-immunite", name: "Complement C4", specimen: "sang", unit: "g/L", reference: "0.1 - 0.4", low: 0.1, high: 0.4 },
  { cat: "Immunologie / auto-immunite", name: "CH50", specimen: "sang", unit: "U/mL", reference: "70 - 150", low: 70, high: 150 },
  { cat: "Immunologie / auto-immunite", name: "IgG serique", specimen: "sang", unit: "g/L", reference: "7 - 16", low: 7, high: 16 },
  { cat: "Immunologie / auto-immunite", name: "IgA serique", specimen: "sang", unit: "g/L", reference: "0.7 - 4", low: 0.7, high: 4 },
  { cat: "Immunologie / auto-immunite", name: "IgM serique", specimen: "sang", unit: "g/L", reference: "0.4 - 2.3", low: 0.4, high: 2.3 },
  { cat: "Immunologie / auto-immunite", name: "IgE totales", specimen: "sang", unit: "UI/mL", reference: "< 100", low: null, high: 100 },
  { cat: "Immunologie / auto-immunite", name: "Anticorps anti-TPO", specimen: "sang", unit: "UI/mL", reference: "< 34", low: null, high: 34 },
  { cat: "Immunologie / auto-immunite", name: "Anticorps anti-transglutaminase", specimen: "sang", unit: "U/mL", reference: "< 10", low: null, high: 10 },
  // Hormonologie
  { cat: "Hormonologie", name: "Cortisol plasmatique (8h)", specimen: "sang", unit: "ug/dL", reference: "5 - 25", low: 5, high: 25 },
  { cat: "Hormonologie", name: "ACTH", specimen: "sang", unit: "pg/mL", reference: "10 - 60", low: 10, high: 60 },
  { cat: "Hormonologie", name: "Testosterone totale", specimen: "sang", unit: "ng/mL", reference: "H 2.8-8 / F 0.1-0.9", low: null, high: null },
  { cat: "Hormonologie", name: "Oestradiol", specimen: "sang", unit: "pg/mL", reference: "Variable selon cycle", low: null, high: null },
  { cat: "Hormonologie", name: "Progesterone", specimen: "sang", unit: "ng/mL", reference: "Variable selon cycle", low: null, high: null },
  { cat: "Hormonologie", name: "LH", specimen: "sang", unit: "UI/L", reference: "Variable selon cycle", low: null, high: null },
  { cat: "Hormonologie", name: "FSH", specimen: "sang", unit: "UI/L", reference: "Variable selon cycle", low: null, high: null },
  { cat: "Hormonologie", name: "PTH (parathormone)", specimen: "sang", unit: "pg/mL", reference: "15 - 65", low: 15, high: 65 },
  { cat: "Hormonologie", name: "IGF-1 (somatomedine C)", specimen: "sang", unit: "ng/mL", reference: "Selon age", low: null, high: null },
  { cat: "Hormonologie", name: "Aldosterone", specimen: "sang", unit: "pg/mL", reference: "Debout 40 - 310", low: 40, high: 310 },
  { cat: "Hormonologie", name: "Renine", specimen: "sang", unit: "uUI/mL", reference: "Debout 4.4 - 46", low: 4.4, high: 46 },
  { cat: "Hormonologie", name: "Insuline a jeun", specimen: "sang", unit: "uUI/mL", reference: "2.6 - 24.9", low: 2.6, high: 24.9 },
  { cat: "Hormonologie", name: "Peptide C", specimen: "sang", unit: "ng/mL", reference: "1.1 - 4.4", low: 1.1, high: 4.4 },
  { cat: "Hormonologie", name: "25-OH Vitamine D", specimen: "sang", unit: "ng/mL", reference: "30 - 60", low: 30, high: 60 },
  // Marqueurs tumoraux
  { cat: "Marqueurs tumoraux", name: "PSA total", specimen: "sang", unit: "ng/mL", reference: "< 4", low: null, high: 4 },
  { cat: "Marqueurs tumoraux", name: "PSA libre", specimen: "sang", unit: "ng/mL", reference: "Ratio libre/total > 15%", low: null, high: null },
  { cat: "Marqueurs tumoraux", name: "CA 125", specimen: "sang", unit: "U/mL", reference: "< 35", low: null, high: 35 },
  { cat: "Marqueurs tumoraux", name: "CA 15-3", specimen: "sang", unit: "U/mL", reference: "< 30", low: null, high: 30 },
  { cat: "Marqueurs tumoraux", name: "CA 19-9", specimen: "sang", unit: "U/mL", reference: "< 37", low: null, high: 37 },
  { cat: "Marqueurs tumoraux", name: "ACE (antigene carcino-embryonnaire)", specimen: "sang", unit: "ng/mL", reference: "< 5", low: null, high: 5 },
  { cat: "Marqueurs tumoraux", name: "AFP (alpha-foetoproteine)", specimen: "sang", unit: "ng/mL", reference: "< 10", low: null, high: 10 },
  { cat: "Marqueurs tumoraux", name: "Calcitonine", specimen: "sang", unit: "pg/mL", reference: "< 10", low: null, high: 10 },
  { cat: "Marqueurs tumoraux", name: "Thyroglobuline", specimen: "sang", unit: "ng/mL", reference: "1.4 - 78", low: 1.4, high: 78 },
  { cat: "Marqueurs tumoraux", name: "NSE (enolase neurospecifique)", specimen: "sang", unit: "ng/mL", reference: "< 17", low: null, high: 17 },
  { cat: "Marqueurs tumoraux", name: "Beta-2-microglobuline", specimen: "sang", unit: "mg/L", reference: "0.8 - 2.4", low: 0.8, high: 2.4 },
  // Serologies & infectieux (complement)
  { cat: "Serologies & infectieux", name: "Ac anti-HBs", specimen: "sang", unit: "UI/L", reference: "> 10 (protege)", low: null, high: null },
  { cat: "Serologies & infectieux", name: "Ac anti-HBc", specimen: "sang", unit: "", reference: "Negatif", low: null, high: null },
  { cat: "Serologies & infectieux", name: "Ag HBe", specimen: "sang", unit: "", reference: "Negatif", low: null, high: null },
  { cat: "Serologies & infectieux", name: "Charge virale VHB (ADN)", specimen: "sang", unit: "UI/mL", reference: "Indetectable", low: null, high: null },
  { cat: "Serologies & infectieux", name: "Charge virale VHC (ARN)", specimen: "sang", unit: "UI/mL", reference: "Indetectable", low: null, high: null },
  { cat: "Serologies & infectieux", name: "Toxoplasmose IgG", specimen: "sang", unit: "UI/mL", reference: "Selon immunite", low: null, high: null },
  { cat: "Serologies & infectieux", name: "Toxoplasmose IgM", specimen: "sang", unit: "", reference: "Negatif", low: null, high: null },
  { cat: "Serologies & infectieux", name: "Rubeole IgG", specimen: "sang", unit: "UI/mL", reference: "> 10 (immunise)", low: null, high: null },
  { cat: "Serologies & infectieux", name: "CMV IgG / IgM", specimen: "sang", unit: "", reference: "Selon immunite", low: null, high: null },
  { cat: "Serologies & infectieux", name: "EBV (MNI-test)", specimen: "sang", unit: "", reference: "Negatif", low: null, high: null },
  { cat: "Serologies & infectieux", name: "Serologie Helicobacter pylori", specimen: "sang", unit: "", reference: "Negatif", low: null, high: null },
  // Gaz du sang / equilibre acido-basique
  { cat: "Gaz du sang", name: "pH sanguin", specimen: "sang", unit: "", reference: "7.35 - 7.45", low: 7.35, high: 7.45 },
  { cat: "Gaz du sang", name: "pCO2", specimen: "sang", unit: "mmHg", reference: "35 - 45", low: 35, high: 45 },
  { cat: "Gaz du sang", name: "pO2", specimen: "sang", unit: "mmHg", reference: "80 - 100", low: 80, high: 100 },
  { cat: "Gaz du sang", name: "Bicarbonates (HCO3)", specimen: "sang", unit: "mmol/L", reference: "22 - 26", low: 22, high: 26 },
  { cat: "Gaz du sang", name: "Saturation O2 (SaO2)", specimen: "sang", unit: "%", reference: "95 - 100", low: 95, high: 100 },
  { cat: "Gaz du sang", name: "Exces de base (BE)", specimen: "sang", unit: "mmol/L", reference: "-2 a +2", low: -2, high: 2 },
  // Electrophorese / proteines specifiques
  { cat: "Electrophorese / proteines", name: "Electrophorese des proteines", specimen: "sang", unit: "", reference: "Profil normal", low: null, high: null },
  { cat: "Electrophorese / proteines", name: "Immunofixation", specimen: "sang", unit: "", reference: "Absence de pic monoclonal", low: null, high: null },
  { cat: "Electrophorese / proteines", name: "Haptoglobine", specimen: "sang", unit: "g/L", reference: "0.3 - 2", low: 0.3, high: 2 },
  { cat: "Electrophorese / proteines", name: "Ceruloplasmine", specimen: "sang", unit: "g/L", reference: "0.2 - 0.6", low: 0.2, high: 0.6 },
  // Urines (complement)
  { cat: "Urines", name: "Ionogramme urinaire", specimen: "urine", unit: "mmol/24h", reference: "Selon diurese", low: null, high: null },
  { cat: "Urines", name: "Creatininurie 24h", specimen: "urine", unit: "mmol/24h", reference: "H 9-21 / F 7-16", low: null, high: null },
  { cat: "Urines", name: "Clairance de la creatinine", specimen: "urine", unit: "mL/min", reference: "90 - 140", low: 90, high: 140 },
  { cat: "Urines", name: "Microalbuminurie", specimen: "urine", unit: "mg/24h", reference: "< 30", low: null, high: 30 },
  { cat: "Urines", name: "Uree urinaire", specimen: "urine", unit: "g/24h", reference: "20 - 35", low: 20, high: 35 },
  { cat: "Urines", name: "Calciurie 24h", specimen: "urine", unit: "mg/24h", reference: "100 - 300", low: 100, high: 300 },
  { cat: "Urines", name: "Uricurie 24h", specimen: "urine", unit: "mg/24h", reference: "250 - 750", low: 250, high: 750 },
  { cat: "Urines", name: "Compte d'Addis (HLM)", specimen: "urine", unit: "/min", reference: "GR < 5000 / GB < 5000", low: null, high: null },
  { cat: "Urines", name: "Proteinurie de Bence-Jones", specimen: "urine", unit: "", reference: "Negatif", low: null, high: null },
  { cat: "Urines", name: "Cytologie urinaire", specimen: "urine", unit: "", reference: "Absence de cellules atypiques", low: null, high: null },
  { cat: "Urines", name: "Antigenurie legionelle", specimen: "urine", unit: "", reference: "Negatif", low: null, high: null },
  { cat: "Urines", name: "Antigenurie pneumocoque", specimen: "urine", unit: "", reference: "Negatif", low: null, high: null },
  { cat: "Urines", name: "Toxiques urinaires (depistage)", specimen: "urine", unit: "", reference: "Negatif", low: null, high: null },
  // Selles
  { cat: "Selles", name: "Coproculture", specimen: "selles", unit: "", reference: "Flore normale", low: null, high: null },
  { cat: "Selles", name: "Parasitologie des selles", specimen: "selles", unit: "", reference: "Absence de parasite", low: null, high: null },
  { cat: "Selles", name: "Recherche de sang occulte", specimen: "selles", unit: "", reference: "Negatif", low: null, high: null },
  { cat: "Selles", name: "Calprotectine fecale", specimen: "selles", unit: "ug/g", reference: "< 50", low: null, high: 50 },
  { cat: "Selles", name: "Antigene H. pylori (selles)", specimen: "selles", unit: "", reference: "Negatif", low: null, high: null },
  { cat: "Selles", name: "Antigenes viraux (rotavirus/adenovirus)", specimen: "selles", unit: "", reference: "Negatif", low: null, high: null },
  { cat: "Selles", name: "Toxine Clostridium difficile", specimen: "selles", unit: "", reference: "Negatif", low: null, high: null },
  // LCR (ponction lombaire)
  { cat: "LCR", name: "Cytologie du LCR", specimen: "LCR", unit: "/mm3", reference: "< 5 elements", low: null, high: 5 },
  { cat: "LCR", name: "Proteinorachie", specimen: "LCR", unit: "g/L", reference: "0.2 - 0.4", low: 0.2, high: 0.4 },
  { cat: "LCR", name: "Glycorachie", specimen: "LCR", unit: "mmol/L", reference: "2.2 - 3.9 (60% glycemie)", low: 2.2, high: 3.9 },
  { cat: "LCR", name: "Culture du LCR", specimen: "LCR", unit: "", reference: "Sterile", low: null, high: null },
  { cat: "LCR", name: "Examen direct LCR", specimen: "LCR", unit: "", reference: "Absence de germe", low: null, high: null },
  { cat: "LCR", name: "PCR meningite (multiplex)", specimen: "LCR", unit: "", reference: "Negatif", low: null, high: null },
  { cat: "LCR", name: "Lactates LCR", specimen: "LCR", unit: "mmol/L", reference: "< 2.1", low: null, high: 2.1 },
  // Pus / bacteriologie
  { cat: "Bacteriologie", name: "Examen direct + Gram", specimen: "pus", unit: "", reference: "Selon prelevement", low: null, high: null },
  { cat: "Bacteriologie", name: "Culture + antibiogramme", specimen: "pus", unit: "", reference: "Selon prelevement", low: null, high: null },
  { cat: "Bacteriologie", name: "Recherche BAAR (tuberculose)", specimen: "pus", unit: "", reference: "Negatif", low: null, high: null },
];

// Milieu de prelevement par categorie d'analyse (pour filtrer selon le type de prelevement)
const CAT_SPECIMEN = {
  "Hematologie": "sang", "Coagulation": "sang", "Glucides / Diabete": "sang",
  "Fonction renale": "sang", "Ionogramme & mineraux": "sang", "Foie & enzymes": "sang",
  "Lipides": "sang", "Inflammation": "sang", "Thyroide": "sang",
  "Marqueurs cardiaques": "sang", "Bilan martial (fer)": "sang",
  "Hormones & marqueurs": "sang", "Vitamines": "sang", "Serologies & infectieux": "sang",
  "Urines": "urine",
};
// Renvoie le milieu d'une analyse (defaut: sang)
function analysisSpecimen(item) { return item.specimen || CAT_SPECIMEN[item.cat] || "sang"; }
const LAB_BOUNDS = {};
LAB_CATALOG.forEach((it) => { LAB_BOUNDS[it.name] = { low: it.low, high: it.high }; });
function labFlag(name, result) {
  const b = LAB_BOUNDS[name];
  if (!b || result == null || result === "") return null;
  const v = parseFloat(String(result).replace(",", "."));
  if (isNaN(v)) return null;
  if (b.low != null && v < b.low) return "low";
  if (b.high != null && v > b.high) return "high";
  return null;
}

const LAB_STATUS = {
  pending: ["#FAEEDA", "#854F0B", "En attente"],
  in_progress: ["#E0ECFA", "#185FA5", "En cours"],
  completed: ["#EAF3DE", "#3B6D11", "Termine"],
};

function LabModule({ can }) {
  const [requests, setRequests] = useState([]);
  const [saving, setSaving] = useState(false);
  const [patients, setPatients] = useState([]);
  const [services, setServices] = useState([]);
  const [staff, setStaff] = useState([]);
  const [prescriberOther, setPrescriberOther] = useState(false);
  const [labSearch, setLabSearch] = useState("");
  const [showAllCat, setShowAllCat] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState("");
  const [detail, setDetail] = useState(null);
  const [resEdit, setResEdit] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const emptyForm = { patient_id: "", service_id: "", analyses: [], priority: "normal", reason: "", requested_by: "" };
  const [form, setForm] = useState(emptyForm);

  const load = () => {
    setLoading(true);
    const calls = [axios.get(`${API_URL}/api/sih/lab/requests`, getAuthConfig())];
    const canForm = can("lab:request");
    if (canForm) {
      calls.push(axios.get(`${API_URL}/api/sih/patients`, getAuthConfig()));
      calls.push(axios.get(`${API_URL}/api/sih/services`, getAuthConfig()));
      calls.push(axios.get(`${API_URL}/api/sih/staff`, getAuthConfig()));
    }
    Promise.all(calls).then((res) => {
      setRequests(res[0].data.requests || []);
      if (canForm) {
        setPatients(res[1].data.patients || []);
        setServices(res[2].data.services || []);
        setStaff(res[3].data.staff || []);
      }
    }).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const toggleAnalysis = (item) => {
    const exists = form.analyses.find((a) => a.name === item.name);
    const next = exists ? form.analyses.filter((a) => a.name !== item.name) : [...form.analyses, { name: item.name, unit: item.unit, result: "", reference: item.reference || "" }];
    setForm({ ...form, analyses: next });
  };
  const save = async () => {
    if (!form.patient_id) { setError("Patient obligatoire"); return; }
    if (form.analyses.length === 0) { setError("Selectionnez au moins une analyse"); return; }
    setError("");
    if (saving) return;
    setSaving(true);
    try { await axios.post(`${API_URL}/api/sih/lab/requests`, form, getAuthConfig()); setShowForm(false); setForm(emptyForm); load(); }
    catch (e) { setError(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Erreur"); }
    finally { setSaving(false); }
  };
  const openDetail = async (r) => {
    try { const x = await axios.get(`${API_URL}/api/sih/lab/requests/${r.request_id}`, getAuthConfig()); setDetail(x.data.request); }
    catch (e) { setError("Impossible de charger la demande"); }
  };
  const openResults = async (r) => {
    setError("");
    try { const x = await axios.get(`${API_URL}/api/sih/lab/requests/${r.request_id}`, getAuthConfig()); const req = x.data.request; setResEdit({ ...req, analyses: (req.analyses || []).map((a) => ({ ...a })), status: req.status === "pending" ? "in_progress" : req.status }); }
    catch (e) { setError("Impossible de charger la demande"); }
  };
  const saveResults = async () => {
    try { await axios.put(`${API_URL}/api/sih/lab/requests/${resEdit.request_id}/results`, { analyses: resEdit.analyses, status: resEdit.status }, getAuthConfig()); setResEdit(null); load(); }
    catch (e) { setError(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Erreur"); }
  };
  const doDelete = async () => { if (!toDelete) return; try { await axios.delete(`${API_URL}/api/sih/lab/requests/${toDelete.request_id}`, getAuthConfig()); setToDelete(null); load(); } catch (e) { setError("Erreur"); setToDelete(null); } };

  const iS = { width: "100%", padding: "9px 12px", border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 14, boxSizing: "border-box", background: "#fff" };
  const lab = { fontSize: 12, color: "#64748B", marginBottom: 4, display: "block" };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: "#0F172A", margin: 0 }}>Laboratoire</h1>
        {can("lab:request") && <button onClick={() => { setForm(emptyForm); setError(""); setPrescriberOther(false); setLabSearch(""); setShowAllCat(false); setShowForm(true); }} style={{ padding: "9px 18px", background: "#185FA5", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, cursor: "pointer" }}>+ Nouvelle demande</button>}
      </div>
      {error && !showForm && !resEdit && <div style={{ background: "#FCEBEB", border: "1px solid #F7C1C1", color: "#A32D2D", borderRadius: 8, padding: "10px 14px", marginTop: 14, fontSize: 13 }}>{error}</div>}

      {showForm && (
        <div style={{ background: "#fff", border: "1px solid #B5D4F4", borderRadius: 12, padding: 20, marginTop: 16 }}>
          <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 14 }}>Nouvelle demande d'analyses</div>
          {error && <div style={{ background: "#FCEBEB", border: "1px solid #F7C1C1", color: "#A32D2D", borderRadius: 8, padding: "10px 14px", marginBottom: 12, fontSize: 13 }}>{error}</div>}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
            <div><label style={lab}>Patient *</label><select style={iS} value={form.patient_id} onChange={(e) => setForm({ ...form, patient_id: e.target.value })}><option value="">...</option>{patients.map((p) => <option key={p.patient_id} value={p.patient_id}>{p.first_name} {p.last_name}</option>)}</select></div>
            <div><label style={lab}>Service</label><select style={iS} value={form.service_id} onChange={(e) => setForm({ ...form, service_id: e.target.value })}><option value="">—</option>{services.map((sv) => <option key={sv.service_id} value={sv.service_id}>{sv.name}</option>)}</select></div>
            <div><label style={lab}>Priorite</label><select style={iS} value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}><option value="normal">Normale</option><option value="urgent">Urgente</option></select></div>
          </div>
          <div style={{ marginTop: 14 }}>
            <label style={lab}>Analyses demandees * {form.analyses.length > 0 && <span style={{ color: "#185FA5" }}>({form.analyses.length})</span>}</label>
            {form.analyses.length > 0 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 10 }}>
                {form.analyses.map((a) => (
                  <span key={a.name} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, padding: "5px 8px 5px 12px", borderRadius: 16, background: "#185FA5", color: "#fff" }}>
                    {a.name}
                    <button type="button" onClick={() => toggleAnalysis(a)} style={{ border: "none", background: "rgba(255,255,255,0.25)", color: "#fff", borderRadius: "50%", width: 16, height: 16, cursor: "pointer", lineHeight: "14px", padding: 0 }}>×</button>
                  </span>
                ))}
              </div>
            )}
            <input style={iS} placeholder="Rechercher une analyse (ex: glycemie, NFS, sodium...)" value={labSearch} onChange={(e) => setLabSearch(e.target.value)} />
            {labSearch.trim() && (
              <div style={{ border: "1px solid #E2E8F0", borderRadius: 8, marginTop: 6, maxHeight: 220, overflowY: "auto" }}>
                {LAB_CATALOG.filter((it) => it.name.toLowerCase().includes(labSearch.trim().toLowerCase())).map((item) => { const on = !!form.analyses.find((a) => a.name === item.name); return (
                  <button type="button" key={item.name} onClick={() => toggleAnalysis(item)}
                    style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%", textAlign: "left", padding: "8px 12px", border: "none", borderBottom: "1px solid #F1F5F9", background: on ? "#E6F1FB" : "#fff", cursor: "pointer", fontSize: 13, color: on ? "#185FA5" : "#0F172A" }}>
                    <span>{on ? "\u2713 " : ""}{item.name}{item.unit ? ` (${item.unit})` : ""}</span>
                    <span style={{ fontSize: 11, color: "#94A3B8" }}>{item.cat}</span>
                  </button>); })}
                {LAB_CATALOG.filter((it) => it.name.toLowerCase().includes(labSearch.trim().toLowerCase())).length === 0 && <div style={{ padding: "10px 12px", fontSize: 13, color: "#94A3B8" }}>Aucune analyse trouvee</div>}
              </div>
            )}
            <button type="button" onClick={() => setShowAllCat(!showAllCat)} style={{ marginTop: 8, fontSize: 13, color: "#185FA5", background: "none", border: "none", cursor: "pointer", padding: 0 }}>{showAllCat ? "▴ Masquer le catalogue" : "▾ Parcourir par categorie"}</button>
            {showAllCat && (
              <div style={{ maxHeight: 280, overflowY: "auto", border: "1px solid #E2E8F0", borderRadius: 8, padding: 12, marginTop: 6 }}>
                {LAB_CATEGORIES.map((catName) => (
                  <div key={catName} style={{ marginBottom: 8 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "#94A3B8", textTransform: "uppercase", letterSpacing: 0.4, margin: "6px 0 6px" }}>{catName}</div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                      {LAB_CATALOG.filter((it) => it.cat === catName).map((item) => { const on = !!form.analyses.find((a) => a.name === item.name); return (
                        <button type="button" key={item.name} onClick={() => toggleAnalysis(item)}
                          style={{ fontSize: 12, padding: "5px 10px", borderRadius: 16, cursor: "pointer", border: on ? "1px solid #185FA5" : "1px solid #CBD5E1", background: on ? "#E6F1FB" : "#fff", color: on ? "#185FA5" : "#475569" }}>
                          {on ? "\u2713 " : ""}{item.name}{item.unit ? ` (${item.unit})` : ""}</button>); })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 14 }}>
            <div><label style={lab}>Medecin prescripteur</label>{prescriberOther ? (
              <input style={iS} placeholder="Nom du medecin" value={form.requested_by} onChange={(e) => setForm({ ...form, requested_by: e.target.value })} />
            ) : (
              <select style={iS} value={form.requested_by} onChange={(e) => { if (e.target.value === "__autre__") { setPrescriberOther(true); setForm({ ...form, requested_by: "" }); } else { setForm({ ...form, requested_by: e.target.value }); } }}>
                <option value="">...</option>
                {staff.filter((m) => m.role === "medecin" || m.role === "chef_service").map((m) => <option key={m.staff_id || m.full_name} value={m.full_name}>{m.full_name}</option>)}
                <option value="__autre__">Autre (saisie libre)</option>
              </select>
            )}</div>
            <div><label style={lab}>Motif</label><input style={iS} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} /></div>
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
            <button onClick={save} style={{ padding: "8px 18px", background: "#185FA5", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, cursor: "pointer" }}>Enregistrer</button>
            <button onClick={() => { setShowForm(false); setForm(emptyForm); }} style={{ padding: "8px 18px", background: "transparent", border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 14, cursor: "pointer" }}>Annuler</button>
          </div>
        </div>
      )}

      <div style={{ marginTop: 18, background: "#fff", border: "1px solid #E2E8F0", borderRadius: 12, overflow: "hidden" }}>
        {loading ? <div style={{ padding: 30, textAlign: "center", color: "#94A3B8" }}>Chargement...</div>
        : requests.length === 0 ? <div style={{ padding: 30, textAlign: "center", color: "#94A3B8", fontSize: 14 }}>Aucune demande d'analyses.</div>
        : (
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr style={{ background: "#F8FAFC", textAlign: "left" }}>{["Patient", "Service", "Analyses", "Priorite", "Statut", "Date", "Actions"].map((h) => <th key={h} style={{ padding: "10px 16px", fontSize: 12, color: "#64748B", fontWeight: 600 }}>{h}</th>)}</tr></thead>
            <tbody>
              {requests.map((r) => { const c = LAB_STATUS[r.status] || LAB_STATUS.pending; return (
                <tr key={r.request_id} style={{ borderTop: "1px solid #F1F5F9" }}>
                  <td style={{ padding: "12px 16px", fontSize: 14, fontWeight: 500 }}>{r.patient_name || "—"}</td>
                  <td style={{ padding: "12px 16px", fontSize: 13, color: "#475569" }}>{r.service_name || "—"}</td>
                  <td style={{ padding: "12px 16px", fontSize: 13, color: "#475569" }}>{r.analyses_count}</td>
                  <td style={{ padding: "12px 16px" }}>{r.priority === "urgent" ? <span style={{ fontSize: 12, padding: "3px 10px", borderRadius: 12, background: "#FCEBEB", color: "#A32D2D" }}>Urgente</span> : <span style={{ fontSize: 13, color: "#94A3B8" }}>Normale</span>}</td>
                  <td style={{ padding: "12px 16px" }}><span style={{ fontSize: 12, padding: "3px 10px", borderRadius: 12, background: c[0], color: c[1] }}>{c[2]}</span></td>
                  <td style={{ padding: "12px 16px", fontSize: 13, color: "#475569" }}>{fmtDate(r.created_at)}</td>
                  <td style={{ padding: "12px 16px" }}><span style={{ display: "flex", gap: 12 }}>
                    <button onClick={() => openDetail(r)} style={{ fontSize: 13, color: "#185FA5", background: "none", border: "none", cursor: "pointer" }}>Voir</button>
                    <button onClick={() => printExamRequest(r, "labo")} style={{ fontSize: 13, color: "#185FA5", background: "none", border: "none", cursor: "pointer" }}>Bon</button>
                    {can("lab:result") && <button onClick={() => openResults(r)} style={{ fontSize: 13, color: "#185FA5", background: "none", border: "none", cursor: "pointer" }}>Resultats</button>}
                    {r.status === "completed" && <button onClick={() => printLabResults(r)} style={{ fontSize: 13, color: "#3B6D11", background: "none", border: "none", cursor: "pointer" }}>Imprimer</button>}
                    {can("lab:request") && <button onClick={() => setToDelete(r)} style={{ fontSize: 13, color: "#A32D2D", background: "none", border: "none", cursor: "pointer" }}>Supprimer</button>}
                  </span></td>
                </tr>); })}
            </tbody>
          </table>
        )}
      </div>

      {detail && (
        <div onClick={() => setDetail(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: 24, overflow: "auto" }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 12, width: "90%", maxWidth: 620, padding: 24, marginTop: 20 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
              <div style={{ fontSize: 18, fontWeight: 700 }}>{detail.patient_name}</div>
              <button onClick={() => setDetail(null)} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#94A3B8" }}>×</button>
            </div>
            <div style={{ fontSize: 13, color: "#64748B", marginBottom: 16 }}>{detail.service_name || "—"}{detail.requested_by ? ` · Dr ${detail.requested_by}` : ""}{detail.reason ? ` · ${detail.reason}` : ""}</div>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr style={{ textAlign: "left" }}>{["Analyse", "Resultat", "Unite", "Reference"].map((h) => <th key={h} style={{ padding: "6px 8px", fontSize: 12, color: "#64748B" }}>{h}</th>)}</tr></thead>
              <tbody>
                {(detail.analyses || []).map((a, i) => { const fl = labFlag(a.name, a.result); return (
                  <tr key={i} style={{ borderTop: "1px solid #F1F5F9" }}>
                    <td style={{ padding: "8px 8px", fontSize: 13 }}>{a.name}</td>
                    <td style={{ padding: "8px 8px", fontSize: 13, fontWeight: 600, color: fl ? "#E11D48" : (a.result ? "#0F172A" : "#94A3B8") }}>{a.result || "—"}{fl === "high" ? " \u2191" : fl === "low" ? " \u2193" : ""}</td>
                    <td style={{ padding: "8px 8px", fontSize: 13, color: "#64748B" }}>{a.unit || ""}</td>
                    <td style={{ padding: "8px 8px", fontSize: 12, color: "#94A3B8" }}>{a.reference || ""}</td>
                  </tr>
                ); })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {resEdit && (
        <div onClick={() => setResEdit(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: 24, overflow: "auto" }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 12, width: "90%", maxWidth: 620, padding: 24, marginTop: 20 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <div style={{ fontSize: 18, fontWeight: 700 }}>Saisie des resultats</div>
              <button onClick={() => setResEdit(null)} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#94A3B8" }}>×</button>
            </div>
            {(resEdit.analyses || []).map((a, i) => { const fl = labFlag(a.name, a.result); return (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 0", borderBottom: "1px solid #F1F5F9" }}>
                <span style={{ flex: 1, fontSize: 13 }}>{a.name}</span>
                <input style={{ ...iS, maxWidth: 130, borderColor: fl ? "#E11D48" : "#CBD5E1", color: fl ? "#E11D48" : "#0F172A", fontWeight: fl ? 700 : 400 }} placeholder="Resultat" value={a.result || ""} onChange={(e) => { const na = resEdit.analyses.map((x, j) => j === i ? { ...x, result: e.target.value } : x); setResEdit({ ...resEdit, analyses: na }); }} />
                <span style={{ width: 45, fontSize: 12, color: "#64748B" }}>{a.unit || ""}</span>
                <span style={{ width: 120, fontSize: 11, color: "#94A3B8" }}>{a.reference ? `Ref: ${a.reference}` : ""}</span>
                <span style={{ width: 16, fontSize: 14, color: "#E11D48", fontWeight: 700 }}>{fl === "high" ? "\u2191" : fl === "low" ? "\u2193" : ""}</span>
              </div>
            ); })}
            <div style={{ marginTop: 14, display: "flex", alignItems: "center", gap: 10 }}>
              <label style={lab}>Statut</label>
              <select style={{ ...iS, maxWidth: 200 }} value={resEdit.status} onChange={(e) => setResEdit({ ...resEdit, status: e.target.value })}>
                <option value="in_progress">En cours</option><option value="completed">Termine</option>
              </select>
            </div>
            {error && <div style={{ background: "#FCEBEB", border: "1px solid #F7C1C1", color: "#A32D2D", borderRadius: 8, padding: "10px 14px", marginTop: 14, fontSize: 13 }}>{error}</div>}
            <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
              <button onClick={saveResults} style={{ padding: "8px 18px", background: "#185FA5", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, cursor: "pointer" }}>Enregistrer</button>
              <button onClick={() => setResEdit(null)} style={{ padding: "8px 18px", background: "transparent", border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 14, cursor: "pointer" }}>Annuler</button>
            </div>
          </div>
        </div>
      )}

      {toDelete && (
        <div onClick={() => setToDelete(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 12, width: "90%", maxWidth: 400, padding: 24 }}>
            <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>Supprimer cette demande ?</div>
            <div style={{ fontSize: 13, color: "#64748B", marginBottom: 20 }}>Demande de <b>{toDelete.patient_name}</b>. Action irreversible.</div>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button onClick={() => setToDelete(null)} style={{ fontSize: 13, padding: "8px 16px", borderRadius: 8, border: "1px solid #CBD5E1", background: "#fff", color: "#64748B", cursor: "pointer" }}>Annuler</button>
              <button onClick={doDelete} style={{ fontSize: 13, padding: "8px 16px", borderRadius: 8, border: "none", background: "#A32D2D", color: "#fff", cursor: "pointer" }}>Supprimer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


const IMAGING_CAT_ORDER = [
  "Radiologie conventionnelle", "Echographie / Doppler", "Scanner (TDM)",
  "IRM", "Medecine nucleaire", "Autres / interventionnel",
];
const IMAGING_CATALOG = [
  // Radiologie conventionnelle
  { cat: "Radiologie conventionnelle", name: "Radiographie thorax (face/profil)" },
  { cat: "Radiologie conventionnelle", name: "Radiographie abdomen sans preparation (ASP)" },
  { cat: "Radiologie conventionnelle", name: "Radiographie rachis cervical" },
  { cat: "Radiologie conventionnelle", name: "Radiographie rachis dorsal" },
  { cat: "Radiologie conventionnelle", name: "Radiographie rachis lombaire" },
  { cat: "Radiologie conventionnelle", name: "Radiographie bassin" },
  { cat: "Radiologie conventionnelle", name: "Radiographie crane" },
  { cat: "Radiologie conventionnelle", name: "Radiographie membre superieur" },
  { cat: "Radiologie conventionnelle", name: "Radiographie membre inferieur" },
  { cat: "Radiologie conventionnelle", name: "Radiographie sinus" },
  { cat: "Radiologie conventionnelle", name: "Panoramique dentaire" },
  { cat: "Radiologie conventionnelle", name: "Mammographie" },
  { cat: "Radiologie conventionnelle", name: "Hysterosalpingographie (HSG)" },
  { cat: "Radiologie conventionnelle", name: "Transit oeso-gastro-duodenal (TOGD)" },
  { cat: "Radiologie conventionnelle", name: "Lavement baryte" },
  { cat: "Radiologie conventionnelle", name: "Urographie intraveineuse (UIV)" },
  // Echographie / Doppler
  { cat: "Echographie / Doppler", name: "Echographie abdominale" },
  { cat: "Echographie / Doppler", name: "Echographie pelvienne" },
  { cat: "Echographie / Doppler", name: "Echographie renale / voies urinaires" },
  { cat: "Echographie / Doppler", name: "Echographie thyroidienne" },
  { cat: "Echographie / Doppler", name: "Echographie mammaire" },
  { cat: "Echographie / Doppler", name: "Echographie des parties molles" },
  { cat: "Echographie / Doppler", name: "Echographie obstetricale" },
  { cat: "Echographie / Doppler", name: "Echographie testiculaire" },
  { cat: "Echographie / Doppler", name: "Echocardiographie" },
  { cat: "Echographie / Doppler", name: "Echo-Doppler des membres inferieurs" },
  { cat: "Echographie / Doppler", name: "Echo-Doppler des troncs supra-aortiques (carotides)" },
  // Scanner (TDM)
  { cat: "Scanner (TDM)", name: "Scanner cerebral" },
  { cat: "Scanner (TDM)", name: "Scanner thoracique" },
  { cat: "Scanner (TDM)", name: "Scanner abdomino-pelvien" },
  { cat: "Scanner (TDM)", name: "Scanner du rachis" },
  { cat: "Scanner (TDM)", name: "Scanner des sinus / massif facial" },
  { cat: "Scanner (TDM)", name: "Scanner cervical" },
  { cat: "Scanner (TDM)", name: "Scanner osteo-articulaire" },
  { cat: "Scanner (TDM)", name: "Angioscanner" },
  { cat: "Scanner (TDM)", name: "Uroscanner" },
  { cat: "Scanner (TDM)", name: "Coloscanner" },
  // IRM
  { cat: "IRM", name: "IRM cerebrale" },
  { cat: "IRM", name: "IRM medullaire / rachis" },
  { cat: "IRM", name: "IRM abdominale" },
  { cat: "IRM", name: "IRM pelvienne" },
  { cat: "IRM", name: "IRM osteo-articulaire" },
  { cat: "IRM", name: "IRM cardiaque" },
  { cat: "IRM", name: "Angio-IRM" },
  { cat: "IRM", name: "Cholangio-IRM (bili-IRM)" },
  // Medecine nucleaire
  { cat: "Medecine nucleaire", name: "Scintigraphie osseuse" },
  { cat: "Medecine nucleaire", name: "Scintigraphie thyroidienne" },
  { cat: "Medecine nucleaire", name: "Scintigraphie myocardique" },
  { cat: "Medecine nucleaire", name: "Scintigraphie renale" },
  { cat: "Medecine nucleaire", name: "TEP-Scanner (PET-CT)" },
  // Autres / interventionnel
  { cat: "Autres / interventionnel", name: "Angiographie" },
  { cat: "Autres / interventionnel", name: "Osteodensitometrie (DMO)" },
  { cat: "Autres / interventionnel", name: "Echo-endoscopie" },
  { cat: "Autres / interventionnel", name: "Biopsie sous imagerie" },
];

const IMAGING_CONCLUSIONS = [
  "Examen normal. Pas d'anomalie decelable.",
  "Anomalie detectee (cf. observations).",
  "Aspect a surveiller, controle recommande.",
  "Avis specialise recommande.",
  "Examen non contributif, a refaire.",
];

const IMAGING_TEMPLATES = {
  "Radiographie thorax (face/profil)": "Technique : radiographie thoracique de face (+/- profil).\n\nResultats :\n- Champs pulmonaires libres, sans foyer de condensation.\n- Pas d'epanchement pleural.\n- Silhouette cardio-mediastinale de taille et morphologie normales.\n- Coupoles diaphragmatiques nettes.\n- Structures osseuses sans anomalie.\n",
  "Echographie abdominale": "Technique : echographie abdominale.\n\nResultats :\n- Foie de taille et d'echostructure normales, sans lesion focale.\n- Voies biliaires non dilatees. Vesicule biliaire alithiasique.\n- Pancreas sans anomalie visible.\n- Rate homogene, de taille normale.\n- Reins de taille normale, sans dilatation des cavites pyelocalicielles.\n- Pas d'epanchement intra-peritoneal.\n",
  "Scanner cerebral": "Technique : TDM cerebrale sans injection.\n\nResultats :\n- Pas d'hemorragie intra ou extra-axiale.\n- Pas de lesion focale parenchymateuse.\n- Structures medianes en place.\n- Systeme ventriculaire de taille normale, pas d'effet de masse.\n",
  "IRM cerebrale": "Technique : IRM cerebrale (T1, T2, FLAIR, diffusion).\n\nResultats :\n- Pas d'anomalie de signal du parenchyme cerebral.\n- Pas de prise de contraste anormale.\n- Pas de lesion ischemique recente en diffusion.\n- Structures medianes en place.\n",
  "Mammographie": "Technique : mammographie bilaterale (incidences face et oblique).\n\nResultats :\n- Densite mammaire : [A / B / C / D].\n- Pas d'opacite suspecte, pas de microcalcifications suspectes.\n- Pas de distorsion architecturale.\n\nClassification ACR : [a preciser].\n",
  "Echographie pelvienne": "Technique : echographie pelvienne (sus-pubienne +/- endovaginale).\n\nResultats :\n- Uterus de taille normale, endometre fin et regulier.\n- Ovaires de taille normale, sans formation suspecte.\n- Pas d'epanchement dans le cul-de-sac de Douglas.\n",
  "Echo-Doppler des membres inferieurs": "Technique : echo-Doppler veineux/arteriel des membres inferieurs.\n\nResultats :\n- Axes vasculaires permeables, sans thrombose.\n- Flux conserves, pas de stenose significative.\n",
};

function imagingTemplate(exam) {
  return IMAGING_TEMPLATES[exam] || ("Technique : " + (exam || "examen") + ".\n\nResultats :\n- \n");
}

function ImagingFiles({ req, can, onChange }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const files = req.files || [];
  const openFile = async (f) => {
    try { const x = await axios.get(`${API_URL}/api/sih/imaging/requests/${req.request_id}/files/${f.file_id}`, getAuthConfig()); window.open(x.data.url, "_blank"); }
    catch (e) { setErr("Impossible d'ouvrir le fichier"); }
  };
  const upload = async (e) => {
    const file = e.target.files && e.target.files[0]; if (!file) return;
    setErr(""); setBusy(true);
    const fd = new FormData(); fd.append("file", file);
    const cfg = getUploadConfig();
    try { await axios.post(`${API_URL}/api/sih/imaging/requests/${req.request_id}/files`, fd, cfg); onChange(); }
    catch (er) { setErr(er.response?.data?.detail || "Erreur lors de l'envoi"); }
    finally { setBusy(false); e.target.value = ""; }
  };
  const del = async (f) => {
    setErr("");
    try { await axios.delete(`${API_URL}/api/sih/imaging/requests/${req.request_id}/files/${f.file_id}`, getAuthConfig()); onChange(); }
    catch (er) { setErr("Erreur lors de la suppression"); }
  };
  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: "#185FA5", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>Images / fichiers</div>
      {err && <div style={{ background: "#FCEBEB", border: "1px solid #F7C1C1", color: "#A32D2D", borderRadius: 8, padding: "8px 12px", marginBottom: 8, fontSize: 12 }}>{err}</div>}
      {files.length === 0
        ? <div style={{ fontSize: 13, color: "#94A3B8", marginBottom: 8 }}>Aucun fichier joint.</div>
        : <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 8 }}>
            {files.map((f) => (
              <div key={f.file_id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", border: "1px solid #E2E8F0", borderRadius: 8, padding: "8px 12px" }}>
                <span style={{ fontSize: 13, color: "#0F172A" }}>{f.filename} <span style={{ color: "#94A3B8", fontSize: 12 }}>· {(f.size / 1024 / 1024).toFixed(2)} Mo</span></span>
                <span style={{ display: "flex", gap: 12 }}>
                  <button type="button" onClick={() => openFile(f)} style={{ fontSize: 13, color: "#185FA5", background: "none", border: "none", cursor: "pointer" }}>Voir</button>
                  {can("imaging:result") && <button type="button" onClick={() => del(f)} style={{ fontSize: 13, color: "#A32D2D", background: "none", border: "none", cursor: "pointer" }}>Supprimer</button>}
                </span>
              </div>
            ))}
          </div>}
      {can("imaging:result") && <label style={{ display: "inline-block", fontSize: 13, color: "#185FA5", cursor: "pointer", border: "1px dashed #B5D4F4", borderRadius: 8, padding: "8px 14px" }}>{busy ? "Envoi en cours..." : "+ Ajouter un fichier (JPEG, PNG, PDF - max 10 Mo)"}<input type="file" accept="image/jpeg,image/png,application/pdf" onChange={upload} style={{ display: "none" }} disabled={busy} /></label>}
    </div>
  );
}

function ImagingModule({ can }) {
  const [requests, setRequests] = useState([]);
  const [saving, setSaving] = useState(false);
  const [patients, setPatients] = useState([]);
  const [services, setServices] = useState([]);
  const [staff, setStaff] = useState([]);
  const [prescriberOther, setPrescriberOther] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState("");
  const [detail, setDetail] = useState(null);
  const [report, setReport] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const emptyForm = { patient_id: "", service_id: "", exam_type: "", region: "", priority: "normal", reason: "", requested_by: "" };
  const [form, setForm] = useState(emptyForm);

  const load = () => {
    setLoading(true);
    const calls = [axios.get(`${API_URL}/api/sih/imaging/requests`, getAuthConfig())];
    const canForm = can("imaging:request");
    if (canForm) {
      calls.push(axios.get(`${API_URL}/api/sih/patients`, getAuthConfig()));
      calls.push(axios.get(`${API_URL}/api/sih/services`, getAuthConfig()));
      calls.push(axios.get(`${API_URL}/api/sih/staff`, getAuthConfig()));
    }
    Promise.all(calls).then((res) => {
      setRequests(res[0].data.requests || []);
      if (canForm) { setPatients(res[1].data.patients || []); setServices(res[2].data.services || []); setStaff(res[3].data.staff || []); }
    }).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.patient_id) { setError("Patient obligatoire"); return; }
    if (!form.exam_type) { setError("Type d'examen obligatoire"); return; }
    setError("");
    if (saving) return;
    setSaving(true);
    try { await axios.post(`${API_URL}/api/sih/imaging/requests`, form, getAuthConfig()); setShowForm(false); setForm(emptyForm); load(); }
    catch (e) { setError(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Erreur"); }
    finally { setSaving(false); }
  };
  const openDetail = async (r) => {
    try { const x = await axios.get(`${API_URL}/api/sih/imaging/requests/${r.request_id}`, getAuthConfig()); setDetail(x.data.request); }
    catch (e) { setError("Impossible de charger la demande"); }
  };
  const openReport = async (r) => {
    setError("");
    try { const x = await axios.get(`${API_URL}/api/sih/imaging/requests/${r.request_id}`, getAuthConfig()); const req = x.data.request; setReport({ ...req, findings: req.findings || imagingTemplate(req.exam_type), conclusion: req.conclusion || "", status: req.status === "pending" ? "in_progress" : req.status }); }
    catch (e) { setError("Impossible de charger la demande"); }
  };
  const saveReport = async () => {
    try { await axios.put(`${API_URL}/api/sih/imaging/requests/${report.request_id}/report`, { findings: report.findings, conclusion: report.conclusion, status: report.status }, getAuthConfig()); setReport(null); load(); }
    catch (e) { setError(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Erreur"); }
  };
  const reloadReportFiles = async () => {
    try { const x = await axios.get(`${API_URL}/api/sih/imaging/requests/${report.request_id}`, getAuthConfig()); setReport((r) => r ? { ...r, files: x.data.request.files || [] } : r); } catch (e) {}
  };
  const reloadDetail = async () => {
    try { const x = await axios.get(`${API_URL}/api/sih/imaging/requests/${detail.request_id}`, getAuthConfig()); setDetail(x.data.request); } catch (e) {}
  };
  const doDelete = async () => { if (!toDelete) return; try { await axios.delete(`${API_URL}/api/sih/imaging/requests/${toDelete.request_id}`, getAuthConfig()); setToDelete(null); load(); } catch (e) { setError("Erreur"); setToDelete(null); } };

  const iS = { width: "100%", padding: "9px 12px", border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 14, boxSizing: "border-box", background: "#fff" };
  const taS = { ...iS, minHeight: 90, resize: "vertical" };
  const lab = { fontSize: 12, color: "#64748B", marginBottom: 4, display: "block" };
  const medStaff = staff.filter((m) => m.role === "medecin" || m.role === "chef_service");

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: "#0F172A", margin: 0 }}>Imagerie</h1>
        {can("imaging:request") && <button onClick={() => { setForm(emptyForm); setError(""); setPrescriberOther(false); setShowForm(true); }} style={{ padding: "9px 18px", background: "#185FA5", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, cursor: "pointer" }}>+ Nouvelle demande</button>}
      </div>
      {error && !showForm && !report && <div style={{ background: "#FCEBEB", border: "1px solid #F7C1C1", color: "#A32D2D", borderRadius: 8, padding: "10px 14px", marginTop: 14, fontSize: 13 }}>{error}</div>}

      {showForm && (
        <div style={{ background: "#fff", border: "1px solid #B5D4F4", borderRadius: 12, padding: 20, marginTop: 16 }}>
          <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 14 }}>Nouvelle demande d'imagerie</div>
          {error && <div style={{ background: "#FCEBEB", border: "1px solid #F7C1C1", color: "#A32D2D", borderRadius: 8, padding: "10px 14px", marginBottom: 12, fontSize: 13 }}>{error}</div>}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
            <div><label style={lab}>Patient *</label><select style={iS} value={form.patient_id} onChange={(e) => setForm({ ...form, patient_id: e.target.value })}><option value="">...</option>{patients.map((p) => <option key={p.patient_id} value={p.patient_id}>{p.first_name} {p.last_name}</option>)}</select></div>
            <div><label style={lab}>Service</label><select style={iS} value={form.service_id} onChange={(e) => setForm({ ...form, service_id: e.target.value })}><option value="">—</option>{services.map((sv) => <option key={sv.service_id} value={sv.service_id}>{sv.name}</option>)}</select></div>
            <div><label style={lab}>Priorite</label><select style={iS} value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}><option value="normal">Normale</option><option value="urgent">Urgente</option></select></div>
            <div><label style={lab}>Type d'examen *</label><select style={iS} value={form.exam_type} onChange={(e) => setForm({ ...form, exam_type: e.target.value })}><option value="">...</option>{IMAGING_CAT_ORDER.map((cat) => (<optgroup key={cat} label={cat}>{IMAGING_CATALOG.filter((x) => x.cat === cat).map((x) => <option key={x.name} value={x.name}>{x.name}</option>)}</optgroup>))}</select></div>
            <div><label style={lab}>Region anatomique</label><input style={iS} placeholder="ex: thorax, genou droit..." value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })} /></div>
            <div><label style={lab}>Medecin prescripteur</label>{prescriberOther ? (
              <input style={iS} placeholder="Nom du medecin" value={form.requested_by} onChange={(e) => setForm({ ...form, requested_by: e.target.value })} />
            ) : (
              <select style={iS} value={form.requested_by} onChange={(e) => { if (e.target.value === "__autre__") { setPrescriberOther(true); setForm({ ...form, requested_by: "" }); } else { setForm({ ...form, requested_by: e.target.value }); } }}>
                <option value="">...</option>
                {medStaff.map((m) => <option key={m.staff_id || m.full_name} value={m.full_name}>{m.full_name}</option>)}
                <option value="__autre__">Autre (saisie libre)</option>
              </select>
            )}</div>
          </div>
          <div style={{ marginTop: 12 }}><label style={lab}>Indication / motif</label><input style={iS} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} /></div>
          <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
            <button onClick={save} style={{ padding: "8px 18px", background: "#185FA5", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, cursor: "pointer" }}>Enregistrer</button>
            <button onClick={() => { setShowForm(false); setForm(emptyForm); }} style={{ padding: "8px 18px", background: "transparent", border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 14, cursor: "pointer" }}>Annuler</button>
          </div>
        </div>
      )}

      <div style={{ marginTop: 18, background: "#fff", border: "1px solid #E2E8F0", borderRadius: 12, overflow: "hidden" }}>
        {loading ? <div style={{ padding: 30, textAlign: "center", color: "#94A3B8" }}>Chargement...</div>
        : requests.length === 0 ? <div style={{ padding: 30, textAlign: "center", color: "#94A3B8", fontSize: 14 }}>Aucune demande d'imagerie.</div>
        : (
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr style={{ background: "#F8FAFC", textAlign: "left" }}>{["Patient", "Examen", "Region", "Priorite", "Statut", "Date", "Actions"].map((h) => <th key={h} style={{ padding: "10px 16px", fontSize: 12, color: "#64748B", fontWeight: 600 }}>{h}</th>)}</tr></thead>
            <tbody>
              {requests.map((r) => { const c = LAB_STATUS[r.status] || LAB_STATUS.pending; return (
                <tr key={r.request_id} style={{ borderTop: "1px solid #F1F5F9" }}>
                  <td style={{ padding: "12px 16px", fontSize: 14, fontWeight: 500 }}>{r.patient_name || "—"}</td>
                  <td style={{ padding: "12px 16px", fontSize: 13, color: "#475569" }}>{r.exam_type || "—"}</td>
                  <td style={{ padding: "12px 16px", fontSize: 13, color: "#475569" }}>{r.region || "—"}</td>
                  <td style={{ padding: "12px 16px" }}>{r.priority === "urgent" ? <span style={{ fontSize: 12, padding: "3px 10px", borderRadius: 12, background: "#FCEBEB", color: "#A32D2D" }}>Urgente</span> : <span style={{ fontSize: 13, color: "#94A3B8" }}>Normale</span>}</td>
                  <td style={{ padding: "12px 16px" }}><span style={{ fontSize: 12, padding: "3px 10px", borderRadius: 12, background: c[0], color: c[1] }}>{c[2]}</span></td>
                  <td style={{ padding: "12px 16px", fontSize: 13, color: "#475569" }}>{fmtDate(r.created_at)}</td>
                  <td style={{ padding: "12px 16px" }}><span style={{ display: "flex", gap: 12 }}>
                    <button onClick={() => openDetail(r)} style={{ fontSize: 13, color: "#185FA5", background: "none", border: "none", cursor: "pointer" }}>Voir</button>
                    <button onClick={() => printExamRequest(r, "imagerie")} style={{ fontSize: 13, color: "#185FA5", background: "none", border: "none", cursor: "pointer" }}>Bon</button>
                    {r.status === "completed" && <button onClick={() => printImagingReport(r)} style={{ fontSize: 13, color: "#3B6D11", background: "none", border: "none", cursor: "pointer" }}>Imprimer CR</button>}
                    {can("imaging:result") && <button onClick={() => openReport(r)} style={{ fontSize: 13, color: "#185FA5", background: "none", border: "none", cursor: "pointer" }}>Compte rendu</button>}
                    {can("imaging:request") && <button onClick={() => setToDelete(r)} style={{ fontSize: 13, color: "#A32D2D", background: "none", border: "none", cursor: "pointer" }}>Supprimer</button>}
                  </span></td>
                </tr>); })}
            </tbody>
          </table>
        )}
      </div>

      {detail && (
        <div onClick={() => setDetail(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: 24, overflow: "auto" }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 12, width: "90%", maxWidth: 620, padding: 24, marginTop: 20 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
              <div style={{ fontSize: 18, fontWeight: 700 }}>{detail.patient_name}</div>
              <button onClick={() => setDetail(null)} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#94A3B8" }}>×</button>
            </div>
            <div style={{ fontSize: 14, color: "#0F172A", fontWeight: 600, marginBottom: 2 }}>{detail.exam_type}{detail.region ? ` — ${detail.region}` : ""}</div>
            <div style={{ fontSize: 13, color: "#64748B", marginBottom: 16 }}>{detail.service_name || "—"}{detail.requested_by ? ` · Dr ${detail.requested_by}` : ""}{detail.reason ? ` · ${detail.reason}` : ""}</div>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#185FA5", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>Observations</div>
            <div style={{ fontSize: 13, color: detail.findings ? "#0F172A" : "#94A3B8", whiteSpace: "pre-wrap", marginBottom: 14 }}>{detail.findings || "Non renseigne"}</div>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#185FA5", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>Conclusion</div>
            <div style={{ fontSize: 13, color: detail.conclusion ? "#0F172A" : "#94A3B8", whiteSpace: "pre-wrap" }}>{detail.conclusion || "Non renseigne"}</div>
            <ImagingFiles req={detail} can={can} onChange={reloadDetail} />
          </div>
        </div>
      )}

      {report && (
        <div onClick={() => setReport(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: 24, overflow: "auto" }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 12, width: "90%", maxWidth: 620, padding: 24, marginTop: 20 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
              <div style={{ fontSize: 18, fontWeight: 700 }}>Compte rendu</div>
              <button onClick={() => setReport(null)} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#94A3B8" }}>×</button>
            </div>
            <div style={{ fontSize: 13, color: "#64748B", marginBottom: 16 }}>{report.patient_name} · {report.exam_type}{report.region ? ` — ${report.region}` : ""}</div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <label style={lab}>Observations</label>
              <button type="button" onClick={() => setReport({ ...report, findings: imagingTemplate(report.exam_type) })} style={{ fontSize: 12, color: "#185FA5", background: "none", border: "none", cursor: "pointer" }}>Inserer le modele</button>
            </div>
            <textarea style={taS} value={report.findings} onChange={(e) => setReport({ ...report, findings: e.target.value })} />
            <label style={{ ...lab, marginTop: 12 }}>Conclusion</label>
            <select style={{ ...iS, marginBottom: 8 }} value="" onChange={(e) => { if (e.target.value) setReport({ ...report, conclusion: e.target.value }); }}>
              <option value="">Conclusion type (a inserer)...</option>
              {IMAGING_CONCLUSIONS.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <textarea style={{ ...taS, minHeight: 60 }} value={report.conclusion} onChange={(e) => setReport({ ...report, conclusion: e.target.value })} />
            <div style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 10 }}>
              <label style={lab}>Statut</label>
              <select style={{ ...iS, maxWidth: 200 }} value={report.status} onChange={(e) => setReport({ ...report, status: e.target.value })}>
                <option value="in_progress">En cours</option><option value="completed">Termine</option>
              </select>
            </div>
            <ImagingFiles req={report} can={can} onChange={reloadReportFiles} />
            {error && <div style={{ background: "#FCEBEB", border: "1px solid #F7C1C1", color: "#A32D2D", borderRadius: 8, padding: "10px 14px", marginTop: 14, fontSize: 13 }}>{error}</div>}
            <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
              <button onClick={saveReport} style={{ padding: "8px 18px", background: "#185FA5", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, cursor: "pointer" }}>Enregistrer</button>
              <button onClick={() => setReport(null)} style={{ padding: "8px 18px", background: "transparent", border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 14, cursor: "pointer" }}>Annuler</button>
            </div>
          </div>
        </div>
      )}

      {toDelete && (
        <div onClick={() => setToDelete(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 12, width: "90%", maxWidth: 400, padding: 24 }}>
            <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>Supprimer cette demande ?</div>
            <div style={{ fontSize: 13, color: "#64748B", marginBottom: 20 }}>Examen de <b>{toDelete.patient_name}</b>. Action irreversible.</div>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button onClick={() => setToDelete(null)} style={{ fontSize: 13, padding: "8px 16px", borderRadius: 8, border: "1px solid #CBD5E1", background: "#fff", color: "#64748B", cursor: "pointer" }}>Annuler</button>
              <button onClick={doDelete} style={{ fontSize: 13, padding: "8px 16px", borderRadius: 8, border: "none", background: "#A32D2D", color: "#fff", cursor: "pointer" }}>Supprimer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


const MED_CATEGORIES = ["Antibiotique", "Antalgique", "Anti-inflammatoire (AINS)", "Antihypertenseur", "Antidiabetique", "Anticoagulant", "Corticoide", "Psychotrope", "Antiemetique", "Antispasmodique", "Solute / perfusion", "Antiseptique", "Vitamine / supplement", "Autre"];
const MED_FORMS = ["Comprime", "Gelule", "Sirop", "Solution buvable", "Injectable", "Perfusion", "Pommade / creme", "Collyre", "Suppositoire", "Spray / aerosol", "Sachet", "Autre"];
const MED_ROUTES = ["Orale", "Intraveineuse (IV)", "Intramusculaire (IM)", "Sous-cutanee (SC)", "Locale / cutanee", "Inhalee", "Rectale", "Ophtalmique", "Autre"];
const MED_UNITS = ["boite", "plaquette", "comprime", "gelule", "ampoule", "flacon", "poche", "tube", "sachet", "seringue"];
const REG_CLASSES = [["courant", "Courant"], ["liste_I", "Liste I"], ["liste_II", "Liste II"], ["stupefiant", "Stupefiant"]];

const MED_CATALOG = [
  { dci: "Paracetamol", category: "Antalgique", form: "Comprime", dosage: "500mg", route: "Orale", unit: "boite" },
  { dci: "Paracetamol", category: "Antalgique", form: "Comprime", dosage: "1g", route: "Orale", unit: "boite" },
  { dci: "Paracetamol", category: "Antalgique", form: "Injectable", dosage: "1g", route: "Intraveineuse (IV)", unit: "flacon" },
  { dci: "Amoxicilline", category: "Antibiotique", form: "Gelule", dosage: "500mg", route: "Orale", unit: "boite" },
  { dci: "Amoxicilline + Ac. clavulanique", category: "Antibiotique", form: "Comprime", dosage: "1g", route: "Orale", unit: "boite" },
  { dci: "Ceftriaxone", category: "Antibiotique", form: "Injectable", dosage: "1g", route: "Intraveineuse (IV)", unit: "flacon" },
  { dci: "Gentamicine", category: "Antibiotique", form: "Injectable", dosage: "80mg", route: "Intramusculaire (IM)", unit: "ampoule" },
  { dci: "Ciprofloxacine", category: "Antibiotique", form: "Comprime", dosage: "500mg", route: "Orale", unit: "boite" },
  { dci: "Metronidazole", category: "Antibiotique", form: "Perfusion", dosage: "500mg/100ml", route: "Intraveineuse (IV)", unit: "poche" },
  { dci: "Ibuprofene", category: "Anti-inflammatoire (AINS)", form: "Comprime", dosage: "400mg", route: "Orale", unit: "boite" },
  { dci: "Diclofenac", category: "Anti-inflammatoire (AINS)", form: "Injectable", dosage: "75mg", route: "Intramusculaire (IM)", unit: "ampoule" },
  { dci: "Aspirine", category: "Antalgique", form: "Comprime", dosage: "100mg", route: "Orale", unit: "boite" },
  { dci: "Omeprazole", category: "Autre", form: "Gelule", dosage: "20mg", route: "Orale", unit: "boite" },
  { dci: "Metformine", category: "Antidiabetique", form: "Comprime", dosage: "850mg", route: "Orale", unit: "boite" },
  { dci: "Insuline rapide", category: "Antidiabetique", form: "Injectable", dosage: "100UI/ml", route: "Sous-cutanee (SC)", unit: "flacon" },
  { dci: "Amlodipine", category: "Antihypertenseur", form: "Comprime", dosage: "5mg", route: "Orale", unit: "boite" },
  { dci: "Enalapril", category: "Antihypertenseur", form: "Comprime", dosage: "20mg", route: "Orale", unit: "boite" },
  { dci: "Bisoprolol", category: "Antihypertenseur", form: "Comprime", dosage: "5mg", route: "Orale", unit: "boite" },
  { dci: "Furosemide", category: "Antihypertenseur", form: "Comprime", dosage: "40mg", route: "Orale", unit: "boite" },
  { dci: "Atorvastatine", category: "Autre", form: "Comprime", dosage: "20mg", route: "Orale", unit: "boite" },
  { dci: "Salbutamol", category: "Autre", form: "Spray / aerosol", dosage: "100ug", route: "Inhalee", unit: "boite" },
  { dci: "Prednisolone", category: "Corticoide", form: "Comprime", dosage: "20mg", route: "Orale", unit: "boite" },
  { dci: "Dexamethasone", category: "Corticoide", form: "Injectable", dosage: "4mg", route: "Intraveineuse (IV)", unit: "ampoule" },
  { dci: "Diazepam", category: "Psychotrope", form: "Injectable", dosage: "10mg", route: "Intraveineuse (IV)", unit: "ampoule" },
  { dci: "Tramadol", category: "Antalgique", form: "Gelule", dosage: "50mg", route: "Orale", unit: "boite" },
  { dci: "Morphine", category: "Antalgique", form: "Injectable", dosage: "10mg", route: "Intraveineuse (IV)", unit: "ampoule" },
  { dci: "Metoclopramide", category: "Antiemetique", form: "Injectable", dosage: "10mg", route: "Intraveineuse (IV)", unit: "ampoule" },
  { dci: "Phloroglucinol (Spasfon)", category: "Antispasmodique", form: "Comprime", dosage: "80mg", route: "Orale", unit: "boite" },
  { dci: "Enoxaparine", category: "Anticoagulant", form: "Injectable", dosage: "4000UI", route: "Sous-cutanee (SC)", unit: "seringue" },
  { dci: "Serum sale 0.9%", category: "Solute / perfusion", form: "Perfusion", dosage: "500ml", route: "Intraveineuse (IV)", unit: "poche" },
  { dci: "Serum glucose 5%", category: "Solute / perfusion", form: "Perfusion", dosage: "500ml", route: "Intraveineuse (IV)", unit: "poche" },
  { dci: "Ringer lactate", category: "Solute / perfusion", form: "Perfusion", dosage: "500ml", route: "Intraveineuse (IV)", unit: "poche" },
  { dci: "Adrenaline", category: "Autre", form: "Injectable", dosage: "1mg/ml", route: "Intraveineuse (IV)", unit: "ampoule" },
  { dci: "Atropine", category: "Autre", form: "Injectable", dosage: "0.5mg", route: "Intraveineuse (IV)", unit: "ampoule" },
  { dci: "Povidone iodee (Betadine)", category: "Antiseptique", form: "Solution buvable", dosage: "10%", route: "Locale / cutanee", unit: "flacon" },
  { dci: "Sulfate ferreux", category: "Vitamine / supplement", form: "Comprime", dosage: "200mg", route: "Orale", unit: "boite" },
  { dci: "Acide folique", category: "Vitamine / supplement", form: "Comprime", dosage: "5mg", route: "Orale", unit: "boite" },
];

const PUI_ALERT_LABELS = {
  stock_bas: ["Stock bas", "#FCEBEB", "#A32D2D"],
  peremption_proche: ["Peremption proche", "#FAEEDA", "#854F0B"],
  lot_perime: ["Lot perime", "#FCEBEB", "#A32D2D"],
};

function regBadge(cls) {
  if (cls === "stupefiant") return ["Stupefiant", "#3A1212", "#fff"];
  if (cls === "liste_I") return ["Liste I", "#FCEBEB", "#A32D2D"];
  if (cls === "liste_II") return ["Liste II", "#FAEEDA", "#854F0B"];
  return null;
}

function CareModule({ can }) {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [doneFor, setDoneFor] = useState(null);
  const [doneNote, setDoneNote] = useState("");
  const [busyIds, setBusyIds] = useState(() => new Set());
  const [completing, setCompleting] = useState(false);
  const load = () => {
    setLoading(true);
    axios.get(`${API_URL}/api/sih/care/tasks`, getAuthConfig())
      .then((r) => setTasks((r.data.tasks || []).filter((t) => t.status === "todo" || t.status === "in_progress")))
      .catch(() => setError("Erreur de chargement"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);
  const take = async (id) => {
    // Correctif L-DOUBLECLICK : empeche un second clic de "prendre" deux
    // fois le meme soin avant le rafraichissement de la liste.
    if (busyIds.has(id)) return;
    setBusyIds((prev) => new Set(prev).add(id));
    try { await axios.put(`${API_URL}/api/sih/care/tasks/${id}/take`, { note: "" }, getAuthConfig()); load(); }
    catch (e) { setError(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Erreur"); }
    finally { setBusyIds((prev) => { const next = new Set(prev); next.delete(id); return next; }); }
  };
  const complete = async (id) => {
    if (completing) return;
    setCompleting(true);
    try { await axios.put(`${API_URL}/api/sih/care/tasks/${id}/done`, { note: doneNote }, getAuthConfig()); setDoneFor(null); setDoneNote(""); load(); }
    catch (e) { setError(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Erreur"); }
    finally { setCompleting(false); }
  };
  const badge = (s) => {
    const map = { todo: ["A faire", "#B45309", "#FEF3C7"], in_progress: ["En cours", "#185FA5", "#E6F1FB"] };
    const [lbl, col, bg] = map[s] || [s, "#475569", "#F1F5F9"];
    return <span style={{ fontSize: 12, fontWeight: 600, padding: "3px 10px", borderRadius: 12, color: col, background: bg }}>{lbl}</span>;
  };
  const prio = (p) => p === "urgent"
    ? <span style={{ fontSize: 11, fontWeight: 700, color: "#A32D2D", background: "#FCEBEB", padding: "2px 8px", borderRadius: 10 }}>URGENT</span>
    : <span style={{ fontSize: 11, color: "#94A3B8" }}>Normal</span>;
  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: "#0F172A", margin: 0 }}>Soins infirmiers</h1>
        <button onClick={load} style={{ padding: "9px 18px", background: "#fff", color: "#185FA5", border: "1px solid #185FA5", borderRadius: 8, fontSize: 14, cursor: "pointer" }}>Rafraichir</button>
      </div>
      {error && <div style={{ background: "#FCEBEB", border: "1px solid #F7C1C1", color: "#A32D2D", borderRadius: 8, padding: "10px 14px", marginTop: 14, fontSize: 13 }}>{error}</div>}
      <div style={{ marginTop: 18, background: "#fff", border: "1px solid #E2E8F0", borderRadius: 12, overflow: "hidden" }}>
        {loading ? <div style={{ padding: 30, textAlign: "center", color: "#94A3B8" }}>Chargement...</div>
        : tasks.length === 0 ? <div style={{ padding: 30, textAlign: "center", color: "#94A3B8", fontSize: 14 }}>Aucun soin a faire.</div>
        : (
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr style={{ background: "#F8FAFC", textAlign: "left" }}>
              <th style={{ padding: "12px 16px", fontSize: 12, color: "#64748B", fontWeight: 600 }}>Soin</th>
              <th style={{ padding: "12px 16px", fontSize: 12, color: "#64748B", fontWeight: 600 }}>Priorite</th>
              <th style={{ padding: "12px 16px", fontSize: 12, color: "#64748B", fontWeight: 600 }}>Prevu</th>
              <th style={{ padding: "12px 16px", fontSize: 12, color: "#64748B", fontWeight: 600 }}>Prescrit par</th>
              <th style={{ padding: "12px 16px", fontSize: 12, color: "#64748B", fontWeight: 600 }}>Statut</th>
              <th style={{ padding: "12px 16px", fontSize: 12, color: "#64748B", fontWeight: 600 }}>Pris par</th>
              <th style={{ padding: "12px 16px", fontSize: 12, color: "#64748B", fontWeight: 600 }}>Action</th>
            </tr></thead>
            <tbody>
              {tasks.map((t) => (
                <tr key={t.task_id} style={{ borderTop: "1px solid #F1F5F9" }}>
                  <td style={{ padding: "12px 16px", fontSize: 14, color: "#0F172A", fontWeight: 500 }}>{t.description}{t.recurrence ? <div style={{ fontSize: 12, color: "#94A3B8" }}>{t.recurrence}</div> : null}</td>
                  <td style={{ padding: "12px 16px" }}>{prio(t.priority)}</td>
                  <td style={{ padding: "12px 16px", fontSize: 13, color: "#475569" }}>{t.scheduled_at ? t.scheduled_at.replace("T", " ").slice(0, 16) : "\u2014"}</td>
                  <td style={{ padding: "12px 16px", fontSize: 13, color: "#475569" }}>{t.prescribed_by || "\u2014"}</td>
                  <td style={{ padding: "12px 16px" }}>{badge(t.status)}</td>
                  <td style={{ padding: "12px 16px", fontSize: 13, color: "#475569" }}>{t.taken_by || "\u2014"}</td>
                  <td style={{ padding: "12px 16px" }}>
                    {t.status === "todo" && can("care:perform") && <button onClick={() => take(t.task_id)} disabled={busyIds.has(t.task_id)} style={{ fontSize: 13, padding: "6px 12px", borderRadius: 8, border: "1px solid #185FA5", background: "#185FA5", color: "#fff", cursor: busyIds.has(t.task_id) ? "default" : "pointer", opacity: busyIds.has(t.task_id) ? 0.6 : 1 }}>Prendre</button>}
                    {t.status === "in_progress" && can("care:perform") && <button onClick={() => { setDoneFor(t.task_id); setDoneNote(""); }} style={{ fontSize: 13, padding: "6px 12px", borderRadius: 8, border: "1px solid #15803D", background: "#15803D", color: "#fff", cursor: "pointer" }}>Valider</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {doneFor && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
          <div style={{ background: "#fff", borderRadius: 12, padding: 24, width: 440, maxWidth: "90%" }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#0F172A", marginBottom: 12 }}>Valider le soin</div>
            <label style={{ fontSize: 13, color: "#475569" }}>Note (optionnel)</label>
            <textarea value={doneNote} onChange={(e) => setDoneNote(e.target.value)} rows={3} style={{ width: "100%", marginTop: 6, padding: 10, borderRadius: 8, border: "1px solid #CBD5E1", fontSize: 14, boxSizing: "border-box" }} placeholder="Ex: pansement refait, plaie propre" />
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 16 }}>
              <button onClick={() => { setDoneFor(null); setDoneNote(""); }} disabled={completing} style={{ padding: "8px 16px", background: "transparent", border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 14, cursor: completing ? "default" : "pointer" }}>Annuler</button>
              <button onClick={() => complete(doneFor)} disabled={completing} style={{ padding: "8px 16px", background: "#15803D", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, cursor: completing ? "default" : "pointer", opacity: completing ? 0.7 : 1 }}>{completing ? "Enregistrement..." : "Valider le soin"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
function PharmacyModule({ can }) {
  const [sub, setSub] = useState("livret");
  const tabs = [["livret", "Livret & stock"], ["mov", "Mouvements"], ["stup", "Stupefiants"], ["alerts", "Alertes"]];
  return (
    <div>
      <h1 style={{ fontSize: 22, fontWeight: 700, color: "#0F172A", margin: "0 0 16px" }}>Pharmacie (PUI)</h1>
      <div style={{ display: "flex", gap: 8, marginBottom: 18, flexWrap: "wrap" }}>
        {tabs.map(([id, label]) => (
          <button key={id} onClick={() => setSub(id)}
            style={{ fontSize: 14, padding: "7px 16px", borderRadius: 8, cursor: "pointer",
              border: sub === id ? "1px solid #185FA5" : "1px solid #CBD5E1",
              background: sub === id ? "#185FA5" : "#fff", color: sub === id ? "#fff" : "#475569" }}>{label}</button>
        ))}
      </div>
      {sub === "livret" && <PharmacyLivret can={can} stupOnly={false} />}
      {sub === "mov" && <PharmacyMovements stupOnly={false} />}
      {sub === "stup" && <PharmacyLivret can={can} stupOnly={true} />}
      {sub === "alerts" && <PharmacyAlerts />}
    </div>
  );
}

const pIS = { width: "100%", padding: "9px 12px", border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 14, boxSizing: "border-box", background: "#fff" };
const pLab = { fontSize: 12, color: "#64748B", marginBottom: 4, display: "block" };

function PharmacyLivret({ can, stupOnly }) {
  const [meds, setMeds] = useState([]);
  const [saving, setSaving] = useState(false);
  const [services, setServices] = useState([]);
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [catSearch, setCatSearch] = useState("");
  const [toDelete, setToDelete] = useState(null);
  const [lotsFor, setLotsFor] = useState(null);
  const [transferFor, setTransferFor] = useState(null);
  const [dispenseFor, setDispenseFor] = useState(null);
  const empty = { dci: "", brand_name: "", category: "", form: "", dosage: "", route: "", unit: "boite", pack_size: "", regulatory_class: "courant", threshold: 0 };
  const [form, setForm] = useState(empty);
  const canStock = can("pharmacy:stock"), canDisp = can("pharmacy:dispense");

  const load = () => {
    setLoading(true);
    const calls = [axios.get(`${API_URL}/api/sih/pharmacy/medications`, getAuthConfig()), axios.get(`${API_URL}/api/sih/services`, getAuthConfig())];
    if (can("patients:read")) calls.push(axios.get(`${API_URL}/api/sih/patients`, getAuthConfig()));
    Promise.all(calls).then((res) => {
      let m = res[0].data.medications || [];
      if (stupOnly) m = m.filter((x) => x.regulatory_class === "stupefiant");
      setMeds(m); setServices(res[1].data.services || []); if (res[2]) setPatients(res[2].data.patients || []);
    }).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [stupOnly]);

  const save = async () => {
    if (!form.dci.trim()) { setError("La DCI (nom generique) est obligatoire"); return; }
    setError("");
    if (saving) return;
    setSaving(true);
    const payload = { ...form, threshold: Number(form.threshold) || 0, pack_size: form.pack_size ? Number(form.pack_size) : null };
    try {
      if (editing) await axios.put(`${API_URL}/api/sih/pharmacy/medications/${editing}`, payload, getAuthConfig());
      else await axios.post(`${API_URL}/api/sih/pharmacy/medications`, payload, getAuthConfig());
      setShowForm(false); setForm(empty); setEditing(null); setCatSearch(""); load();
    } catch (e) { setError(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Erreur"); }
    finally { setSaving(false); }
  };
  const startEdit = (m) => { setEditing(m.medication_id); setForm({ dci: m.dci || m.name || "", brand_name: m.brand_name || "", category: m.category || "", form: m.form || "", dosage: m.dosage || "", route: m.route || "", unit: m.unit || "boite", pack_size: m.pack_size || "", regulatory_class: m.regulatory_class || "courant", threshold: m.threshold || 0 }); setError(""); setCatSearch(""); setShowForm(true); };
  const doDelete = async () => { if (!toDelete) return; try { await axios.delete(`${API_URL}/api/sih/pharmacy/medications/${toDelete.medication_id}`, getAuthConfig()); setToDelete(null); load(); } catch (e) { setError(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Erreur"); setToDelete(null); } };
  const pickCatalog = (it) => { setForm({ ...form, dci: it.dci, category: it.category, form: it.form, dosage: it.dosage, route: it.route, unit: it.unit }); setCatSearch(""); };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h2 style={{ fontSize: 17, fontWeight: 700, color: "#0F172A", margin: 0 }}>{stupOnly ? "Registre des stupefiants" : "Livret therapeutique & stock"}</h2>
        {canStock && !stupOnly && <button onClick={() => { setForm(empty); setEditing(null); setError(""); setCatSearch(""); setShowForm(true); }} style={{ padding: "9px 18px", background: "#185FA5", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, cursor: "pointer" }}>+ Nouveau medicament</button>}
      </div>
      {error && !showForm && <div style={{ background: "#FCEBEB", border: "1px solid #F7C1C1", color: "#A32D2D", borderRadius: 8, padding: "10px 14px", marginTop: 14, fontSize: 13 }}>{error}</div>}

      {showForm && (
        <div style={{ background: "#fff", border: "1px solid #B5D4F4", borderRadius: 12, padding: 20, marginTop: 16 }}>
          <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 14 }}>{editing ? "Modifier la fiche" : "Nouveau medicament"}</div>
          {error && <div style={{ background: "#FCEBEB", border: "1px solid #F7C1C1", color: "#A32D2D", borderRadius: 8, padding: "10px 14px", marginBottom: 12, fontSize: 13 }}>{error}</div>}
          {!editing && (
            <div style={{ marginBottom: 14 }}>
              <label style={pLab}>Catalogue (pre-remplit la fiche, modifiable)</label>
              <input style={pIS} placeholder="Rechercher un medicament..." value={catSearch} onChange={(e) => setCatSearch(e.target.value)} />
              {catSearch.trim() && (
                <div style={{ border: "1px solid #E2E8F0", borderRadius: 8, marginTop: 6, maxHeight: 180, overflowY: "auto" }}>
                  {MED_CATALOG.filter((it) => (it.dci + " " + it.dosage).toLowerCase().includes(catSearch.trim().toLowerCase())).map((it, i) => (
                    <button type="button" key={i} onClick={() => pickCatalog(it)} style={{ display: "block", width: "100%", textAlign: "left", padding: "8px 12px", border: "none", borderBottom: "1px solid #F1F5F9", background: "#fff", cursor: "pointer", fontSize: 13 }}>{it.dci} {it.dosage} <span style={{ color: "#94A3B8" }}>· {it.form} · {it.category}</span></button>
                  ))}
                </div>
              )}
            </div>
          )}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
            <div><label style={pLab}>DCI (nom generique) *</label><input style={pIS} value={form.dci} onChange={(e) => setForm({ ...form, dci: e.target.value })} /></div>
            <div><label style={pLab}>Nom commercial</label><input style={pIS} value={form.brand_name} onChange={(e) => setForm({ ...form, brand_name: e.target.value })} /></div>
            <div><label style={pLab}>Categorie therapeutique</label><select style={pIS} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}><option value="">...</option>{MED_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}</select></div>
            <div><label style={pLab}>Forme galenique</label><select style={pIS} value={form.form} onChange={(e) => setForm({ ...form, form: e.target.value })}><option value="">...</option>{MED_FORMS.map((c) => <option key={c} value={c}>{c}</option>)}</select></div>
            <div><label style={pLab}>Dosage</label><input style={pIS} placeholder="500mg..." value={form.dosage} onChange={(e) => setForm({ ...form, dosage: e.target.value })} /></div>
            <div><label style={pLab}>Voie d'administration</label><select style={pIS} value={form.route} onChange={(e) => setForm({ ...form, route: e.target.value })}><option value="">...</option>{MED_ROUTES.map((c) => <option key={c} value={c}>{c}</option>)}</select></div>
            <div><label style={pLab}>Unite de stock</label><select style={pIS} value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })}>{MED_UNITS.map((c) => <option key={c} value={c}>{c}</option>)}</select></div>
            <div><label style={pLab}>Conditionnement (unites/boite)</label><input type="number" style={pIS} value={form.pack_size} onChange={(e) => setForm({ ...form, pack_size: e.target.value })} /></div>
            <div><label style={pLab}>Classe reglementaire</label><select style={pIS} value={form.regulatory_class} onChange={(e) => setForm({ ...form, regulatory_class: e.target.value })}>{REG_CLASSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></div>
            <div><label style={pLab}>Seuil d'alerte</label><input type="number" style={pIS} value={form.threshold} onChange={(e) => setForm({ ...form, threshold: e.target.value })} /></div>
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
            <button onClick={save} style={{ padding: "8px 18px", background: "#185FA5", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, cursor: "pointer" }}>Enregistrer</button>
            <button onClick={() => { setShowForm(false); setForm(empty); setEditing(null); }} style={{ padding: "8px 18px", background: "transparent", border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 14, cursor: "pointer" }}>Annuler</button>
          </div>
        </div>
      )}

      <div style={{ marginTop: 18, background: "#fff", border: "1px solid #E2E8F0", borderRadius: 12, overflow: "auto" }}>
        {loading ? <div style={{ padding: 30, textAlign: "center", color: "#94A3B8" }}>Chargement...</div>
        : meds.length === 0 ? <div style={{ padding: 30, textAlign: "center", color: "#94A3B8", fontSize: 14 }}>{stupOnly ? "Aucun stupefiant au livret." : "Aucun medicament au livret."}</div>
        : (
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 880 }}>
            <thead><tr style={{ background: "#F8FAFC", textAlign: "left" }}>{["Medicament", "Categorie", "Voie", "Stock total", "Central / Services", "Seuil", "Alerte", "Actions"].map((h) => <th key={h} style={{ padding: "10px 14px", fontSize: 12, color: "#64748B", fontWeight: 600 }}>{h}</th>)}</tr></thead>
            <tbody>
              {meds.map((m) => { const rb = regBadge(m.regulatory_class); return (
                <tr key={m.medication_id} style={{ borderTop: "1px solid #F1F5F9" }}>
                  <td style={{ padding: "12px 14px", fontSize: 14 }}>
                    <span style={{ fontWeight: 500 }}>{m.dci || m.name}</span> {m.dosage && <span style={{ color: "#64748B", fontSize: 13 }}>{m.dosage}</span>}
                    {m.brand_name && <span style={{ color: "#94A3B8", fontSize: 12 }}> · {m.brand_name}</span>}
                    {rb && <span style={{ fontSize: 10, padding: "2px 7px", borderRadius: 10, background: rb[1], color: rb[2], marginLeft: 6 }}>{rb[0]}</span>}
                    <div style={{ fontSize: 11, color: "#94A3B8" }}>{m.form}</div>
                  </td>
                  <td style={{ padding: "12px 14px", fontSize: 13, color: "#475569" }}>{m.category || "—"}</td>
                  <td style={{ padding: "12px 14px", fontSize: 13, color: "#475569" }}>{m.route || "—"}</td>
                  <td style={{ padding: "12px 14px", fontSize: 14, fontWeight: 600 }}>{m.total_quantity || 0} <span style={{ fontSize: 11, fontWeight: 400, color: "#94A3B8" }}>{m.unit || ""}</span></td>
                  <td style={{ padding: "12px 14px", fontSize: 13, color: "#475569" }}>{m.central_quantity || 0} / {m.service_quantity || 0}</td>
                  <td style={{ padding: "12px 14px", fontSize: 13, color: "#475569" }}>{m.threshold || 0}</td>
                  <td style={{ padding: "12px 14px" }}>{(!m.alerts || m.alerts.length === 0) ? <span style={{ fontSize: 12, color: "#3B6D11" }}>OK</span> : m.alerts.map((a, i) => { const l = PUI_ALERT_LABELS[a]; return l ? <span key={i} style={{ fontSize: 10, padding: "3px 7px", borderRadius: 10, background: l[1], color: l[2], marginRight: 4, display: "inline-block", marginBottom: 2 }}>{l[0]}</span> : null; })}</td>
                  <td style={{ padding: "12px 14px" }}><span style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                    <button onClick={() => setLotsFor(m)} style={{ fontSize: 13, color: "#185FA5", background: "none", border: "none", cursor: "pointer" }}>Lots</button>
                    {canStock && <button onClick={() => setTransferFor(m)} style={{ fontSize: 13, color: "#3B6D11", background: "none", border: "none", cursor: "pointer" }}>Doter</button>}
                    {canDisp && <button onClick={() => setDispenseFor(m)} style={{ fontSize: 13, color: "#185FA5", background: "none", border: "none", cursor: "pointer" }}>Dispenser</button>}
                    {canStock && <button onClick={() => startEdit(m)} style={{ fontSize: 13, color: "#64748B", background: "none", border: "none", cursor: "pointer" }}>Modifier</button>}
                    {canStock && <button onClick={() => setToDelete(m)} style={{ fontSize: 13, color: "#A32D2D", background: "none", border: "none", cursor: "pointer" }}>Suppr.</button>}
                  </span></td>
                </tr>); })}
            </tbody>
          </table>
        )}
      </div>

      {lotsFor && <LotsModal med={lotsFor} canStock={canStock} onClose={() => setLotsFor(null)} onChange={load} />}
      {transferFor && <TransferModal med={transferFor} services={services} onClose={() => setTransferFor(null)} onDone={() => { setTransferFor(null); load(); }} />}
      {dispenseFor && <DispenseModal med={dispenseFor} services={services} patients={patients} onClose={() => setDispenseFor(null)} onDone={() => { setDispenseFor(null); load(); }} />}

      {toDelete && (
        <div onClick={() => setToDelete(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 12, width: "90%", maxWidth: 400, padding: 24 }}>
            <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>Supprimer ce medicament ?</div>
            <div style={{ fontSize: 13, color: "#64748B", marginBottom: 20 }}><b>{toDelete.dci || toDelete.name} {toDelete.dosage}</b>. Impossible s'il reste du stock.</div>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button onClick={() => setToDelete(null)} style={{ fontSize: 13, padding: "8px 16px", borderRadius: 8, border: "1px solid #CBD5E1", background: "#fff", color: "#64748B", cursor: "pointer" }}>Annuler</button>
              <button onClick={doDelete} style={{ fontSize: 13, padding: "8px 16px", borderRadius: 8, border: "none", background: "#A32D2D", color: "#fff", cursor: "pointer" }}>Supprimer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function LotsModal({ med, canStock, onClose, onChange }) {
  const [lots, setLots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [showRecv, setShowRecv] = useState(false);
  const empty = { lot_number: "", quantity: "", expiry_date: "", unit_price: "", supplier: "" };
  const [recv, setRecv] = useState(empty);
  const [adjust, setAdjust] = useState(null); // {lot, location}
  const [adjForm, setAdjForm] = useState({ delta: "", reason: "" });

  const load = () => { setLoading(true); axios.get(`${API_URL}/api/sih/pharmacy/medications/${med.medication_id}/lots`, getAuthConfig()).then((r) => setLots(r.data.lots || [])).catch(() => {}).finally(() => setLoading(false)); };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const doReceive = async () => {
    if (!recv.lot_number.trim() || !recv.quantity || !recv.expiry_date) { setErr("N° de lot, quantite et peremption obligatoires"); return; }
    setErr("");
    try {
      await axios.post(`${API_URL}/api/sih/pharmacy/lots`, { medication_id: med.medication_id, lot_number: recv.lot_number, quantity: Number(recv.quantity), expiry_date: recv.expiry_date, unit_price: recv.unit_price ? Number(recv.unit_price) : null, supplier: recv.supplier || null }, getAuthConfig());
      setRecv(empty); setShowRecv(false); load(); onChange();
    } catch (e) { setErr(e.response?.data?.detail || "Erreur"); }
  };
  const doAdjust = async () => {
    if (!adjForm.delta || Number(adjForm.delta) === 0 || !adjForm.reason.trim()) { setErr("Variation (non nulle) et motif obligatoires"); return; }
    setErr("");
    try {
      await axios.post(`${API_URL}/api/sih/pharmacy/adjust`, { lot_id: adjust.lot.lot_id, location: adjust.location, delta: Number(adjForm.delta), reason: adjForm.reason }, getAuthConfig());
      setAdjust(null); setAdjForm({ delta: "", reason: "" }); load(); onChange();
    } catch (e) { setErr(e.response?.data?.detail || "Erreur"); }
  };

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: 24, overflow: "auto" }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 12, width: "90%", maxWidth: 640, padding: 24, marginTop: 20 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
          <div style={{ fontSize: 17, fontWeight: 700 }}>Lots — {med.dci || med.name} {med.dosage}</div>
          <button onClick={onClose} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#94A3B8" }}>×</button>
        </div>
        <div style={{ fontSize: 12, color: "#64748B", marginBottom: 14 }}>Dispensation en FEFO (le lot qui perime en premier sort en premier).</div>
        {err && <div style={{ background: "#FCEBEB", border: "1px solid #F7C1C1", color: "#A32D2D", borderRadius: 8, padding: "8px 12px", marginBottom: 12, fontSize: 12 }}>{err}</div>}

        {canStock && (showRecv ? (
          <div style={{ background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 10, padding: 16, marginBottom: 16 }}>
            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 10 }}>Receptionner un lot (PUI centrale)</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
              <div><label style={pLab}>N° de lot *</label><input style={pIS} value={recv.lot_number} onChange={(e) => setRecv({ ...recv, lot_number: e.target.value })} /></div>
              <div><label style={pLab}>Quantite *</label><input type="number" style={pIS} value={recv.quantity} onChange={(e) => setRecv({ ...recv, quantity: e.target.value })} /></div>
              <div><label style={pLab}>Peremption *</label><input type="date" style={pIS} value={recv.expiry_date} onChange={(e) => setRecv({ ...recv, expiry_date: e.target.value })} /></div>
              <div><label style={pLab}>Prix unitaire (DA)</label><input type="number" style={pIS} value={recv.unit_price} onChange={(e) => setRecv({ ...recv, unit_price: e.target.value })} /></div>
              <div style={{ gridColumn: "span 2" }}><label style={pLab}>Fournisseur</label><input style={pIS} value={recv.supplier} onChange={(e) => setRecv({ ...recv, supplier: e.target.value })} /></div>
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
              <button onClick={doReceive} style={{ padding: "8px 16px", background: "#3B6D11", color: "#fff", border: "none", borderRadius: 8, fontSize: 13, cursor: "pointer" }}>Receptionner</button>
              <button onClick={() => { setShowRecv(false); setRecv(empty); }} style={{ padding: "8px 16px", background: "transparent", border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 13, cursor: "pointer" }}>Annuler</button>
            </div>
          </div>
        ) : <button onClick={() => { setErr(""); setShowRecv(true); }} style={{ marginBottom: 16, padding: "8px 16px", background: "#3B6D11", color: "#fff", border: "none", borderRadius: 8, fontSize: 13, cursor: "pointer" }}>+ Receptionner un lot</button>)}

        {loading ? <div style={{ padding: 20, textAlign: "center", color: "#94A3B8" }}>Chargement...</div>
        : lots.length === 0 ? <div style={{ padding: 20, textAlign: "center", color: "#94A3B8", fontSize: 13 }}>Aucun lot en stock.</div>
        : lots.map((l) => (
          <div key={l.lot_id} style={{ border: "1px solid #E2E8F0", borderRadius: 10, padding: 12, marginBottom: 8 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ fontSize: 14 }}>
                <b>Lot {l.lot_number}</b> · péremption {l.expiry_date}
                {l.expired ? <span style={{ fontSize: 10, padding: "2px 7px", borderRadius: 10, background: "#FCEBEB", color: "#A32D2D", marginLeft: 6 }}>Perime</span>
                  : (l.days_left != null && l.days_left <= 60 ? <span style={{ fontSize: 10, padding: "2px 7px", borderRadius: 10, background: "#FAEEDA", color: "#854F0B", marginLeft: 6 }}>J-{l.days_left}</span> : null)}
                <div style={{ fontSize: 12, color: "#64748B", marginTop: 2 }}>Total : {l.total} {med.unit || ""} {l.unit_price ? `· ${l.unit_price} DA/u` : ""} {l.supplier ? `· ${l.supplier}` : ""}</div>
                <div style={{ fontSize: 12, color: "#94A3B8" }}>{l.locations.map((x) => `${x.location_name}: ${x.quantity}`).join("  ·  ") || "—"}</div>
              </div>
              {canStock && <button onClick={() => { setErr(""); setAdjust({ lot: l, location: "central" }); setAdjForm({ delta: "", reason: "" }); }} style={{ fontSize: 12, color: "#A32D2D", background: "none", border: "none", cursor: "pointer" }}>Ajuster</button>}
            </div>
            {adjust && adjust.lot.lot_id === l.lot_id && (
              <div style={{ marginTop: 10, borderTop: "1px dashed #E2E8F0", paddingTop: 10 }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 2fr", gap: 8 }}>
                  <div><label style={pLab}>Localisation</label><select style={pIS} value={adjust.location} onChange={(e) => setAdjust({ ...adjust, location: e.target.value })}><option value="central">PUI centrale</option>{l.locations.filter((x) => x.location !== "central").map((x) => <option key={x.location} value={x.location}>{x.location_name}</option>)}</select></div>
                  <div><label style={pLab}>Variation (+/-)</label><input type="number" style={pIS} placeholder="-5" value={adjForm.delta} onChange={(e) => setAdjForm({ ...adjForm, delta: e.target.value })} /></div>
                  <div><label style={pLab}>Motif *</label><input style={pIS} placeholder="Casse, destruction perime..." value={adjForm.reason} onChange={(e) => setAdjForm({ ...adjForm, reason: e.target.value })} /></div>
                </div>
                <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                  <button onClick={doAdjust} style={{ padding: "6px 14px", background: "#A32D2D", color: "#fff", border: "none", borderRadius: 8, fontSize: 13, cursor: "pointer" }}>Valider l'ajustement</button>
                  <button onClick={() => setAdjust(null)} style={{ padding: "6px 14px", background: "transparent", border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 13, cursor: "pointer" }}>Annuler</button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function TransferModal({ med, services, onClose, onDone }) {
  const [serviceId, setServiceId] = useState("");
  const [qty, setQty] = useState("");
  const [reason, setReason] = useState("");
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!serviceId) { setErr("Choisissez un service"); return; }
    if (!qty || Number(qty) <= 0) { setErr("Quantite invalide"); return; }
    setErr(""); setSaving(true);
    try { await axios.post(`${API_URL}/api/sih/pharmacy/transfer`, { medication_id: med.medication_id, service_id: serviceId, quantity: Number(qty), reason }, getAuthConfig()); onDone(); }
    catch (e) { setErr(e.response?.data?.detail || "Erreur"); setSaving(false); }
  };
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 12, width: "90%", maxWidth: 440, padding: 24 }}>
        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>Dotation vers un service</div>
        <div style={{ fontSize: 13, color: "#64748B", marginBottom: 16 }}>{med.dci || med.name} {med.dosage} · central : {med.central_quantity || 0} {med.unit || ""} (FEFO)</div>
        {err && <div style={{ background: "#FCEBEB", border: "1px solid #F7C1C1", color: "#A32D2D", borderRadius: 8, padding: "8px 12px", marginBottom: 12, fontSize: 12 }}>{err}</div>}
        <div style={{ marginBottom: 12 }}><label style={pLab}>Service *</label><select style={pIS} value={serviceId} onChange={(e) => setServiceId(e.target.value)}><option value="">...</option>{services.map((s) => <option key={s.service_id} value={s.service_id}>{s.name}</option>)}</select></div>
        <div style={{ marginBottom: 12 }}><label style={pLab}>Quantite *</label><input type="number" style={pIS} value={qty} onChange={(e) => setQty(e.target.value)} /></div>
        <div style={{ marginBottom: 16 }}><label style={pLab}>Motif</label><input style={pIS} value={reason} onChange={(e) => setReason(e.target.value)} /></div>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button onClick={onClose} style={{ fontSize: 13, padding: "8px 16px", borderRadius: 8, border: "1px solid #CBD5E1", background: "#fff", color: "#64748B", cursor: "pointer" }}>Annuler</button>
          <button onClick={submit} disabled={saving} style={{ fontSize: 13, padding: "8px 16px", borderRadius: 8, border: "none", background: "#3B6D11", color: "#fff", cursor: "pointer" }}>{saving ? "..." : "Doter"}</button>
        </div>
      </div>
    </div>
  );
}

function DispenseModal({ med, services, patients, onClose, onDone }) {
  const [location, setLocation] = useState("central");
  const [patientId, setPatientId] = useState("");
  const [qty, setQty] = useState("");
  const [reason, setReason] = useState("");
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);
  const isStup = med.regulatory_class === "stupefiant";
  const submit = async () => {
    if (!qty || Number(qty) <= 0) { setErr("Quantite invalide"); return; }
    if (isStup && !patientId) { setErr("Stupefiant : le patient est obligatoire"); return; }
    setErr(""); setSaving(true);
    try { await axios.post(`${API_URL}/api/sih/pharmacy/dispense`, { medication_id: med.medication_id, quantity: Number(qty), location, patient_id: patientId || null, reason }, getAuthConfig()); onDone(); }
    catch (e) { setErr(e.response?.data?.detail || "Erreur"); setSaving(false); }
  };
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 12, width: "90%", maxWidth: 440, padding: 24 }}>
        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>Dispensation {isStup && <span style={{ fontSize: 11, padding: "2px 7px", borderRadius: 10, background: "#3A1212", color: "#fff" }}>Stupefiant</span>}</div>
        <div style={{ fontSize: 13, color: "#64748B", marginBottom: 16 }}>{med.dci || med.name} {med.dosage} (FEFO)</div>
        {err && <div style={{ background: "#FCEBEB", border: "1px solid #F7C1C1", color: "#A32D2D", borderRadius: 8, padding: "8px 12px", marginBottom: 12, fontSize: 12 }}>{err}</div>}
        <div style={{ marginBottom: 12 }}><label style={pLab}>Depuis</label><select style={pIS} value={location} onChange={(e) => setLocation(e.target.value)}><option value="central">PUI centrale ({med.central_quantity || 0})</option>{services.map((s) => <option key={s.service_id} value={s.service_id}>{s.name}</option>)}</select></div>
        <div style={{ marginBottom: 12 }}><label style={pLab}>Patient {isStup ? "*" : "(optionnel)"}</label><select style={pIS} value={patientId} onChange={(e) => setPatientId(e.target.value)}><option value="">—</option>{patients.map((p) => <option key={p.patient_id} value={p.patient_id}>{p.first_name} {p.last_name}</option>)}</select></div>
        <div style={{ marginBottom: 12 }}><label style={pLab}>Quantite *</label><input type="number" style={pIS} value={qty} onChange={(e) => setQty(e.target.value)} /></div>
        <div style={{ marginBottom: 16 }}><label style={pLab}>Motif</label><input style={pIS} value={reason} onChange={(e) => setReason(e.target.value)} /></div>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button onClick={onClose} style={{ fontSize: 13, padding: "8px 16px", borderRadius: 8, border: "1px solid #CBD5E1", background: "#fff", color: "#64748B", cursor: "pointer" }}>Annuler</button>
          <button onClick={submit} disabled={saving} style={{ fontSize: 13, padding: "8px 16px", borderRadius: 8, border: "none", background: "#185FA5", color: "#fff", cursor: "pointer" }}>{saving ? "..." : "Dispenser"}</button>
        </div>
      </div>
    </div>
  );
}

const PUI_MOV_TYPE = {
  in: ["Entree", "#EAF3DE", "#3B6D11"], transfer: ["Dotation", "#E0ECFA", "#185FA5"],
  out: ["Sortie", "#FCEBEB", "#A32D2D"], adjust: ["Ajustement", "#F1F5F9", "#475569"],
};

function PharmacyMovements({ stupOnly }) {
  const [movs, setMovs] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    axios.get(`${API_URL}/api/sih/pharmacy/movements${stupOnly ? "?stupefiants=1" : ""}`, getAuthConfig())
      .then((r) => setMovs(r.data.movements || [])).catch(() => {}).finally(() => setLoading(false));
  }, [stupOnly]);
  return (
    <div>
      <h2 style={{ fontSize: 17, fontWeight: 700, color: "#0F172A", margin: "0 0 16px" }}>Mouvements de stock</h2>
      <div style={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 12, overflow: "auto" }}>
        {loading ? <div style={{ padding: 30, textAlign: "center", color: "#94A3B8" }}>Chargement...</div>
        : movs.length === 0 ? <div style={{ padding: 30, textAlign: "center", color: "#94A3B8", fontSize: 14 }}>Aucun mouvement.</div>
        : (
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 820 }}>
            <thead><tr style={{ background: "#F8FAFC", textAlign: "left" }}>{["Date", "Medicament", "Type", "Qte", "Lot", "Trajet", "Patient", "Par"].map((h) => <th key={h} style={{ padding: "10px 14px", fontSize: 12, color: "#64748B", fontWeight: 600 }}>{h}</th>)}</tr></thead>
            <tbody>
              {movs.map((m) => { const t = PUI_MOV_TYPE[m.type] || PUI_MOV_TYPE.adjust; const trajet = [m.from_location_name, m.to_location_name].filter(Boolean).join(" → ") || "—"; return (
                <tr key={m.movement_id} style={{ borderTop: "1px solid #F1F5F9" }}>
                  <td style={{ padding: "11px 14px", fontSize: 13, color: "#475569" }}>{fmtDate(m.created_at)}</td>
                  <td style={{ padding: "11px 14px", fontSize: 14, fontWeight: 500 }}>{m.medication_name}{m.regulatory_class === "stupefiant" && <span style={{ fontSize: 10, padding: "2px 6px", borderRadius: 8, background: "#3A1212", color: "#fff", marginLeft: 6 }}>Stup.</span>}</td>
                  <td style={{ padding: "11px 14px" }}><span style={{ fontSize: 12, padding: "3px 10px", borderRadius: 12, background: t[1], color: t[2] }}>{t[0]}</span></td>
                  <td style={{ padding: "11px 14px", fontSize: 14, fontWeight: 600 }}>{m.type === "in" || (m.type === "adjust" && m.quantity > 0) ? "+" : (m.type === "transfer" ? "" : "-")}{Math.abs(m.quantity)}</td>
                  <td style={{ padding: "11px 14px", fontSize: 13, color: "#475569" }}>{m.lot_number || "—"}</td>
                  <td style={{ padding: "11px 14px", fontSize: 13, color: "#475569" }}>{trajet}</td>
                  <td style={{ padding: "11px 14px", fontSize: 13, color: "#475569" }}>{m.patient_name || "—"}</td>
                  <td style={{ padding: "11px 14px", fontSize: 13, color: "#475569" }}>{m.by || "—"}</td>
                </tr>); })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function PharmacyAlerts() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => { axios.get(`${API_URL}/api/sih/pharmacy/alerts`, getAuthConfig()).then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false)); }, []);
  const card = (title, color) => ({ background: "#fff", border: "1px solid #E2E8F0", borderLeft: `4px solid ${color}`, borderRadius: 10, padding: 16, marginBottom: 14 });
  if (loading) return <div style={{ padding: 30, textAlign: "center", color: "#94A3B8" }}>Chargement...</div>;
  if (!data) return <div style={{ padding: 30, textAlign: "center", color: "#94A3B8" }}>—</div>;
  return (
    <div>
      <h2 style={{ fontSize: 17, fontWeight: 700, color: "#0F172A", margin: "0 0 16px" }}>Alertes</h2>
      <div style={card("Stock bas", "#A32D2D")}>
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>Stock bas ({data.low_stock.length})</div>
        {data.low_stock.length === 0 ? <div style={{ fontSize: 13, color: "#94A3B8" }}>Aucun.</div>
          : data.low_stock.map((x, i) => <div key={i} style={{ fontSize: 13, color: "#475569", padding: "4px 0" }}>{x.medication} — stock {x.total} (seuil {x.threshold})</div>)}
      </div>
      <div style={card("Peremption proche", "#854F0B")}>
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>Péremption proche — sous 60 j ({data.expiring.length})</div>
        {data.expiring.length === 0 ? <div style={{ fontSize: 13, color: "#94A3B8" }}>Aucun.</div>
          : data.expiring.map((x, i) => <div key={i} style={{ fontSize: 13, color: "#475569", padding: "4px 0" }}>{x.medication} — lot {x.lot_number} — {x.expiry_date} (J-{x.days_left}) — {x.quantity} u — {x.location}</div>)}
      </div>
      <div style={card("Lots perimes", "#A32D2D")}>
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>Lots périmés à retirer ({data.expired.length})</div>
        {data.expired.length === 0 ? <div style={{ fontSize: 13, color: "#94A3B8" }}>Aucun.</div>
          : data.expired.map((x, i) => <div key={i} style={{ fontSize: 13, color: "#A32D2D", padding: "4px 0" }}>{x.medication} — lot {x.lot_number} — périmé le {x.expiry_date} — {x.quantity} u — {x.location}</div>)}
      </div>
    </div>
  );
}


const TRIAGE = {
  P1: { label: "P1 — Immediat", short: "P1", target: 0, bg: "#FCEBEB", color: "#A32D2D" },
  P2: { label: "P2 — Tres urgent", short: "P2", target: 20, bg: "#FAEEDA", color: "#854F0B" },
  P3: { label: "P3 — Urgent", short: "P3", target: 60, bg: "#E0ECFA", color: "#185FA5" },
  P4: { label: "P4 — Peu urgent", short: "P4", target: 120, bg: "#EAF3DE", color: "#3B6D11" },
  P5: { label: "P5 — Non urgent", short: "P5", target: 240, bg: "#F1F5F9", color: "#475569" },
};
const TRIAGE_KEYS = ["P1", "P2", "P3", "P4", "P5"];

function waitMin(iso) { try { return Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 60000)); } catch (e) { return 0; } }
function fmtWait(m) { return m < 60 ? `${m} min` : `${Math.floor(m / 60)}h ${m % 60}min`; }
function vitalsLine(v) {
  if (!v) return "";
  const p = [];
  if (v.bp) p.push(`TA ${v.bp}`);
  if (v.pulse) p.push(`${v.pulse} bpm`);
  if (v.temperature) p.push(`${v.temperature}°C`);
  if (v.spo2) p.push(`SpO2 ${v.spo2}%`);
  if (v.glycemia) p.push(`Gly ${v.glycemia}`);
  if (v.pain != null && v.pain !== "") p.push(`EVA ${v.pain}`);
  return p.join(" · ");
}

const uIS = { width: "100%", padding: "9px 12px", border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 14, boxSizing: "border-box", background: "#fff" };
const uLab = { fontSize: 12, color: "#64748B", marginBottom: 4, display: "block" };

function TriagePicker({ value, onChange }) {
  return (
    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
      {TRIAGE_KEYS.map((k) => {
        const t = TRIAGE[k], sel = value === k;
        return <button type="button" key={k} onClick={() => onChange(k)}
          style={{ flex: "1 1 120px", padding: "10px", borderRadius: 8, cursor: "pointer", fontSize: 13, fontWeight: 600,
            border: sel ? `2px solid ${t.color}` : "1px solid #E2E8F0", background: sel ? t.bg : "#fff", color: t.color }}>{t.label}</button>;
      })}
    </div>
  );
}

function UrgencesModule({ can }) {
  const [data, setData] = useState({ active: [], closed: [], counts: {} });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [services, setServices] = useState([]);
  const [patients, setPatients] = useState([]);
  const [beds, setBeds] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState("");
  // Correctif L-URGENCES-ERROR-LEAK : etat d'erreur separe pour les actions
  // sur la file active (prendre en charge / supprimer une visite), afin
  // qu'une erreur de ces actions n'apparaisse plus, a tort, dans le
  // formulaire "Nouveau patient" s'il est ouvert en parallele (les deux
  // partageaient auparavant la meme variable `error`).
  const [queueError, setQueueError] = useState("");
  const [reevalFor, setReevalFor] = useState(null);
  const [outcomeFor, setOutcomeFor] = useState(null);
  const [showClosed, setShowClosed] = useState(false);
  const [mode, setMode] = useState("new"); // new | existing
  const emptyForm = { patient_id: "", first_name: "", last_name: "", age: "", sex: "Masculin", complaint: "", triage: "", bp: "", pulse: "", temperature: "", spo2: "", glycemia: "", pain: "" };
  const [form, setForm] = useState(emptyForm);
  const canManage = can("emergency:manage");

  const loadVisits = () => axios.get(`${API_URL}/api/sih/emergency/visits`, getAuthConfig()).then((r) => setData(r.data)).catch(() => {});
  const loadRefs = () => {
    axios.get(`${API_URL}/api/sih/services`, getAuthConfig()).then((r) => setServices(r.data.services || [])).catch(() => {});
    axios.get(`${API_URL}/api/sih/beds`, getAuthConfig()).then((r) => setBeds(r.data.beds || [])).catch(() => {});
    if (can("patients:read")) axios.get(`${API_URL}/api/sih/patients`, getAuthConfig()).then((r) => setPatients(r.data.patients || [])).catch(() => {});
  };
  useEffect(() => {
    setLoading(true);
    loadVisits().finally(() => setLoading(false));
    loadRefs();
    const t = setInterval(loadVisits, 30000);
    return () => clearInterval(t);
    /* eslint-disable-next-line */
  }, []);

  const refresh = () => { loadVisits(); loadRefs(); };

  const submitIntake = async () => {
    if (!form.complaint.trim()) { setError("Le motif de consultation est obligatoire"); return; }
    if (!form.triage) { setError("Le niveau de triage est obligatoire"); return; }
    if (mode === "existing" && !form.patient_id) { setError("Selectionnez un patient"); return; }
    if (mode === "new" && !form.last_name.trim()) { setError("Le nom du patient est obligatoire"); return; }
    setError("");
    if (saving) return;
    setSaving(true);
    const payload = {
      complaint: form.complaint, triage: form.triage,
      bp: form.bp || null, pulse: form.pulse ? Number(form.pulse) : null,
      temperature: form.temperature ? Number(form.temperature) : null,
      spo2: form.spo2 ? Number(form.spo2) : null, glycemia: form.glycemia ? Number(form.glycemia) : null,
      pain: form.pain !== "" ? Number(form.pain) : null,
    };
    if (mode === "existing") payload.patient_id = form.patient_id;
    else { payload.first_name = form.first_name; payload.last_name = form.last_name; payload.sex = form.sex; payload.age = form.age ? Number(form.age) : null; }
    try { await axios.post(`${API_URL}/api/sih/emergency/visits`, payload, getAuthConfig()); setShowForm(false); setForm(emptyForm); refresh(); }
    catch (e) { setError(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Erreur"); }
    finally { setSaving(false); }
  };
  const take = async (v) => { try { await axios.put(`${API_URL}/api/sih/emergency/visits/${v.visit_id}/take`, {}, getAuthConfig()); refresh(); } catch (e) { setQueueError(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Erreur"); } };
  const removeVisit = async (v) => { try { await axios.delete(`${API_URL}/api/sih/emergency/visits/${v.visit_id}`, getAuthConfig()); refresh(); } catch (e) { setQueueError("Erreur"); } };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: "#0F172A", margin: 0 }}>Service des urgences</h1>
          <div style={{ fontSize: 13, color: "#64748B", marginTop: 2 }}>{data.active.length} patient(s) en file · actualisation auto 30s</div>
        </div>
        {canManage && <button onClick={() => { setForm(emptyForm); setMode("new"); setError(""); setShowForm(true); }} style={{ padding: "9px 18px", background: "#A32D2D", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, cursor: "pointer" }}>+ Nouveau patient</button>}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 10, marginTop: 16 }}>
        {TRIAGE_KEYS.map((k) => { const t = TRIAGE[k]; return (
          <div key={k} style={{ background: t.bg, borderRadius: 10, padding: "14px 12px", textAlign: "center" }}>
            <div style={{ fontSize: 24, fontWeight: 700, color: t.color }}>{data.counts?.[k] || 0}</div>
            <div style={{ fontSize: 11, color: t.color, fontWeight: 600 }}>{t.label}</div>
          </div>); })}
      </div>

      {queueError && !showForm && <div style={{ background: "#FCEBEB", border: "1px solid #F7C1C1", color: "#A32D2D", borderRadius: 8, padding: "10px 14px", marginTop: 14, fontSize: 13 }}>{queueError}</div>}

      {showForm && (
        <div style={{ background: "#fff", border: "1px solid #F2C9C9", borderRadius: 12, padding: 20, marginTop: 16 }}>
          <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 14 }}>Nouveau patient aux urgences</div>
          {error && <div style={{ background: "#FCEBEB", border: "1px solid #F7C1C1", color: "#A32D2D", borderRadius: 8, padding: "10px 14px", marginBottom: 12, fontSize: 13 }}>{error}</div>}
          <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
            <button type="button" onClick={() => setMode("new")} style={{ fontSize: 13, padding: "6px 14px", borderRadius: 8, cursor: "pointer", border: mode === "new" ? "1px solid #185FA5" : "1px solid #CBD5E1", background: mode === "new" ? "#E6F1FB" : "#fff", color: mode === "new" ? "#185FA5" : "#475569" }}>Nouveau patient</button>
            {can("patients:read") && <button type="button" onClick={() => setMode("existing")} style={{ fontSize: 13, padding: "6px 14px", borderRadius: 8, cursor: "pointer", border: mode === "existing" ? "1px solid #185FA5" : "1px solid #CBD5E1", background: mode === "existing" ? "#E6F1FB" : "#fff", color: mode === "existing" ? "#185FA5" : "#475569" }}>Patient existant</button>}
          </div>

          {mode === "existing" ? (
            <div style={{ marginBottom: 14 }}><label style={uLab}>Patient *</label><select style={uIS} value={form.patient_id} onChange={(e) => setForm({ ...form, patient_id: e.target.value })}><option value="">...</option>{patients.map((p) => <option key={p.patient_id} value={p.patient_id}>{p.first_name} {p.last_name}</option>)}</select></div>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 100px 140px", gap: 12, marginBottom: 14 }}>
              <div><label style={uLab}>Prenom</label><input style={uIS} value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} /></div>
              <div><label style={uLab}>Nom *</label><input style={uIS} value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} /></div>
              <div><label style={uLab}>Age</label><input type="number" style={uIS} value={form.age} onChange={(e) => setForm({ ...form, age: e.target.value })} /></div>
              <div><label style={uLab}>Sexe</label><select style={uIS} value={form.sex} onChange={(e) => setForm({ ...form, sex: e.target.value })}><option>Masculin</option><option>Feminin</option></select></div>
            </div>
          )}

          <div style={{ marginBottom: 14 }}><label style={uLab}>Motif de consultation *</label><textarea style={{ ...uIS, minHeight: 60, resize: "vertical" }} value={form.complaint} onChange={(e) => setForm({ ...form, complaint: e.target.value })} /></div>
          <label style={{ ...uLab, marginBottom: 8 }}>Niveau de triage *</label>
          <TriagePicker value={form.triage} onChange={(k) => setForm({ ...form, triage: k })} />

          <div style={{ fontSize: 12, fontWeight: 700, color: "#A32D2D", textTransform: "uppercase", letterSpacing: 0.5, margin: "18px 0 10px" }}>Constantes initiales</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: 10 }}>
            <div><label style={uLab}>TA</label><input style={uIS} placeholder="120/80" value={form.bp} onChange={(e) => setForm({ ...form, bp: e.target.value })} /></div>
            <div><label style={uLab}>Pouls</label><input type="number" style={uIS} value={form.pulse} onChange={(e) => setForm({ ...form, pulse: e.target.value })} /></div>
            <div><label style={uLab}>Temp (°C)</label><input type="number" style={uIS} value={form.temperature} onChange={(e) => setForm({ ...form, temperature: e.target.value })} /></div>
            <div><label style={uLab}>SpO2 (%)</label><input type="number" style={uIS} value={form.spo2} onChange={(e) => setForm({ ...form, spo2: e.target.value })} /></div>
            <div><label style={uLab}>Glycemie</label><input type="number" style={uIS} value={form.glycemia} onChange={(e) => setForm({ ...form, glycemia: e.target.value })} /></div>
            <div><label style={uLab}>Douleur EVA</label><input type="number" style={uIS} value={form.pain} onChange={(e) => setForm({ ...form, pain: e.target.value })} /></div>
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
            <button onClick={submitIntake} style={{ padding: "8px 18px", background: "#A32D2D", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, cursor: "pointer" }}>Enregistrer</button>
            <button onClick={() => { setShowForm(false); setForm(emptyForm); }} style={{ padding: "8px 18px", background: "transparent", border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 14, cursor: "pointer" }}>Annuler</button>
          </div>
        </div>
      )}

      <div style={{ marginTop: 18, background: "#fff", border: "1px solid #E2E8F0", borderRadius: 12, overflow: "auto" }}>
        {loading ? <div style={{ padding: 30, textAlign: "center", color: "#94A3B8" }}>Chargement...</div>
        : data.active.length === 0 ? <div style={{ padding: 30, textAlign: "center", color: "#94A3B8", fontSize: 14 }}>Aucun patient en file.</div>
        : (
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 900 }}>
            <thead><tr style={{ background: "#F8FAFC", textAlign: "left" }}>{["Triage", "Patient", "Motif", "Constantes", "Attente", "Statut", "Actions"].map((h) => <th key={h} style={{ padding: "10px 14px", fontSize: 12, color: "#64748B", fontWeight: 600 }}>{h}</th>)}</tr></thead>
            <tbody>
              {data.active.map((v) => {
                const t = TRIAGE[v.triage] || TRIAGE.P5;
                const m = waitMin(v.arrival_at);
                const overdue = v.status === "waiting" && m > t.target;
                return (
                  <tr key={v.visit_id} style={{ borderTop: "1px solid #F1F5F9" }}>
                    <td style={{ padding: "12px 14px" }}><span style={{ fontSize: 12, padding: "4px 10px", borderRadius: 12, background: t.bg, color: t.color, fontWeight: 600 }}>{t.short}</span></td>
                    <td style={{ padding: "12px 14px", fontSize: 14 }}><span style={{ fontWeight: 500 }}>{v.patient_name || "—"}</span><div style={{ fontSize: 12, color: "#94A3B8" }}>{[v.age ? `${v.age} ans` : null, v.sex].filter(Boolean).join(" · ")}</div></td>
                    <td style={{ padding: "12px 14px", fontSize: 13, color: "#475569", maxWidth: 220 }}>{v.complaint}</td>
                    <td style={{ padding: "12px 14px", fontSize: 12, color: "#64748B", maxWidth: 200 }}>{vitalsLine(v.vitals) || "—"}</td>
                    <td style={{ padding: "12px 14px", fontSize: 13, fontWeight: 600, color: overdue ? "#A32D2D" : "#475569" }}>{fmtWait(m)}{overdue && <span style={{ fontSize: 10, display: "block", fontWeight: 500 }}>délai dépassé</span>}</td>
                    <td style={{ padding: "12px 14px" }}>{v.status === "waiting" ? <span style={{ fontSize: 12, color: "#854F0B" }}>En attente</span> : <span style={{ fontSize: 12, color: "#185FA5" }}>En cours</span>}</td>
                    <td style={{ padding: "12px 14px" }}><span style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                      {canManage && v.status === "waiting" && <button onClick={() => take(v)} style={{ fontSize: 13, color: "#185FA5", background: "none", border: "none", cursor: "pointer" }}>Prendre en charge</button>}
                      {canManage && <button onClick={() => { setError(""); setReevalFor(v); }} style={{ fontSize: 13, color: "#64748B", background: "none", border: "none", cursor: "pointer" }}>Réévaluer</button>}
                      {canManage && <button onClick={() => { setError(""); setOutcomeFor(v); }} style={{ fontSize: 13, color: "#3B6D11", background: "none", border: "none", cursor: "pointer" }}>Orienter</button>}
                      {canManage && <button onClick={() => removeVisit(v)} style={{ fontSize: 13, color: "#A32D2D", background: "none", border: "none", cursor: "pointer" }}>Suppr.</button>}
                    </span></td>
                  </tr>);
              })}
            </tbody>
          </table>
        )}
      </div>

      <div style={{ marginTop: 16 }}>
        <button onClick={() => setShowClosed(!showClosed)} style={{ fontSize: 13, color: "#185FA5", background: "none", border: "none", cursor: "pointer" }}>{showClosed ? "▾" : "▸"} Passages clôturés ({data.closed.length})</button>
        {showClosed && (
          <div style={{ marginTop: 8, background: "#fff", border: "1px solid #E2E8F0", borderRadius: 12, overflow: "auto" }}>
            {data.closed.length === 0 ? <div style={{ padding: 20, textAlign: "center", color: "#94A3B8", fontSize: 13 }}>Aucun.</div>
            : (
              <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 700 }}>
                <thead><tr style={{ background: "#F8FAFC", textAlign: "left" }}>{["Patient", "Triage", "Motif", "Orientation", "Clôture", ""].map((h) => <th key={h} style={{ padding: "10px 14px", fontSize: 12, color: "#64748B", fontWeight: 600 }}>{h}</th>)}</tr></thead>
                <tbody>
                  {data.closed.map((v) => { const oc = { discharge: "Sortie", hospitalisation: "Hospitalisation", transfer: "Transfert" }[v.outcome] || v.outcome; return (
                    <tr key={v.visit_id} style={{ borderTop: "1px solid #F1F5F9" }}>
                      <td style={{ padding: "10px 14px", fontSize: 14, fontWeight: 500 }}>{v.patient_name}</td>
                      <td style={{ padding: "10px 14px", fontSize: 13, color: "#475569" }}>{v.triage}</td>
                      <td style={{ padding: "10px 14px", fontSize: 13, color: "#475569" }}>{v.complaint}</td>
                      <td style={{ padding: "10px 14px", fontSize: 13, color: "#0F172A" }}>{oc}</td>
                      <td style={{ padding: "10px 14px", fontSize: 13, color: "#475569" }}>{fmtDate(v.closed_at)}</td>
                      <td style={{ padding: "10px 14px" }}>{canManage && <button onClick={() => { if (window.confirm("Supprimer ce passage clôturé ?")) removeVisit(v); }} style={{ fontSize: 13, color: "#A32D2D", background: "none", border: "none", cursor: "pointer" }}>Supprimer</button>}</td>
                    </tr>); })}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>

      {reevalFor && <ReevalModal visit={reevalFor} onClose={() => setReevalFor(null)} onDone={() => { setReevalFor(null); refresh(); }} />}
      {outcomeFor && <OutcomeModal visit={outcomeFor} services={services} beds={beds} onClose={() => setOutcomeFor(null)} onDone={() => { setOutcomeFor(null); refresh(); }} />}
    </div>
  );
}

function ReevalModal({ visit, onClose, onDone }) {
  const v0 = visit.vitals || {};
  const [triage, setTriage] = useState(visit.triage);
  const [f, setF] = useState({ bp: v0.bp || "", pulse: v0.pulse || "", temperature: v0.temperature || "", spo2: v0.spo2 || "", glycemia: v0.glycemia || "", pain: v0.pain != null ? v0.pain : "" });
  const [note, setNote] = useState("");
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setErr(""); setSaving(true);
    const payload = { triage, bp: f.bp || null, pulse: f.pulse ? Number(f.pulse) : null, temperature: f.temperature ? Number(f.temperature) : null, spo2: f.spo2 ? Number(f.spo2) : null, glycemia: f.glycemia ? Number(f.glycemia) : null, pain: f.pain !== "" ? Number(f.pain) : null, note: note || null };
    try { await axios.put(`${API_URL}/api/sih/emergency/visits/${visit.visit_id}`, payload, getAuthConfig()); onDone(); }
    catch (e) { setErr(e.response?.data?.detail || "Erreur"); setSaving(false); }
  };
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: 24, overflow: "auto" }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 12, width: "90%", maxWidth: 560, padding: 24, marginTop: 20 }}>
        <div style={{ fontSize: 17, fontWeight: 700, marginBottom: 4 }}>Réévaluation — {visit.patient_name}</div>
        <div style={{ fontSize: 13, color: "#64748B", marginBottom: 14 }}>{visit.complaint}</div>
        {err && <div style={{ background: "#FCEBEB", border: "1px solid #F7C1C1", color: "#A32D2D", borderRadius: 8, padding: "8px 12px", marginBottom: 12, fontSize: 12 }}>{err}</div>}
        <label style={{ ...uLab, marginBottom: 8 }}>Niveau de triage</label>
        <TriagePicker value={triage} onChange={setTriage} />
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10, marginTop: 14 }}>
          <div><label style={uLab}>TA</label><input style={uIS} value={f.bp} onChange={(e) => setF({ ...f, bp: e.target.value })} /></div>
          <div><label style={uLab}>Pouls</label><input type="number" style={uIS} value={f.pulse} onChange={(e) => setF({ ...f, pulse: e.target.value })} /></div>
          <div><label style={uLab}>Temp (°C)</label><input type="number" style={uIS} value={f.temperature} onChange={(e) => setF({ ...f, temperature: e.target.value })} /></div>
          <div><label style={uLab}>SpO2 (%)</label><input type="number" style={uIS} value={f.spo2} onChange={(e) => setF({ ...f, spo2: e.target.value })} /></div>
          <div><label style={uLab}>Glycemie</label><input type="number" style={uIS} value={f.glycemia} onChange={(e) => setF({ ...f, glycemia: e.target.value })} /></div>
          <div><label style={uLab}>Douleur EVA</label><input type="number" style={uIS} value={f.pain} onChange={(e) => setF({ ...f, pain: e.target.value })} /></div>
        </div>
        <div style={{ marginTop: 12 }}><label style={uLab}>Note clinique</label><input style={uIS} value={note} onChange={(e) => setNote(e.target.value)} /></div>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 16 }}>
          <button onClick={onClose} style={{ fontSize: 13, padding: "8px 16px", borderRadius: 8, border: "1px solid #CBD5E1", background: "#fff", color: "#64748B", cursor: "pointer" }}>Annuler</button>
          <button onClick={submit} disabled={saving} style={{ fontSize: 13, padding: "8px 16px", borderRadius: 8, border: "none", background: "#185FA5", color: "#fff", cursor: "pointer" }}>{saving ? "..." : "Enregistrer"}</button>
        </div>
      </div>
    </div>
  );
}

function OutcomeModal({ visit, services, beds, onClose, onDone }) {
  const [outcome, setOutcome] = useState("");
  const [serviceId, setServiceId] = useState("");
  const [bedId, setBedId] = useState("");
  const [doctor, setDoctor] = useState("");
  const [note, setNote] = useState("");
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);
  const availBeds = beds.filter((b) => b.service_id === serviceId && b.status === "available");
  const submit = async () => {
    if (!outcome) { setErr("Choisissez une orientation"); return; }
    if (outcome === "hospitalisation" && (!serviceId || !bedId)) { setErr("Service et lit requis"); return; }
    setErr(""); setSaving(true);
    const payload = { outcome, note: note || null, doctor_name: doctor || null };
    if (outcome === "hospitalisation") { payload.service_id = serviceId; payload.bed_id = bedId; }
    try { await axios.put(`${API_URL}/api/sih/emergency/visits/${visit.visit_id}/outcome`, payload, getAuthConfig()); onDone(); }
    catch (e) { setErr(e.response?.data?.detail || "Erreur"); setSaving(false); }
  };
  const opt = (val, label, color) => (
    <button type="button" onClick={() => { setOutcome(val); }} style={{ flex: 1, padding: "10px", borderRadius: 8, cursor: "pointer", fontSize: 13, fontWeight: 600, border: outcome === val ? `2px solid ${color}` : "1px solid #E2E8F0", background: outcome === val ? "#F8FAFC" : "#fff", color }}>{label}</button>
  );
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: 24, overflow: "auto" }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 12, width: "90%", maxWidth: 520, padding: 24, marginTop: 20 }}>
        <div style={{ fontSize: 17, fontWeight: 700, marginBottom: 4 }}>Orientation — {visit.patient_name}</div>
        <div style={{ fontSize: 13, color: "#64748B", marginBottom: 14 }}>{visit.complaint}</div>
        {err && <div style={{ background: "#FCEBEB", border: "1px solid #F7C1C1", color: "#A32D2D", borderRadius: 8, padding: "8px 12px", marginBottom: 12, fontSize: 12 }}>{err}</div>}
        <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
          {opt("discharge", "Sortie (domicile)", "#3B6D11")}
          {opt("hospitalisation", "Hospitalisation", "#185FA5")}
          {opt("transfer", "Transfert", "#854F0B")}
        </div>
        {outcome === "hospitalisation" && (
          <div style={{ background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 10, padding: 14, marginBottom: 12 }}>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10 }}>Affectation du lit</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <div><label style={uLab}>Service *</label><select style={uIS} value={serviceId} onChange={(e) => { setServiceId(e.target.value); setBedId(""); }}><option value="">...</option>{services.map((s) => <option key={s.service_id} value={s.service_id}>{s.name}</option>)}</select></div>
              <div><label style={uLab}>Lit disponible *</label><select style={uIS} value={bedId} onChange={(e) => setBedId(e.target.value)} disabled={!serviceId}><option value="">{serviceId ? (availBeds.length ? "..." : "Aucun lit libre") : "Choisir un service"}</option>{availBeds.map((b) => <option key={b.bed_id} value={b.bed_id}>{b.bed_number}</option>)}</select></div>
            </div>
            <div style={{ marginTop: 10 }}><label style={uLab}>Médecin</label><input style={uIS} value={doctor} onChange={(e) => setDoctor(e.target.value)} /></div>
          </div>
        )}
        <div style={{ marginBottom: 16 }}><label style={uLab}>Note de sortie</label><input style={uIS} value={note} onChange={(e) => setNote(e.target.value)} /></div>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button onClick={onClose} style={{ fontSize: 13, padding: "8px 16px", borderRadius: 8, border: "1px solid #CBD5E1", background: "#fff", color: "#64748B", cursor: "pointer" }}>Annuler</button>
          <button onClick={submit} disabled={saving} style={{ fontSize: 13, padding: "8px 16px", borderRadius: 8, border: "none", background: "#185FA5", color: "#fff", cursor: "pointer" }}>{saving ? "..." : "Valider"}</button>
        </div>
      </div>
    </div>
  );
}


const OR_CHECKLIST = [
  { phase: "before_induction", title: "1. Avant induction anesthesique", items: [
    ["identity", "Identite du patient confirmee"],
    ["procedure_site", "Intervention et site operatoire confirmes"],
    ["consent", "Consentement verifie"],
    ["site_marking", "Site operatoire marque"],
    ["anesth_check", "Verification anesthesie (materiel/medicaments)"],
    ["allergy", "Allergies connues verifiees"],
    ["airway_risk", "Risque d'intubation difficile evalue"],
  ] },
  { phase: "time_out", title: "2. Time-out (avant incision)", items: [
    ["team_intro", "Presentation de l'equipe"],
    ["patient_confirm", "Patient, site et intervention confirmes a voix haute"],
    ["antibio", "Antibioprophylaxie administree si indiquee"],
    ["critical_steps", "Etapes critiques partagees (chirurgien / anesth / IBODE)"],
    ["imaging", "Imagerie essentielle disponible"],
  ] },
  { phase: "before_exit", title: "3. Avant sortie de salle", items: [
    ["procedure_recorded", "Intervention enregistree"],
    ["count", "Compte compresses / instruments correct"],
    ["specimen", "Etiquetage des prelevements verifie"],
    ["equipment_issue", "Problemes de materiel signales"],
    ["postop", "Consignes postoperatoires donnees"],
  ] },
];
const OR_STATUS = {
  scheduled: ["Planifiee", "#E0ECFA", "#185FA5"], in_progress: ["En cours", "#FAEEDA", "#854F0B"],
  done: ["Terminee", "#EAF3DE", "#3B6D11"], cancelled: ["Annulee", "#F1F5F9", "#94A3B8"],
};
function fmtDT(iso) { return (iso || "").replace("T", " ").slice(0, 16); }
function orProgress(op) {
  let done = 0, total = 0;
  OR_CHECKLIST.forEach((ph) => ph.items.forEach(([k]) => { total++; if (op.checklist && op.checklist[ph.phase] && op.checklist[ph.phase][k]) done++; }));
  return { done, total };
}
const oIS = { width: "100%", padding: "9px 12px", border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 14, boxSizing: "border-box", background: "#fff" };
const oLab = { fontSize: 12, color: "#64748B", marginBottom: 4, display: "block" };

const PHLEB_REJECT_LABELS = {
  tube_hemolyse: "Tube hemolyse", tube_coagule: "Tube coagule", identite_non_conforme: "Identite non conforme",
  quantite_insuffisante: "Quantite insuffisante", tube_inadapte: "Tube inadapte", prelevement_souille: "Prelevement souille",
  delai_depasse: "Delai depasse", autre: "Autre",
};
const PHLEB_STATUS_LABELS = {
  a_prelever: "A prelever", preleve: "Preleve", achemine: "Achemine", recu_labo: "Recu labo", rejete: "Rejete",
};
const PHLEB_STATUS_COLORS = {
  a_prelever: ["#FEF3C7", "#854F0B"], preleve: ["#E0E7FF", "#3730A3"], achemine: ["#DBEAFE", "#1E40AF"],
  recu_labo: ["#EAF3DE", "#3B6D11"], rejete: ["#FCEBEB", "#A32D2D"],
};
const TUBE_LABELS = { sec: "Sec (biochimie)", EDTA: "EDTA (hematologie)", citrate: "Citrate (coagulation)", heparine: "Heparine", fluorure: "Fluorure (glycemie)", autre: "Autre" };
const PATH_STATUS_LABELS = { recu: "Recu", macroscopie: "Macroscopie", microscopie: "Microscopie", valide: "Valide" };
const PATH_STATUS_COLORS = { recu: ["#FEF3C7", "#854F0B"], macroscopie: ["#E0E7FF", "#3730A3"], microscopie: ["#DBEAFE", "#1E40AF"], valide: ["#EAF3DE", "#3B6D11"] };
const PATH_PRIORITY_COLORS = { normal: ["#F1F5F9", "#475569"], urgent: ["#FEF3C7", "#854F0B"], extemporane: ["#FCEBEB", "#A32D2D"] };
function PathologyModule({ can }) {
  const [specimens, setSpecimens] = useState([]);
  const [patients, setPatients] = useState([]);
  const [error, setError] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [report, setReport] = useState(null);
  const empty = { patient_id: "", specimen_nature: "piece operatoire", origin: "", clinical_info: "", priority: "normal", note: "" };
  const [form, setForm] = useState(empty);
  const [rForm, setRForm] = useState({});
  const load = async () => {
    // Correctif B6.c : `setSaving` n'existe pas dans ce composant (aucun
    // useState correspondant). load() etant appele au montage via useEffect,
    // le bloc finally levait un ReferenceError et l'onglet Anatomopathologie
    // ne s'affichait jamais. L'indicateur n'etant pas utilise ici, on retire
    // simplement le finally.
    try { const s = await axios.get(`${API_URL}/api/sih/pathology/specimens`, getAuthConfig()); setSpecimens(s.data.specimens || []); }
    catch (e) { setError("Impossible de charger les prelevements"); }
  };
  const loadPatients = async () => {
    try { const p = await axios.get(`${API_URL}/api/sih/patients`, getAuthConfig()); setPatients(p.data.patients || []); } catch (e) {}
  };
  useEffect(() => { load(); loadPatients(); }, []);
  const save = async () => {
    try { await axios.post(`${API_URL}/api/sih/pathology/specimens`, form, getAuthConfig()); setShowForm(false); setForm(empty); load(); }
    catch (e) { setError(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Erreur"); }
  };
  const openReport = (s) => { setReport(s); setRForm({ status: s.status, macroscopy: s.macroscopy || "", microscopy: s.microscopy || "", conclusion: s.conclusion || "", icd_o: s.icd_o || "", note: s.note || "" }); setError(""); };
  const saveReport = async (validate) => {
    try {
      const payload = { ...rForm };
      if (validate) payload.status = "valide";
      await axios.put(`${API_URL}/api/sih/pathology/specimens/${report.specimen_id}`, payload, getAuthConfig());
      setReport(null); load();
    } catch (e) { setError(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Erreur"); }
  };
  const remove = async (s) => {
    try { await axios.delete(`${API_URL}/api/sih/pathology/specimens/${s.specimen_id}`, getAuthConfig()); load(); }
    catch (e) { setError("Erreur"); }
  };
  const iS = { width: "100%", padding: "9px 12px", border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 14, boxSizing: "border-box", background: "#fff" };
  const lab = { fontSize: 12, color: "#64748B", marginBottom: 4, display: "block" };
  const ta = { ...iS, fontFamily: "inherit", resize: "vertical" };
  const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString("fr-FR"); } catch (e) { return iso; } };
  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: "#0F172A", margin: 0 }}>Anatomopathologie</h1>
        {can("pathology:manage") && <button onClick={() => { setForm(empty); setError(""); setShowForm(true); }} style={{ padding: "9px 18px", background: "#185FA5", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, cursor: "pointer" }}>+ Nouveau prelevement</button>}
      </div>
      {error && !showForm && !report && <div style={{ background: "#FCEBEB", border: "1px solid #F7C1C1", color: "#A32D2D", borderRadius: 8, padding: "10px 14px", marginTop: 14, fontSize: 13 }}>{error}</div>}
      <div style={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 12, marginTop: 16, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead><tr style={{ background: "#F8FAFC", textAlign: "left" }}>
            <th style={{ padding: "10px 14px", fontSize: 12, color: "#64748B", fontWeight: 600 }}>N° AP</th>
            <th style={{ padding: "10px 14px", fontSize: 12, color: "#64748B", fontWeight: 600 }}>Patient</th>
            <th style={{ padding: "10px 14px", fontSize: 12, color: "#64748B", fontWeight: 600 }}>Nature</th>
            <th style={{ padding: "10px 14px", fontSize: 12, color: "#64748B", fontWeight: 600 }}>Origine</th>
            <th style={{ padding: "10px 14px", fontSize: 12, color: "#64748B", fontWeight: 600 }}>Priorite</th>
            <th style={{ padding: "10px 14px", fontSize: 12, color: "#64748B", fontWeight: 600 }}>Statut</th>
            <th style={{ padding: "10px 14px", fontSize: 12, color: "#64748B", fontWeight: 600 }}>Actions</th>
          </tr></thead>
          <tbody>
            {specimens.length === 0 && <tr><td colSpan={7} style={{ padding: "16px 14px", fontSize: 13, color: "#94A3B8", textAlign: "center" }}>Aucun prelevement anatomopathologique.</td></tr>}
            {specimens.map((s) => { const sc = PATH_STATUS_COLORS[s.status] || ["#F1F5F9", "#64748B"]; const pc = PATH_PRIORITY_COLORS[s.priority] || ["#F1F5F9", "#475569"]; return (
              <tr key={s.specimen_id} style={{ borderTop: "1px solid #F1F5F9" }}>
                <td style={{ padding: "12px 14px", fontSize: 13, fontWeight: 600, color: "#185FA5", whiteSpace: "nowrap" }}>{s.accession}</td>
                <td style={{ padding: "12px 14px", fontSize: 14 }}><div style={{ fontWeight: 500 }}>{s.patient_name || "—"}</div>{s.patient_number && <div style={{ fontSize: 11, color: "#94A3B8" }}>{s.patient_number}</div>}</td>
                <td style={{ padding: "12px 14px", fontSize: 13, color: "#475569" }}>{s.specimen_nature}</td>
                <td style={{ padding: "12px 14px", fontSize: 13, color: "#475569" }}>{s.origin}</td>
                <td style={{ padding: "12px 14px" }}>{s.priority !== "normal" ? <span style={{ fontSize: 12, padding: "3px 10px", borderRadius: 12, background: pc[0], color: pc[1], fontWeight: 600 }}>{s.priority}</span> : <span style={{ fontSize: 12, color: "#94A3B8" }}>normal</span>}</td>
                <td style={{ padding: "12px 14px" }}><span style={{ fontSize: 12, padding: "3px 10px", borderRadius: 12, background: sc[0], color: sc[1] }}>{PATH_STATUS_LABELS[s.status] || s.status}</span></td>
                <td style={{ padding: "12px 14px" }}><span style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                  {can("pathology:manage") && <button onClick={() => openReport(s)} style={{ fontSize: 13, color: "#185FA5", background: "none", border: "none", cursor: "pointer", fontWeight: 600 }}>{s.status === "valide" ? "Voir CR" : "Rediger CR"}</button>}
                  {s.status === "valide" && <button onClick={() => printPathologyReport(s)} style={{ fontSize: 13, color: "#3B6D11", background: "none", border: "none", cursor: "pointer" }}>Imprimer</button>}
                  {can("pathology:manage") && s.status !== "valide" && <button onClick={() => remove(s)} style={{ fontSize: 13, color: "#A32D2D", background: "none", border: "none", cursor: "pointer" }}>Suppr.</button>}
                </span></td>
              </tr>); })}
          </tbody>
        </table>
      </div>
      {showForm && (
        <div onClick={() => setShowForm(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "flex-start", justifyContent: "center", zIndex: 1000, overflowY: "auto", paddingBottom: 40 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 12, width: "92%", maxWidth: 560, padding: 24, marginTop: 20 }}>
            <h3 style={{ fontSize: 17, fontWeight: 700, color: "#0F172A", margin: "0 0 16px" }}>Nouveau prelevement anatomopathologique</h3>
            {error && <div style={{ background: "#FCEBEB", border: "1px solid #F7C1C1", color: "#A32D2D", borderRadius: 8, padding: "10px 14px", marginBottom: 14, fontSize: 13 }}>{error}</div>}
            <div style={{ marginBottom: 12 }}><label style={lab}>Patient *</label><select value={form.patient_id} onChange={(e) => setForm({ ...form, patient_id: e.target.value })} style={iS}><option value="">— Selectionner —</option>{patients.map((p) => <option key={p.patient_id} value={p.patient_id}>{p.first_name} {p.last_name}{p.patient_number ? ` (${p.patient_number})` : ""}</option>)}</select></div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div><label style={lab}>Nature du prelevement</label><select value={form.specimen_nature} onChange={(e) => setForm({ ...form, specimen_nature: e.target.value })} style={iS}><option value="piece operatoire">Piece operatoire</option><option value="biopsie">Biopsie</option><option value="cytologie">Cytologie</option><option value="frottis">Frottis</option></select></div>
              <div><label style={lab}>Priorite</label><select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} style={iS}><option value="normal">Normal</option><option value="urgent">Urgent</option><option value="extemporane">Extemporane</option></select></div>
            </div>
            <div style={{ marginTop: 12 }}><label style={lab}>Origine (organe/site) *</label><input value={form.origin} onChange={(e) => setForm({ ...form, origin: e.target.value })} style={iS} placeholder="Ex: colon sigmoide, sein droit, col uterin..." /></div>
            <div style={{ marginTop: 12 }}><label style={lab}>Renseignements cliniques</label><textarea value={form.clinical_info} onChange={(e) => setForm({ ...form, clinical_info: e.target.value })} rows={3} style={ta} placeholder="Contexte clinique, hypothese diagnostique..." /></div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 20 }}>
              <button onClick={() => setShowForm(false)} style={{ padding: "9px 18px", background: "#fff", color: "#475569", border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 14, cursor: "pointer" }}>Annuler</button>
              <button onClick={save} style={{ padding: "9px 18px", background: "#185FA5", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, cursor: "pointer", fontWeight: 600 }}>Enregistrer</button>
            </div>
          </div>
        </div>
      )}
      {report && (
        <div onClick={() => setReport(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "flex-start", justifyContent: "center", zIndex: 1000, overflowY: "auto", paddingBottom: 40 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 12, width: "94%", maxWidth: 720, padding: 24, marginTop: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
              <h3 style={{ fontSize: 17, fontWeight: 700, color: "#0F172A", margin: 0 }}>Compte-rendu anatomopathologique</h3>
              <span style={{ fontSize: 13, fontWeight: 600, color: "#185FA5" }}>{report.accession}</span>
            </div>
            <div style={{ fontSize: 13, color: "#64748B", marginBottom: 16 }}>{report.patient_name} — {report.specimen_nature} — {report.origin}{report.clinical_info ? ` · ${report.clinical_info}` : ""}</div>
            {error && <div style={{ background: "#FCEBEB", border: "1px solid #F7C1C1", color: "#A32D2D", borderRadius: 8, padding: "10px 14px", marginBottom: 14, fontSize: 13 }}>{error}</div>}
            <div style={{ display: "grid", gap: 12 }}>
              <div><label style={lab}>Examen macroscopique</label><textarea value={rForm.macroscopy} onChange={(e) => setRForm({ ...rForm, macroscopy: e.target.value, status: rForm.status === "recu" ? "macroscopie" : rForm.status })} rows={4} style={ta} placeholder="Description macroscopique de la piece, dimensions, aspect, prelevements realises..." /></div>
              <div><label style={lab}>Examen microscopique</label><textarea value={rForm.microscopy} onChange={(e) => setRForm({ ...rForm, microscopy: e.target.value, status: (rForm.status === "recu" || rForm.status === "macroscopie") ? "microscopie" : rForm.status })} rows={5} style={ta} placeholder="Description histologique, architecture, cytologie, immunohistochimie..." /></div>
              <div><label style={lab}>Conclusion diagnostique</label><textarea value={rForm.conclusion} onChange={(e) => setRForm({ ...rForm, conclusion: e.target.value })} rows={3} style={{ ...ta, borderColor: "#185FA5", borderWidth: 2 }} placeholder="Diagnostic anatomopathologique final..." /></div>
              <div><label style={lab}>Code CIM-O (oncologie, si applicable)</label><input value={rForm.icd_o} onChange={(e) => setRForm({ ...rForm, icd_o: e.target.value })} style={iS} placeholder="Ex: 8140/3" /></div>
            </div>
            {report.status === "valide" && report.validated_by && <div style={{ marginTop: 14, fontSize: 12, color: "#3B6D11", background: "#EAF3DE", padding: "8px 12px", borderRadius: 8 }}>Valide par {report.validated_by}{report.validated_at ? ` le ${fmtDate(report.validated_at)}` : ""}</div>}
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 20 }}>
              <button onClick={() => setReport(null)} style={{ padding: "9px 18px", background: "#fff", color: "#475569", border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 14, cursor: "pointer" }}>Fermer</button>
              {report.status !== "valide" && <button onClick={() => saveReport(false)} style={{ padding: "9px 18px", background: "#fff", color: "#185FA5", border: "1px solid #185FA5", borderRadius: 8, fontSize: 14, cursor: "pointer", fontWeight: 600 }}>Enregistrer (brouillon)</button>}
              {report.status !== "valide" && <button onClick={() => saveReport(true)} style={{ padding: "9px 18px", background: "#3B6D11", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, cursor: "pointer", fontWeight: 600 }}>Valider le CR</button>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
function VitalsModule({ can }) {
  const [patients, setPatients] = useState([]);
  const [patientId, setPatientId] = useState("");
  const [records, setRecords] = useState([]);
  const [error, setError] = useState("");
  const [showForm, setShowForm] = useState(false);
  const empty = { bp: "", pulse: "", temperature: "", spo2: "", respiratory_rate: "", glycemia: "", pain: "", diuresis: "", consciousness: "", note: "" };
  const [form, setForm] = useState(empty);
  const loadPatients = async () => {
    try { const p = await axios.get(`${API_URL}/api/sih/patients`, getAuthConfig()); setPatients(p.data.patients || []); } catch (e) {}
  };
  const loadRecords = async (pid) => {
    if (!pid) { setRecords([]); return; }
    try { const r = await axios.get(`${API_URL}/api/sih/vitals/patient/${pid}`, getAuthConfig()); setRecords(r.data.records || []); }
    catch (e) { setError("Impossible de charger les releves"); }
  };
  useEffect(() => { loadPatients(); }, []);
  useEffect(() => { loadRecords(patientId); }, [patientId]);
  const save = async () => {
    try {
      const num = (v) => (v !== "" && v != null ? Number(v) : null);
      const payload = {
        patient_id: patientId, bp: form.bp || null, pulse: num(form.pulse),
        temperature: num(form.temperature), spo2: num(form.spo2), respiratory_rate: num(form.respiratory_rate),
        glycemia: num(form.glycemia), pain: num(form.pain), diuresis: num(form.diuresis),
        consciousness: form.consciousness || null, note: form.note || null,
      };
      await axios.post(`${API_URL}/api/sih/vitals`, payload, getAuthConfig());
      setShowForm(false); setForm(empty); loadRecords(patientId);
    } catch (e) { setError(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Erreur"); }
  };
  const remove = async (r) => {
    try { await axios.delete(`${API_URL}/api/sih/vitals/${r.record_id}`, getAuthConfig()); loadRecords(patientId); }
    catch (e) { setError("Erreur"); }
  };
  const iS = { width: "100%", padding: "9px 12px", border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 14, boxSizing: "border-box", background: "#fff" };
  const lab = { fontSize: 12, color: "#64748B", marginBottom: 4, display: "block" };
  const fmtTime = (iso) => { try { return new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }); } catch (e) { return iso; } };
  // Donnees pour les courbes (systole/diastole extraites de bp "120/80")
  const chartData = records.map((r) => {
    let sys = null, dia = null;
    if (r.bp && r.bp.includes("/")) { const parts = r.bp.split("/"); sys = parseInt(parts[0]) || null; dia = parseInt(parts[1]) || null; }
    return { t: fmtTime(r.recorded_at), temperature: r.temperature, pulse: r.pulse, spo2: r.spo2, sys, dia };
  });
  const Chart = ({ title, children }) => (
    <div style={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 12, padding: 16 }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: "#334155", marginBottom: 10 }}>{title}</div>
      <ResponsiveContainer width="100%" height={180}>{children}</ResponsiveContainer>
    </div>
  );
  const selectedPatient = patients.find((p) => p.patient_id === patientId);
  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: "#0F172A", margin: 0 }}>Pancarte / feuille de surveillance</h1>
        {can("vitals:record") && patientId && <button onClick={() => { setForm(empty); setError(""); setShowForm(true); }} style={{ padding: "9px 18px", background: "#185FA5", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, cursor: "pointer" }}>+ Releve</button>}
      </div>
      <div style={{ marginTop: 16, maxWidth: 420 }}>
        <label style={lab}>Patient</label>
        <select value={patientId} onChange={(e) => setPatientId(e.target.value)} style={iS}><option value="">— Selectionner un patient —</option>{patients.map((p) => <option key={p.patient_id} value={p.patient_id}>{p.first_name} {p.last_name}{p.patient_number ? ` (${p.patient_number})` : ""}</option>)}</select>
      </div>
      {error && !showForm && <div style={{ background: "#FCEBEB", border: "1px solid #F7C1C1", color: "#A32D2D", borderRadius: 8, padding: "10px 14px", marginTop: 14, fontSize: 13 }}>{error}</div>}
      {!patientId && <div style={{ marginTop: 24, fontSize: 14, color: "#94A3B8" }}>Selectionnez un patient pour afficher sa feuille de surveillance.</div>}
      {patientId && records.length === 0 && <div style={{ marginTop: 24, fontSize: 14, color: "#94A3B8" }}>Aucun releve pour {selectedPatient ? `${selectedPatient.first_name} ${selectedPatient.last_name}` : "ce patient"}. Cliquez sur "+ Releve" pour commencer.</div>}
      {patientId && records.length > 0 && (
        <div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginTop: 20 }}>
            <Chart title="Temperature (°C)">
              <LineChart data={chartData} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                <XAxis dataKey="t" tick={{ fontSize: 10 }} />
                <YAxis domain={[35, 41]} tick={{ fontSize: 10 }} />
                <Tooltip />
                <ReferenceArea y1={36} y2={37.5} fill="#EAF3DE" fillOpacity={0.4} />
                <Line type="monotone" dataKey="temperature" stroke="#DC2626" strokeWidth={2} dot={{ r: 3 }} connectNulls />
              </LineChart>
            </Chart>
            <Chart title="Pouls (bpm)">
              <LineChart data={chartData} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                <XAxis dataKey="t" tick={{ fontSize: 10 }} />
                <YAxis domain={[40, 160]} tick={{ fontSize: 10 }} />
                <Tooltip />
                <ReferenceArea y1={60} y2={100} fill="#EAF3DE" fillOpacity={0.4} />
                <Line type="monotone" dataKey="pulse" stroke="#185FA5" strokeWidth={2} dot={{ r: 3 }} connectNulls />
              </LineChart>
            </Chart>
            <Chart title="Tension arterielle (mmHg)">
              <LineChart data={chartData} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                <XAxis dataKey="t" tick={{ fontSize: 10 }} />
                <YAxis domain={[40, 200]} tick={{ fontSize: 10 }} />
                <Tooltip />
                <Line type="monotone" dataKey="sys" name="Systole" stroke="#7C3AED" strokeWidth={2} dot={{ r: 3 }} connectNulls />
                <Line type="monotone" dataKey="dia" name="Diastole" stroke="#A78BFA" strokeWidth={2} dot={{ r: 3 }} connectNulls />
              </LineChart>
            </Chart>
            <Chart title="Saturation O2 (%)">
              <LineChart data={chartData} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                <XAxis dataKey="t" tick={{ fontSize: 10 }} />
                <YAxis domain={[85, 100]} tick={{ fontSize: 10 }} />
                <Tooltip />
                <ReferenceArea y1={95} y2={100} fill="#EAF3DE" fillOpacity={0.4} />
                <Line type="monotone" dataKey="spo2" stroke="#0891B2" strokeWidth={2} dot={{ r: 3 }} connectNulls />
              </LineChart>
            </Chart>
          </div>
          <div style={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 12, marginTop: 20, overflow: "hidden" }}>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 900 }}>
                <thead><tr style={{ background: "#F8FAFC", textAlign: "left" }}>
                  <th style={{ padding: "10px 14px", fontSize: 12, color: "#64748B", fontWeight: 600 }}>Date/heure</th>
                  <th style={{ padding: "10px 14px", fontSize: 12, color: "#64748B", fontWeight: 600 }}>TA</th>
                  <th style={{ padding: "10px 14px", fontSize: 12, color: "#64748B", fontWeight: 600 }}>Pouls</th>
                  <th style={{ padding: "10px 14px", fontSize: 12, color: "#64748B", fontWeight: 600 }}>T°</th>
                  <th style={{ padding: "10px 14px", fontSize: 12, color: "#64748B", fontWeight: 600 }}>SpO2</th>
                  <th style={{ padding: "10px 14px", fontSize: 12, color: "#64748B", fontWeight: 600 }}>FR</th>
                  <th style={{ padding: "10px 14px", fontSize: 12, color: "#64748B", fontWeight: 600 }}>Glyc.</th>
                  <th style={{ padding: "10px 14px", fontSize: 12, color: "#64748B", fontWeight: 600 }}>EVA</th>
                  <th style={{ padding: "10px 14px", fontSize: 12, color: "#64748B", fontWeight: 600 }}>Diurese</th>
                  <th style={{ padding: "10px 14px", fontSize: 12, color: "#64748B", fontWeight: 600 }}>Conscience</th>
                  <th style={{ padding: "10px 14px", fontSize: 12, color: "#64748B", fontWeight: 600 }}>Par</th>
                  <th style={{ padding: "10px 14px", fontSize: 12, color: "#64748B", fontWeight: 600 }}></th>
                </tr></thead>
                <tbody>
                  {[...records].reverse().map((r) => (
                    <tr key={r.record_id} style={{ borderTop: "1px solid #F1F5F9" }}>
                      <td style={{ padding: "10px 14px", fontSize: 13, color: "#334155", whiteSpace: "nowrap" }}>{fmtTime(r.recorded_at)}</td>
                      <td style={{ padding: "10px 14px", fontSize: 13 }}>{r.bp || "—"}</td>
                      <td style={{ padding: "10px 14px", fontSize: 13 }}>{r.pulse ?? "—"}</td>
                      <td style={{ padding: "10px 14px", fontSize: 13 }}>{r.temperature != null ? r.temperature + "°" : "—"}</td>
                      <td style={{ padding: "10px 14px", fontSize: 13 }}>{r.spo2 != null ? r.spo2 + "%" : "—"}</td>
                      <td style={{ padding: "10px 14px", fontSize: 13 }}>{r.respiratory_rate ?? "—"}</td>
                      <td style={{ padding: "10px 14px", fontSize: 13 }}>{r.glycemia ?? "—"}</td>
                      <td style={{ padding: "10px 14px", fontSize: 13 }}>{r.pain != null ? r.pain + "/10" : "—"}</td>
                      <td style={{ padding: "10px 14px", fontSize: 13 }}>{r.diuresis != null ? r.diuresis + " mL" : "—"}</td>
                      <td style={{ padding: "10px 14px", fontSize: 13 }}>{r.consciousness || "—"}</td>
                      <td style={{ padding: "10px 14px", fontSize: 12, color: "#94A3B8" }}>{r.recorded_by || "—"}</td>
                      <td style={{ padding: "10px 14px" }}>{can("vitals:record") && <button onClick={() => remove(r)} style={{ fontSize: 12, color: "#A32D2D", background: "none", border: "none", cursor: "pointer" }}>Suppr.</button>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
      {showForm && (
        <div onClick={() => setShowForm(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "flex-start", justifyContent: "center", zIndex: 1000, overflowY: "auto", paddingBottom: 40 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 12, width: "94%", maxWidth: 620, padding: 24, marginTop: 16 }}>
            <h3 style={{ fontSize: 17, fontWeight: 700, color: "#0F172A", margin: "0 0 16px" }}>Nouveau releve de constantes</h3>
            {error && <div style={{ background: "#FCEBEB", border: "1px solid #F7C1C1", color: "#A32D2D", borderRadius: 8, padding: "10px 14px", marginBottom: 14, fontSize: 13 }}>{error}</div>}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
              <div><label style={lab}>TA (ex: 120/80)</label><input value={form.bp} onChange={(e) => setForm({ ...form, bp: e.target.value })} style={iS} placeholder="120/80" /></div>
              <div><label style={lab}>Pouls (bpm)</label><input type="number" value={form.pulse} onChange={(e) => setForm({ ...form, pulse: e.target.value })} style={iS} /></div>
              <div><label style={lab}>Temperature (°C)</label><input type="number" step="0.1" value={form.temperature} onChange={(e) => setForm({ ...form, temperature: e.target.value })} style={iS} /></div>
              <div><label style={lab}>SpO2 (%)</label><input type="number" value={form.spo2} onChange={(e) => setForm({ ...form, spo2: e.target.value })} style={iS} /></div>
              <div><label style={lab}>FR (/min)</label><input type="number" value={form.respiratory_rate} onChange={(e) => setForm({ ...form, respiratory_rate: e.target.value })} style={iS} /></div>
              <div><label style={lab}>Glycemie (g/L)</label><input type="number" step="0.01" value={form.glycemia} onChange={(e) => setForm({ ...form, glycemia: e.target.value })} style={iS} /></div>
              <div><label style={lab}>Douleur (EVA 0-10)</label><input type="number" min="0" max="10" value={form.pain} onChange={(e) => setForm({ ...form, pain: e.target.value })} style={iS} /></div>
              <div><label style={lab}>Diurese (mL)</label><input type="number" value={form.diuresis} onChange={(e) => setForm({ ...form, diuresis: e.target.value })} style={iS} /></div>
              <div><label style={lab}>Conscience</label><input value={form.consciousness} onChange={(e) => setForm({ ...form, consciousness: e.target.value })} style={iS} placeholder="Conscient, Glasgow 15..." /></div>
            </div>
            <div style={{ marginTop: 12 }}><label style={lab}>Note</label><input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} style={iS} /></div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 20 }}>
              <button onClick={() => setShowForm(false)} style={{ padding: "9px 18px", background: "#fff", color: "#475569", border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 14, cursor: "pointer" }}>Annuler</button>
              <button onClick={save} style={{ padding: "9px 18px", background: "#185FA5", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, cursor: "pointer", fontWeight: 600 }}>Enregistrer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
// Accueil & file d'attente (cahier des charges labo, §12-14). Regroupe en un
// seul onglet la recherche/arrivee du patient (Accueil) et la file
// d'attente temps reel (dont les boutons "Commencer"/"Terminer" servent
// aussi d'ecran preleveur simplifie — une vraie vue dediee, plus depouillee,
// pourra etre ajoutee ensuite si le besoin s'en fait sentir).
const RECEPTION_STATUS_LABELS = { attente: "En attente", appelee: "Appelée", en_cours: "Prise en charge" };
const RECEPTION_STATUS_COLORS = {
  attente: ["#FEF3C7", "#92400E"],
  appelee: ["#DBEAFE", "#1E40AF"],
  en_cours: ["#DCFCE7", "#166534"],
};

function ReceptionModule({ can }) {
  const [queue, setQueue] = useState([]);
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [arriving, setArriving] = useState(null); // patient selectionne pour l'arrivee
  const [actTypes, setActTypes] = useState([]); // catalogue (section 7), si configure
  const [actType, setActType] = useState("");
  const [hasApt, setHasApt] = useState(false);
  const [error, setError] = useState("");
  const [now, setNow] = useState(Date.now());

  const loadQueue = async () => {
    try { const r = await axios.get(`${API_URL}/api/sih/reception/queue`, getAuthConfig()); setQueue(r.data.queue || []); }
    catch (e) { /* silencieux : le rafraichissement periodique reessaiera */ }
  };

  useEffect(() => {
    loadQueue();
    const poll = setInterval(loadQueue, 8000);
    const tick = setInterval(() => setNow(Date.now()), 30000);
    // Catalogue des types d'actes (section 7), pour proposer une liste plutot
    // qu'un champ libre quand l'etablissement l'a configure.
    axios.get(`${API_URL}/api/sih/act-types/`, getAuthConfig()).then((r) => setActTypes(r.data.act_types || [])).catch(() => {});
    return () => { clearInterval(poll); clearInterval(tick); };
  }, []);

  const doSearch = async (q) => {
    setSearch(q);
    if (q.trim().length < 2) { setResults([]); return; }
    setSearching(true);
    try {
      const r = await axios.get(`${API_URL}/api/sih/patients`, { ...getAuthConfig(), params: { search: q, limit: 8 } });
      setResults(r.data.patients || []);
    } catch (e) { setResults([]); }
    finally { setSearching(false); }
  };

  const openArrival = (p) => { setArriving(p); setActType(""); setHasApt(false); setError(""); };

  const confirmArrival = async () => {
    if (!arriving) return;
    try {
      await axios.post(`${API_URL}/api/sih/reception/arrivals`, {
        patient_id: arriving.patient_id, act_type: actType, has_appointment: hasApt,
      }, getAuthConfig());
      setArriving(null); setSearch(""); setResults([]);
      toast.success(`${arriving.first_name} ${arriving.last_name} ajoute a la file d'attente`);
      loadQueue();
    } catch (e) { setError(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Erreur"); }
  };

  const act = async (q, action) => {
    try { await axios.put(`${API_URL}/api/sih/reception/queue/${q.queue_id}/${action}`, {}, getAuthConfig()); loadQueue(); }
    catch (e) { toast.error("Erreur"); }
  };

  const waitMinutes = (arrivedAt) => Math.max(0, Math.round((now - new Date(arrivedAt).getTime()) / 60000));

  const iS = { width: "100%", padding: "9px 12px", border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 14, boxSizing: "border-box", background: "#fff" };
  const lab = { fontSize: 12, color: "#64748B", marginBottom: 4, display: "block" };

  return (
    <div>
      <h1 style={{ fontSize: 22, fontWeight: 700, color: "#0F172A", margin: 0 }}>Accueil</h1>

      {/* Accueil : recherche patient + bouton "Patient arrive" (section 12) */}
      {can("reception:manage") && (
        <div style={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 12, marginTop: 16, padding: 16, position: "relative" }}>
          <label style={lab}>Rechercher un patient (nom, numero de dossier, telephone...)</label>
          <input style={iS} value={search} onChange={(e) => doSearch(e.target.value)} placeholder="Ex: Dupont, P-A1B2C3D4, 0555..." />
          {searching && <div style={{ fontSize: 12, color: "#94A3B8", marginTop: 6 }}>Recherche...</div>}
          {results.length > 0 && (
            <div style={{ marginTop: 8, border: "1px solid #E2E8F0", borderRadius: 8, overflow: "hidden" }}>
              {results.map((p) => (
                <div key={p.patient_id} onClick={() => openArrival(p)} style={{ padding: "10px 14px", borderTop: "1px solid #F1F5F9", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div>
                    <div style={{ fontWeight: 500, fontSize: 14 }}>{p.first_name} {p.last_name}</div>
                    <div style={{ fontSize: 12, color: "#94A3B8" }}>{p.patient_number}{p.phone ? ` · ${p.phone}` : ""}</div>
                  </div>
                  <button style={{ padding: "6px 14px", background: "#185FA5", color: "#fff", border: "none", borderRadius: 8, fontSize: 13, cursor: "pointer" }}>Patient arrivé</button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* File d'attente temps reel (section 13). Les boutons Commencer/Terminer
          servent aussi d'ecran preleveur simplifie (section 14). */}
      <div style={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 12, marginTop: 16, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead><tr style={{ background: "#F8FAFC", textAlign: "left" }}>
            <th style={{ padding: "10px 14px", fontSize: 12, color: "#64748B", fontWeight: 600 }}>Patient</th>
            <th style={{ padding: "10px 14px", fontSize: 12, color: "#64748B", fontWeight: 600 }}>Arrivée</th>
            <th style={{ padding: "10px 14px", fontSize: 12, color: "#64748B", fontWeight: 600 }}>Attente</th>
            <th style={{ padding: "10px 14px", fontSize: 12, color: "#64748B", fontWeight: 600 }}>Statut</th>
            <th style={{ padding: "10px 14px", fontSize: 12, color: "#64748B", fontWeight: 600 }}>Actions</th>
          </tr></thead>
          <tbody>
            {queue.length === 0 && <tr><td colSpan={5} style={{ padding: "16px 14px", fontSize: 13, color: "#94A3B8", textAlign: "center" }}>Aucun patient en attente.</td></tr>}
            {queue.map((q) => { const sc = RECEPTION_STATUS_COLORS[q.status] || ["#F1F5F9", "#64748B"]; return (
              <tr key={q.queue_id} style={{ borderTop: "1px solid #F1F5F9" }}>
                <td style={{ padding: "12px 14px", fontSize: 14 }}>
                  <div style={{ fontWeight: 500 }}>{q.patient_name || "—"}{q.priority && <span style={{ marginLeft: 6, fontSize: 11, color: "#A32D2D" }}>● Prioritaire</span>}</div>
                  {(q.patient_number || q.act_type) && <div style={{ fontSize: 11, color: "#94A3B8" }}>{[q.patient_number, q.act_type].filter(Boolean).join(" · ")}</div>}
                </td>
                <td style={{ padding: "12px 14px", fontSize: 13, color: "#475569" }}>{new Date(q.arrived_at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}</td>
                <td style={{ padding: "12px 14px", fontSize: 13, color: "#475569" }}>{waitMinutes(q.arrived_at)} min</td>
                <td style={{ padding: "12px 14px" }}><span style={{ fontSize: 12, padding: "3px 10px", borderRadius: 12, background: sc[0], color: sc[1] }}>{RECEPTION_STATUS_LABELS[q.status] || q.status}</span></td>
                <td style={{ padding: "12px 14px" }}><span style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                  {can("reception:manage") && q.status === "attente" && <button onClick={() => act(q, "call")} style={{ fontSize: 13, color: "#185FA5", background: "none", border: "none", cursor: "pointer", fontWeight: 600 }}>Appeler</button>}
                  {can("reception:manage") && q.status === "appelee" && <button onClick={() => act(q, "start")} style={{ fontSize: 13, color: "#3730A3", background: "none", border: "none", cursor: "pointer", fontWeight: 600 }}>Commencer prise en charge</button>}
                  {can("reception:manage") && q.status === "en_cours" && <button onClick={() => act(q, "done")} style={{ fontSize: 13, color: "#166534", background: "none", border: "none", cursor: "pointer", fontWeight: 600 }}>Terminer prise en charge</button>}
                  {can("reception:manage") && <button onClick={() => act(q, "cancel")} style={{ fontSize: 13, color: "#A32D2D", background: "none", border: "none", cursor: "pointer" }}>Annuler</button>}
                </span></td>
              </tr>); })}
          </tbody>
        </table>
      </div>

      {arriving && (
        <div onClick={() => setArriving(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "flex-start", justifyContent: "center", zIndex: 1000, overflowY: "auto", paddingBottom: 40 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 12, width: "94%", maxWidth: 480, padding: 24, marginTop: 60 }}>
            <h3 style={{ fontSize: 17, fontWeight: 700, color: "#0F172A", margin: "0 0 4px" }}>{arriving.first_name} {arriving.last_name}</h3>
            <p style={{ fontSize: 13, color: "#64748B", margin: "0 0 12px" }}>{arriving.patient_number}</p>
            {/* Checklist d'accueil (section 12) : ce qui manque au dossier avant la
                prise en charge. "Documents" n'est pas encore verifie ici (module
                Documents non raccorde) — on ne l'affiche donc pas a tort. */}
            <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 16, fontSize: 12 }}>
              <span style={{ color: (arriving.first_name && arriving.last_name && arriving.date_of_birth) ? "#166534" : "#A32D2D" }}>
                {(arriving.first_name && arriving.last_name && arriving.date_of_birth) ? "✓" : "⚠"} Identité
              </span>
              <span style={{ color: arriving.phone ? "#166534" : "#A32D2D" }}>
                {arriving.phone ? "✓" : "⚠"} Coordonnées
              </span>
            </div>
            {error && <div style={{ background: "#FCEBEB", border: "1px solid #F7C1C1", color: "#A32D2D", borderRadius: 8, padding: "10px 14px", marginBottom: 14, fontSize: 13 }}>{error}</div>}
            <div style={{ marginBottom: 12 }}>
              <label style={lab}>Type d'acte (optionnel)</label>
              {actTypes.length > 0 ? (
                <select style={iS} value={actType} onChange={(e) => setActType(e.target.value)}>
                  <option value="">— Non precise —</option>
                  {actTypes.map((a) => <option key={a.act_type_id} value={a.name}>{a.name} ({a.duration_minutes} min)</option>)}
                </select>
              ) : (
                <input style={iS} value={actType} onChange={(e) => setActType(e.target.value)} placeholder="Ex: Prise de sang, Bilan..." />
              )}
            </div>
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#334155", marginBottom: 18, cursor: "pointer" }}>
              <input type="checkbox" checked={hasApt} onChange={(e) => setHasApt(e.target.checked)} /> Le patient avait un rendez-vous
            </label>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button onClick={() => setArriving(null)} style={{ padding: "9px 16px", background: "#F1F5F9", color: "#334155", border: "none", borderRadius: 8, fontSize: 14, cursor: "pointer" }}>Annuler</button>
              <button onClick={confirmArrival} style={{ padding: "9px 18px", background: "#185FA5", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, cursor: "pointer" }}>Confirmer l'arrivée</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Ecran preleveur (cahier des charges labo, section 14) : interface
// volontairement separee de l'Accueil (ReceptionModule ci-dessus), qui
// affiche toute la file dans un tableau. Ici, un seul patient a la fois —
// "le" patient courant — avec le moins de clics possibles entre "Appeler",
// "Commencer prise en charge" et "Terminer prise en charge". Reutilise la
// meme file (`reception_queue` via GET /api/sih/reception/queue) : ce n'est
// pas un flux different, seulement une vue differente, taillee pour le
// poste de prelevement plutot que pour le secretariat.
function PreleveurModule({ can }) {
  const [queue, setQueue] = useState([]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const r = await axios.get(`${API_URL}/api/sih/reception/queue`, getAuthConfig()); setQueue(r.data.queue || []); }
    catch (e) { setError("Impossible de charger la file d'attente"); }
  };
  useEffect(() => {
    load();
    const poll = setInterval(load, 5000);
    return () => clearInterval(poll);
  }, []);

  // "Patient actuel" (section 14) : deja en cours en priorite, sinon deja
  // appele (en attente du geste de prelevement), sinon personne — on ne
  // saute jamais directement un patient encore en simple "attente" pour ne
  // pas contourner l'appel fait depuis l'Accueil.
  const current = queue.find((q) => q.status === "en_cours") || queue.find((q) => q.status === "appelee") || null;
  const nextWaiting = queue.find((q) => q.status === "attente") || null;
  const remaining = queue.filter((q) => q.status === "attente").length;

  const act = async (q, action) => {
    setBusy(true); setError("");
    try { await axios.put(`${API_URL}/api/sih/reception/queue/${q.queue_id}/${action}`, {}, getAuthConfig()); await load(); }
    catch (e) { setError("Erreur"); }
    finally { setBusy(false); }
  };

  const fmtHeure = (iso) => iso ? new Date(iso).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }) : "—";

  return (
    <div>
      <h1 style={{ fontSize: 22, fontWeight: 700, color: "#0F172A", margin: 0 }}>Ecran preleveur</h1>
      {error && <div style={{ background: "#FCEBEB", border: "1px solid #F7C1C1", color: "#A32D2D", borderRadius: 8, padding: "10px 14px", marginTop: 14, fontSize: 13 }}>{error}</div>}

      <div style={{ display: "flex", justifyContent: "center", marginTop: 24 }}>
        <div style={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 16, padding: 32, width: "100%", maxWidth: 460, textAlign: "center" }}>
          {current ? (
            <>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#64748B", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>Patient actuel</div>
              <div style={{ fontSize: 26, fontWeight: 700, color: "#0F172A" }}>{current.patient_name || "—"}</div>
              {current.patient_number && <div style={{ fontSize: 13, color: "#94A3B8", marginTop: 4 }}>{current.patient_number}</div>}
              <div style={{ fontSize: 14, color: "#475569", marginTop: 10 }}>
                {current.act_type ? current.act_type : "Acte non precise"}
                {current.called_at && ` · Appele a ${fmtHeure(current.called_at)}`}
              </div>
              <div style={{ display: "flex", justifyContent: "center", gap: 16, marginTop: 18, fontSize: 13 }}>
                <span style={{ color: current.checklist?.identite ? "#166534" : "#A32D2D" }}>{current.checklist?.identite ? "✓" : "⚠"} Identite</span>
                <span style={{ color: current.checklist?.infos ? "#166534" : "#A32D2D" }}>{current.checklist?.infos ? "✓" : "⚠"} Informations</span>
              </div>
              <div style={{ marginTop: 26 }}>
                {current.status === "appelee" && can("reception:manage") && (
                  <button disabled={busy} onClick={() => act(current, "start")} style={{ width: "100%", padding: "16px", background: "#3730A3", color: "#fff", border: "none", borderRadius: 12, fontSize: 17, fontWeight: 700, cursor: "pointer" }}>
                    Commencer prise en charge
                  </button>
                )}
                {current.status === "en_cours" && can("reception:manage") && (
                  <button disabled={busy} onClick={() => act(current, "done")} style={{ width: "100%", padding: "16px", background: "#166534", color: "#fff", border: "none", borderRadius: 12, fontSize: 17, fontWeight: 700, cursor: "pointer" }}>
                    Terminer prise en charge
                  </button>
                )}
              </div>
            </>
          ) : nextWaiting ? (
            <>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#64748B", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>Patient suivant</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: "#0F172A" }}>{nextWaiting.patient_name || "—"}</div>
              <div style={{ fontSize: 13, color: "#94A3B8", marginTop: 4 }}>Arrive a {fmtHeure(nextWaiting.arrived_at)}</div>
              {can("reception:manage") && (
                <button disabled={busy} onClick={() => act(nextWaiting, "call")} style={{ width: "100%", marginTop: 22, padding: "16px", background: "#185FA5", color: "#fff", border: "none", borderRadius: 12, fontSize: 17, fontWeight: 700, cursor: "pointer" }}>
                  Appeler
                </button>
              )}
            </>
          ) : (
            <div style={{ padding: "24px 0", color: "#94A3B8", fontSize: 15 }}>Aucun patient en attente de prelevement.</div>
          )}
        </div>
      </div>
      {remaining > 0 && (
        <div style={{ textAlign: "center", marginTop: 16, fontSize: 13, color: "#64748B" }}>{remaining} patient(s) encore en attente</div>
      )}
    </div>
  );
}

// Catalogue des types d'actes (cahier des charges labo, §7) : nom, duree,
// personnel requis, ressources, instructions, preparation. Premiere brique
// du futur moteur de rendez-vous (§8-9) — pour l'instant un catalogue de
// configuration, pas encore relie a des creneaux.
function ActTypesModule({ can, ctx }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const empty = { name: "", duration_minutes: "15", personnel_requis: "", ressources: "", instructions: "", preparation: "" };
  const [form, setForm] = useState(empty);

  const load = () => {
    setLoading(true);
    axios.get(`${API_URL}/api/sih/act-types/`, { ...getAuthConfig(), params: { include_inactive: true } })
      .then((r) => setItems(r.data.act_types || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const resetForm = () => { setForm(empty); setEditing(null); setError(""); };

  const save = async () => {
    if (!form.name.trim()) { setError("Le nom de l'acte est obligatoire"); return; }
    setSaving(true); setError("");
    const payload = { ...form, duration_minutes: parseInt(form.duration_minutes, 10) || 15 };
    try {
      if (editing) await axios.put(`${API_URL}/api/sih/act-types/${editing}`, payload, getAuthConfig());
      else await axios.post(`${API_URL}/api/sih/act-types/`, payload, getAuthConfig());
      setShowForm(false); resetForm(); load();
    } catch (e) {
      setError(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Erreur lors de l'enregistrement");
    } finally { setSaving(false); }
  };

  const startEdit = (a) => {
    setEditing(a.act_type_id);
    setForm({
      name: a.name || "", duration_minutes: String(a.duration_minutes || 15),
      personnel_requis: a.personnel_requis || "", ressources: a.ressources || "",
      instructions: a.instructions || "", preparation: a.preparation || "",
    });
    setError(""); setShowForm(true);
  };

  const toggleActif = async (a) => {
    try { await axios.put(`${API_URL}/api/sih/act-types/${a.act_type_id}`, { actif: !a.actif }, getAuthConfig()); load(); }
    catch (e) { setError("Erreur"); }
  };

  const doDelete = async () => {
    if (!toDelete) return;
    try { await axios.delete(`${API_URL}/api/sih/act-types/${toDelete.act_type_id}`, getAuthConfig()); setToDelete(null); load(); }
    catch (e) { setError("Erreur lors de la suppression"); setToDelete(null); }
  };

  const iS = { width: "100%", padding: "9px 12px", border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 14, boxSizing: "border-box", background: "#fff" };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: "#0F172A", margin: 0 }}>Types d'actes</h1>
        {can("acts:write") && <button onClick={() => { resetForm(); setShowForm(true); }} style={{ padding: "9px 18px", background: "#185FA5", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, cursor: "pointer" }}>+ Nouveau type d'acte</button>}
      </div>

      {error && !showForm && <div style={{ background: "#FCEBEB", border: "1px solid #F7C1C1", color: "#A32D2D", borderRadius: 8, padding: "10px 14px", marginTop: 14, fontSize: 13 }}>{error}</div>}

      {showForm && (
        <div style={{ background: "#fff", border: "1px solid #B5D4F4", borderRadius: 12, padding: 20, marginTop: 16 }}>
          <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 14, color: "#0F172A" }}>{editing ? "Modifier le type d'acte" : "Nouveau type d'acte"}</div>
          {error && <div style={{ background: "#FCEBEB", border: "1px solid #F7C1C1", color: "#A32D2D", borderRadius: 8, padding: "10px 14px", marginBottom: 12, fontSize: 13 }}>{error}</div>}
          <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 12 }}>
            <input style={iS} placeholder="Nom de l'acte * (ex: Prise de sang)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <input style={iS} type="number" min="1" placeholder="Duree (minutes)" value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })} />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 12 }}>
            <input style={iS} placeholder="Personnel requis (ex: Preleveur)" value={form.personnel_requis} onChange={(e) => setForm({ ...form, personnel_requis: e.target.value })} />
            <input style={iS} placeholder="Ressources (ex: Salle, tube EDTA)" value={form.ressources} onChange={(e) => setForm({ ...form, ressources: e.target.value })} />
          </div>
          <textarea style={{ ...iS, marginTop: 12, minHeight: 50, resize: "vertical" }} placeholder="Consignes (optionnel)" value={form.instructions} onChange={(e) => setForm({ ...form, instructions: e.target.value })} />
          <textarea style={{ ...iS, marginTop: 12, minHeight: 50, resize: "vertical" }} placeholder="Preparation demandee au patient (ex: A jeun depuis 8h)" value={form.preparation} onChange={(e) => setForm({ ...form, preparation: e.target.value })} />
          <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
            <button onClick={save} disabled={saving} style={{ padding: "8px 18px", background: "#185FA5", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, cursor: "pointer" }}>{saving ? "Enregistrement..." : "Enregistrer"}</button>
            <button onClick={() => { setShowForm(false); resetForm(); }} style={{ padding: "8px 18px", background: "transparent", border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 14, cursor: "pointer" }}>Annuler</button>
          </div>
        </div>
      )}

      <div style={{ marginTop: 20, background: "#fff", border: "1px solid #E2E8F0", borderRadius: 12, overflow: "hidden" }}>
        {loading ? (
          <div style={{ padding: 30, textAlign: "center", color: "#94A3B8", fontSize: 14 }}>Chargement...</div>
        ) : items.length === 0 ? (
          <div style={{ padding: 30, textAlign: "center", color: "#94A3B8", fontSize: 14 }}>Aucun type d'acte. Cliquez sur « Nouveau type d'acte ».</div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#F8FAFC", textAlign: "left" }}>
                {["Acte", "Duree", "Personnel", "Ressources", "Statut", "Actions"].map((h) => (
                  <th key={h} style={{ padding: "10px 16px", fontSize: 12, color: "#64748B", fontWeight: 600 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((a) => (
                <tr key={a.act_type_id} style={{ borderTop: "1px solid #F1F5F9", opacity: a.actif ? 1 : 0.55 }}>
                  <td style={{ padding: "12px 16px", fontSize: 14, fontWeight: 500, color: "#0F172A" }}>{a.name}{a.preparation && <div style={{ fontSize: 12, color: "#94A3B8", fontWeight: 400 }}>{a.preparation}</div>}</td>
                  <td style={{ padding: "12px 16px", fontSize: 13, color: "#475569" }}>{a.duration_minutes} min</td>
                  <td style={{ padding: "12px 16px", fontSize: 13, color: "#475569" }}>{a.personnel_requis || "—"}</td>
                  <td style={{ padding: "12px 16px", fontSize: 13, color: "#475569" }}>{a.ressources || "—"}</td>
                  <td style={{ padding: "12px 16px" }}><span style={{ fontSize: 12, padding: "3px 10px", borderRadius: 12, background: a.actif ? "#DCFCE7" : "#F1F5F9", color: a.actif ? "#166534" : "#64748B" }}>{a.actif ? "Actif" : "Inactif"}</span></td>
                  <td style={{ padding: "12px 16px" }}><span style={{ display: "flex", gap: 10 }}>
                    {can("acts:write") && <button onClick={() => startEdit(a)} style={{ fontSize: 13, color: "#185FA5", background: "none", border: "none", cursor: "pointer" }}>Modifier</button>}
                    {can("acts:write") && <button onClick={() => toggleActif(a)} style={{ fontSize: 13, color: "#334155", background: "none", border: "none", cursor: "pointer" }}>{a.actif ? "Desactiver" : "Activer"}</button>}
                    {can("acts:write") && <button onClick={() => setToDelete(a)} style={{ fontSize: 13, color: "#A32D2D", background: "none", border: "none", cursor: "pointer" }}>Supprimer</button>}
                  </span></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {toDelete && (
        <div onClick={() => setToDelete(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 12, width: "90%", maxWidth: 380, padding: 22 }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: "#0F172A", marginBottom: 8 }}>Supprimer « {toDelete.name} » ?</div>
            <div style={{ fontSize: 13, color: "#64748B", marginBottom: 18 }}>Cette action est definitive.</div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button onClick={() => setToDelete(null)} style={{ padding: "8px 16px", background: "#F1F5F9", color: "#334155", border: "none", borderRadius: 8, fontSize: 14, cursor: "pointer" }}>Annuler</button>
              <button onClick={doDelete} style={{ padding: "8px 16px", background: "#A32D2D", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, cursor: "pointer" }}>Supprimer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Agenda & moteur de rendez-vous (cahier des charges labo, §6, §8-9).
// Vue "liste" pour une date donnee (la plus simple des 4 vues du §6.1) :
// pas encore de calendrier visuel semaine/mois. Le moteur de creneaux
// (disponibilite reelle selon la duree de l'acte) est, lui, fonctionnel.
const AGENDA_STATUS_LABELS = { confirme: "Confirme", annule: "Annule", termine: "Termine", no_show: "Absent" };
const AGENDA_STATUS_COLORS = {
  confirme: ["#E6F1FB", "#185FA5"],
  annule: ["#F1F5F9", "#94A3B8"],
  termine: ["#DCFCE7", "#166534"],
  no_show: ["#FEF2E8", "#B45309"],
};

// Gestion des creneaux avancee (cahier des charges labo, section 25) :
// index de jour = date.getDay() ramene a la convention backend (lundi = 0),
// voir routes/schedule_config.py.
const SCHEDULE_WEEKDAY_LABELS = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"];

function AgendaModule({ can }) {
  const todayStr = () => new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(todayStr());
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [actTypes, setActTypes] = useState([]);

  // Vues semaine/mois (cahier des charges labo, section 6.1 : les 4 vues de
  // l'agenda sont journee/semaine/mois/liste). "liste" reste le comportement
  // par defaut inchange ; semaine/mois chargent une periode via le meme
  // endpoint (date_from/date_to) puis groupent cote client par jour.
  const [viewMode, setViewMode] = useState("liste");
  const [rangeAppointments, setRangeAppointments] = useState([]);
  const [rangeLoading, setRangeLoading] = useState(false);

  // Assistant de creation / deplacement (meme modale pour les deux usages).
  const [wizard, setWizard] = useState(null); // { mode: "create"|"move", appointment? }
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [patient, setPatient] = useState(null);
  const [actTypeId, setActTypeId] = useState("");
  const [wizDate, setWizDate] = useState(todayStr());
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [wizError, setWizError] = useState("");

  // Liste d'attente (section 24) : modale separee du wizard RDV (recherche
  // patient propre) pour ne pas melanger les deux flux.
  const [showWaitlist, setShowWaitlist] = useState(false);
  const [waitlist, setWaitlist] = useState([]);
  const [waitlistCount, setWaitlistCount] = useState(0);
  const [wlSearch, setWlSearch] = useState("");
  const [wlResults, setWlResults] = useState([]);
  const [wlPatient, setWlPatient] = useState(null);
  const [wlActTypeId, setWlActTypeId] = useState("");
  const [wlDate, setWlDate] = useState("");
  const [wlNotes, setWlNotes] = useState("");
  const [wlSaving, setWlSaving] = useState(false);
  const [wlError, setWlError] = useState("");

  // Gestion des creneaux avancee (cahier des charges labo, section 25) :
  // horaires hebdomadaires (avec pauses), capacite simultanee, fermetures
  // exceptionnelles. Chargee a la demande (pas au montage du module) : c'est
  // un ecran de configuration, pas un affichage du quotidien.
  const [showSchedule, setShowSchedule] = useState(false);
  const [weekly, setWeekly] = useState(null);
  const [capacity, setCapacity] = useState(1);
  const [confirmRequired, setConfirmRequired] = useState(false);
  const [confirmDeadlineHours, setConfirmDeadlineHours] = useState(24);
  const [closures, setClosures] = useState([]);
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [scheduleSaving, setScheduleSaving] = useState(false);
  const [scheduleError, setScheduleError] = useState("");
  const [newClosureDate, setNewClosureDate] = useState("");
  const [newClosureFullDay, setNewClosureFullDay] = useState(true);
  const [newClosureStart, setNewClosureStart] = useState("");
  const [newClosureEnd, setNewClosureEnd] = useState("");
  const [newClosureReason, setNewClosureReason] = useState("");

  const load = () => {
    setLoading(true); setError("");
    axios.get(`${API_URL}/api/sih/agenda/appointments`, { ...getAuthConfig(), params: { date } })
      .then((r) => setAppointments(r.data.appointments || []))
      .catch(() => setError("Impossible de charger l'agenda"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [date]);
  useEffect(() => {
    axios.get(`${API_URL}/api/sih/act-types/`, getAuthConfig()).then((r) => setActTypes(r.data.act_types || [])).catch(() => {});
  }, []);

  // Bornes de la semaine (lundi-dimanche) contenant `date`.
  const weekDays = () => {
    const d = new Date(`${date}T00:00:00`);
    const dow = d.getDay(); // 0=dimanche..6=samedi
    const mondayOffset = dow === 0 ? -6 : 1 - dow;
    const monday = new Date(d); monday.setDate(d.getDate() + mondayOffset);
    return Array.from({ length: 7 }, (_, i) => {
      const day = new Date(monday); day.setDate(monday.getDate() + i);
      return day.toISOString().slice(0, 10);
    });
  };
  // Bornes du mois (1er au dernier jour) contenant `date`.
  const monthBounds = () => {
    const d = new Date(`${date}T00:00:00`);
    const first = new Date(d.getFullYear(), d.getMonth(), 1);
    const last = new Date(d.getFullYear(), d.getMonth() + 1, 0);
    return { first: first.toISOString().slice(0, 10), last: last.toISOString().slice(0, 10), year: d.getFullYear(), month: d.getMonth() };
  };
  useEffect(() => {
    if (viewMode === "liste") return;
    setRangeLoading(true);
    const [from, to] = viewMode === "semaine"
      ? [weekDays()[0], weekDays()[6]]
      : [monthBounds().first, monthBounds().last];
    axios.get(`${API_URL}/api/sih/agenda/appointments`, { ...getAuthConfig(), params: { date_from: from, date_to: to } })
      .then((r) => setRangeAppointments(r.data.appointments || []))
      .catch(() => setRangeAppointments([]))
      .finally(() => setRangeLoading(false));
    // eslint-disable-next-line
  }, [viewMode, date]);
  const appointmentsByDay = () => {
    const map = {};
    for (const a of rangeAppointments) { (map[a.date] = map[a.date] || []).push(a); }
    return map;
  };

  const loadWaitlist = () => {
    axios.get(`${API_URL}/api/sih/agenda/waitlist`, { ...getAuthConfig(), params: { status: "all" } })
      .then((r) => {
        const items = r.data.waitlist || [];
        setWaitlist(items);
        setWaitlistCount(items.filter((w) => w.status === "attente" || w.status === "notifie").length);
      })
      .catch(() => {});
  };
  useEffect(() => { loadWaitlist(); }, []);

  const resetWaitlistForm = () => {
    setWlSearch(""); setWlResults([]); setWlPatient(null);
    setWlActTypeId(""); setWlDate(""); setWlNotes(""); setWlError("");
  };
  const doWlSearch = async (q) => {
    setWlSearch(q);
    if (q.trim().length < 2) { setWlResults([]); return; }
    try {
      const r = await axios.get(`${API_URL}/api/sih/patients`, { ...getAuthConfig(), params: { search: q, limit: 8 } });
      setWlResults(r.data.patients || []);
    } catch (e) { setWlResults([]); }
  };
  const addToWaitlist = async () => {
    if (!wlPatient) return;
    setWlSaving(true); setWlError("");
    try {
      await axios.post(`${API_URL}/api/sih/agenda/waitlist`, {
        patient_id: wlPatient.patient_id, act_type_id: wlActTypeId || null,
        desired_date: wlDate || null, notes: wlNotes,
      }, getAuthConfig());
      toast.success("Patient ajoute a la liste d'attente");
      resetWaitlistForm(); loadWaitlist();
    } catch (e) {
      setWlError(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Erreur");
    } finally { setWlSaving(false); }
  };
  const setWaitlistStatus = async (entry, status) => {
    try {
      await axios.put(`${API_URL}/api/sih/agenda/waitlist/${entry.waitlist_id}`, { status }, getAuthConfig());
      loadWaitlist();
    } catch (e) { toast.error("Erreur"); }
  };

  // Gestion des creneaux avancee (cahier des charges labo, section 25).
  const loadSchedule = () => {
    setScheduleLoading(true); setScheduleError("");
    Promise.all([
      axios.get(`${API_URL}/api/sih/schedule/config`, getAuthConfig()),
      axios.get(`${API_URL}/api/sih/schedule/closures`, getAuthConfig()),
    ]).then(([rc, rf]) => {
      setWeekly(rc.data.weekly); setCapacity(rc.data.capacity);
      setConfirmRequired(!!rc.data.confirmation?.required);
      setConfirmDeadlineHours(rc.data.confirmation?.deadline_hours || 24);
      setClosures(rf.data.closures || []);
    }).catch(() => setScheduleError("Impossible de charger la configuration"))
      .finally(() => setScheduleLoading(false));
  };
  const openSchedule = () => { setShowSchedule(true); loadSchedule(); };
  const toggleDayOpen = (day, isOpen) => {
    setWeekly((w) => ({ ...w, [day]: isOpen ? [{ start: "08:00", end: "17:00" }] : [] }));
  };
  const setRangeField = (day, idx, field, value) => {
    setWeekly((w) => {
      const ranges = [...(w[day] || [])];
      ranges[idx] = { ...ranges[idx], [field]: value };
      return { ...w, [day]: ranges };
    });
  };
  const addRange = (day) => {
    setWeekly((w) => ({ ...w, [day]: [...(w[day] || []), { start: "13:00", end: "17:00" }] }));
  };
  const removeRange = (day, idx) => {
    setWeekly((w) => {
      const ranges = [...(w[day] || [])];
      ranges.splice(idx, 1);
      return { ...w, [day]: ranges };
    });
  };
  const saveSchedule = async () => {
    setScheduleSaving(true); setScheduleError("");
    try {
      await axios.put(`${API_URL}/api/sih/schedule/config`, {
        weekly, capacity,
        confirmation: { required: confirmRequired, deadline_hours: confirmDeadlineHours },
      }, getAuthConfig());
      toast.success("Horaires mis a jour");
    } catch (e) {
      setScheduleError(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Erreur lors de l'enregistrement");
    } finally { setScheduleSaving(false); }
  };
  const addClosure = async () => {
    if (!newClosureDate) return;
    setScheduleError("");
    try {
      await axios.post(`${API_URL}/api/sih/schedule/closures`, {
        date: newClosureDate,
        start: newClosureFullDay ? null : (newClosureStart || null),
        end: newClosureFullDay ? null : (newClosureEnd || null),
        reason: newClosureReason,
      }, getAuthConfig());
      setNewClosureDate(""); setNewClosureFullDay(true); setNewClosureStart(""); setNewClosureEnd(""); setNewClosureReason("");
      loadSchedule();
    } catch (e) {
      setScheduleError(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Erreur lors de l'ajout");
    }
  };
  const removeClosure = async (c) => {
    try {
      await axios.delete(`${API_URL}/api/sih/schedule/closures/${c.closure_id}`, getAuthConfig());
      loadSchedule();
    } catch (e) { toast.error("Erreur"); }
  };

  const resetWizard = () => {
    setWizard(null); setSearch(""); setResults([]); setPatient(null);
    setActTypeId(""); setWizDate(todayStr()); setSlots([]); setNotes(""); setWizError("");
  };

  const openCreate = () => { resetWizard(); setWizDate(date); setWizard({ mode: "create" }); };
  const openMove = (apt) => {
    resetWizard();
    setWizDate(apt.date);
    setActTypeId(apt.act_type_id || "");
    setWizard({ mode: "move", appointment: apt });
  };

  const doSearch = async (q) => {
    setSearch(q);
    if (q.trim().length < 2) { setResults([]); return; }
    try {
      const r = await axios.get(`${API_URL}/api/sih/patients`, { ...getAuthConfig(), params: { search: q, limit: 8 } });
      setResults(r.data.patients || []);
    } catch (e) { setResults([]); }
  };

  const currentDuration = () => {
    const a = actTypes.find((a) => a.act_type_id === actTypeId);
    return a ? a.duration_minutes : 15;
  };

  // Recharge les creneaux disponibles a chaque changement de date/acte, aussi
  // bien en creation qu'en deplacement.
  useEffect(() => {
    if (!wizard || !wizDate) { setSlots([]); return; }
    setLoadingSlots(true);
    axios.get(`${API_URL}/api/sih/agenda/slots`, {
      ...getAuthConfig(),
      params: { date: wizDate, act_type_id: actTypeId || undefined, duration_minutes: actTypeId ? undefined : 15 },
    })
      .then((r) => setSlots(r.data.slots || []))
      .catch(() => setSlots([]))
      .finally(() => setLoadingSlots(false));
    // eslint-disable-next-line
  }, [wizard, wizDate, actTypeId]);

  const confirmCreate = async (time) => {
    if (!patient) return;
    setSaving(true); setWizError("");
    try {
      await axios.post(`${API_URL}/api/sih/agenda/appointments`, {
        patient_id: patient.patient_id, act_type_id: actTypeId || null, date: wizDate, time, notes,
      }, getAuthConfig());
      toast.success("Rendez-vous cree");
      resetWizard(); setDate(wizDate); load();
    } catch (e) {
      setWizError(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Erreur");
    } finally { setSaving(false); }
  };

  const confirmMove = async (time) => {
    if (!wizard?.appointment) return;
    setSaving(true); setWizError("");
    try {
      await axios.put(`${API_URL}/api/sih/agenda/appointments/${wizard.appointment.appointment_id}`, {
        date: wizDate, time,
      }, getAuthConfig());
      toast.success("Rendez-vous deplace");
      resetWizard(); setDate(wizDate); load();
    } catch (e) {
      setWizError(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Erreur");
    } finally { setSaving(false); }
  };

  const setStatus = async (apt, status) => {
    try {
      await axios.put(`${API_URL}/api/sih/agenda/appointments/${apt.appointment_id}`, { status }, getAuthConfig());
      load();
    } catch (e) {
      // Correctif L-STATUS-TRANSITION : le backend refuse desormais de
      // rouvrir un rendez-vous deja clos (annule/termine/no_show) avec un
      // message explicite — l'afficher plutot qu'un "Erreur" generique qui
      // ne dit pas pourquoi l'action a echoue.
      toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Erreur");
    }
  };

  const iS = { width: "100%", padding: "9px 12px", border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 14, boxSizing: "border-box", background: "#fff" };
  const lab = { fontSize: 12, color: "#64748B", marginBottom: 4, display: "block" };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: "#0F172A", margin: 0 }}>Agenda</h1>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} style={{ ...iS, width: "auto" }} />
          <div style={{ display: "flex", border: "1px solid #CBD5E1", borderRadius: 8, overflow: "hidden" }}>
            {[["liste", "Liste"], ["semaine", "Semaine"], ["mois", "Mois"]].map(([id, label]) => (
              <button key={id} onClick={() => setViewMode(id)}
                style={{
                  padding: "8px 14px", fontSize: 13, border: "none", cursor: "pointer",
                  background: viewMode === id ? "#185FA5" : "#fff",
                  color: viewMode === id ? "#fff" : "#334155", fontWeight: viewMode === id ? 600 : 400,
                }}>{label}</button>
            ))}
          </div>
          {can("agenda:read") && (
            <button onClick={() => { resetWaitlistForm(); setShowWaitlist(true); }} style={{ padding: "9px 16px", background: "#fff", color: "#334155", border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 14, cursor: "pointer", position: "relative" }}>
              Liste d'attente
              {waitlistCount > 0 && <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 700, background: "#F59E0B", color: "#fff", borderRadius: 10, padding: "1px 7px" }}>{waitlistCount}</span>}
            </button>
          )}
          {can("schedule:manage") && (
            <button onClick={openSchedule} style={{ padding: "9px 16px", background: "#fff", color: "#334155", border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 14, cursor: "pointer" }}>
              Horaires
            </button>
          )}
          {can("agenda:manage") && <button onClick={openCreate} style={{ padding: "9px 18px", background: "#185FA5", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, cursor: "pointer" }}>+ Nouveau rendez-vous</button>}
        </div>
      </div>

      {error && <div style={{ background: "#FCEBEB", border: "1px solid #F7C1C1", color: "#A32D2D", borderRadius: 8, padding: "10px 14px", marginTop: 14, fontSize: 13 }}>{error}</div>}

      {viewMode === "liste" && (
      <div style={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 12, marginTop: 16, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead><tr style={{ background: "#F8FAFC", textAlign: "left" }}>
            <th style={{ padding: "10px 14px", fontSize: 12, color: "#64748B", fontWeight: 600 }}>Heure</th>
            <th style={{ padding: "10px 14px", fontSize: 12, color: "#64748B", fontWeight: 600 }}>Patient</th>
            <th style={{ padding: "10px 14px", fontSize: 12, color: "#64748B", fontWeight: 600 }}>Acte</th>
            <th style={{ padding: "10px 14px", fontSize: 12, color: "#64748B", fontWeight: 600 }}>Statut</th>
            <th style={{ padding: "10px 14px", fontSize: 12, color: "#64748B", fontWeight: 600 }}>Actions</th>
          </tr></thead>
          <tbody>
            {loading && <tr><td colSpan={5} style={{ padding: "16px 14px", fontSize: 13, color: "#94A3B8", textAlign: "center" }}>Chargement...</td></tr>}
            {!loading && appointments.length === 0 && <tr><td colSpan={5} style={{ padding: "16px 14px", fontSize: 13, color: "#94A3B8", textAlign: "center" }}>Aucun rendez-vous ce jour.</td></tr>}
            {!loading && appointments.map((a) => { const sc = AGENDA_STATUS_COLORS[a.status] || ["#F1F5F9", "#64748B"]; return (
              <tr key={a.appointment_id} style={{ borderTop: "1px solid #F1F5F9", opacity: (a.status === "annule" || a.status === "no_show") ? 0.55 : 1 }}>
                <td style={{ padding: "12px 14px", fontSize: 14, fontWeight: 600, color: "#0F172A" }}>{a.time}</td>
                <td style={{ padding: "12px 14px", fontSize: 14 }}><div style={{ fontWeight: 500 }}>{a.patient_name || "—"}</div>{a.patient_number && <div style={{ fontSize: 11, color: "#94A3B8" }}>{a.patient_number}</div>}</td>
                <td style={{ padding: "12px 14px", fontSize: 13, color: "#475569" }}>{a.act_type_name || "—"}{a.duration_minutes ? ` (${a.duration_minutes} min)` : ""}</td>
                <td style={{ padding: "12px 14px" }}>
                  <span style={{ fontSize: 12, padding: "3px 10px", borderRadius: 12, background: sc[0], color: sc[1] }}>{AGENDA_STATUS_LABELS[a.status] || a.status}</span>
                  {a.confirmation_status === "en_attente" && <span style={{ marginLeft: 6, fontSize: 11, padding: "2px 8px", borderRadius: 10, background: "#F1F5F9", color: "#64748B" }}>Confirmation attendue</span>}
                  {a.confirmation_status === "non_confirme" && <span style={{ marginLeft: 6, fontSize: 11, padding: "2px 8px", borderRadius: 10, background: "#FEF2E8", color: "#B45309", fontWeight: 600 }}>Non confirme</span>}
                </td>
                <td style={{ padding: "12px 14px" }}><span style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                  {can("agenda:manage") && a.status === "confirme" && <button onClick={() => openMove(a)} style={{ fontSize: 13, color: "#185FA5", background: "none", border: "none", cursor: "pointer", fontWeight: 600 }}>Deplacer</button>}
                  {can("agenda:manage") && a.status === "confirme" && <button onClick={() => setStatus(a, "termine")} style={{ fontSize: 13, color: "#166534", background: "none", border: "none", cursor: "pointer", fontWeight: 600 }}>Terminer</button>}
                  {can("agenda:manage") && a.status === "confirme" && <button onClick={() => setStatus(a, "no_show")} style={{ fontSize: 13, color: "#B45309", background: "none", border: "none", cursor: "pointer" }}>Absent</button>}
                  {can("agenda:manage") && a.status === "confirme" && <button onClick={() => setStatus(a, "annule")} style={{ fontSize: 13, color: "#A32D2D", background: "none", border: "none", cursor: "pointer" }}>Annuler</button>}
                </span></td>
              </tr>); })}
          </tbody>
        </table>
      </div>
      )}

      {viewMode === "semaine" && (
        <div style={{ marginTop: 16 }}>
          {rangeLoading && <div style={{ padding: 16, fontSize: 13, color: "#94A3B8" }}>Chargement...</div>}
          {!rangeLoading && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(7, minmax(140px, 1fr))", gap: 10, overflowX: "auto" }}>
              {weekDays().map((d) => {
                const dayAppts = (appointmentsByDay()[d] || []).slice().sort((a, b) => a.time.localeCompare(b.time));
                const isToday = d === todayStr();
                const label = new Date(`${d}T00:00:00`).toLocaleDateString("fr-FR", { weekday: "short", day: "numeric", month: "short" });
                return (
                  <div key={d} style={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 10, minHeight: 220, overflow: "hidden" }}>
                    <div
                      onClick={() => { setDate(d); setViewMode("liste"); }}
                      style={{ padding: "8px 10px", fontSize: 12, fontWeight: 700, textTransform: "capitalize", cursor: "pointer", background: isToday ? "#E6F1FB" : "#F8FAFC", color: isToday ? "#185FA5" : "#334155" }}
                    >{label}</div>
                    <div style={{ padding: 8 }}>
                      {dayAppts.length === 0 && <div style={{ fontSize: 12, color: "#CBD5E1", padding: "6px 4px" }}>—</div>}
                      {dayAppts.map((a) => {
                        const sc = AGENDA_STATUS_COLORS[a.status] || ["#F1F5F9", "#64748B"];
                        return (
                          <div key={a.appointment_id} onClick={() => { setDate(d); setViewMode("liste"); }}
                            style={{ fontSize: 12, padding: "5px 7px", marginBottom: 5, borderRadius: 6, background: sc[0], color: sc[1], cursor: "pointer", opacity: (a.status === "annule" || a.status === "no_show") ? 0.55 : 1 }}>
                            <div style={{ fontWeight: 700 }}>{a.time}</div>
                            <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.patient_name || "—"}</div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {viewMode === "mois" && (
        <div style={{ marginTop: 16 }}>
          {rangeLoading && <div style={{ padding: 16, fontSize: 13, color: "#94A3B8" }}>Chargement...</div>}
          {!rangeLoading && (() => {
            const { year, month } = monthBounds();
            const firstOfMonth = new Date(year, month, 1);
            const startOffset = firstOfMonth.getDay() === 0 ? 6 : firstOfMonth.getDay() - 1; // lundi = 0
            const daysInMonth = new Date(year, month + 1, 0).getDate();
            const cells = [];
            for (let i = 0; i < startOffset; i++) cells.push(null);
            for (let day = 1; day <= daysInMonth; day++) cells.push(day);
            while (cells.length % 7 !== 0) cells.push(null);
            const byDay = appointmentsByDay();
            const monthLabel = firstOfMonth.toLocaleDateString("fr-FR", { month: "long", year: "numeric" });
            return (
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: "#0F172A", marginBottom: 10, textTransform: "capitalize" }}>{monthLabel}</div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 6 }}>
                  {["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"].map((d) => (
                    <div key={d} style={{ fontSize: 11, fontWeight: 700, color: "#64748B", textAlign: "center", padding: "4px 0" }}>{d}</div>
                  ))}
                  {cells.map((day, idx) => {
                    if (day === null) return <div key={`empty-${idx}`} />;
                    const iso = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
                    const dayAppts = byDay[iso] || [];
                    const isToday = iso === todayStr();
                    return (
                      <div key={iso} onClick={() => { setDate(iso); setViewMode("liste"); }}
                        style={{ minHeight: 78, border: "1px solid #E2E8F0", borderRadius: 8, padding: 6, cursor: "pointer", background: isToday ? "#E6F1FB" : "#fff" }}>
                        <div style={{ fontSize: 12, fontWeight: isToday ? 700 : 500, color: isToday ? "#185FA5" : "#334155" }}>{day}</div>
                        {dayAppts.length > 0 && (
                          <div style={{ marginTop: 4, fontSize: 11, fontWeight: 600, color: "#fff", background: "#185FA5", borderRadius: 10, padding: "1px 7px", display: "inline-block" }}>
                            {dayAppts.length} RDV
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {wizard && (
        <div onClick={resetWizard} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "flex-start", justifyContent: "center", zIndex: 1000, overflowY: "auto", paddingBottom: 40 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 12, width: "94%", maxWidth: 520, padding: 24, marginTop: 40 }}>
            <h3 style={{ fontSize: 17, fontWeight: 700, color: "#0F172A", margin: "0 0 16px" }}>
              {wizard.mode === "create" ? "Nouveau rendez-vous" : `Deplacer le RDV de ${wizard.appointment.patient_name || ""}`}
            </h3>
            {wizError && <div style={{ background: "#FCEBEB", border: "1px solid #F7C1C1", color: "#A32D2D", borderRadius: 8, padding: "10px 14px", marginBottom: 14, fontSize: 13 }}>{wizError}</div>}

            {wizard.mode === "create" && (
              <div style={{ marginBottom: 14 }}>
                <label style={lab}>Patient *</label>
                {patient ? (
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "9px 12px", border: "1px solid #E2E8F0", borderRadius: 8, background: "#F8FAFC" }}>
                    <span style={{ fontSize: 14 }}>{patient.first_name} {patient.last_name} <span style={{ color: "#94A3B8" }}>({patient.patient_number})</span></span>
                    <button onClick={() => setPatient(null)} style={{ fontSize: 12, color: "#A32D2D", background: "none", border: "none", cursor: "pointer" }}>Changer</button>
                  </div>
                ) : (
                  <>
                    <input style={iS} value={search} onChange={(e) => doSearch(e.target.value)} placeholder="Rechercher un patient (nom, numero, telephone...)" />
                    {results.length > 0 && (
                      <div style={{ marginTop: 6, border: "1px solid #E2E8F0", borderRadius: 8, overflow: "hidden" }}>
                        {results.map((p) => (
                          <div key={p.patient_id} onClick={() => { setPatient(p); setResults([]); setSearch(""); }} style={{ padding: "9px 12px", borderTop: "1px solid #F1F5F9", cursor: "pointer", fontSize: 14 }}>
                            {p.first_name} {p.last_name} <span style={{ color: "#94A3B8", fontSize: 12 }}>({p.patient_number})</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            <div style={{ display: "grid", gridTemplateColumns: wizard.mode === "create" ? "1fr 1fr" : "1fr", gap: 12, marginBottom: 14 }}>
              <div>
                <label style={lab}>Date</label>
                <input type="date" style={iS} value={wizDate} onChange={(e) => setWizDate(e.target.value)} />
              </div>
              {wizard.mode === "create" && (
                <div>
                  <label style={lab}>Type d'acte</label>
                  <select style={iS} value={actTypeId} onChange={(e) => setActTypeId(e.target.value)}>
                    <option value="">— Non precise (15 min) —</option>
                    {actTypes.map((a) => <option key={a.act_type_id} value={a.act_type_id}>{a.name} ({a.duration_minutes} min)</option>)}
                  </select>
                </div>
              )}
            </div>

            {wizard.mode === "create" && (
              <div style={{ marginBottom: 14 }}>
                <label style={lab}>Notes (optionnel)</label>
                <input style={iS} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Ex: Bilan de controle" />
              </div>
            )}

            <label style={lab}>Creneaux disponibles ({currentDuration()} min)</label>
            {loadingSlots ? (
              <div style={{ fontSize: 13, color: "#94A3B8", padding: "8px 0" }}>Recherche des creneaux...</div>
            ) : slots.length === 0 ? (
              <div style={{ fontSize: 13, color: "#94A3B8", padding: "8px 0" }}>Aucun creneau disponible pour cette date.</div>
            ) : (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, maxHeight: 180, overflowY: "auto" }}>
                {slots.map((s) => (
                  <button
                    key={s}
                    disabled={saving || (wizard.mode === "create" && !patient)}
                    onClick={() => (wizard.mode === "create" ? confirmCreate(s) : confirmMove(s))}
                    style={{
                      padding: "7px 12px", borderRadius: 8, fontSize: 13, cursor: "pointer",
                      border: "1px solid #B5D4F4", background: "#E6F1FB", color: "#185FA5",
                      opacity: (wizard.mode === "create" && !patient) ? 0.5 : 1,
                    }}
                  >{s}</button>
                ))}
              </div>
            )}

            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 18 }}>
              <button onClick={resetWizard} style={{ padding: "9px 16px", background: "#F1F5F9", color: "#334155", border: "none", borderRadius: 8, fontSize: 14, cursor: "pointer" }}>Fermer</button>
            </div>
          </div>
        </div>
      )}

      {showWaitlist && (
        <div onClick={() => setShowWaitlist(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "flex-start", justifyContent: "center", zIndex: 1000, overflowY: "auto", paddingBottom: 40 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 12, width: "94%", maxWidth: 560, padding: 24, marginTop: 40 }}>
            <h3 style={{ fontSize: 17, fontWeight: 700, color: "#0F172A", margin: "0 0 4px" }}>Liste d'attente</h3>
            <div style={{ fontSize: 12, color: "#94A3B8", marginBottom: 16 }}>Si un creneau se libere, les patients compatibles sont prevenus par email.</div>

            {can("agenda:manage") && (
              <div style={{ background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 10, padding: 14, marginBottom: 18 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#334155", marginBottom: 10 }}>Ajouter un patient</div>
                {wlError && <div style={{ background: "#FCEBEB", border: "1px solid #F7C1C1", color: "#A32D2D", borderRadius: 8, padding: "8px 12px", marginBottom: 10, fontSize: 13 }}>{wlError}</div>}
                {!wlPatient ? (
                  <div>
                    <input style={iS} placeholder="Rechercher un patient (nom, prenom)..." value={wlSearch} onChange={(e) => doWlSearch(e.target.value)} />
                    {wlResults.length > 0 && (
                      <div style={{ border: "1px solid #E2E8F0", borderRadius: 8, marginTop: 6, maxHeight: 160, overflowY: "auto" }}>
                        {wlResults.map((p) => (
                          <div key={p.patient_id} onClick={() => { setWlPatient(p); setWlResults([]); setWlSearch(""); }} style={{ padding: "8px 12px", cursor: "pointer", fontSize: 13, borderBottom: "1px solid #F1F5F9" }}>
                            {p.first_name} {p.last_name} {p.patient_number && <span style={{ color: "#94A3B8" }}>({p.patient_number})</span>}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <div>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 14, marginBottom: 10 }}>
                      <span><strong>{wlPatient.first_name} {wlPatient.last_name}</strong></span>
                      <button onClick={() => setWlPatient(null)} style={{ fontSize: 12, color: "#A32D2D", background: "none", border: "none", cursor: "pointer" }}>Changer</button>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
                      <select style={iS} value={wlActTypeId} onChange={(e) => setWlActTypeId(e.target.value)}>
                        <option value="">Tout acte</option>
                        {actTypes.map((a) => <option key={a.act_type_id} value={a.act_type_id}>{a.name}</option>)}
                      </select>
                      <input type="date" style={iS} value={wlDate} onChange={(e) => setWlDate(e.target.value)} placeholder="Toute date" />
                    </div>
                    <input style={iS} placeholder="Notes (optionnel)" value={wlNotes} onChange={(e) => setWlNotes(e.target.value)} />
                    <button onClick={addToWaitlist} disabled={wlSaving} style={{ marginTop: 10, padding: "9px 16px", background: "#185FA5", color: "#fff", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
                      {wlSaving ? "Ajout..." : "Ajouter a la liste d'attente"}
                    </button>
                  </div>
                )}
              </div>
            )}

            <div style={{ fontSize: 13, fontWeight: 600, color: "#334155", marginBottom: 10 }}>Patients en attente ({waitlist.filter((w) => w.status === "attente" || w.status === "notifie").length})</div>
            {waitlist.filter((w) => w.status === "attente" || w.status === "notifie").length === 0 && (
              <div style={{ fontSize: 13, color: "#94A3B8", padding: "12px 0" }}>Aucun patient en liste d'attente.</div>
            )}
            {waitlist.filter((w) => w.status === "attente" || w.status === "notifie").map((w) => (
              <div key={w.waitlist_id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: "1px solid #F1F5F9" }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 500, color: "#0F172A" }}>{w.patient_name}</div>
                  <div style={{ fontSize: 12, color: "#94A3B8" }}>
                    {w.act_type_name || "Tout acte"} · {w.desired_date || "Toute date"}
                    {w.status === "notifie" && <span style={{ marginLeft: 8, color: "#B45309", fontWeight: 600 }}>Notifie</span>}
                  </div>
                </div>
                {can("agenda:manage") && (
                  <span style={{ display: "flex", gap: 10 }}>
                    <button onClick={() => setWaitlistStatus(w, "confirme")} style={{ fontSize: 12, color: "#166534", background: "none", border: "none", cursor: "pointer", fontWeight: 600 }}>Confirme</button>
                    <button onClick={() => setWaitlistStatus(w, "annule")} style={{ fontSize: 12, color: "#A32D2D", background: "none", border: "none", cursor: "pointer" }}>Retirer</button>
                  </span>
                )}
              </div>
            ))}

            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 18 }}>
              <button onClick={() => setShowWaitlist(false)} style={{ padding: "9px 16px", background: "#F1F5F9", color: "#334155", border: "none", borderRadius: 8, fontSize: 14, cursor: "pointer" }}>Fermer</button>
            </div>
          </div>
        </div>
      )}

      {showSchedule && (
        <div onClick={() => setShowSchedule(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "flex-start", justifyContent: "center", zIndex: 1000, overflowY: "auto", paddingBottom: 40 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 12, width: "94%", maxWidth: 680, padding: 24, marginTop: 40 }}>
            <h3 style={{ fontSize: 17, fontWeight: 700, color: "#0F172A", margin: "0 0 4px" }}>Horaires, capacite et fermetures</h3>
            <div style={{ fontSize: 12, color: "#94A3B8", marginBottom: 16 }}>Definit les creneaux proposes dans l'agenda (section 25 du cahier des charges).</div>
            {scheduleError && <div style={{ background: "#FCEBEB", border: "1px solid #F7C1C1", color: "#A32D2D", borderRadius: 8, padding: "8px 12px", marginBottom: 14, fontSize: 13 }}>{scheduleError}</div>}

            {scheduleLoading || !weekly ? (
              <div style={{ padding: 20, textAlign: "center", color: "#94A3B8", fontSize: 13 }}>Chargement...</div>
            ) : (
              <>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#334155", marginBottom: 10 }}>Horaires hebdomadaires</div>
                {SCHEDULE_WEEKDAY_LABELS.map((label, idx) => {
                  const day = String(idx);
                  const ranges = weekly[day] || [];
                  const isOpen = ranges.length > 0;
                  return (
                    <div key={day} style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: "8px 0", borderBottom: "1px solid #F1F5F9" }}>
                      <label style={{ width: 110, fontSize: 13, display: "flex", alignItems: "center", gap: 6, paddingTop: 6 }}>
                        <input type="checkbox" checked={isOpen} onChange={(e) => toggleDayOpen(day, e.target.checked)} />
                        {label}
                      </label>
                      <div style={{ flex: 1 }}>
                        {isOpen ? (
                          <>
                            {ranges.map((r, i) => (
                              <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                                <input type="time" value={r.start} onChange={(e) => setRangeField(day, i, "start", e.target.value)} style={{ padding: "6px 8px", border: "1px solid #CBD5E1", borderRadius: 6, fontSize: 13 }} />
                                <span style={{ color: "#94A3B8", fontSize: 12 }}>a</span>
                                <input type="time" value={r.end} onChange={(e) => setRangeField(day, i, "end", e.target.value)} style={{ padding: "6px 8px", border: "1px solid #CBD5E1", borderRadius: 6, fontSize: 13 }} />
                                {ranges.length > 1 && <button onClick={() => removeRange(day, i)} style={{ fontSize: 12, color: "#A32D2D", background: "none", border: "none", cursor: "pointer" }}>Retirer</button>}
                              </div>
                            ))}
                            <button onClick={() => addRange(day)} style={{ fontSize: 12, color: "#185FA5", background: "none", border: "none", cursor: "pointer", padding: 0 }}>+ Ajouter une pause (ex: dejeuner)</button>
                          </>
                        ) : (
                          <span style={{ fontSize: 13, color: "#94A3B8" }}>Ferme</span>
                        )}
                      </div>
                    </div>
                  );
                })}

                <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 16 }}>
                  <label style={{ fontSize: 13, color: "#334155" }}>Capacite simultanee (rendez-vous en parallele) :</label>
                  <input type="number" min={1} value={capacity} onChange={(e) => setCapacity(Math.max(1, parseInt(e.target.value, 10) || 1))} style={{ width: 70, padding: "6px 8px", border: "1px solid #CBD5E1", borderRadius: 6, fontSize: 13 }} />
                </div>

                <div style={{ fontSize: 13, fontWeight: 600, color: "#334155", margin: "20px 0 8px", paddingTop: 16, borderTop: "1px solid #E2E8F0" }}>Confirmation obligatoire (section 23)</div>
                <label style={{ fontSize: 13, color: "#334155", display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
                  <input type="checkbox" checked={confirmRequired} onChange={(e) => setConfirmRequired(e.target.checked)} />
                  Exiger que le patient confirme son rendez-vous depuis le portail
                </label>
                {confirmRequired && (
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <label style={{ fontSize: 13, color: "#334155" }}>Delai avant le RDV pour confirmer (heures) :</label>
                    <input type="number" min={1} value={confirmDeadlineHours} onChange={(e) => setConfirmDeadlineHours(Math.max(1, parseInt(e.target.value, 10) || 24))} style={{ width: 70, padding: "6px 8px", border: "1px solid #CBD5E1", borderRadius: 6, fontSize: 13 }} />
                  </div>
                )}
                <div style={{ fontSize: 12, color: "#94A3B8", marginTop: 6 }}>
                  Le systeme ne desiste jamais automatiquement : passe le delai sans confirmation, le personnel est seulement alerte et decide (relancer, annuler, remettre le creneau en liste d'attente). S'applique aux rendez-vous crees APRES l'enregistrement de ce reglage.
                </div>

                <button onClick={saveSchedule} disabled={scheduleSaving} style={{ marginTop: 14, padding: "9px 18px", background: "#185FA5", color: "#fff", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
                  {scheduleSaving ? "Enregistrement..." : "Enregistrer les horaires"}
                </button>

                <div style={{ fontSize: 13, fontWeight: 600, color: "#334155", margin: "24px 0 10px", paddingTop: 16, borderTop: "1px solid #E2E8F0" }}>Fermetures exceptionnelles</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", marginBottom: 10 }}>
                  <input type="date" value={newClosureDate} onChange={(e) => setNewClosureDate(e.target.value)} style={{ padding: "7px 10px", border: "1px solid #CBD5E1", borderRadius: 6, fontSize: 13 }} />
                  <label style={{ fontSize: 12, color: "#334155", display: "flex", alignItems: "center", gap: 4 }}>
                    <input type="checkbox" checked={newClosureFullDay} onChange={(e) => setNewClosureFullDay(e.target.checked)} /> Journee complete
                  </label>
                  {!newClosureFullDay && (
                    <>
                      <input type="time" value={newClosureStart} onChange={(e) => setNewClosureStart(e.target.value)} style={{ padding: "6px 8px", border: "1px solid #CBD5E1", borderRadius: 6, fontSize: 13 }} />
                      <span style={{ color: "#94A3B8", fontSize: 12 }}>a</span>
                      <input type="time" value={newClosureEnd} onChange={(e) => setNewClosureEnd(e.target.value)} style={{ padding: "6px 8px", border: "1px solid #CBD5E1", borderRadius: 6, fontSize: 13 }} />
                    </>
                  )}
                  <input placeholder="Motif (optionnel)" value={newClosureReason} onChange={(e) => setNewClosureReason(e.target.value)} style={{ padding: "7px 10px", border: "1px solid #CBD5E1", borderRadius: 6, fontSize: 13, flex: 1, minWidth: 140 }} />
                  <button onClick={addClosure} disabled={!newClosureDate} style={{ padding: "8px 14px", background: "#185FA5", color: "#fff", border: "none", borderRadius: 8, fontSize: 13, cursor: "pointer" }}>Ajouter</button>
                </div>
                {closures.length === 0 ? (
                  <div style={{ fontSize: 13, color: "#94A3B8", padding: "8px 0" }}>Aucune fermeture exceptionnelle a venir.</div>
                ) : closures.map((c) => (
                  <div key={c.closure_id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: "1px solid #F1F5F9" }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 500, color: "#0F172A" }}>{c.date}{c.start ? ` · ${c.start}-${c.end}` : " · journee complete"}</div>
                      {c.reason && <div style={{ fontSize: 12, color: "#94A3B8" }}>{c.reason}</div>}
                    </div>
                    <button onClick={() => removeClosure(c)} style={{ fontSize: 12, color: "#A32D2D", background: "none", border: "none", cursor: "pointer" }}>Retirer</button>
                  </div>
                ))}
              </>
            )}

            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 18 }}>
              <button onClick={() => setShowSchedule(false)} style={{ padding: "9px 16px", background: "#F1F5F9", color: "#334155", border: "none", borderRadius: 8, fontSize: 14, cursor: "pointer" }}>Fermer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Integration SIL (cahier des charges labo, §27) : configuration d'un
// webhook sortant signe + journal des envois. Voir backend/sil.py pour le
// mecanisme et les limites assumees de cette premiere version (push
// sortant uniquement, pas de retry automatique en tache de fond).
const SIL_EVENT_LABELS = {
  "patient.created": "Patient créé",
  "patient.updated": "Patient modifié",
  "appointment.created": "Rendez-vous créé",
  "appointment.updated": "Rendez-vous modifié / annulé",
  "reception.arrival": "Arrivée à l'accueil",
  "phlebotomy.received": "Prélèvement reçu au labo",
};
const SIL_STATUS_LABELS = { pending: "En attente", sent: "Envoyé", failed: "Échec" };
const SIL_STATUS_COLORS = {
  pending: ["#FEF3C7", "#854F0B"], sent: ["#DCFCE7", "#166534"], failed: ["#FCEBEB", "#A32D2D"],
};

function SILIntegrationModule({ can }) {
  const [config, setConfig] = useState(null);
  const [eventTypes, setEventTypes] = useState([]);
  const [webhookUrl, setWebhookUrl] = useState("");
  const [enabled, setEnabled] = useState(false);
  const [selectedEvents, setSelectedEvents] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [newSecret, setNewSecret] = useState(null);
  const [events, setEvents] = useState([]);
  const [eventsTotal, setEventsTotal] = useState(0);
  const [loadingEvents, setLoadingEvents] = useState(true);

  const loadConfig = async () => {
    try {
      const [c, t] = await Promise.all([
        axios.get(`${API_URL}/api/sih/integration/config`, getAuthConfig()),
        axios.get(`${API_URL}/api/sih/integration/event-types`, getAuthConfig()),
      ]);
      setConfig(c.data);
      setWebhookUrl(c.data.webhook_url || "");
      setEnabled(c.data.enabled || false);
      setSelectedEvents(c.data.events_subscribed || []);
      setEventTypes(t.data.event_types || []);
    } catch (e) { setError("Impossible de charger la configuration"); }
  };
  const loadEvents = async () => {
    setLoadingEvents(true);
    try {
      const r = await axios.get(`${API_URL}/api/sih/integration/events`, { ...getAuthConfig(), params: { limit: 50 } });
      setEvents(r.data.events || []); setEventsTotal(r.data.total || 0);
    } catch (e) { /* silencieux : pas de configuration = pas de journal */ }
    finally { setLoadingEvents(false); }
  };
  useEffect(() => { loadConfig(); loadEvents(); }, []);

  const toggleEvent = (id) => {
    setSelectedEvents((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const save = async () => {
    setSaving(true); setError("");
    try {
      const r = await axios.put(`${API_URL}/api/sih/integration/config`, {
        webhook_url: webhookUrl, enabled, events_subscribed: selectedEvents,
      }, getAuthConfig());
      setConfig(r.data);
      toast.success("Configuration enregistrée");
    } catch (e) {
      setError(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Erreur");
    } finally { setSaving(false); }
  };

  const regenerateSecret = async () => {
    try {
      const r = await axios.post(`${API_URL}/api/sih/integration/config/regenerate-secret`, {}, getAuthConfig());
      setNewSecret(r.data.secret);
      loadConfig();
    } catch (e) { toast.error("Erreur lors de la génération du secret"); }
  };

  const retryEvent = async (ev) => {
    try {
      await axios.post(`${API_URL}/api/sih/integration/events/${ev.event_id}/retry`, {}, getAuthConfig());
      toast.success("Nouvel essai envoyé");
      loadEvents();
    } catch (e) { toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Erreur"); }
  };

  const iS = { width: "100%", padding: "9px 12px", border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 14, boxSizing: "border-box", background: "#fff" };
  const lab = { fontSize: 12, color: "#64748B", marginBottom: 4, display: "block" };

  return (
    <div>
      <h1 style={{ fontSize: 22, fontWeight: 700, color: "#0F172A", margin: 0 }}>Intégration SIL</h1>
      <p style={{ fontSize: 13, color: "#94A3B8", marginTop: 4 }}>
        Webhook sortant signé : chaque événement choisi ci-dessous est envoyé en HTTP POST à l'URL configurée, avec une signature HMAC à vérifier côté SIL.
      </p>

      {error && <div style={{ background: "#FCEBEB", border: "1px solid #F7C1C1", color: "#A32D2D", borderRadius: 8, padding: "10px 14px", marginTop: 14, fontSize: 13 }}>{error}</div>}

      <div style={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 12, marginTop: 16, padding: 20 }}>
        <div style={{ fontSize: 15, fontWeight: 600, color: "#0F172A", marginBottom: 14 }}>Configuration</div>

        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#334155", marginBottom: 14, cursor: "pointer" }}>
          <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} disabled={!can("integration:write")} />
          Intégration active
        </label>

        <label style={lab}>URL du webhook</label>
        <input style={iS} value={webhookUrl} onChange={(e) => setWebhookUrl(e.target.value)}
               placeholder="https://votre-sil.exemple.dz/webhooks/tabbibak" disabled={!can("integration:write")} />

        <div style={{ marginTop: 16 }}>
          <label style={lab}>Événements à transmettre</label>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {eventTypes.map((et) => (
              <label key={et} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#334155", cursor: "pointer" }}>
                <input type="checkbox" checked={selectedEvents.includes(et)} onChange={() => toggleEvent(et)} disabled={!can("integration:write")} />
                {SIL_EVENT_LABELS[et] || et}
              </label>
            ))}
          </div>
        </div>

        {can("integration:write") && (
          <div style={{ display: "flex", gap: 10, marginTop: 18 }}>
            <button onClick={save} disabled={saving} style={{ padding: "9px 18px", background: "#185FA5", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, cursor: "pointer" }}>
              {saving ? "Enregistrement..." : "Enregistrer"}
            </button>
            <button onClick={regenerateSecret} style={{ padding: "9px 18px", background: "#fff", color: "#334155", border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 14, cursor: "pointer" }}>
              {config?.secret_configured ? "Générer un nouveau secret" : "Générer un secret"}
            </button>
          </div>
        )}

        {config?.secret_configured && !newSecret && (
          <div style={{ fontSize: 12, color: "#94A3B8", marginTop: 10 }}>Secret configuré (se termine par « {config.secret_hint} »).</div>
        )}

        {newSecret && (
          <div style={{ background: "#FEF3C7", border: "1px solid #FDE68A", borderRadius: 8, padding: "12px 14px", marginTop: 14, fontSize: 13 }}>
            <div style={{ fontWeight: 600, color: "#854F0B", marginBottom: 6 }}>Notez ce secret maintenant — il ne sera plus jamais affiché :</div>
            <code style={{ fontSize: 13, color: "#0F172A", wordBreak: "break-all" }}>{newSecret}</code>
            <div style={{ marginTop: 8 }}>
              <button onClick={() => setNewSecret(null)} style={{ fontSize: 12, color: "#185FA5", background: "none", border: "none", cursor: "pointer" }}>J'ai noté le secret</button>
            </div>
          </div>
        )}
      </div>

      <div style={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 12, marginTop: 16, overflow: "hidden" }}>
        <div style={{ padding: "14px 16px", borderBottom: "1px solid #F1F5F9", fontSize: 15, fontWeight: 600, color: "#0F172A" }}>
          Journal des envois {eventsTotal ? `(${eventsTotal})` : ""}
        </div>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead><tr style={{ background: "#F8FAFC", textAlign: "left" }}>
            <th style={{ padding: "10px 14px", fontSize: 12, color: "#64748B", fontWeight: 600 }}>Date</th>
            <th style={{ padding: "10px 14px", fontSize: 12, color: "#64748B", fontWeight: 600 }}>Événement</th>
            <th style={{ padding: "10px 14px", fontSize: 12, color: "#64748B", fontWeight: 600 }}>Statut</th>
            <th style={{ padding: "10px 14px", fontSize: 12, color: "#64748B", fontWeight: 600 }}>Réponse</th>
            <th style={{ padding: "10px 14px", fontSize: 12, color: "#64748B", fontWeight: 600 }}>Actions</th>
          </tr></thead>
          <tbody>
            {loadingEvents && <tr><td colSpan={5} style={{ padding: "16px 14px", fontSize: 13, color: "#94A3B8", textAlign: "center" }}>Chargement...</td></tr>}
            {!loadingEvents && events.length === 0 && <tr><td colSpan={5} style={{ padding: "16px 14px", fontSize: 13, color: "#94A3B8", textAlign: "center" }}>Aucun événement envoyé.</td></tr>}
            {!loadingEvents && events.map((ev) => { const sc = SIL_STATUS_COLORS[ev.status] || ["#F1F5F9", "#64748B"]; return (
              <tr key={ev.event_id} style={{ borderTop: "1px solid #F1F5F9" }}>
                <td style={{ padding: "12px 14px", fontSize: 13, color: "#475569" }}>{new Date(ev.created_at).toLocaleString("fr-FR")}</td>
                <td style={{ padding: "12px 14px", fontSize: 13, color: "#0F172A" }}>{SIL_EVENT_LABELS[ev.event_type] || ev.event_type}</td>
                <td style={{ padding: "12px 14px" }}><span style={{ fontSize: 12, padding: "3px 10px", borderRadius: 12, background: sc[0], color: sc[1] }}>{SIL_STATUS_LABELS[ev.status] || ev.status}</span></td>
                <td style={{ padding: "12px 14px", fontSize: 12, color: "#94A3B8" }}>{ev.response_code || ev.last_error || "—"}</td>
                <td style={{ padding: "12px 14px" }}>
                  {ev.status === "failed" && can("integration:write") && (
                    <button onClick={() => retryEvent(ev)} style={{ fontSize: 13, color: "#185FA5", background: "none", border: "none", cursor: "pointer", fontWeight: 600 }}>Réessayer</button>
                  )}
                </td>
              </tr>); })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Statistiques du site (cahier des charges labo, section 21, "Niveau site").
// Etend LabOverview (chiffres du jour) a une evolution sur une periode
// (7/30/90 jours). "Niveau groupe" (comparaison entre sites) est hors
// perimetre : le multi-sites n'existe pas encore dans le produit.
function LabStatsModule({ can }) {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    setLoading(true); setError("");
    axios.get(`${API_URL}/api/sih/reception/stats/trend`, { ...getAuthConfig(), params: { days } })
      .then((r) => setData(r.data))
      .catch(() => setError("Impossible de charger les statistiques"))
      .finally(() => setLoading(false));
  }, [days]);

  const Kpi = ({ label, value, sub, color }) => (
    <div style={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 12, padding: "16px 18px" }}>
      <div style={{ fontSize: 12, color: "#64748B", marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 28, fontWeight: 700, color: color || "#0F172A", lineHeight: 1 }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: "#94A3B8", marginTop: 6 }}>{sub}</div>}
    </div>
  );
  const Panel = ({ title, children }) => (
    <div style={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 12, padding: 18 }}>
      <div style={{ fontSize: 14, fontWeight: 600, color: "#334155", marginBottom: 14 }}>{title}</div>
      {children}
    </div>
  );

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: "#0F172A", margin: 0 }}>Statistiques</h1>
          <div style={{ fontSize: 13, color: "#94A3B8", marginTop: 4 }}>Indicateurs du site sur la periode selectionnee</div>
        </div>
        <select value={days} onChange={(e) => setDays(Number(e.target.value))} style={{ padding: "9px 12px", border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 14, background: "#fff" }}>
          <option value={7}>7 derniers jours</option>
          <option value={30}>30 derniers jours</option>
          <option value={90}>90 derniers jours</option>
        </select>
      </div>

      {loading && <div style={{ marginTop: 20, color: "#94A3B8", fontSize: 14 }}>Chargement des indicateurs...</div>}
      {error && <div style={{ background: "#FCEBEB", border: "1px solid #F7C1C1", color: "#A32D2D", borderRadius: 8, padding: "10px 14px", marginTop: 14, fontSize: 13 }}>{error}</div>}

      {!loading && data && (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 14, marginTop: 18 }}>
            <Kpi label="Patients accueillis" value={data.summary.patients_arrives} color="#185FA5" />
            <Kpi label="Rendez-vous" value={data.summary.rendez_vous} color="#0596DE" />
            <Kpi label="Annulations" value={data.summary.annulations} sub={data.summary.taux_annulation_pct != null ? `${data.summary.taux_annulation_pct}% des RDV` : "—"} color="#854F0B" />
            <Kpi label="No-show" value={data.summary.no_show} sub={data.summary.taux_no_show_pct != null ? `${data.summary.taux_no_show_pct}% des RDV honores` : "—"} color="#A32D2D" />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginTop: 22 }}>
            <Panel title="Patients accueillis / rendez-vous par jour">
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={data.trend} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                  <XAxis dataKey="day" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="patients_arrives" name="Patients" fill="#185FA5" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="rendez_vous" name="Rendez-vous" fill="#0596DE" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Panel>
            <Panel title="Taux d'occupation (%)">
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={data.trend} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                  <XAxis dataKey="day" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 11 }} domain={[0, 100]} />
                  <Tooltip />
                  <Line type="monotone" dataKey="taux_occupation_pct" name="Occupation" stroke="#7C3AED" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </Panel>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginTop: 16 }}>
            <Panel title="Temps d'attente / duree de prise en charge (min)">
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={data.trend} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                  <XAxis dataKey="day" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Line type="monotone" dataKey="temps_attente_moyen_min" name="Attente" stroke="#F59E0B" strokeWidth={2} dot={false} connectNulls />
                  <Line type="monotone" dataKey="duree_prise_en_charge_moyenne_min" name="Prise en charge" stroke="#3B6D11" strokeWidth={2} dot={false} connectNulls />
                </LineChart>
              </ResponsiveContainer>
            </Panel>
            <Panel title="Annulations / no-show par jour">
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={data.trend} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                  <XAxis dataKey="day" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="annulations" name="Annulations" fill="#94A3B8" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="no_show" name="No-show" fill="#A32D2D" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Panel>
          </div>

          <div style={{ fontSize: 12, color: "#94A3B8", marginTop: 18, lineHeight: 1.6 }}>
            Le taux d'occupation est calcule sur une ressource partagee (pas encore par salle ou agent). Le no-show est marque manuellement par le personnel dans l'agenda (bouton "Absent"). La comparaison entre sites n'est pas encore disponible (multi-sites a venir).
          </div>
        </>
      )}
    </div>
  );
}

// Centre d'aide + tickets de support (cahier des charges labo, section 37).
// Le FAQ est du contenu statique (pas de section editoriale dynamique pour
// cette version) ; les tickets sont adresses a l'equipe Tabbibak, pas au
// patient (voir routes/support.py pour le cote plateforme).
function SupportModule({ can }) {
  const [tickets, setTickets] = useState(null);
  const [loadingList, setLoadingList] = useState(true);
  const [listError, setListError] = useState("");
  const [openTicketId, setOpenTicketId] = useState(null);
  const [form, setForm] = useState({ category: "technique", priority: "normale", description: "" });
  const [file, setFile] = useState(null);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState("");
  const [replyBody, setReplyBody] = useState("");
  const [replySending, setReplySending] = useState(false);
  const [replyError, setReplyError] = useState("");

  const load = () => {
    setLoadingList(true); setListError("");
    axios.get(`${API_URL}/api/sih/support/tickets`, getAuthConfig())
      .then((r) => setTickets(r.data.tickets || []))
      .catch(() => setListError("Impossible de charger vos tickets"))
      .finally(() => setLoadingList(false));
  };
  useEffect(() => { load(); }, []);

  const createTicket = async () => {
    if (!form.description.trim()) { setCreateError("Description requise"); return; }
    setCreating(true); setCreateError("");
    const fd = new FormData();
    fd.append("category", form.category); fd.append("priority", form.priority); fd.append("description", form.description.trim());
    if (file) fd.append("file", file);
    try {
      await axios.post(`${API_URL}/api/sih/support/tickets`, fd, getUploadConfig());
      setForm({ category: "technique", priority: "normale", description: "" }); setFile(null);
      load();
    } catch (e) { setCreateError(e.response?.data?.detail || "Erreur lors de la creation du ticket"); }
    finally { setCreating(false); }
  };

  const viewAttachment = async (t) => {
    try {
      const r = await axios.get(`${API_URL}/api/sih/support/tickets/${t.ticket_id}/attachment`, getAuthConfig());
      window.open(r.data.url, "_blank");
    } catch (e) { /* silencieux : piece jointe optionnelle */ }
  };

  const sendReply = async (ticketId) => {
    if (!replyBody.trim()) return;
    setReplySending(true); setReplyError("");
    try {
      await axios.post(`${API_URL}/api/sih/support/tickets/${ticketId}/reply`, { body: replyBody.trim() }, getAuthConfig());
      setReplyBody(""); load();
    } catch (e) { setReplyError(e.response?.data?.detail || "Erreur lors de l'envoi"); }
    finally { setReplySending(false); }
  };

  const CATEGORY_LABELS = { technique: "Technique", facturation: "Facturation", compte: "Compte", fonctionnalite: "Suggestion", autre: "Autre" };
  const PRIORITY_LABELS = { basse: "Basse", normale: "Normale", haute: "Haute", urgente: "Urgente" };
  const STATUS_COLORS = { ouvert: ["#FEF3C7", "#92400E"], en_cours: ["#DBEAFE", "#1E40AF"], resolu: ["#DCFCE7", "#166534"], ferme: ["#F1F5F9", "#64748B"] };
  const STATUS_LABELS = { ouvert: "Ouvert", en_cours: "En cours", resolu: "Resolu", ferme: "Ferme" };
  const iS = { width: "100%", padding: "9px 12px", border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 14, boxSizing: "border-box", background: "#fff" };
  const lab = { fontSize: 12, color: "#64748B", marginBottom: 4, display: "block" };

  const FAQ = [
    ["Comment configurer les horaires et la capacite de mon laboratoire ?", "Dans Agenda, bouton \"Horaires\" : definissez vos plages d'ouverture (avec pauses), la capacite simultanee et les fermetures exceptionnelles."],
    ["Comment activer la confirmation obligatoire des rendez-vous ?", "Dans Agenda > Horaires, activez \"Confirmation obligatoire\" et choisissez le delai avant le rendez-vous. Le patient confirme depuis son portail ; le personnel est alerte si le delai est depasse, mais le rendez-vous n'est jamais annule automatiquement."],
    ["Comment un patient accede-t-il a son portail ?", "Un lien securise, sans mot de passe, est envoye automatiquement par email lors de la creation du rendez-vous ou du rappel."],
    ["Comment repondre a un message envoye par un patient ?", "Ouvrez la fiche du patient concerne, onglet Messages : vous pouvez y lire et repondre, avec piece jointe si besoin."],
    ["Comment exporter ou anonymiser les donnees d'un patient (RGPD) ?", "Dans la fiche patient, onglet RGPD : \"Exporter\" telecharge toutes les donnees du patient ; \"Anonymiser\" efface irreversiblement son identite (reserve au gerant de l'etablissement)."],
    ["Que faire si une fonctionnalite ne marche pas comme prevu ?", "Ouvrez un ticket ci-dessous en decrivant le probleme le plus precisement possible (categorie \"Technique\"), avec une capture d'ecran en piece jointe si possible."],
  ];

  return (
    <div>
      <h1 style={{ fontSize: 22, fontWeight: 700, color: "#0F172A", margin: 0 }}>Aide & Support</h1>
      <div style={{ fontSize: 13, color: "#94A3B8", marginTop: 4, marginBottom: 20 }}>Centre d'aide et tickets adresses a l'equipe Tabbibak (section 37)</div>

      <div style={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 12, padding: 18, marginBottom: 24 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: "#334155", marginBottom: 14 }}>Questions frequentes</div>
        {FAQ.map(([q, a], i) => (
          <div key={i} style={{ marginBottom: i < FAQ.length - 1 ? 14 : 0, paddingBottom: i < FAQ.length - 1 ? 14 : 0, borderBottom: i < FAQ.length - 1 ? "1px solid #F1F5F9" : "none" }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: "#0F172A", marginBottom: 4 }}>{q}</div>
            <div style={{ fontSize: 13, color: "#64748B", lineHeight: 1.5 }}>{a}</div>
          </div>
        ))}
      </div>

      {can("support:write") && (
        <div style={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 12, padding: 18, marginBottom: 24 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: "#334155", marginBottom: 14 }}>Ouvrir un ticket</div>
          {createError && <div style={{ background: "#FCEBEB", border: "1px solid #F7C1C1", color: "#A32D2D", borderRadius: 8, padding: "8px 12px", marginBottom: 12, fontSize: 12 }}>{createError}</div>}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
            <div><label style={lab}>Categorie</label><select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} style={iS}>
              {Object.entries(CATEGORY_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select></div>
            <div><label style={lab}>Priorite</label><select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} style={iS}>
              {Object.entries(PRIORITY_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select></div>
          </div>
          <label style={lab}>Description</label>
          <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Decrivez votre probleme ou votre demande..." style={{ ...iS, minHeight: 80, resize: "vertical" }} />
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 10 }}>
            <label style={{ fontSize: 12, color: "#185FA5", cursor: "pointer" }}>
              {file ? `📎 ${file.name}` : "+ Piece jointe (JPEG, PNG, PDF)"}
              <input type="file" accept="image/jpeg,image/png,application/pdf" onChange={(e) => setFile(e.target.files?.[0] || null)} style={{ display: "none" }} />
            </label>
            <button onClick={createTicket} disabled={creating || !form.description.trim()} style={{ padding: "8px 18px", background: "#185FA5", color: "#fff", border: "none", borderRadius: 8, fontSize: 13, cursor: "pointer", opacity: (creating || !form.description.trim()) ? 0.6 : 1 }}>
              {creating ? "Envoi..." : "Envoyer le ticket"}
            </button>
          </div>
        </div>
      )}

      <div style={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 12, padding: 18 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: "#334155", marginBottom: 14 }}>Mes tickets</div>
        {loadingList && <div style={{ fontSize: 13, color: "#94A3B8" }}>Chargement...</div>}
        {listError && <div style={{ background: "#FCEBEB", border: "1px solid #F7C1C1", color: "#A32D2D", borderRadius: 8, padding: "8px 12px", fontSize: 12 }}>{listError}</div>}
        {!loadingList && tickets && tickets.length === 0 && <div style={{ fontSize: 13, color: "#94A3B8" }}>Aucun ticket.</div>}
        {!loadingList && tickets && tickets.map((t) => {
          const sc = STATUS_COLORS[t.status] || ["#F1F5F9", "#64748B"];
          const open = openTicketId === t.ticket_id;
          return (
            <div key={t.ticket_id} style={{ borderTop: "1px solid #F1F5F9", padding: "12px 0" }}>
              <div onClick={() => {
                // Correctif L-SUPPORT-DRAFT (voir frontend/src/lab/SupportModule.js,
                // meme copie du meme bug) : reinitialiser le brouillon de
                // reponse en changeant de ticket ouvert, pour ne jamais
                // envoyer par erreur une reponse tapee pour un autre ticket.
                setOpenTicketId(open ? null : t.ticket_id);
                setReplyBody("");
                setReplyError("");
              }} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer" }}>
                <div>
                  <div style={{ fontWeight: 500, color: "#0F172A" }}>{CATEGORY_LABELS[t.category] || t.category} · {t.description.slice(0, 70)}{t.description.length > 70 ? "…" : ""}</div>
                  <div style={{ fontSize: 12, color: "#94A3B8", marginTop: 2 }}>{fmtDate(t.created_at)} · {PRIORITY_LABELS[t.priority] || t.priority}</div>
                </div>
                <span style={{ fontSize: 12, padding: "3px 10px", borderRadius: 12, background: sc[0], color: sc[1], whiteSpace: "nowrap" }}>{STATUS_LABELS[t.status] || t.status}</span>
              </div>
              {open && (
                <div style={{ marginTop: 10, paddingLeft: 4 }}>
                  <div style={{ fontSize: 13, color: "#334155", whiteSpace: "pre-wrap", marginBottom: 8 }}>{t.description}</div>
                  {t.attachment && <button onClick={() => viewAttachment(t)} style={{ fontSize: 12, color: "#185FA5", background: "none", border: "none", cursor: "pointer", padding: 0, marginBottom: 8, display: "block" }}>📎 {t.attachment.filename}</button>}
                  {(t.replies || []).map((r, i) => (
                    <div key={i} style={{ background: r.from === "admin" ? "#E6F1FB" : "#F8FAFC", borderRadius: 8, padding: "8px 10px", marginBottom: 6 }}>
                      <div style={{ fontSize: 11, fontWeight: 600, color: r.from === "admin" ? "#185FA5" : "#334155" }}>{r.author} · {fmtDate(r.created_at)}</div>
                      <div style={{ fontSize: 13, color: "#334155", whiteSpace: "pre-wrap", marginTop: 2 }}>{r.body}</div>
                    </div>
                  ))}
                  {can("support:write") && t.status !== "ferme" && (
                    <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                      <input value={replyBody} onChange={(e) => setReplyBody(e.target.value)} placeholder="Repondre..." style={{ ...iS, flex: 1 }} />
                      <button onClick={() => sendReply(t.ticket_id)} disabled={replySending || !replyBody.trim()} style={{ padding: "8px 14px", background: "#185FA5", color: "#fff", border: "none", borderRadius: 8, fontSize: 13, cursor: "pointer", opacity: (replySending || !replyBody.trim()) ? 0.6 : 1 }}>Envoyer</button>
                    </div>
                  )}
                  {replyError && <div style={{ fontSize: 12, color: "#A32D2D", marginTop: 6 }}>{replyError}</div>}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function PhlebotomyModule({ can }) {
  const [samples, setSamples] = useState([]);
  const [patients, setPatients] = useState([]);
  const [error, setError] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [rejectFor, setRejectFor] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [anSearch, setAnSearch] = useState("");
  const [showCat, setShowCat] = useState(false);
  const empty = { patient_id: "", sample_type: "sang", tube_type: "sec", tubes_count: 1, analyses: [], fasting: false, priority: "normal", conditions: "", note: "" };
  const [form, setForm] = useState(empty);
  const load = async () => {
    try { const s = await axios.get(`${API_URL}/api/sih/phlebotomy/samples`, getAuthConfig()); setSamples(s.data.samples || []); }
    catch (e) { setError("Impossible de charger les prelevements"); }
  };
  const loadPatients = async () => {
    try { const p = await axios.get(`${API_URL}/api/sih/patients`, getAuthConfig()); setPatients(p.data.patients || []); } catch (e) {}
  };
  useEffect(() => { load(); loadPatients(); }, []);
  const toggleAnalysis = (item) => {
    setForm((f) => {
      const on = f.analyses.find((a) => a.name === item.name);
      return { ...f, analyses: on ? f.analyses.filter((a) => a.name !== item.name) : [...f.analyses, { name: item.name, cat: item.cat }] };
    });
  };
  const save = async () => {
    try {
      const payload = { ...form, tubes_count: parseInt(form.tubes_count) || 1 };
      await axios.post(`${API_URL}/api/sih/phlebotomy/samples`, payload, getAuthConfig());
      setShowForm(false); setForm(empty); setAnSearch(""); setShowCat(false); load();
    } catch (e) { setError(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Erreur"); }
  };
  const advance = async (s, status) => {
    try { await axios.put(`${API_URL}/api/sih/phlebotomy/samples/${s.sample_id}`, { status }, getAuthConfig()); load(); }
    catch (e) { setError(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Erreur"); }
  };
  const doReject = async () => {
    if (!rejectReason) { setError("Motif de rejet obligatoire"); return; }
    try { await axios.put(`${API_URL}/api/sih/phlebotomy/samples/${rejectFor.sample_id}`, { status: "rejete", reject_reason: rejectReason }, getAuthConfig()); setRejectFor(null); setRejectReason(""); load(); }
    catch (e) { setError(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Erreur"); }
  };
  const remove = async (s) => {
    try { await axios.delete(`${API_URL}/api/sih/phlebotomy/samples/${s.sample_id}`, getAuthConfig()); load(); }
    catch (e) { setError("Erreur"); }
  };
  const iS = { width: "100%", padding: "9px 12px", border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 14, boxSizing: "border-box", background: "#fff" };
  const lab = { fontSize: 12, color: "#64748B", marginBottom: 4, display: "block" };
  const catNames = [...new Set(LAB_CATALOG.filter((it) => analysisSpecimen(it) === form.sample_type).map((it) => it.cat))];
  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: "#0F172A", margin: 0 }}>Prelevements</h1>
        {can("phlebotomy:manage") && <button onClick={() => { setForm(empty); setError(""); setAnSearch(""); setShowCat(false); setShowForm(true); }} style={{ padding: "9px 18px", background: "#185FA5", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, cursor: "pointer" }}>+ Nouveau prelevement</button>}
      </div>
      {error && !showForm && !rejectFor && <div style={{ background: "#FCEBEB", border: "1px solid #F7C1C1", color: "#A32D2D", borderRadius: 8, padding: "10px 14px", marginTop: 14, fontSize: 13 }}>{error}</div>}
      <div style={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 12, marginTop: 16, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead><tr style={{ background: "#F8FAFC", textAlign: "left" }}>
            <th style={{ padding: "10px 14px", fontSize: 12, color: "#64748B", fontWeight: 600 }}>Patient</th>
            <th style={{ padding: "10px 14px", fontSize: 12, color: "#64748B", fontWeight: 600 }}>Type / Tube</th>
            <th style={{ padding: "10px 14px", fontSize: 12, color: "#64748B", fontWeight: 600 }}>Analyses</th>
            <th style={{ padding: "10px 14px", fontSize: 12, color: "#64748B", fontWeight: 600 }}>Priorite</th>
            <th style={{ padding: "10px 14px", fontSize: 12, color: "#64748B", fontWeight: 600 }}>Statut</th>
            <th style={{ padding: "10px 14px", fontSize: 12, color: "#64748B", fontWeight: 600 }}>Actions</th>
          </tr></thead>
          <tbody>
            {samples.length === 0 && <tr><td colSpan={6} style={{ padding: "16px 14px", fontSize: 13, color: "#94A3B8", textAlign: "center" }}>Aucun prelevement.</td></tr>}
            {samples.map((s) => { const sc = PHLEB_STATUS_COLORS[s.status] || ["#F1F5F9", "#64748B"]; return (
              <tr key={s.sample_id} style={{ borderTop: "1px solid #F1F5F9" }}>
                <td style={{ padding: "12px 14px", fontSize: 14 }}><div style={{ fontWeight: 500 }}>{s.patient_name || "—"}</div>{s.patient_number && <div style={{ fontSize: 11, color: "#94A3B8" }}>{s.patient_number}</div>}</td>
                <td style={{ padding: "12px 14px", fontSize: 13, color: "#475569" }}>{s.sample_type}{s.tube_type ? ` · ${TUBE_LABELS[s.tube_type] || s.tube_type}` : ""}{s.tubes_count > 1 ? ` ×${s.tubes_count}` : ""}</td>
                <td style={{ padding: "12px 14px", fontSize: 12, color: "#475569", maxWidth: 240 }}>{(s.analyses || []).length ? s.analyses.map((a) => a.name).join(", ") : <span style={{ color: "#94A3B8" }}>—</span>}</td>
                <td style={{ padding: "12px 14px" }}>{s.priority === "urgent" ? <span style={{ fontSize: 12, padding: "3px 10px", borderRadius: 12, background: "#FCEBEB", color: "#A32D2D", fontWeight: 600 }}>Urgent</span> : <span style={{ fontSize: 12, color: "#94A3B8" }}>Normal</span>}</td>
                <td style={{ padding: "12px 14px" }}><span style={{ fontSize: 12, padding: "3px 10px", borderRadius: 12, background: sc[0], color: sc[1] }}>{PHLEB_STATUS_LABELS[s.status] || s.status}</span>{s.status === "rejete" && s.reject_reason && <div style={{ fontSize: 11, color: "#A32D2D", marginTop: 3 }}>{PHLEB_REJECT_LABELS[s.reject_reason] || s.reject_reason}</div>}</td>
                <td style={{ padding: "12px 14px" }}><span style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                  {can("phlebotomy:manage") && s.status === "a_prelever" && <button onClick={() => advance(s, "preleve")} style={{ fontSize: 13, color: "#3730A3", background: "none", border: "none", cursor: "pointer", fontWeight: 600 }}>Preleve</button>}
                  {can("phlebotomy:manage") && s.status === "preleve" && <button onClick={() => advance(s, "achemine")} style={{ fontSize: 13, color: "#1E40AF", background: "none", border: "none", cursor: "pointer" }}>Acheminer</button>}
                  {can("phlebotomy:manage") && s.status === "achemine" && <button onClick={() => advance(s, "recu_labo")} style={{ fontSize: 13, color: "#3B6D11", background: "none", border: "none", cursor: "pointer", fontWeight: 600 }}>Recu labo</button>}
                  {can("phlebotomy:manage") && (s.status === "preleve" || s.status === "achemine" || s.status === "recu_labo") && <button onClick={() => { setRejectFor(s); setRejectReason(""); setError(""); }} style={{ fontSize: 13, color: "#A32D2D", background: "none", border: "none", cursor: "pointer" }}>Rejeter</button>}
                  {can("phlebotomy:manage") && s.status === "a_prelever" && <button onClick={() => remove(s)} style={{ fontSize: 13, color: "#A32D2D", background: "none", border: "none", cursor: "pointer" }}>Suppr.</button>}
                </span></td>
              </tr>); })}
          </tbody>
        </table>
      </div>
      {showForm && (
        <div onClick={() => setShowForm(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "flex-start", justifyContent: "center", zIndex: 1000, overflowY: "auto", paddingBottom: 40 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 12, width: "94%", maxWidth: 640, padding: 24, marginTop: 16 }}>
            <h3 style={{ fontSize: 17, fontWeight: 700, color: "#0F172A", margin: "0 0 16px" }}>Nouveau prelevement</h3>
            {error && <div style={{ background: "#FCEBEB", border: "1px solid #F7C1C1", color: "#A32D2D", borderRadius: 8, padding: "10px 14px", marginBottom: 14, fontSize: 13 }}>{error}</div>}
            <div style={{ marginBottom: 12 }}><label style={lab}>Patient *</label><select value={form.patient_id} onChange={(e) => setForm({ ...form, patient_id: e.target.value })} style={iS}><option value="">— Selectionner —</option>{patients.map((p) => <option key={p.patient_id} value={p.patient_id}>{p.first_name} {p.last_name}{p.patient_number ? ` (${p.patient_number})` : ""}</option>)}</select></div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
              <div><label style={lab}>Type</label><select value={form.sample_type} onChange={(e) => setForm({ ...form, sample_type: e.target.value, analyses: form.analyses.filter((a) => analysisSpecimen(a) === e.target.value) })} style={iS}><option value="sang">Sang</option><option value="urine">Urine</option><option value="selles">Selles</option><option value="LCR">LCR</option><option value="pus">Pus</option><option value="autre">Autre</option></select></div>
              <div><label style={lab}>Tube</label><select value={form.tube_type} onChange={(e) => setForm({ ...form, tube_type: e.target.value })} style={iS}><option value="sec">Sec (biochimie)</option><option value="EDTA">EDTA (hemato)</option><option value="citrate">Citrate (coag)</option><option value="heparine">Heparine</option><option value="fluorure">Fluorure (glycemie)</option><option value="autre">Autre</option></select></div>
              <div><label style={lab}>Nb tubes</label><input type="number" min="1" value={form.tubes_count} onChange={(e) => setForm({ ...form, tubes_count: e.target.value })} style={iS} /></div>
            </div>
            <div style={{ marginTop: 14 }}>
              <label style={lab}>Analyses demandees</label>
              {form.analyses.length > 0 && <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>{form.analyses.map((a) => <span key={a.name} onClick={() => toggleAnalysis(a)} style={{ fontSize: 12, background: "#E6F1FB", color: "#185FA5", padding: "4px 10px", borderRadius: 12, cursor: "pointer" }}>{a.name} ✕</span>)}</div>}
              <input style={iS} placeholder="Rechercher une analyse (ex: glycemie, NFS, sodium...)" value={anSearch} onChange={(e) => setAnSearch(e.target.value)} />
              {anSearch.trim() && (
                <div style={{ border: "1px solid #E2E8F0", borderRadius: 8, marginTop: 6, maxHeight: 200, overflowY: "auto" }}>
                  {LAB_CATALOG.filter((it) => analysisSpecimen(it) === form.sample_type && it.name.toLowerCase().includes(anSearch.trim().toLowerCase())).map((item) => { const on = !!form.analyses.find((a) => a.name === item.name); return (
                    <div key={item.name} onClick={() => toggleAnalysis(item)} style={{ padding: "8px 12px", fontSize: 13, cursor: "pointer", background: on ? "#E6F1FB" : "transparent", borderBottom: "1px solid #F1F5F9", display: "flex", justifyContent: "space-between" }}><span>{item.name}</span><span style={{ fontSize: 11, color: "#94A3B8" }}>{item.cat}{on ? " ✓" : ""}</span></div>
                  ); })}
                  {LAB_CATALOG.filter((it) => analysisSpecimen(it) === form.sample_type && it.name.toLowerCase().includes(anSearch.trim().toLowerCase())).length === 0 && <div style={{ padding: "10px 12px", fontSize: 13, color: "#94A3B8" }}>Aucune analyse trouvee</div>}
                </div>
              )}
              <button type="button" onClick={() => setShowCat(!showCat)} style={{ marginTop: 8, fontSize: 13, color: "#185FA5", background: "none", border: "none", cursor: "pointer", padding: 0 }}>{showCat ? "▴ Masquer le catalogue" : "▾ Parcourir par categorie"}</button>
              {showCat && (
                <div style={{ marginTop: 8, border: "1px solid #E2E8F0", borderRadius: 8, maxHeight: 260, overflowY: "auto" }}>
                  {catNames.map((catName) => (
                    <div key={catName}>
                      <div style={{ padding: "6px 12px", fontSize: 12, fontWeight: 600, color: "#64748B", background: "#F8FAFC" }}>{catName}</div>
                      {LAB_CATALOG.filter((it) => it.cat === catName && analysisSpecimen(it) === form.sample_type).map((item) => { const on = !!form.analyses.find((a) => a.name === item.name); return (
                        <div key={item.name} onClick={() => toggleAnalysis(item)} style={{ padding: "7px 16px", fontSize: 13, cursor: "pointer", background: on ? "#E6F1FB" : "transparent", borderBottom: "1px solid #F8FAFC", display: "flex", justifyContent: "space-between" }}><span>{item.name}</span>{on && <span style={{ color: "#185FA5" }}>✓</span>}</div>
                      ); })}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 14 }}>
              <div><label style={lab}>Priorite</label><select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} style={iS}><option value="normal">Normal</option><option value="urgent">Urgent</option></select></div>
              <div style={{ display: "flex", alignItems: "flex-end", paddingBottom: 9 }}><label style={{ fontSize: 14, color: "#334155", display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}><input type="checkbox" checked={form.fasting} onChange={(e) => setForm({ ...form, fasting: e.target.checked })} /> Patient a jeun</label></div>
            </div>
            <div style={{ marginTop: 12 }}><label style={lab}>Conditions particulieres</label><input value={form.conditions} onChange={(e) => setForm({ ...form, conditions: e.target.value })} style={iS} placeholder="Ex: garrot < 1 min, position couchee..." /></div>
            <div style={{ marginTop: 12 }}><label style={lab}>Note</label><input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} style={iS} /></div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 20 }}>
              <button onClick={() => setShowForm(false)} style={{ padding: "9px 18px", background: "#fff", color: "#475569", border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 14, cursor: "pointer" }}>Annuler</button>
              <button onClick={save} style={{ padding: "9px 18px", background: "#185FA5", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, cursor: "pointer", fontWeight: 600 }}>Enregistrer</button>
            </div>
          </div>
        </div>
      )}
      {rejectFor && (
        <div onClick={() => setRejectFor(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "flex-start", justifyContent: "center", zIndex: 1000, overflowY: "auto", paddingBottom: 40 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 12, width: "92%", maxWidth: 480, padding: 24, marginTop: 30 }}>
            <h3 style={{ fontSize: 17, fontWeight: 700, color: "#0F172A", margin: "0 0 4px" }}>Rejeter le prelevement</h3>
            <div style={{ fontSize: 13, color: "#64748B", marginBottom: 16 }}>{rejectFor.patient_name} — non-conformite pre-analytique</div>
            {error && <div style={{ background: "#FCEBEB", border: "1px solid #F7C1C1", color: "#A32D2D", borderRadius: 8, padding: "10px 14px", marginBottom: 14, fontSize: 13 }}>{error}</div>}
            <label style={lab}>Motif de rejet *</label>
            <select value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} style={iS}><option value="">— Selectionner un motif —</option>{Object.entries(PHLEB_REJECT_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 20 }}>
              <button onClick={() => setRejectFor(null)} style={{ padding: "9px 18px", background: "#fff", color: "#475569", border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 14, cursor: "pointer" }}>Annuler</button>
              <button onClick={doReject} style={{ padding: "9px 18px", background: "#A32D2D", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, cursor: "pointer", fontWeight: 600 }}>Confirmer le rejet</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
function BloodModule({ can }) {
  const [view, setView] = useState("stock");
  const [units, setUnits] = useState([]);
  const [requests, setRequests] = useState([]);
  const [summary, setSummary] = useState({});
  const [patients, setPatients] = useState([]);
  const [error, setError] = useState("");
  const [showUnitForm, setShowUnitForm] = useState(false);
  const [showReqForm, setShowReqForm] = useState(false);
  const [assignFor, setAssignFor] = useState(null);
  const [assignSel, setAssignSel] = useState([]);
  const emptyUnit = { blood_group: "O", rhesus: "+", component: "CGR", volume_ml: "", collected_at: "", expires_at: "", donor_ref: "", note: "" };
  const emptyReq = { patient_id: "", blood_group: "O", rhesus: "+", component: "CGR", units_requested: 1, urgency: "programmee", indication: "", note: "" };
  const [uForm, setUForm] = useState(emptyUnit);
  const [rForm, setRForm] = useState(emptyReq);
  const load = async () => {
    try {
      const [u, r, s] = await Promise.all([
        axios.get(`${API_URL}/api/sih/blood-bank/units`, getAuthConfig()),
        axios.get(`${API_URL}/api/sih/blood-bank/requests`, getAuthConfig()),
        axios.get(`${API_URL}/api/sih/blood-bank/stock-summary`, getAuthConfig()),
      ]);
      setUnits(u.data.units || []);
      setRequests(r.data.requests || []);
      setSummary(s.data.summary || {});
    } catch (e) { setError("Impossible de charger la banque de sang"); }
  };
  const loadPatients = async () => {
    try { const p = await axios.get(`${API_URL}/api/sih/patients`, getAuthConfig()); setPatients(p.data.patients || []); }
    catch (e) {}
  };
  useEffect(() => { load(); loadPatients(); }, []);
  const saveUnit = async () => {
    try {
      const payload = { ...uForm, volume_ml: uForm.volume_ml ? parseInt(uForm.volume_ml) : null };
      await axios.post(`${API_URL}/api/sih/blood-bank/units`, payload, getAuthConfig());
      setShowUnitForm(false); setUForm(emptyUnit); load();
    } catch (e) { setError(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Erreur"); }
  };
  const setUnitStatus = async (u, status) => {
    try { await axios.put(`${API_URL}/api/sih/blood-bank/units/${u.unit_id}`, { status }, getAuthConfig()); load(); }
    catch (e) { setError(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Erreur"); }
  };
  const removeUnit = async (u) => {
    try { await axios.delete(`${API_URL}/api/sih/blood-bank/units/${u.unit_id}`, getAuthConfig()); load(); }
    catch (e) { setError("Erreur"); }
  };
  const saveReq = async () => {
    try {
      const payload = { ...rForm, units_requested: parseInt(rForm.units_requested) || 1 };
      await axios.post(`${API_URL}/api/sih/blood-bank/requests`, payload, getAuthConfig());
      setShowReqForm(false); setRForm(emptyReq); load();
    } catch (e) { setError(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Erreur"); }
  };
  const cancelReq = async (r) => {
    try { await axios.put(`${API_URL}/api/sih/blood-bank/requests/${r.request_id}`, { status: "annulee" }, getAuthConfig()); load(); }
    catch (e) { setError("Erreur"); }
  };
  const openAssign = (r) => { setAssignFor(r); setAssignSel([]); setError(""); };
  const toggleSel = (uid) => { setAssignSel((s) => s.includes(uid) ? s.filter((x) => x !== uid) : [...s, uid]); };
  const doAssign = async () => {
    try {
      await axios.put(`${API_URL}/api/sih/blood-bank/requests/${assignFor.request_id}/assign`, { unit_ids: assignSel }, getAuthConfig());
      setAssignFor(null); setAssignSel([]); load();
    } catch (e) { setError(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Erreur"); }
  };
  const abo = { O: ["O"], A: ["O", "A"], B: ["O", "B"], AB: ["O", "A", "B", "AB"] };
  const isCompat = (u, r) => {
    if (!abo[r.blood_group] || !abo[r.blood_group].includes(u.blood_group)) return false;
    if (r.rhesus === "-" && u.rhesus === "+") return false;
    return true;
  };
  const iS = { width: "100%", padding: "9px 12px", border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 14, boxSizing: "border-box", background: "#fff" };
  const lab = { fontSize: 12, color: "#64748B", marginBottom: 4, display: "block" };
  const compLabel = { sang_total: "Sang total", CGR: "CGR", plasma: "Plasma", plaquettes: "Plaquettes" };
  const statusColor = { disponible: ["#EAF3DE", "#3B6D11"], reservee: ["#FEF3C7", "#854F0B"], transfusee: ["#E0E7FF", "#3730A3"], perimee: ["#FCEBEB", "#A32D2D"], detruite: ["#F1F5F9", "#64748B"] };
  const urgColor = { programmee: ["#F1F5F9", "#475569"], urgente: ["#FEF3C7", "#854F0B"], vitale: ["#FCEBEB", "#A32D2D"] };
  const reqStatusColor = { en_attente: ["#FEF3C7", "#854F0B"], servie: ["#EAF3DE", "#3B6D11"], annulee: ["#F1F5F9", "#64748B"] };
  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: "#0F172A", margin: 0 }}>Banque de sang</h1>
        <div style={{ display: "flex", gap: 10 }}>
          {view === "stock" && can("blood:manage") && <button onClick={() => { setUForm(emptyUnit); setError(""); setShowUnitForm(true); }} style={{ padding: "9px 18px", background: "#185FA5", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, cursor: "pointer" }}>+ Entree de poche</button>}
          {view === "requests" && can("blood:request") && <button onClick={() => { setRForm(emptyReq); setError(""); setShowReqForm(true); }} style={{ padding: "9px 18px", background: "#185FA5", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, cursor: "pointer" }}>+ Demande de transfusion</button>}
        </div>
      </div>
      <div style={{ display: "flex", gap: 8, marginTop: 16, borderBottom: "1px solid #E2E8F0" }}>
        <button onClick={() => setView("stock")} style={{ padding: "9px 16px", border: "none", background: "none", borderBottom: view === "stock" ? "2px solid #185FA5" : "2px solid transparent", color: view === "stock" ? "#185FA5" : "#64748B", fontWeight: view === "stock" ? 600 : 400, fontSize: 14, cursor: "pointer" }}>Stock des poches</button>
        <button onClick={() => setView("requests")} style={{ padding: "9px 16px", border: "none", background: "none", borderBottom: view === "requests" ? "2px solid #185FA5" : "2px solid transparent", color: view === "requests" ? "#185FA5" : "#64748B", fontWeight: view === "requests" ? 600 : 400, fontSize: 14, cursor: "pointer" }}>Demandes de transfusion</button>
      </div>
      {error && !showUnitForm && !showReqForm && !assignFor && <div style={{ background: "#FCEBEB", border: "1px solid #F7C1C1", color: "#A32D2D", borderRadius: 8, padding: "10px 14px", marginTop: 14, fontSize: 13 }}>{error}</div>}
      {view === "stock" && (
        <div>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 16 }}>
            {Object.keys(summary).length === 0 && <div style={{ fontSize: 13, color: "#94A3B8" }}>Aucune poche disponible en stock.</div>}
            {Object.entries(summary).map(([grp, comps]) => (
              <div key={grp} style={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 10, padding: "12px 16px", minWidth: 120 }}>
                <div style={{ fontSize: 20, fontWeight: 700, color: "#A32D2D" }}>{grp}</div>
                {Object.entries(comps).map(([c, n]) => <div key={c} style={{ fontSize: 12, color: "#475569" }}>{compLabel[c] || c} : <b>{n}</b></div>)}
              </div>
            ))}
          </div>
          <div style={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 12, marginTop: 16, overflow: "hidden" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr style={{ background: "#F8FAFC", textAlign: "left" }}>
                <th style={{ padding: "10px 14px", fontSize: 12, color: "#64748B", fontWeight: 600 }}>Groupe</th>
                <th style={{ padding: "10px 14px", fontSize: 12, color: "#64748B", fontWeight: 600 }}>Composant</th>
                <th style={{ padding: "10px 14px", fontSize: 12, color: "#64748B", fontWeight: 600 }}>Volume</th>
                <th style={{ padding: "10px 14px", fontSize: 12, color: "#64748B", fontWeight: 600 }}>Peremption</th>
                <th style={{ padding: "10px 14px", fontSize: 12, color: "#64748B", fontWeight: 600 }}>Statut</th>
                <th style={{ padding: "10px 14px", fontSize: 12, color: "#64748B", fontWeight: 600 }}>Actions</th>
              </tr></thead>
              <tbody>
                {units.length === 0 && <tr><td colSpan={6} style={{ padding: "16px 14px", fontSize: 13, color: "#94A3B8", textAlign: "center" }}>Aucune poche enregistree.</td></tr>}
                {units.map((u) => { const sc = statusColor[u.status] || ["#F1F5F9", "#64748B"]; return (
                  <tr key={u.unit_id} style={{ borderTop: "1px solid #F1F5F9" }}>
                    <td style={{ padding: "12px 14px", fontSize: 15, fontWeight: 700, color: "#A32D2D" }}>{u.blood_group}{u.rhesus}</td>
                    <td style={{ padding: "12px 14px", fontSize: 13, color: "#475569" }}>{compLabel[u.component] || u.component}</td>
                    <td style={{ padding: "12px 14px", fontSize: 13, color: "#475569" }}>{u.volume_ml ? u.volume_ml + " ml" : "—"}</td>
                    <td style={{ padding: "12px 14px", fontSize: 13, color: "#475569" }}>{u.expires_at ? new Date(u.expires_at).toLocaleDateString("fr-FR") : "—"}</td>
                    <td style={{ padding: "12px 14px" }}><span style={{ fontSize: 12, padding: "3px 10px", borderRadius: 12, background: sc[0], color: sc[1] }}>{u.status}</span></td>
                    <td style={{ padding: "12px 14px" }}><span style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                      {can("blood:manage") && u.status === "reservee" && <button onClick={() => setUnitStatus(u, "transfusee")} style={{ fontSize: 13, color: "#3730A3", background: "none", border: "none", cursor: "pointer" }}>Transfusee</button>}
                      {can("blood:manage") && u.status === "disponible" && <button onClick={() => setUnitStatus(u, "detruite")} style={{ fontSize: 13, color: "#64748B", background: "none", border: "none", cursor: "pointer" }}>Detruire</button>}
                      {can("blood:manage") && <button onClick={() => removeUnit(u)} style={{ fontSize: 13, color: "#A32D2D", background: "none", border: "none", cursor: "pointer" }}>Suppr.</button>}
                    </span></td>
                  </tr>); })}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {view === "requests" && (
        <div style={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 12, marginTop: 16, overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr style={{ background: "#F8FAFC", textAlign: "left" }}>
              <th style={{ padding: "10px 14px", fontSize: 12, color: "#64748B", fontWeight: 600 }}>Patient</th>
              <th style={{ padding: "10px 14px", fontSize: 12, color: "#64748B", fontWeight: 600 }}>Groupe</th>
              <th style={{ padding: "10px 14px", fontSize: 12, color: "#64748B", fontWeight: 600 }}>Composant</th>
              <th style={{ padding: "10px 14px", fontSize: 12, color: "#64748B", fontWeight: 600 }}>Nb</th>
              <th style={{ padding: "10px 14px", fontSize: 12, color: "#64748B", fontWeight: 600 }}>Urgence</th>
              <th style={{ padding: "10px 14px", fontSize: 12, color: "#64748B", fontWeight: 600 }}>Statut</th>
              <th style={{ padding: "10px 14px", fontSize: 12, color: "#64748B", fontWeight: 600 }}>Actions</th>
            </tr></thead>
            <tbody>
              {requests.length === 0 && <tr><td colSpan={7} style={{ padding: "16px 14px", fontSize: 13, color: "#94A3B8", textAlign: "center" }}>Aucune demande de transfusion.</td></tr>}
              {requests.map((r) => { const uc = urgColor[r.urgency] || ["#F1F5F9", "#475569"]; const rc = reqStatusColor[r.status] || ["#F1F5F9", "#64748B"]; return (
                <tr key={r.request_id} style={{ borderTop: "1px solid #F1F5F9" }}>
                  <td style={{ padding: "12px 14px", fontSize: 14, fontWeight: 500 }}>{r.patient_name || "—"}</td>
                  <td style={{ padding: "12px 14px", fontSize: 15, fontWeight: 700, color: "#A32D2D" }}>{r.blood_group}{r.rhesus}</td>
                  <td style={{ padding: "12px 14px", fontSize: 13, color: "#475569" }}>{compLabel[r.component] || r.component}</td>
                  <td style={{ padding: "12px 14px", fontSize: 13, color: "#475569" }}>{r.units_requested}</td>
                  <td style={{ padding: "12px 14px" }}><span style={{ fontSize: 12, padding: "3px 10px", borderRadius: 12, background: uc[0], color: uc[1] }}>{r.urgency}</span></td>
                  <td style={{ padding: "12px 14px" }}><span style={{ fontSize: 12, padding: "3px 10px", borderRadius: 12, background: rc[0], color: rc[1] }}>{r.status}{r.assigned_units && r.assigned_units.length ? ` (${r.assigned_units.length})` : ""}</span></td>
                  <td style={{ padding: "12px 14px" }}><span style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                    {can("blood:manage") && r.status === "en_attente" && <button onClick={() => openAssign(r)} style={{ fontSize: 13, color: "#185FA5", background: "none", border: "none", cursor: "pointer", fontWeight: 600 }}>Attribuer</button>}
                    {can("blood:request") && r.status !== "annulee" && <button onClick={() => cancelReq(r)} style={{ fontSize: 13, color: "#A32D2D", background: "none", border: "none", cursor: "pointer" }}>Annuler</button>}
                  </span></td>
                </tr>); })}
            </tbody>
          </table>
        </div>
      )}
      {showUnitForm && (
        <div onClick={() => setShowUnitForm(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "flex-start", justifyContent: "center", zIndex: 1000, overflowY: "auto", paddingBottom: 40 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 12, width: "92%", maxWidth: 560, padding: 24, marginTop: 20 }}>
            <h3 style={{ fontSize: 17, fontWeight: 700, color: "#0F172A", margin: "0 0 16px" }}>Entree d'une poche de sang</h3>
            {error && <div style={{ background: "#FCEBEB", border: "1px solid #F7C1C1", color: "#A32D2D", borderRadius: 8, padding: "10px 14px", marginBottom: 14, fontSize: 13 }}>{error}</div>}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div><label style={lab}>Groupe sanguin</label><select value={uForm.blood_group} onChange={(e) => setUForm({ ...uForm, blood_group: e.target.value })} style={iS}><option value="O">O</option><option value="A">A</option><option value="B">B</option><option value="AB">AB</option></select></div>
              <div><label style={lab}>Rhesus</label><select value={uForm.rhesus} onChange={(e) => setUForm({ ...uForm, rhesus: e.target.value })} style={iS}><option value="+">+</option><option value="-">-</option></select></div>
              <div><label style={lab}>Composant</label><select value={uForm.component} onChange={(e) => setUForm({ ...uForm, component: e.target.value })} style={iS}><option value="CGR">CGR</option><option value="sang_total">Sang total</option><option value="plasma">Plasma</option><option value="plaquettes">Plaquettes</option></select></div>
              <div><label style={lab}>Volume (ml)</label><input type="number" value={uForm.volume_ml} onChange={(e) => setUForm({ ...uForm, volume_ml: e.target.value })} style={iS} /></div>
              <div><label style={lab}>Date de prelevement</label><input type="date" value={uForm.collected_at} onChange={(e) => setUForm({ ...uForm, collected_at: e.target.value })} style={iS} /></div>
              <div><label style={lab}>Date de peremption</label><input type="date" value={uForm.expires_at} onChange={(e) => setUForm({ ...uForm, expires_at: e.target.value })} style={iS} /></div>
            </div>
            <div style={{ marginTop: 12 }}><label style={lab}>Reference donneur (optionnel)</label><input value={uForm.donor_ref} onChange={(e) => setUForm({ ...uForm, donor_ref: e.target.value })} style={iS} /></div>
            <div style={{ marginTop: 12 }}><label style={lab}>Note</label><input value={uForm.note} onChange={(e) => setUForm({ ...uForm, note: e.target.value })} style={iS} /></div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 20 }}>
              <button onClick={() => setShowUnitForm(false)} style={{ padding: "9px 18px", background: "#fff", color: "#475569", border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 14, cursor: "pointer" }}>Annuler</button>
              <button onClick={saveUnit} style={{ padding: "9px 18px", background: "#185FA5", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, cursor: "pointer", fontWeight: 600 }}>Enregistrer</button>
            </div>
          </div>
        </div>
      )}
      {showReqForm && (
        <div onClick={() => setShowReqForm(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "flex-start", justifyContent: "center", zIndex: 1000, overflowY: "auto", paddingBottom: 40 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 12, width: "92%", maxWidth: 560, padding: 24, marginTop: 20 }}>
            <h3 style={{ fontSize: 17, fontWeight: 700, color: "#0F172A", margin: "0 0 16px" }}>Demande de transfusion</h3>
            {error && <div style={{ background: "#FCEBEB", border: "1px solid #F7C1C1", color: "#A32D2D", borderRadius: 8, padding: "10px 14px", marginBottom: 14, fontSize: 13 }}>{error}</div>}
            <div style={{ marginBottom: 12 }}><label style={lab}>Patient</label><select value={rForm.patient_id} onChange={(e) => setRForm({ ...rForm, patient_id: e.target.value })} style={iS}><option value="">— Selectionner —</option>{patients.map((p) => <option key={p.patient_id} value={p.patient_id}>{p.first_name} {p.last_name}</option>)}</select></div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div><label style={lab}>Groupe sanguin</label><select value={rForm.blood_group} onChange={(e) => setRForm({ ...rForm, blood_group: e.target.value })} style={iS}><option value="O">O</option><option value="A">A</option><option value="B">B</option><option value="AB">AB</option></select></div>
              <div><label style={lab}>Rhesus</label><select value={rForm.rhesus} onChange={(e) => setRForm({ ...rForm, rhesus: e.target.value })} style={iS}><option value="+">+</option><option value="-">-</option></select></div>
              <div><label style={lab}>Composant</label><select value={rForm.component} onChange={(e) => setRForm({ ...rForm, component: e.target.value })} style={iS}><option value="CGR">CGR</option><option value="sang_total">Sang total</option><option value="plasma">Plasma</option><option value="plaquettes">Plaquettes</option></select></div>
              <div><label style={lab}>Nombre de poches</label><input type="number" min="1" value={rForm.units_requested} onChange={(e) => setRForm({ ...rForm, units_requested: e.target.value })} style={iS} /></div>
            </div>
            <div style={{ marginTop: 12 }}><label style={lab}>Urgence</label><select value={rForm.urgency} onChange={(e) => setRForm({ ...rForm, urgency: e.target.value })} style={iS}><option value="programmee">Programmee</option><option value="urgente">Urgente</option><option value="vitale">Vitale</option></select></div>
            <div style={{ marginTop: 12 }}><label style={lab}>Indication</label><input value={rForm.indication} onChange={(e) => setRForm({ ...rForm, indication: e.target.value })} style={iS} /></div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 20 }}>
              <button onClick={() => setShowReqForm(false)} style={{ padding: "9px 18px", background: "#fff", color: "#475569", border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 14, cursor: "pointer" }}>Annuler</button>
              <button onClick={saveReq} style={{ padding: "9px 18px", background: "#185FA5", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, cursor: "pointer", fontWeight: 600 }}>Creer la demande</button>
            </div>
          </div>
        </div>
      )}
      {assignFor && (
        <div onClick={() => setAssignFor(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "flex-start", justifyContent: "center", zIndex: 1000, overflowY: "auto", paddingBottom: 40 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 12, width: "92%", maxWidth: 600, padding: 24, marginTop: 20 }}>
            <h3 style={{ fontSize: 17, fontWeight: 700, color: "#0F172A", margin: "0 0 4px" }}>Attribuer des poches</h3>
            <div style={{ fontSize: 13, color: "#64748B", marginBottom: 16 }}>{assignFor.patient_name} — receveur <b style={{ color: "#A32D2D" }}>{assignFor.blood_group}{assignFor.rhesus}</b> — {assignFor.units_requested} poche(s) demandee(s)</div>
            {error && <div style={{ background: "#FCEBEB", border: "1px solid #F7C1C1", color: "#A32D2D", borderRadius: 8, padding: "10px 14px", marginBottom: 14, fontSize: 13 }}>{error}</div>}
            <div style={{ maxHeight: 340, overflowY: "auto", border: "1px solid #E2E8F0", borderRadius: 8 }}>
              {units.filter((u) => u.status === "disponible" && u.component === assignFor.component).length === 0 && <div style={{ padding: 16, fontSize: 13, color: "#94A3B8", textAlign: "center" }}>Aucune poche disponible pour ce composant.</div>}
              {units.filter((u) => u.status === "disponible" && u.component === assignFor.component).map((u) => {
                const compat = isCompat(u, assignFor);
                const sel = assignSel.includes(u.unit_id);
                return (
                  <div key={u.unit_id} onClick={() => compat && toggleSel(u.unit_id)} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 14px", borderBottom: "1px solid #F1F5F9", cursor: compat ? "pointer" : "not-allowed", background: sel ? "#E6F1FB" : "transparent", opacity: compat ? 1 : 0.5 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <input type="checkbox" checked={sel} disabled={!compat} readOnly />
                      <span style={{ fontSize: 15, fontWeight: 700, color: "#A32D2D" }}>{u.blood_group}{u.rhesus}</span>
                      <span style={{ fontSize: 12, color: "#475569" }}>{compLabel[u.component] || u.component}{u.expires_at ? " · exp. " + new Date(u.expires_at).toLocaleDateString("fr-FR") : ""}</span>
                    </div>
                    {!compat && <span style={{ fontSize: 11, color: "#A32D2D", fontWeight: 600 }}>Incompatible</span>}
                    {compat && <span style={{ fontSize: 11, color: "#3B6D11", fontWeight: 600 }}>Compatible</span>}
                  </div>
                );
              })}
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 16 }}>
              <span style={{ fontSize: 13, color: "#64748B" }}>{assignSel.length} poche(s) selectionnee(s)</span>
              <div style={{ display: "flex", gap: 10 }}>
                <button onClick={() => setAssignFor(null)} style={{ padding: "9px 18px", background: "#fff", color: "#475569", border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 14, cursor: "pointer" }}>Annuler</button>
                <button onClick={doAssign} disabled={assignSel.length === 0} style={{ padding: "9px 18px", background: assignSel.length ? "#185FA5" : "#CBD5E1", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, cursor: assignSel.length ? "pointer" : "not-allowed", fontWeight: 600 }}>Confirmer l'attribution</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
function BlocModule({ can }) {
  const [sub, setSub] = useState("program");
  const canManage = can("or:manage");
  return (
    <div>
      <h1 style={{ fontSize: 22, fontWeight: 700, color: "#0F172A", margin: "0 0 16px" }}>Bloc operatoire</h1>
      <div style={{ display: "flex", gap: 8, marginBottom: 18 }}>
        {[["program", "Programme operatoire"], ["rooms", "Salles"]].map(([id, label]) => (
          <button key={id} onClick={() => setSub(id)} style={{ fontSize: 14, padding: "7px 16px", borderRadius: 8, cursor: "pointer", border: sub === id ? "1px solid #185FA5" : "1px solid #CBD5E1", background: sub === id ? "#185FA5" : "#fff", color: sub === id ? "#fff" : "#475569" }}>{label}</button>
        ))}
      </div>
      {sub === "program" ? <ORProgram can={can} canManage={canManage} /> : <ORRooms canManage={canManage} />}
    </div>
  );
}

function ORRooms({ canManage }) {
  const [rooms, setRooms] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ name: "", note: "" });
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const load = () => { setLoading(true); axios.get(`${API_URL}/api/sih/or/rooms`, getAuthConfig()).then((r) => setRooms(r.data.rooms || [])).catch(() => {}).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);
  const save = async () => {
    if (!form.name.trim()) { setError("Nom requis"); return; }
    setError("");
    if (saving) return;
    setSaving(true);
    try { if (editing) await axios.put(`${API_URL}/api/sih/or/rooms/${editing}`, form, getAuthConfig()); else await axios.post(`${API_URL}/api/sih/or/rooms`, form, getAuthConfig()); setShowForm(false); setForm({ name: "", note: "" }); setEditing(null); load(); }
    catch (e) { setError(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Erreur"); }
    finally { setSaving(false); }
  };
  const doDelete = async () => { if (!toDelete) return; try { await axios.delete(`${API_URL}/api/sih/or/rooms/${toDelete.room_id}`, getAuthConfig()); setToDelete(null); load(); } catch (e) { setError(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Erreur"); setToDelete(null); } };
  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h2 style={{ fontSize: 17, fontWeight: 700, color: "#0F172A", margin: 0 }}>Salles d'operation</h2>
        {canManage && <button onClick={() => { setForm({ name: "", note: "" }); setEditing(null); setError(""); setShowForm(true); }} style={{ padding: "9px 18px", background: "#185FA5", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, cursor: "pointer" }}>+ Nouvelle salle</button>}
      </div>
      {error && <div style={{ background: "#FCEBEB", border: "1px solid #F7C1C1", color: "#A32D2D", borderRadius: 8, padding: "10px 14px", marginTop: 14, fontSize: 13 }}>{error}</div>}
      {showForm && (
        <div style={{ background: "#fff", border: "1px solid #B5D4F4", borderRadius: 12, padding: 20, marginTop: 16 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: 12 }}>
            <div><label style={oLab}>Nom *</label><input style={oIS} placeholder="Salle 1" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div><label style={oLab}>Note</label><input style={oIS} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} /></div>
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
            <button onClick={save} style={{ padding: "8px 18px", background: "#185FA5", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, cursor: "pointer" }}>Enregistrer</button>
            <button onClick={() => { setShowForm(false); setEditing(null); }} style={{ padding: "8px 18px", background: "transparent", border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 14, cursor: "pointer" }}>Annuler</button>
          </div>
        </div>
      )}
      <div style={{ marginTop: 18, background: "#fff", border: "1px solid #E2E8F0", borderRadius: 12, overflow: "hidden" }}>
        {loading ? <div style={{ padding: 30, textAlign: "center", color: "#94A3B8" }}>Chargement...</div>
        : rooms.length === 0 ? <div style={{ padding: 30, textAlign: "center", color: "#94A3B8", fontSize: 14 }}>Aucune salle. Creez-en une pour planifier des interventions.</div>
        : (
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr style={{ background: "#F8FAFC", textAlign: "left" }}>{["Salle", "Note", "Actions"].map((h) => <th key={h} style={{ padding: "10px 16px", fontSize: 12, color: "#64748B", fontWeight: 600 }}>{h}</th>)}</tr></thead>
            <tbody>{rooms.map((r) => (
              <tr key={r.room_id} style={{ borderTop: "1px solid #F1F5F9" }}>
                <td style={{ padding: "12px 16px", fontSize: 14, fontWeight: 500 }}>{r.name}</td>
                <td style={{ padding: "12px 16px", fontSize: 13, color: "#475569" }}>{r.note || "—"}</td>
                <td style={{ padding: "12px 16px" }}>{canManage && <span style={{ display: "flex", gap: 12 }}>
                  <button onClick={() => { setEditing(r.room_id); setForm({ name: r.name, note: r.note || "" }); setError(""); setShowForm(true); }} style={{ fontSize: 13, color: "#185FA5", background: "none", border: "none", cursor: "pointer" }}>Modifier</button>
                  <button onClick={() => setToDelete(r)} style={{ fontSize: 13, color: "#A32D2D", background: "none", border: "none", cursor: "pointer" }}>Supprimer</button>
                </span>}</td>
              </tr>))}</tbody>
          </table>
        )}
      </div>
      {toDelete && (
        <div onClick={() => setToDelete(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 12, width: "90%", maxWidth: 400, padding: 24 }}>
            <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>Supprimer la salle ?</div>
            <div style={{ fontSize: 13, color: "#64748B", marginBottom: 20 }}><b>{toDelete.name}</b></div>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button onClick={() => setToDelete(null)} style={{ fontSize: 13, padding: "8px 16px", borderRadius: 8, border: "1px solid #CBD5E1", background: "#fff", color: "#64748B", cursor: "pointer" }}>Annuler</button>
              <button onClick={doDelete} style={{ fontSize: 13, padding: "8px 16px", borderRadius: 8, border: "none", background: "#A32D2D", color: "#fff", cursor: "pointer" }}>Supprimer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ORProgram({ can, canManage }) {
  const canCheck = can("or:checklist");
  const [saving, setSaving] = useState(false);
  const [ops, setOps] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [services, setServices] = useState([]);
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [mode, setMode] = useState("new");
  const [checkFor, setCheckFor] = useState(null);
  const empty = { patient_id: "", first_name: "", last_name: "", procedure: "", room_id: "", start_at: "", duration_min: 60, surgeon: "", anesthetist: "", assistant: "", service_id: "", note: "" };
  const [form, setForm] = useState(empty);

  const load = () => {
    setLoading(true);
    const calls = [axios.get(`${API_URL}/api/sih/or/operations`, getAuthConfig()), axios.get(`${API_URL}/api/sih/or/rooms`, getAuthConfig()), axios.get(`${API_URL}/api/sih/services`, getAuthConfig())];
    if (can("patients:read")) calls.push(axios.get(`${API_URL}/api/sih/patients`, getAuthConfig()));
    Promise.all(calls).then((res) => { setOps(res[0].data.operations || []); setRooms(res[1].data.rooms || []); setServices(res[2].data.services || []); if (res[3]) setPatients(res[3].data.patients || []); })
      .catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const save = async () => {
    if (!form.procedure.trim()) { setError("Type d'intervention requis"); return; }
    if (!form.room_id) { setError("Salle requise"); return; }
    if (!form.start_at) { setError("Date/heure requise"); return; }
    if (mode === "existing" && !form.patient_id) { setError("Selectionnez un patient"); return; }
    setError("");
    if (saving) return;
    setSaving(true);
    const payload = { procedure: form.procedure, room_id: form.room_id, start_at: form.start_at, duration_min: Number(form.duration_min) || 60, surgeon: form.surgeon || null, anesthetist: form.anesthetist || null, assistant: form.assistant || null, service_id: form.service_id || null, note: form.note || null };
    if (mode === "existing") payload.patient_id = form.patient_id;
    else if (form.last_name.trim()) { payload.first_name = form.first_name; payload.last_name = form.last_name; }
    try { await axios.post(`${API_URL}/api/sih/or/operations`, payload, getAuthConfig()); setShowForm(false); setForm(empty); load(); }
    catch (e) { setError(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Erreur"); }
    finally { setSaving(false); }
  };
  // Correctif L-DOUBLECLICK : garde anti double-clic sur les actions de
  // salle d'operation (changement de statut, suppression, enregistrement du
  // compte-rendu) — sinon un double-clic peut envoyer la meme transition ou
  // le meme enregistrement deux fois.
  const [busyOpIds, setBusyOpIds] = useState(() => new Set());
  const [savingCr, setSavingCr] = useState(false);
  const setStatus = async (op, status) => {
    if (busyOpIds.has(op.operation_id)) return;
    setBusyOpIds((prev) => new Set(prev).add(op.operation_id));
    try { await axios.put(`${API_URL}/api/sih/or/operations/${op.operation_id}/status`, { status }, getAuthConfig()); load(); }
    catch (e) { setError(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Erreur"); }
    finally { setBusyOpIds((prev) => { const next = new Set(prev); next.delete(op.operation_id); return next; }); }
  };
  const remove = async (op) => {
    if (busyOpIds.has(op.operation_id)) return;
    setBusyOpIds((prev) => new Set(prev).add(op.operation_id));
    try { await axios.delete(`${API_URL}/api/sih/or/operations/${op.operation_id}`, getAuthConfig()); load(); }
    catch (e) { setError("Erreur"); }
    finally { setBusyOpIds((prev) => { const next = new Set(prev); next.delete(op.operation_id); return next; }); }
  };
  const [crOp, setCrOp] = useState(null);
  const [crForm, setCrForm] = useState({});
  const saveCrOp = async () => {
    if (savingCr) return;
    setSavingCr(true);
    try { await axios.put(`${API_URL}/api/sih/or/operations/${crOp.operation_id}`, crForm, getAuthConfig()); setCrOp(null); setCrForm({}); load(); }
    catch (e) { setError("Erreur enregistrement CR"); }
    finally { setSavingCr(false); }
  };
  const crLbl = { display: "block", fontSize: 13, fontWeight: 600, color: "#334155", marginBottom: 5 };
  const crInp = { width: "100%", padding: "9px 11px", border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 14, fontFamily: "inherit", boxSizing: "border-box", resize: "vertical" };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h2 style={{ fontSize: 17, fontWeight: 700, color: "#0F172A", margin: 0 }}>Programme operatoire</h2>
        {canManage && <button onClick={() => { setForm(empty); setMode("new"); setError(""); setShowForm(true); }} style={{ padding: "9px 18px", background: "#185FA5", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, cursor: "pointer" }}>+ Planifier une intervention</button>}
      </div>
      {error && !showForm && <div style={{ background: "#FCEBEB", border: "1px solid #F7C1C1", color: "#A32D2D", borderRadius: 8, padding: "10px 14px", marginTop: 14, fontSize: 13 }}>{error}</div>}
      {canManage && rooms.length === 0 && <div style={{ background: "#FAEEDA", border: "1px solid #F0D9A8", color: "#854F0B", borderRadius: 8, padding: "10px 14px", marginTop: 14, fontSize: 13 }}>Creez d'abord au moins une salle (onglet Salles).</div>}

      {showForm && (
        <div style={{ background: "#fff", border: "1px solid #B5D4F4", borderRadius: 12, padding: 20, marginTop: 16 }}>
          <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 14 }}>Nouvelle intervention</div>
          {error && <div style={{ background: "#FCEBEB", border: "1px solid #F7C1C1", color: "#A32D2D", borderRadius: 8, padding: "10px 14px", marginBottom: 12, fontSize: 13 }}>{error}</div>}
          <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
            <button type="button" onClick={() => setMode("new")} style={{ fontSize: 13, padding: "6px 14px", borderRadius: 8, cursor: "pointer", border: mode === "new" ? "1px solid #185FA5" : "1px solid #CBD5E1", background: mode === "new" ? "#E6F1FB" : "#fff", color: mode === "new" ? "#185FA5" : "#475569" }}>Nouveau patient</button>
            {can("patients:read") && <button type="button" onClick={() => setMode("existing")} style={{ fontSize: 13, padding: "6px 14px", borderRadius: 8, cursor: "pointer", border: mode === "existing" ? "1px solid #185FA5" : "1px solid #CBD5E1", background: mode === "existing" ? "#E6F1FB" : "#fff", color: mode === "existing" ? "#185FA5" : "#475569" }}>Patient existant</button>}
          </div>
          {mode === "existing" ? (
            <div style={{ marginBottom: 14 }}><label style={oLab}>Patient</label><select style={oIS} value={form.patient_id} onChange={(e) => setForm({ ...form, patient_id: e.target.value })}><option value="">...</option>{patients.map((p) => <option key={p.patient_id} value={p.patient_id}>{p.first_name} {p.last_name}</option>)}</select></div>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
              <div><label style={oLab}>Prenom</label><input style={oIS} value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} /></div>
              <div><label style={oLab}>Nom</label><input style={oIS} value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} /></div>
            </div>
          )}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
            <div style={{ gridColumn: "span 2" }}><label style={oLab}>Type d'intervention *</label><input style={oIS} placeholder="Appendicectomie..." value={form.procedure} onChange={(e) => setForm({ ...form, procedure: e.target.value })} /></div>
            <div><label style={oLab}>Salle *</label><select style={oIS} value={form.room_id} onChange={(e) => setForm({ ...form, room_id: e.target.value })}><option value="">...</option>{rooms.map((r) => <option key={r.room_id} value={r.room_id}>{r.name}</option>)}</select></div>
            <div><label style={oLab}>Date et heure *</label><input type="datetime-local" style={oIS} value={form.start_at} onChange={(e) => setForm({ ...form, start_at: e.target.value })} /></div>
            <div><label style={oLab}>Duree (min)</label><input type="number" style={oIS} value={form.duration_min} onChange={(e) => setForm({ ...form, duration_min: e.target.value })} /></div>
            <div><label style={oLab}>Service</label><select style={oIS} value={form.service_id} onChange={(e) => setForm({ ...form, service_id: e.target.value })}><option value="">—</option>{services.map((s) => <option key={s.service_id} value={s.service_id}>{s.name}</option>)}</select></div>
            <div><label style={oLab}>Chirurgien</label><input style={oIS} value={form.surgeon} onChange={(e) => setForm({ ...form, surgeon: e.target.value })} /></div>
            <div><label style={oLab}>Anesthesiste</label><input style={oIS} value={form.anesthetist} onChange={(e) => setForm({ ...form, anesthetist: e.target.value })} /></div>
            <div><label style={oLab}>Aide operatoire</label><input style={oIS} value={form.assistant} onChange={(e) => setForm({ ...form, assistant: e.target.value })} /></div>
          </div>
          <div style={{ marginTop: 12 }}><label style={oLab}>Note</label><input style={oIS} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} /></div>
          <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
            <button onClick={save} disabled={saving} style={{ padding: "8px 18px", background: saving ? "#94A3B8" : "#185FA5", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, cursor: saving ? "not-allowed" : "pointer" }}>{saving ? "Enregistrement..." : "Programmer"}</button>
            <button onClick={() => { setShowForm(false); setForm(empty); }} style={{ padding: "8px 18px", background: "transparent", border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 14, cursor: "pointer" }}>Annuler</button>
          </div>
        </div>
      )}

      <div style={{ marginTop: 18, background: "#fff", border: "1px solid #E2E8F0", borderRadius: 12, overflow: "auto" }}>
        {loading ? <div style={{ padding: 30, textAlign: "center", color: "#94A3B8" }}>Chargement...</div>
        : ops.length === 0 ? <div style={{ padding: 30, textAlign: "center", color: "#94A3B8", fontSize: 14 }}>Aucune intervention programmee.</div>
        : (
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 940 }}>
            <thead><tr style={{ background: "#F8FAFC", textAlign: "left" }}>{["Date / heure", "Salle", "Patient", "Intervention", "Chirurgien", "Statut", "Check-list", "Actions"].map((h) => <th key={h} style={{ padding: "10px 14px", fontSize: 12, color: "#64748B", fontWeight: 600 }}>{h}</th>)}</tr></thead>
            <tbody>
              {ops.map((o) => { const st = OR_STATUS[o.status] || OR_STATUS.scheduled; const pr = orProgress(o); const full = pr.done === pr.total; return (
                <tr key={o.operation_id} style={{ borderTop: "1px solid #F1F5F9" }}>
                  <td style={{ padding: "12px 14px", fontSize: 13, color: "#475569" }}>{fmtDT(o.start_at)}<div style={{ fontSize: 11, color: "#94A3B8" }}>{o.duration_min} min</div></td>
                  <td style={{ padding: "12px 14px", fontSize: 13, color: "#475569" }}>{o.room_name || "—"}</td>
                  <td style={{ padding: "12px 14px", fontSize: 14, fontWeight: 500 }}>{o.patient_name || "—"}</td>
                  <td style={{ padding: "12px 14px", fontSize: 13, color: "#475569" }}>{o.procedure}</td>
                  <td style={{ padding: "12px 14px", fontSize: 13, color: "#475569" }}>{o.surgeon || "—"}</td>
                  <td style={{ padding: "12px 14px" }}><span style={{ fontSize: 12, padding: "3px 10px", borderRadius: 12, background: st[1], color: st[2] }}>{st[0]}</span></td>
                  <td style={{ padding: "12px 14px" }}><button onClick={() => setCheckFor(o)} style={{ fontSize: 12, padding: "3px 10px", borderRadius: 12, border: "none", cursor: "pointer", background: full ? "#EAF3DE" : "#F1F5F9", color: full ? "#3B6D11" : "#475569" }}>{pr.done}/{pr.total}{full ? " ✓" : ""}</button></td>
                  <td style={{ padding: "12px 14px" }}><span style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                    {canManage && o.status === "scheduled" && <button onClick={() => setStatus(o, "in_progress")} disabled={busyOpIds.has(o.operation_id)} style={{ fontSize: 13, color: "#854F0B", background: "none", border: "none", cursor: busyOpIds.has(o.operation_id) ? "default" : "pointer", opacity: busyOpIds.has(o.operation_id) ? 0.5 : 1 }}>Demarrer</button>}
                    {canManage && o.status === "in_progress" && <button onClick={() => setStatus(o, "done")} disabled={busyOpIds.has(o.operation_id)} style={{ fontSize: 13, color: "#3B6D11", background: "none", border: "none", cursor: busyOpIds.has(o.operation_id) ? "default" : "pointer", opacity: busyOpIds.has(o.operation_id) ? 0.5 : 1 }}>Terminer</button>}
                    {canManage && (o.status === "scheduled" || o.status === "in_progress") && <button onClick={() => setStatus(o, "cancelled")} disabled={busyOpIds.has(o.operation_id)} style={{ fontSize: 13, color: "#64748B", background: "none", border: "none", cursor: busyOpIds.has(o.operation_id) ? "default" : "pointer", opacity: busyOpIds.has(o.operation_id) ? 0.5 : 1 }}>Annuler</button>}
                    {canManage && (o.status === "in_progress" || o.status === "done") && <button onClick={() => { setCrOp(o); setCrForm({ indication: o.indication || "", preop_diagnosis: o.preop_diagnosis || "", postop_diagnosis: o.postop_diagnosis || "", operative_report: o.operative_report || "", complications: o.complications || "", postop_course: o.postop_course || "", asa_score: o.asa_score || "", anesthesia_type: o.anesthesia_type || "", pathology: o.pathology || "", assistant: o.assistant || "", anesthetist: o.anesthetist || "" }); }} style={{ fontSize: 13, color: "#185FA5", background: "none", border: "none", cursor: "pointer", fontWeight: 600 }}>CR opératoire</button>}
                    {canManage && o.status === "done" && <button onClick={() => printOperativeReport(o)} style={{ fontSize: 13, color: "#3B6D11", background: "none", border: "none", cursor: "pointer" }}>Imprimer</button>}
                    {canManage && <button onClick={() => remove(o)} disabled={busyOpIds.has(o.operation_id)} style={{ fontSize: 13, color: "#A32D2D", background: "none", border: "none", cursor: busyOpIds.has(o.operation_id) ? "default" : "pointer", opacity: busyOpIds.has(o.operation_id) ? 0.5 : 1 }}>Suppr.</button>}
                  </span></td>
                </tr>); })}
            </tbody>
          </table>
        )}
      </div>

      {crOp && <div onClick={() => setCrOp(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "flex-start", justifyContent: "center", zIndex: 1000, overflowY: "auto", paddingBottom: 40 }}>
        <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 12, width: "92%", maxWidth: 680, padding: 24, marginTop: 16 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
            <h3 style={{ fontSize: 17, fontWeight: 700, color: "#0F172A", margin: 0 }}>Compte-rendu opératoire</h3>
            <button onClick={() => setCrOp(null)} style={{ fontSize: 22, color: "#94A3B8", background: "none", border: "none", cursor: "pointer", lineHeight: 1 }}>&times;</button>
          </div>
          <div style={{ fontSize: 13, color: "#64748B", marginBottom: 16 }}>{crOp.patient_name} — {crOp.procedure}{crOp.surgeon ? " — " + crOp.surgeon : ""}</div>
          {error && <div style={{ background: "#FCEBEB", border: "1px solid #F7C1C1", color: "#A32D2D", borderRadius: 8, padding: "10px 14px", marginBottom: 14, fontSize: 13 }}>{error}</div>}
          <div style={{ display: "grid", gap: 12 }}>
            <div><label style={crLbl}>Indication opératoire</label><textarea value={crForm.indication} onChange={(e) => setCrForm({ ...crForm, indication: e.target.value })} rows={2} style={crInp} /></div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div><label style={crLbl}>Diagnostic pré-opératoire</label><textarea value={crForm.preop_diagnosis} onChange={(e) => setCrForm({ ...crForm, preop_diagnosis: e.target.value })} rows={2} style={crInp} /></div>
              <div><label style={crLbl}>Diagnostic post-opératoire</label><textarea value={crForm.postop_diagnosis} onChange={(e) => setCrForm({ ...crForm, postop_diagnosis: e.target.value })} rows={2} style={crInp} /></div>
            </div>
            <div><label style={crLbl}>Compte-rendu du geste opératoire</label><textarea value={crForm.operative_report} onChange={(e) => setCrForm({ ...crForm, operative_report: e.target.value })} rows={6} style={crInp} placeholder="Voie d'abord, exploration, gestes réalisés, hémostase, fermeture..." /></div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div><label style={crLbl}>Aide opératoire</label><input value={crForm.assistant} onChange={(e) => setCrForm({ ...crForm, assistant: e.target.value })} style={crInp} /></div>
              <div><label style={crLbl}>Anesthésiste</label><input value={crForm.anesthetist} onChange={(e) => setCrForm({ ...crForm, anesthetist: e.target.value })} style={crInp} /></div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div><label style={crLbl}>Type d'anesthésie</label><input value={crForm.anesthesia_type} onChange={(e) => setCrForm({ ...crForm, anesthesia_type: e.target.value })} style={crInp} placeholder="AG, ALR, rachianesthésie..." /></div>
              <div><label style={crLbl}>Score ASA</label><select value={crForm.asa_score} onChange={(e) => setCrForm({ ...crForm, asa_score: e.target.value })} style={crInp}><option value="">—</option><option value="ASA I">ASA I</option><option value="ASA II">ASA II</option><option value="ASA III">ASA III</option><option value="ASA IV">ASA IV</option><option value="ASA V">ASA V</option></select></div>
            </div>
            <div><label style={crLbl}>Complications per-opératoires</label><textarea value={crForm.complications} onChange={(e) => setCrForm({ ...crForm, complications: e.target.value })} rows={2} style={crInp} placeholder="Aucune complication notable..." /></div>
            <div><label style={crLbl}>Suites opératoires immédiates</label><textarea value={crForm.postop_course} onChange={(e) => setCrForm({ ...crForm, postop_course: e.target.value })} rows={2} style={crInp} /></div>
            <div><label style={crLbl}>Anatomopathologie / prélèvements</label><textarea value={crForm.pathology} onChange={(e) => setCrForm({ ...crForm, pathology: e.target.value })} rows={2} style={crInp} placeholder="Pièce adressée en anatomopathologie..." /></div>
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 20 }}>
            <button onClick={() => setCrOp(null)} disabled={savingCr} style={{ padding: "9px 18px", background: "#fff", color: "#475569", border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 14, cursor: savingCr ? "default" : "pointer" }}>Annuler</button>
            <button onClick={saveCrOp} disabled={savingCr} style={{ padding: "9px 18px", background: "#185FA5", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, cursor: savingCr ? "default" : "pointer", fontWeight: 600, opacity: savingCr ? 0.7 : 1 }}>{savingCr ? "Enregistrement..." : "Enregistrer le CR"}</button>
          </div>
        </div>
      </div>}
      {checkFor && <ORChecklistModal op={checkFor} canCheck={canCheck} onClose={() => { setCheckFor(null); load(); }} />}
    </div>
  );
}

function ORChecklistModal({ op, canCheck, onClose }) {
  const [state, setState] = useState(op.checklist || {});
  const [err, setErr] = useState("");
  const toggle = async (phase, item, checked) => {
    if (!canCheck) return;
    setState((s) => ({ ...s, [phase]: { ...(s[phase] || {}), [item]: checked } }));
    try { await axios.put(`${API_URL}/api/sih/or/operations/${op.operation_id}/checklist`, { phase, item, checked }, getAuthConfig()); }
    catch (e) { setErr("Erreur d'enregistrement"); setState((s) => ({ ...s, [phase]: { ...(s[phase] || {}), [item]: !checked } })); }
  };
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: 24, overflow: "auto" }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 12, width: "90%", maxWidth: 560, padding: 24, marginTop: 20 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
          <div style={{ fontSize: 17, fontWeight: 700 }}>Check-list de securite</div>
          <button onClick={onClose} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#94A3B8" }}>×</button>
        </div>
        <div style={{ fontSize: 13, color: "#64748B", marginBottom: 14 }}>{op.patient_name} · {op.procedure} (HAS / OMS)</div>
        {err && <div style={{ background: "#FCEBEB", border: "1px solid #F7C1C1", color: "#A32D2D", borderRadius: 8, padding: "8px 12px", marginBottom: 12, fontSize: 12 }}>{err}</div>}
        {OR_CHECKLIST.map((ph) => (
          <div key={ph.phase} style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#185FA5", marginBottom: 8 }}>{ph.title}</div>
            {ph.items.map(([k, label]) => { const checked = !!(state[ph.phase] && state[ph.phase][k]); return (
              <label key={k} style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 0", fontSize: 14, color: "#334155", cursor: canCheck ? "pointer" : "default" }}>
                <input type="checkbox" checked={checked} disabled={!canCheck} onChange={(e) => toggle(ph.phase, k, e.target.checked)} style={{ width: 16, height: 16 }} />
                {label}
              </label>); })}
          </div>
        ))}
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <button onClick={onClose} style={{ fontSize: 13, padding: "8px 16px", borderRadius: 8, border: "none", background: "#185FA5", color: "#fff", cursor: "pointer" }}>Fermer</button>
        </div>
      </div>
    </div>
  );
}


const DOC_DAYS = [["monday", "Lundi"], ["tuesday", "Mardi"], ["wednesday", "Mercredi"], ["thursday", "Jeudi"], ["friday", "Vendredi"], ["saturday", "Samedi"], ["sunday", "Dimanche"]];
const DOC_DUR = [15, 20, 30, 45, 60];
function dToMin(s) { const [h, m] = s.split(":").map(Number); return h * 60 + m; }
function dMinToStr(t) { return `${String(Math.floor(t / 60)).padStart(2, "0")}:${String(t % 60).padStart(2, "0")}`; }
function genSlots(start, end, dur) { const out = []; let t = dToMin(start); const e = dToMin(end); while (t + dur <= e) { out.push(dMinToStr(t)); t += dur; } return out; }
function emptyEditor() { const ed = {}; DOC_DAYS.forEach(([k]) => { ed[k] = { open: ["saturday", "sunday"].indexOf(k) === -1, start: "09:00", end: "16:00", dur: 30 }; }); return ed; }
function scheduleToEditor(sch) { const ed = {}; DOC_DAYS.forEach(([k]) => { const arr = (sch && sch[k]) || []; if (arr.length) { const dur = arr.length >= 2 ? (dToMin(arr[1]) - dToMin(arr[0])) : 30; ed[k] = { open: true, start: arr[0], end: dMinToStr(dToMin(arr[arr.length - 1]) + dur), dur }; } else { ed[k] = { open: false, start: "09:00", end: "16:00", dur: 30 }; } }); return ed; }
function editorToSchedule(ed) { const sch = {}; DOC_DAYS.forEach(([k]) => { if (ed[k] && ed[k].open) { const slots = genSlots(ed[k].start, ed[k].end, ed[k].dur); if (slots.length) sch[k] = slots; } }); return sch; }
function scheduleSummary(sch) { const days = DOC_DAYS.filter(([k]) => sch && sch[k] && sch[k].length); if (!days.length) return null; return days.map(([k, l]) => l.slice(0, 3)).join(", "); }

const dIS = { width: "100%", padding: "9px 12px", border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 14, boxSizing: "border-box", background: "#fff" };
const dLab = { fontSize: 12, color: "#64748B", marginBottom: 4, display: "block" };

function ScheduleEditor({ value, onChange }) {
  const setDay = (k, patch) => onChange({ ...value, [k]: { ...value[k], ...patch } });
  return (
    <div style={{ border: "1px solid #E2E8F0", borderRadius: 10, overflow: "hidden" }}>
      {DOC_DAYS.map(([k, label]) => { const d = value[k]; const n = d.open ? genSlots(d.start, d.end, d.dur).length : 0; return (
        <div key={k} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", borderTop: "1px solid #F1F5F9", flexWrap: "wrap", background: d.open ? "#fff" : "#F8FAFC" }}>
          <label style={{ display: "flex", alignItems: "center", gap: 6, width: 110, fontSize: 13, cursor: "pointer" }}>
            <input type="checkbox" checked={d.open} onChange={(e) => setDay(k, { open: e.target.checked })} /> {label}
          </label>
          {d.open ? (
            <>
              <input type="time" value={d.start} onChange={(e) => setDay(k, { start: e.target.value })} style={{ ...dIS, width: 110 }} />
              <span style={{ color: "#94A3B8", fontSize: 13 }}>→</span>
              <input type="time" value={d.end} onChange={(e) => setDay(k, { end: e.target.value })} style={{ ...dIS, width: 110 }} />
              <select value={d.dur} onChange={(e) => setDay(k, { dur: Number(e.target.value) })} style={{ ...dIS, width: 120 }}>{DOC_DUR.map((x) => <option key={x} value={x}>{x} min</option>)}</select>
              <span style={{ fontSize: 12, color: "#185FA5" }}>{n} créneaux</span>
            </>
          ) : <span style={{ fontSize: 13, color: "#94A3B8" }}>Fermé</span>}
        </div>); })}
    </div>
  );
}

function DoctorsModule({ can }) {
  const [doctors, setDoctors] = useState([]);
  const [saving, setSaving] = useState(false);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [toDetach, setToDetach] = useState(null);
  const [photoUp, setPhotoUp] = useState("");
  const empty = { name: "", email: "", phone: "", specialty: "", service_id: "", fees: "", photo_url: "" };
  const [form, setForm] = useState(empty);
  const [editor, setEditor] = useState(emptyEditor());
  const canManage = can("staff:write");

  const load = () => {
    setLoading(true);
    Promise.all([
      axios.get(`${API_URL}/api/sih/doctors`, getAuthConfig()),
      axios.get(`${API_URL}/api/sih/services`, getAuthConfig()),
    ]).then(([d, s]) => { setDoctors(d.data.doctors || []); setServices(s.data.services || []); })
      .catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const openAdd = () => { setEditing(null); setForm(empty); setEditor(emptyEditor()); setError(""); setShowForm(true); };
  const openEdit = (d) => { setEditing(d); setForm({ name: d.name || "", email: d.email || "", phone: d.phone || "", specialty: d.specialty || "", service_id: d.service_id || "", fees: d.fees || "", photo_url: d.photo_url || "" }); setEditor(scheduleToEditor(d.schedule)); setError(""); setShowForm(true); };

  const save = async () => {
    if (!editing && !form.name.trim()) { setError("Nom requis"); return; }
    if (!editing && !form.email.trim()) { setError("Email requis"); return; }
    if (!form.specialty.trim()) { setError("Specialite requise"); return; }
    if (!form.service_id) { setError("Service requis"); return; }
    setError("");
    if (saving) return;
    setSaving(true);
    const schedule = editorToSchedule(editor);
    try {
      if (editing) {
        await axios.put(`${API_URL}/api/sih/doctors/${editing.doctor_id}`, { phone: form.phone || null, specialty: form.specialty, service_id: form.service_id, fees: form.fees ? Number(form.fees) : null, photo_url: form.photo_url || null, schedule }, getAuthConfig());
        setNotice("Medecin mis a jour.");
      } else {
        const r = await axios.post(`${API_URL}/api/sih/doctors`, { name: form.name, email: form.email, phone: form.phone, specialty: form.specialty, service_id: form.service_id, fees: form.fees ? Number(form.fees) : null, photo_url: form.photo_url || null, schedule }, getAuthConfig());
        setNotice(`Dr. ${form.name} ajoute. Mot de passe temporaire a communiquer : ${r.data.temp_password}`);
      }
      setShowForm(false); setForm(empty); setEditing(null); load();
    } catch (e) { setError(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Erreur"); }
    finally { setSaving(false); }
  };
  const doDetach = async () => { if (!toDetach) return; try { await axios.delete(`${API_URL}/api/sih/doctors/${toDetach.doctor_id}`, getAuthConfig()); setToDetach(null); setNotice("Medecin detache."); load(); } catch (e) { setError(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Erreur"); setToDetach(null); } };
  const uploadDoctorPhoto = async (doctorId, file) => {
    if (!file) return;
    setPhotoUp(doctorId); setError(""); setNotice("");
    const fd = new FormData(); fd.append("file", file);
    try { await axios.post(`${API_URL}/api/sih/doctors/${doctorId}/photo`, fd, getUploadConfig()); setNotice("Photo du medecin mise a jour."); load(); }
    catch (e) { setError(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Echec de l'envoi de la photo"); }
    finally { setPhotoUp(""); }
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: "#0F172A", margin: 0 }}>Medecins de l'etablissement</h1>
          <div style={{ fontSize: 13, color: "#64748B", marginTop: 2 }}>Medecins reservables en ligne, rattaches a un service</div>
        </div>
        {canManage && <button onClick={openAdd} style={{ padding: "9px 18px", background: "#185FA5", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, cursor: "pointer" }}>+ Ajouter un medecin</button>}
      </div>

      {notice && <div style={{ background: "#EAF3DE", border: "1px solid #CDE3AE", color: "#3B6D11", borderRadius: 8, padding: "10px 14px", marginTop: 14, fontSize: 13, display: "flex", justifyContent: "space-between", gap: 10 }}><span>{notice}</span><button onClick={() => setNotice("")} style={{ background: "none", border: "none", cursor: "pointer", color: "#3B6D11" }}>×</button></div>}
      {error && !showForm && <div style={{ background: "#FCEBEB", border: "1px solid #F7C1C1", color: "#A32D2D", borderRadius: 8, padding: "10px 14px", marginTop: 14, fontSize: 13 }}>{error}</div>}
      {canManage && services.length === 0 && <div style={{ background: "#FAEEDA", border: "1px solid #F0D9A8", color: "#854F0B", borderRadius: 8, padding: "10px 14px", marginTop: 14, fontSize: 13 }}>Creez d'abord au moins un service (onglet Services).</div>}

      {showForm && (
        <div style={{ background: "#fff", border: "1px solid #B5D4F4", borderRadius: 12, padding: 20, marginTop: 16 }}>
          <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 14 }}>{editing ? `Modifier — Dr. ${editing.name}` : "Nouveau medecin"}</div>
          {error && <div style={{ background: "#FCEBEB", border: "1px solid #F7C1C1", color: "#A32D2D", borderRadius: 8, padding: "10px 14px", marginBottom: 12, fontSize: 13 }}>{error}</div>}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
            <div><label style={dLab}>Nom complet *</label><input style={{ ...dIS, background: editing ? "#F1F5F9" : "#fff" }} value={form.name} disabled={!!editing} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div><label style={dLab}>Email *</label><input style={{ ...dIS, background: editing ? "#F1F5F9" : "#fff" }} value={form.email} disabled={!!editing} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div><label style={dLab}>Telephone</label><input style={dIS} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
            <div><label style={dLab}>Specialite *</label><input style={dIS} placeholder="Cardiologue..." value={form.specialty} onChange={(e) => setForm({ ...form, specialty: e.target.value })} /></div>
            <div><label style={dLab}>Service *</label><select style={dIS} value={form.service_id} onChange={(e) => setForm({ ...form, service_id: e.target.value })}><option value="">...</option>{services.map((s) => <option key={s.service_id} value={s.service_id}>{s.name}</option>)}</select></div>
            <div><label style={dLab}>Honoraires (DA)</label><input type="number" style={dIS} value={form.fees} onChange={(e) => setForm({ ...form, fees: e.target.value })} /></div>
          </div>
          <div style={{ marginTop: 12 }}><label style={dLab}>Photo (URL, optionnel)</label><input style={dIS} value={form.photo_url} onChange={(e) => setForm({ ...form, photo_url: e.target.value })} /></div>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#185FA5", textTransform: "uppercase", letterSpacing: 0.5, margin: "18px 0 8px" }}>Creneaux de consultation</div>
          <div style={{ fontSize: 12, color: "#64748B", marginBottom: 10 }}>Pour chaque jour ouvert : heure de debut, de fin et duree d'un creneau. Les creneaux reservables sont generes automatiquement.</div>
          <ScheduleEditor value={editor} onChange={setEditor} />
          <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
            <button onClick={save} style={{ padding: "8px 18px", background: "#185FA5", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, cursor: "pointer" }}>{editing ? "Enregistrer" : "Ajouter le medecin"}</button>
            <button onClick={() => { setShowForm(false); setEditing(null); setForm(empty); }} style={{ padding: "8px 18px", background: "transparent", border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 14, cursor: "pointer" }}>Annuler</button>
          </div>
        </div>
      )}

      <div style={{ marginTop: 18, background: "#fff", border: "1px solid #E2E8F0", borderRadius: 12, overflow: "auto" }}>
        {loading ? <div style={{ padding: 30, textAlign: "center", color: "#94A3B8" }}>Chargement...</div>
        : doctors.length === 0 ? <div style={{ padding: 30, textAlign: "center", color: "#94A3B8", fontSize: 14 }}>Aucun medecin. Ajoutez-en un pour le rendre reservable en ligne.</div>
        : (
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 880 }}>
            <thead><tr style={{ background: "#F8FAFC", textAlign: "left" }}>{["Medecin", "Specialite", "Service", "Email", "Honoraires", "Creneaux", "Actions"].map((h) => <th key={h} style={{ padding: "10px 14px", fontSize: 12, color: "#64748B", fontWeight: 600 }}>{h}</th>)}</tr></thead>
            <tbody>
              {doctors.map((d) => { const sum = scheduleSummary(d.schedule); return (
                <tr key={d.doctor_id} style={{ borderTop: "1px solid #F1F5F9" }}>
                  <td style={{ padding: "12px 14px", fontSize: 14, fontWeight: 500 }}>
                    <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <span style={{ width: 34, height: 34, borderRadius: "50%", overflow: "hidden", flexShrink: 0, background: "linear-gradient(135deg,#0596DE,#047CBA)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700 }}>
                        {d.photo_url ? <img src={d.photo_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : (d.name?.charAt(0)?.toUpperCase() || "D")}
                      </span>
                      {formatDoctorName(d.name)}
                    </span>
                  </td>
                  <td style={{ padding: "12px 14px", fontSize: 13, color: "#475569" }}>{d.specialty || "—"}</td>
                  <td style={{ padding: "12px 14px", fontSize: 13, color: "#475569" }}>{d.service_name || "—"}</td>
                  <td style={{ padding: "12px 14px", fontSize: 13, color: "#475569" }}>{d.email}</td>
                  <td style={{ padding: "12px 14px", fontSize: 13, color: "#475569" }}>{d.fees ? `${d.fees} DA` : "—"}</td>
                  <td style={{ padding: "12px 14px", fontSize: 12 }}>{sum ? <span style={{ color: "#3B6D11" }}>{sum}</span> : <span style={{ color: "#A32D2D" }}>a configurer</span>}</td>
                  <td style={{ padding: "12px 14px" }}>{canManage && <span style={{ display: "flex", gap: 12 }}>
                    <label style={{ fontSize: 13, color: "#185FA5", cursor: "pointer" }}>{photoUp === d.doctor_id ? "Envoi..." : "Photo"}<input type="file" accept="image/*" style={{ display: "none" }} onChange={(e) => uploadDoctorPhoto(d.doctor_id, e.target.files[0])} /></label>
                    <button onClick={() => openEdit(d)} style={{ fontSize: 13, color: "#185FA5", background: "none", border: "none", cursor: "pointer" }}>Modifier</button>
                    <button onClick={() => setToDetach(d)} style={{ fontSize: 13, color: "#A32D2D", background: "none", border: "none", cursor: "pointer" }}>Detacher</button>
                  </span>}</td>
                </tr>); })}
            </tbody>
          </table>
        )}
      </div>

      {toDetach && (
        <div onClick={() => setToDetach(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 12, width: "90%", maxWidth: 420, padding: 24 }}>
            <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>Detacher ce medecin ?</div>
            <div style={{ fontSize: 13, color: "#64748B", marginBottom: 20 }}><b>{formatDoctorName(toDetach.name)}</b> ne sera plus rattache a l'etablissement (son compte est conserve).</div>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button onClick={() => setToDetach(null)} style={{ fontSize: 13, padding: "8px 16px", borderRadius: 8, border: "1px solid #CBD5E1", background: "#fff", color: "#64748B", cursor: "pointer" }}>Annuler</button>
              <button onClick={doDetach} style={{ fontSize: 13, padding: "8px 16px", borderRadius: 8, border: "none", background: "#A32D2D", color: "#fff", cursor: "pointer" }}>Detacher</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


function VitrineModule({ can, ctx }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const [f, setF] = useState({ description: "", hours_urgences: "", hours_consultations: "", stat_patients: "", stat_interventions: "", stat_urgences: "", stat_consultations: "", conventions: "" });
  const [coverUrl, setCoverUrl] = useState("");
  const [logoUrl, setLogoUrl] = useState("");
  const [bookletUrl, setBookletUrl] = useState("");
  const [uploading, setUploading] = useState("");
  const canManage = can("staff:write");

  useEffect(() => {
    axios.get(`${API_URL}/api/sih/me`, getAuthConfig()).then((r) => {
      const e = r.data.establishment || {};
      const v = e.vitrine || {};
      const st = v.stats || {};
      setCoverUrl(e.cover_url || "");
      setLogoUrl(e.logo_url || "");
      setBookletUrl(e.booklet_url || "");
      setF({
        description: e.description || "",
        hours_urgences: v.hours_urgences || "",
        hours_consultations: v.hours_consultations || "",
        stat_patients: st.patients ?? "",
        stat_interventions: st.interventions ?? "",
        stat_urgences: st.urgences ?? "",
        stat_consultations: st.consultations ?? "",
        conventions: (v.conventions || []).join("\n"),
      });
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const uploadFile = async (kind, file) => {
    if (!file) return;
    setUploading(kind); setError(""); setNotice("");
    const fd = new FormData(); fd.append("file", file);
    const url = kind === "booklet" ? `${API_URL}/api/sih/establishment/booklet` : kind === "logo" ? `${API_URL}/api/sih/establishment/logo` : `${API_URL}/api/sih/establishment/cover`;
    try {
      const r = await axios.post(url, fd, getUploadConfig());
      if (kind === "booklet") { setBookletUrl(r.data.booklet_url); setNotice("Livret mis a jour."); }
      else if (kind === "logo") { setLogoUrl(r.data.logo_url); setNotice("Logo mis a jour."); }
      else { setCoverUrl(r.data.cover_url); setNotice("Banniere mise a jour."); }
    } catch (e) { setError(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Echec de l'envoi"); }
    finally { setUploading(""); }
  };
  const removeFile = async (kind) => {
    setError(""); setNotice("");
    const url = kind === "booklet" ? `${API_URL}/api/sih/establishment/booklet` : kind === "logo" ? `${API_URL}/api/sih/establishment/logo` : `${API_URL}/api/sih/establishment/cover`;
    try { await axios.delete(url, getAuthConfig()); if (kind === "booklet") setBookletUrl(""); else if (kind === "logo") setLogoUrl(""); else setCoverUrl(""); }
    catch (e) { setError("Erreur"); }
  };

  const save = async () => {
    setSaving(true); setError(""); setNotice("");
    const payload = {
      description: f.description,
      hours_urgences: f.hours_urgences, hours_consultations: f.hours_consultations,
      stat_patients: f.stat_patients !== "" ? Number(f.stat_patients) : null,
      stat_interventions: f.stat_interventions !== "" ? Number(f.stat_interventions) : null,
      stat_urgences: f.stat_urgences !== "" ? Number(f.stat_urgences) : null,
      stat_consultations: f.stat_consultations !== "" ? Number(f.stat_consultations) : null,
      conventions: f.conventions.split("\n").map((x) => x.trim()).filter(Boolean),
    };
    try { await axios.put(`${API_URL}/api/sih/establishment/vitrine`, payload, getAuthConfig()); setNotice("Vitrine enregistree. Elle est visible sur la page publique de l'etablissement."); }
    catch (e) { setError(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Erreur"); }
    finally { setSaving(false); }
  };

  const IS = { width: "100%", padding: "9px 12px", border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 14, boxSizing: "border-box", background: "#fff" };
  const LB = { fontSize: 12, color: "#64748B", marginBottom: 4, display: "block" };
  const sectionTitle = { fontSize: 12, fontWeight: 700, color: "#185FA5", textTransform: "uppercase", letterSpacing: 0.5, margin: "22px 0 10px" };

  if (loading) return <div style={{ padding: 30, color: "#94A3B8" }}>Chargement...</div>;

  return (
    <div style={{ maxWidth: 760 }}>
      <h1 style={{ fontSize: 22, fontWeight: 700, color: "#0F172A", margin: 0 }}>Ma vitrine</h1>
      <div style={{ fontSize: 13, color: "#64748B", marginTop: 2 }}>Ces informations apparaissent sur la page publique de votre etablissement.</div>

      {notice && <div style={{ background: "#EAF3DE", border: "1px solid #CDE3AE", color: "#3B6D11", borderRadius: 8, padding: "10px 14px", marginTop: 14, fontSize: 13 }}>{notice}</div>}
      {error && <div style={{ background: "#FCEBEB", border: "1px solid #F7C1C1", color: "#A32D2D", borderRadius: 8, padding: "10px 14px", marginTop: 14, fontSize: 13 }}>{error}</div>}

      <div style={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 12, padding: 20, marginTop: 16 }}>
        <div style={{ ...sectionTitle, marginTop: 0 }}>Banniere de l'etablissement</div>
        {coverUrl ? (
          <div>
            <img src={coverUrl} alt="Banniere" style={{ width: "100%", maxHeight: 160, objectFit: "cover", borderRadius: 10, border: "1px solid #E2E8F0" }} />
            {canManage && <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <label style={{ fontSize: 13, color: "#185FA5", cursor: "pointer" }}>Remplacer<input type="file" accept="image/*" style={{ display: "none" }} onChange={(e) => uploadFile("cover", e.target.files[0])} /></label>
              <button onClick={() => removeFile("cover")} style={{ fontSize: 13, color: "#A32D2D", background: "none", border: "none", cursor: "pointer" }}>Supprimer</button>
            </div>}
          </div>
        ) : (
          canManage && <label style={{ display: "inline-block", fontSize: 13, padding: "9px 16px", border: "1px dashed #CBD5E1", borderRadius: 8, cursor: "pointer", color: "#475569" }}>{uploading === "cover" ? "Envoi..." : "Choisir une image (JPEG/PNG/WEBP, max 5 Mo)"}<input type="file" accept="image/*" style={{ display: "none" }} onChange={(e) => uploadFile("cover", e.target.files[0])} /></label>
        )}

        <div style={{ ...sectionTitle }}>Logo de l'etablissement</div>
        {logoUrl ? (
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <img src={logoUrl} alt="Logo" style={{ width: 72, height: 72, objectFit: "cover", borderRadius: 12, border: "1px solid #E2E8F0", background: "#fff" }} />
            {canManage && <div style={{ display: "flex", gap: 12 }}>
              <label style={{ fontSize: 13, color: "#185FA5", cursor: "pointer" }}>Remplacer<input type="file" accept="image/*" style={{ display: "none" }} onChange={(e) => uploadFile("logo", e.target.files[0])} /></label>
              <button onClick={() => removeFile("logo")} style={{ fontSize: 13, color: "#A32D2D", background: "none", border: "none", cursor: "pointer" }}>Supprimer</button>
            </div>}
          </div>
        ) : (
          canManage && <label style={{ display: "inline-block", fontSize: 13, padding: "9px 16px", border: "1px dashed #CBD5E1", borderRadius: 8, cursor: "pointer", color: "#475569" }}>{uploading === "logo" ? "Envoi..." : "Choisir un logo (JPEG/PNG/WEBP, carré de préférence)"}<input type="file" accept="image/*" style={{ display: "none" }} onChange={(e) => uploadFile("logo", e.target.files[0])} /></label>
        )}

        <div style={{ ...sectionTitle, marginTop: 0 }}>Presentation</div>
        <label style={LB}>A propos de l'etablissement</label>
        <textarea style={{ ...IS, minHeight: 90, resize: "vertical" }} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} disabled={!canManage} placeholder="Presentez votre etablissement, ses specialites, son plateau technique..." />

        <div style={sectionTitle}>Horaires</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <div><label style={LB}>Urgences</label><input style={IS} value={f.hours_urgences} onChange={(e) => setF({ ...f, hours_urgences: e.target.value })} disabled={!canManage} placeholder="24h/24 - 7j/7" /></div>
          <div><label style={LB}>Consultations externes</label><input style={IS} value={f.hours_consultations} onChange={(e) => setF({ ...f, hours_consultations: e.target.value })} disabled={!canManage} placeholder="Samedi - Jeudi : 09h - 16h" /></div>
        </div>

        <div style={sectionTitle}>Chiffres cles (par an)</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 }}>
          <div><label style={LB}>Patients hospitalises</label><input type="number" style={IS} value={f.stat_patients} onChange={(e) => setF({ ...f, stat_patients: e.target.value })} disabled={!canManage} /></div>
          <div><label style={LB}>Interventions</label><input type="number" style={IS} value={f.stat_interventions} onChange={(e) => setF({ ...f, stat_interventions: e.target.value })} disabled={!canManage} /></div>
          <div><label style={LB}>Passages urgences</label><input type="number" style={IS} value={f.stat_urgences} onChange={(e) => setF({ ...f, stat_urgences: e.target.value })} disabled={!canManage} /></div>
          <div><label style={LB}>Consultations</label><input type="number" style={IS} value={f.stat_consultations} onChange={(e) => setF({ ...f, stat_consultations: e.target.value })} disabled={!canManage} /></div>
        </div>

        <div style={sectionTitle}>Conventions / mutuelles</div>
        <label style={LB}>Un organisme par ligne</label>
        <textarea style={{ ...IS, minHeight: 90, resize: "vertical" }} value={f.conventions} onChange={(e) => setF({ ...f, conventions: e.target.value })} disabled={!canManage} placeholder={"CNAS\nCASNOS\nMutuelle des travailleurs..."} />

        <div style={sectionTitle}>Livret d'accueil (PDF)</div>
        {bookletUrl ? (
          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <a href={bookletUrl} target="_blank" rel="noreferrer" style={{ fontSize: 13, color: "#185FA5" }}>Voir le livret actuel</a>
            {canManage && <>
              <label style={{ fontSize: 13, color: "#185FA5", cursor: "pointer" }}>Remplacer<input type="file" accept="application/pdf" style={{ display: "none" }} onChange={(e) => uploadFile("booklet", e.target.files[0])} /></label>
              <button onClick={() => removeFile("booklet")} style={{ fontSize: 13, color: "#A32D2D", background: "none", border: "none", cursor: "pointer" }}>Supprimer</button>
            </>}
          </div>
        ) : (
          canManage && <label style={{ display: "inline-block", fontSize: 13, padding: "9px 16px", border: "1px dashed #CBD5E1", borderRadius: 8, cursor: "pointer", color: "#475569" }}>{uploading === "booklet" ? "Envoi..." : "Choisir un PDF (max 15 Mo)"}<input type="file" accept="application/pdf" style={{ display: "none" }} onChange={(e) => uploadFile("booklet", e.target.files[0])} /></label>
        )}

        {canManage && <div style={{ marginTop: 18 }}>
          <button onClick={save} disabled={saving} style={{ padding: "9px 20px", background: "#185FA5", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, cursor: "pointer" }}>{saving ? "Enregistrement..." : "Enregistrer la vitrine"}</button>
        </div>}
      </div>
    </div>
  );
}


const cIS = { width: "100%", padding: "9px 12px", border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 14, boxSizing: "border-box", background: "#fff" };
const cLB = { fontSize: 12, color: "#64748B", marginBottom: 4, display: "block" };

function ConsultationsModule({ can }) {
  const [appts, setAppts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("todo");
  const [docFor, setDocFor] = useState(null);   // RDV a documenter
  const [viewId, setViewId] = useState(null);    // consultation existante
  const [moveFor, setMoveFor] = useState(null);  // RDV a deplacer
  const [docMap, setDocMap] = useState({});
  const [toDelCons, setToDelCons] = useState(null);
  const delCons = async () => {
    if (!toDelCons) return;
    try { await axios.delete(`${API_URL}/api/sih/consultations/${toDelCons.consultation_id}`, getAuthConfig()); setToDelCons(null); load(); }
    catch (e) {
      // Correctif : la modale se fermait comme si la suppression avait
      // reussi meme en cas d'erreur serveur — l'utilisateur croyait la
      // consultation supprimee alors qu'elle etait toujours la. Meme
      // traitement que delAppt() juste en dessous.
      setToDelCons(null);
      alert(e.response?.data?.detail || "Erreur lors de la suppression de la consultation");
    }
  };
  const delAppt = async (a) => {
    if (!window.confirm("Supprimer définitivement ce rendez-vous ?")) return;
    try { await axios.delete(`${API_URL}/api/sih/consultations/agenda/${a.appointment_id}`, getAuthConfig()); load(); }
    catch (e) { alert(e.response?.data?.detail || "Erreur"); }
  };
  const canWrite = can("consultation:write");
  const today = new Date().toLocaleDateString("en-CA"); // YYYY-MM-DD local

  const load = () => { setLoading(true); axios.get(`${API_URL}/api/sih/consultations/agenda`, getAuthConfig()).then((r) => setAppts(r.data.appointments || [])).catch(() => {}).finally(() => setLoading(false)); };
  useEffect(() => { load(); axios.get(`${API_URL}/api/sih/doctors`, getAuthConfig()).then((r) => { const m = {}; (r.data.doctors || []).forEach((d) => { m[d.doctor_id] = d; }); setDocMap(m); }).catch(() => {}); }, []);

  const todo = appts.filter((a) => !a.consultation_id && a.status !== "cancelled");
  const done = appts.filter((a) => a.consultation_id);
  const list = tab === "todo" ? todo : done;

  const fmtWhen = (a) => {
    const d = a.date === today ? "Aujourd'hui" : a.date;
    return `${d}${a.time ? " · " + a.time : ""}`;
  };

  return (
    <div>
      <div>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: "#0F172A", margin: 0 }}>Consultations</h1>
        <div style={{ fontSize: 13, color: "#64748B", marginTop: 2 }}>Rendez-vous en ligne de l'etablissement. Documentez la consultation pour alimenter le dossier patient.</div>
      </div>

      <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
        {[["todo", `A documenter (${todo.length})`], ["done", `Documentees (${done.length})`]].map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)} style={{ fontSize: 14, padding: "7px 16px", borderRadius: 8, cursor: "pointer", border: tab === id ? "1px solid #185FA5" : "1px solid #CBD5E1", background: tab === id ? "#185FA5" : "#fff", color: tab === id ? "#fff" : "#475569" }}>{label}</button>
        ))}
      </div>

      <div style={{ marginTop: 16, background: "#fff", border: "1px solid #E2E8F0", borderRadius: 12, overflow: "auto" }}>
        {loading ? <div style={{ padding: 30, textAlign: "center", color: "#94A3B8" }}>Chargement...</div>
        : list.length === 0 ? <div style={{ padding: 30, textAlign: "center", color: "#94A3B8", fontSize: 14 }}>{tab === "todo" ? "Aucun RDV a documenter." : "Aucune consultation documentee."}</div>
        : (
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 820 }}>
            <thead><tr style={{ background: "#F8FAFC", textAlign: "left" }}>{["Quand", "Patient", "Medecin", "Service", "Motif", "Action"].map((h) => <th key={h} style={{ padding: "10px 14px", fontSize: 12, color: "#64748B", fontWeight: 600 }}>{h}</th>)}</tr></thead>
            <tbody>
              {list.map((a) => (
                <tr key={a.appointment_id} style={{ borderTop: "1px solid #F1F5F9" }}>
                  <td style={{ padding: "12px 14px", fontSize: 13, color: a.date === today ? "#185FA5" : "#475569", fontWeight: a.date === today ? 600 : 400 }}>{fmtWhen(a)}</td>
                  <td style={{ padding: "12px 14px", fontSize: 14, fontWeight: 500 }}>{a.patient_name || "—"}<div style={{ fontSize: 12, color: "#94A3B8" }}>{a.patient_phone || ""}</div></td>
                  <td style={{ padding: "12px 14px", fontSize: 13, color: "#475569" }}>{a.doctor_name ? formatDoctorName(a.doctor_name) : "—"}</td>
                  <td style={{ padding: "12px 14px", fontSize: 13, color: "#475569" }}>{a.service_name || "—"}</td>
                  <td style={{ padding: "12px 14px", fontSize: 13, color: "#475569", maxWidth: 200 }}>{a.reason || "—"}</td>
                  <td style={{ padding: "12px 14px" }}>
                    {a.consultation_id
                      ? <span style={{ display: "flex", gap: 12 }}>
                          <button onClick={() => setViewId(a.consultation_id)} style={{ fontSize: 13, color: "#185FA5", background: "none", border: "none", cursor: "pointer" }}>Voir / modifier</button>
                          {canWrite && <button onClick={() => setToDelCons(a)} style={{ fontSize: 13, color: "#A32D2D", background: "none", border: "none", cursor: "pointer" }}>Supprimer</button>}
                        </span>
                      : canWrite
                        ? <span style={{ display: "flex", gap: 12, alignItems: "center" }}>
                            <button onClick={() => setDocFor(a)} style={{ fontSize: 13, color: "#fff", background: "#185FA5", border: "none", borderRadius: 8, padding: "6px 12px", cursor: "pointer" }}>Documenter</button>
                            <button onClick={() => setMoveFor(a)} style={{ fontSize: 13, color: "#185FA5", background: "none", border: "none", cursor: "pointer" }}>Déplacer</button>
                            <button onClick={() => delAppt(a)} style={{ fontSize: 13, color: "#A32D2D", background: "none", border: "none", cursor: "pointer" }}>Supprimer</button>
                          </span>
                        : <span style={{ fontSize: 12, color: "#94A3B8" }}>—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {docFor && <ConsultationModal appt={docFor} can={can} onClose={() => setDocFor(null)} onDone={() => { setDocFor(null); load(); }} />}
      {viewId && <ConsultationModal consultationId={viewId} canWrite={canWrite} can={can} onClose={() => setViewId(null)} onDone={() => { setViewId(null); load(); }} />}
      {moveFor && <RescheduleModal appt={moveFor} doctor={docMap[moveFor.doctor_id]} onClose={() => setMoveFor(null)} onDone={() => { setMoveFor(null); load(); }} />}
      {toDelCons && (
        <div onClick={() => setToDelCons(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 12, width: "90%", maxWidth: 430, padding: 24 }}>
            <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>Supprimer cette consultation ?</div>
            <div style={{ fontSize: 13, color: "#64748B", marginBottom: 20 }}>La consultation de <b>{toDelCons.patient_name || "ce patient"}</b> sera supprimée. Le rendez-vous repassera « à documenter ».</div>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button onClick={() => setToDelCons(null)} style={{ fontSize: 13, padding: "8px 16px", borderRadius: 8, border: "1px solid #CBD5E1", background: "#fff", color: "#64748B", cursor: "pointer" }}>Annuler</button>
              <button onClick={delCons} style={{ fontSize: 13, padding: "8px 16px", borderRadius: 8, border: "none", background: "#A32D2D", color: "#fff", cursor: "pointer" }}>Supprimer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function RescheduleModal({ appt, doctor, onClose, onDone }) {
  const [date, setDate] = useState(appt.date || "");
  const [time, setTime] = useState(appt.time || "");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const dayKeys = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
  const sched = doctor?.schedule || {};
  const hasSchedule = Object.keys(sched).length > 0;
  const slots = date && hasSchedule ? (sched[dayKeys[new Date(date + "T00:00:00").getDay()]] || []) : [];
  const noSlotsThisDay = hasSchedule && !!date && slots.length === 0;
  const fld = { width: "100%", padding: "9px 12px", border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 14, boxSizing: "border-box", background: "#fff" };
  const save = async () => {
    if (!date || !time) { setErr("Choisissez une date et une heure"); return; }
    setSaving(true); setErr("");
    try { await axios.put(`${API_URL}/api/sih/consultations/agenda/${appt.appointment_id}/reschedule`, { date, time }, getAuthConfig()); onDone(); }
    catch (e) { setErr(e.response?.data?.detail || "Erreur"); setSaving(false); }
  };
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1100, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: 24, overflow: "auto" }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 12, width: "92%", maxWidth: 440, padding: 22, marginTop: 30 }}>
        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 2 }}>Déplacer le rendez-vous</div>
        <div style={{ fontSize: 13, color: "#64748B", marginBottom: 14 }}>{appt.patient_name || ""} · {formatDoctorName(appt.doctor_name)}</div>
        {err && <div style={{ background: "#FCEBEB", border: "1px solid #F7C1C1", color: "#A32D2D", borderRadius: 8, padding: "8px 12px", marginBottom: 12, fontSize: 12 }}>{err}</div>}
        <div style={{ marginBottom: 12 }}><label style={{ fontSize: 12, color: "#64748B", display: "block", marginBottom: 4 }}>Nouvelle date</label>
          <input type="date" style={fld} value={date} onChange={(e) => { setDate(e.target.value); setTime(""); }} /></div>
        <div style={{ marginBottom: 4 }}><label style={{ fontSize: 12, color: "#64748B", display: "block", marginBottom: 4 }}>Heure</label>
          {noSlotsThisDay ? (
            <div style={{ fontSize: 13, color: "#B45309", background: "#FEF3C7", border: "1px solid #FDE68A", borderRadius: 8, padding: "8px 12px" }}>Le médecin ne consulte pas ce jour-là. Choisissez une autre date.</div>
          ) : slots.length > 0 ? (
            <select style={fld} value={time} onChange={(e) => setTime(e.target.value)}>
              <option value="">Choisir un créneau…</option>
              {slots.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          ) : (
            <>
              <input type="time" style={fld} value={time} onChange={(e) => setTime(e.target.value)} />
              {date && <div style={{ fontSize: 12, color: "#94A3B8", marginTop: 4 }}>Le médecin n'a pas de planning prédéfini — saisis une heure manuellement.</div>}
            </>
          )}
        </div>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 18 }}>
          <button onClick={onClose} style={{ fontSize: 13, padding: "9px 16px", borderRadius: 8, border: "1px solid #CBD5E1", background: "#fff", color: "#64748B", cursor: "pointer" }}>Annuler</button>
          <button onClick={save} disabled={saving || noSlotsThisDay || !time} style={{ fontSize: 13, padding: "9px 16px", borderRadius: 8, border: "none", background: (noSlotsThisDay || !time) ? "#9CB7CE" : "#185FA5", color: "#fff", cursor: (noSlotsThisDay || !time) ? "not-allowed" : "pointer" }}>{saving ? "..." : "Déplacer"}</button>
        </div>
      </div>
    </div>
  );
}


function LabQuickModal({ patientId, consultationId, serviceId, onClose, onDone }) {
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState([]);
  const [priority, setPriority] = useState("normal");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const toggle = (item) => {
    const on = selected.find((a) => a.name === item.name);
    setSelected(on ? selected.filter((a) => a.name !== item.name) : [...selected, { name: item.name, unit: item.unit || "", result: "", reference: item.reference || "" }]);
  };
  const results = q.trim() ? LAB_CATALOG.filter((it) => it.name.toLowerCase().includes(q.trim().toLowerCase())).slice(0, 20) : [];
  const save = async () => {
    if (selected.length === 0) { setErr("Selectionnez au moins une analyse"); return; }
    setSaving(true); setErr("");
    try {
      await axios.post(`${API_URL}/api/sih/lab/requests`, { patient_id: patientId, consultation_id: consultationId, service_id: serviceId || null, analyses: selected, priority, reason }, getAuthConfig());
      onDone && onDone();
    } catch (e) { setErr(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Erreur"); setSaving(false); }
  };
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 60, padding: 16 }}>
      <div style={{ background: "#fff", borderRadius: 12, padding: 22, width: "100%", maxWidth: 560, maxHeight: "85vh", overflow: "auto" }}>
        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 14 }}>Demande d'analyses</div>
        {err && <div style={{ background: "#FCEBEB", color: "#A32D2D", padding: "8px 12px", borderRadius: 8, fontSize: 13, marginBottom: 12 }}>{err}</div>}
        <input autoFocus placeholder="Rechercher une analyse (glycemie, NFS, sodium...)" value={q} onChange={(e) => setQ(e.target.value)} style={{ width: "100%", padding: "9px 12px", border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 14, marginBottom: 8 }} />
        {results.length > 0 && (
          <div style={{ border: "1px solid #E2E8F0", borderRadius: 8, maxHeight: 180, overflow: "auto", marginBottom: 10 }}>
            {results.map((item) => { const on = !!selected.find((a) => a.name === item.name); return (
              <div key={item.name} onClick={() => toggle(item)} style={{ padding: "8px 12px", fontSize: 13, cursor: "pointer", background: on ? "#E6F4FB" : "#fff", borderBottom: "1px solid #F1F5F9" }}>{on ? "OK " : ""}{item.name}</div>
            ); })}
          </div>
        )}
        {selected.length > 0 && <div style={{ fontSize: 13, color: "#475569", marginBottom: 12 }}>Selectionnees : {selected.map((a) => a.name).join(", ")}</div>}
        <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
          <select value={priority} onChange={(e) => setPriority(e.target.value)} style={{ padding: "8px 10px", border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 13 }}>
            <option value="normal">Normal</option><option value="urgent">Urgent</option>
          </select>
          <input placeholder="Motif (optionnel)" value={reason} onChange={(e) => setReason(e.target.value)} style={{ flex: 1, padding: "8px 12px", border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 13 }} />
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button onClick={onClose} style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid #CBD5E1", background: "#fff", color: "#64748B", fontSize: 13, cursor: "pointer" }}>Annuler</button>
          <button onClick={save} disabled={saving} style={{ padding: "8px 16px", borderRadius: 8, border: "none", background: "#185FA5", color: "#fff", fontSize: 13, cursor: "pointer" }}>{saving ? "..." : "Prescrire"}</button>
        </div>
      </div>
    </div>
  );
}

function ImagingQuickModal({ patientId, consultationId, serviceId, onClose, onDone }) {
  const [q, setQ] = useState("");
  const [examType, setExamType] = useState("");
  const [region, setRegion] = useState("");
  const [priority, setPriority] = useState("normal");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const results = q.trim() ? IMAGING_CATALOG.filter((it) => it.name.toLowerCase().includes(q.trim().toLowerCase())).slice(0, 20) : [];
  const save = async () => {
    if (!examType.trim()) { setErr("Choisissez un examen"); return; }
    setSaving(true); setErr("");
    try {
      await axios.post(`${API_URL}/api/sih/imaging/requests`, { patient_id: patientId, consultation_id: consultationId, service_id: serviceId || null, exam_type: examType, region, priority, reason }, getAuthConfig());
      onDone && onDone();
    } catch (e) { setErr(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Erreur"); setSaving(false); }
  };
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 60, padding: 16 }}>
      <div style={{ background: "#fff", borderRadius: 12, padding: 22, width: "100%", maxWidth: 560, maxHeight: "85vh", overflow: "auto" }}>
        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 14 }}>Demande d'imagerie</div>
        {err && <div style={{ background: "#FCEBEB", color: "#A32D2D", padding: "8px 12px", borderRadius: 8, fontSize: 13, marginBottom: 12 }}>{err}</div>}
        <input autoFocus placeholder="Rechercher un examen (thorax, echographie, scanner...)" value={q} onChange={(e) => setQ(e.target.value)} style={{ width: "100%", padding: "9px 12px", border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 14, marginBottom: 8 }} />
        {results.length > 0 && (
          <div style={{ border: "1px solid #E2E8F0", borderRadius: 8, maxHeight: 180, overflow: "auto", marginBottom: 10 }}>
            {results.map((item) => (
              <div key={item.name} onClick={() => { setExamType(item.name); setQ(item.name); }} style={{ padding: "8px 12px", fontSize: 13, cursor: "pointer", background: examType === item.name ? "#E6F4FB" : "#fff", borderBottom: "1px solid #F1F5F9" }}>{examType === item.name ? "OK " : ""}{item.name}</div>
            ))}
          </div>
        )}
        {examType && <div style={{ fontSize: 13, color: "#475569", marginBottom: 10 }}>Examen : <strong>{examType}</strong></div>}
        <input placeholder="Region anatomique (optionnel)" value={region} onChange={(e) => setRegion(e.target.value)} style={{ width: "100%", padding: "8px 12px", border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 13, marginBottom: 10 }} />
        <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
          <select value={priority} onChange={(e) => setPriority(e.target.value)} style={{ padding: "8px 10px", border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 13 }}>
            <option value="normal">Normal</option><option value="urgent">Urgent</option>
          </select>
          <input placeholder="Motif (optionnel)" value={reason} onChange={(e) => setReason(e.target.value)} style={{ flex: 1, padding: "8px 12px", border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 13 }} />
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button onClick={onClose} style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid #CBD5E1", background: "#fff", color: "#64748B", fontSize: 13, cursor: "pointer" }}>Annuler</button>
          <button onClick={save} disabled={saving} style={{ padding: "8px 16px", borderRadius: 8, border: "none", background: "#185FA5", color: "#fff", fontSize: 13, cursor: "pointer" }}>{saving ? "..." : "Prescrire"}</button>
        </div>
      </div>
    </div>
  );
}

function CareQuickModal({ patientId, consultationId, serviceId, onClose, onDone }) {
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState("normal");
  const [scheduledAt, setScheduledAt] = useState("");
  const [recurrence, setRecurrence] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const save = async () => {
    if (!description.trim()) { setErr("Decrivez le soin"); return; }
    setSaving(true); setErr("");
    try {
      await axios.post(`${API_URL}/api/sih/care/tasks`, { patient_id: patientId, consultation_id: consultationId, service_id: serviceId || null, description, priority, scheduled_at: scheduledAt || null, recurrence: recurrence || null }, getAuthConfig());
      onDone && onDone();
    } catch (e) { setErr(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Erreur"); setSaving(false); }
  };
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 60, padding: 16 }}>
      <div style={{ background: "#fff", borderRadius: 12, padding: 22, width: "100%", maxWidth: 520 }}>
        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 14 }}>Prescrire un soin infirmier</div>
        {err && <div style={{ background: "#FCEBEB", color: "#A32D2D", padding: "8px 12px", borderRadius: 8, fontSize: 13, marginBottom: 12 }}>{err}</div>}
        <textarea autoFocus placeholder="Ex: Refection pansement jambe droite" value={description} onChange={(e) => setDescription(e.target.value)} style={{ width: "100%", minHeight: 70, padding: "9px 12px", border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 14, marginBottom: 10, resize: "vertical" }} />
        <div style={{ display: "flex", gap: 10, marginBottom: 10 }}>
          <select value={priority} onChange={(e) => setPriority(e.target.value)} style={{ padding: "8px 10px", border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 13 }}>
            <option value="normal">Normal</option><option value="urgent">Urgent</option>
          </select>
          <input placeholder="Frequence (ex: toutes les 8h)" value={recurrence} onChange={(e) => setRecurrence(e.target.value)} style={{ flex: 1, padding: "8px 12px", border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 13 }} />
        </div>
        <label style={{ fontSize: 12, color: "#64748B" }}>Echeance (optionnel)</label>
        <input type="datetime-local" value={scheduledAt} onChange={(e) => setScheduledAt(e.target.value)} style={{ width: "100%", padding: "8px 12px", border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 13, marginBottom: 14 }} />
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button onClick={onClose} style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid #CBD5E1", background: "#fff", color: "#64748B", fontSize: 13, cursor: "pointer" }}>Annuler</button>
          <button onClick={save} disabled={saving} style={{ padding: "8px 16px", borderRadius: 8, border: "none", background: "#185FA5", color: "#fff", fontSize: 13, cursor: "pointer" }}>{saving ? "..." : "Prescrire"}</button>
        </div>
      </div>
    </div>
  );
}
function ConsultationModal({ appt, consultationId, canWrite = true, can = () => false, onClose, onDone }) {
  const [rxOpen, setRxOpen] = useState(false);
  const [labOpen, setLabOpen] = useState(false);
  const [imgOpen, setImgOpen] = useState(false);
  const [careOpen, setCareOpen] = useState(false);
  const [f, setF] = useState({ motif: "", bp: "", pulse: "", temperature: "", spo2: "", weight: "", height: "", pain: "", exam: "", diagnosis: "", conduite: "", notes: "" });
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(!!consultationId);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const editable = !consultationId || canWrite;

  useEffect(() => {
    if (consultationId) {
      axios.get(`${API_URL}/api/sih/consultations/${consultationId}`, getAuthConfig()).then((r) => {
        const c = r.data; const v = c.vitals || {};
        setMeta(c);
        setF({ motif: c.motif || "", bp: v.bp || "", pulse: v.pulse ?? "", temperature: v.temperature ?? "", spo2: v.spo2 ?? "", weight: v.weight ?? "", height: v.height ?? "", pain: v.pain ?? "", exam: c.exam || "", diagnosis: c.diagnosis || "", conduite: c.conduite || "", notes: c.notes || "" });
      }).catch(() => setErr("Chargement impossible")).finally(() => setLoading(false));
    }
  }, [consultationId]);

  const num = (x) => (x !== "" && x != null ? Number(x) : null);
  const save = async () => {
    setSaving(true); setErr("");
    const payload = {
      motif: f.motif || null, bp: f.bp || null, pulse: num(f.pulse), temperature: num(f.temperature), spo2: num(f.spo2),
      weight: num(f.weight), height: num(f.height), pain: num(f.pain),
      exam: f.exam || null, diagnosis: f.diagnosis || null, conduite: f.conduite || null, notes: f.notes || null,
    };
    try {
      if (consultationId) await axios.put(`${API_URL}/api/sih/consultations/${consultationId}`, payload, getAuthConfig());
      else await axios.post(`${API_URL}/api/sih/consultations`, { appointment_id: appt.appointment_id, ...payload }, getAuthConfig());
      onDone();
    } catch (e) { setErr(e.response?.data?.detail || "Erreur"); setSaving(false); }
  };

  const title = consultationId ? "Consultation" : "Documenter la consultation";
  const sub = consultationId
    ? (meta ? `Patient · Dr. ${meta.doctor_name || ""}` : "")
    : `${appt.patient_name || ""} · Dr. ${appt.doctor_name || ""}${appt.service_name ? " · " + appt.service_name : ""}`;

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: 24, overflow: "auto" }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 12, width: "92%", maxWidth: 640, padding: 24, marginTop: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 2 }}>
          <div style={{ fontSize: 17, fontWeight: 700 }}>{title}</div>
          <button onClick={onClose} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#94A3B8" }}>×</button>
        </div>
        <div style={{ fontSize: 13, color: "#64748B", marginBottom: 14 }}>{sub}</div>
        {err && <div style={{ background: "#FCEBEB", border: "1px solid #F7C1C1", color: "#A32D2D", borderRadius: 8, padding: "8px 12px", marginBottom: 12, fontSize: 12 }}>{err}</div>}
        {loading ? <div style={{ padding: 20, color: "#94A3B8" }}>Chargement...</div> : (
          <>
            <div style={{ marginBottom: 12 }}><label style={cLB}>Motif</label><input style={cIS} value={f.motif} onChange={(e) => setF({ ...f, motif: e.target.value })} disabled={!editable} /></div>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#185FA5", textTransform: "uppercase", letterSpacing: 0.5, margin: "6px 0 8px" }}>Constantes</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10 }}>
              <div><label style={cLB}>TA</label><input style={cIS} placeholder="120/80" value={f.bp} onChange={(e) => setF({ ...f, bp: e.target.value })} disabled={!editable} /></div>
              <div><label style={cLB}>Pouls</label><input type="number" style={cIS} value={f.pulse} onChange={(e) => setF({ ...f, pulse: e.target.value })} disabled={!editable} /></div>
              <div><label style={cLB}>Temp °C</label><input type="number" style={cIS} value={f.temperature} onChange={(e) => setF({ ...f, temperature: e.target.value })} disabled={!editable} /></div>
              <div><label style={cLB}>SpO2 %</label><input type="number" style={cIS} value={f.spo2} onChange={(e) => setF({ ...f, spo2: e.target.value })} disabled={!editable} /></div>
              <div><label style={cLB}>Poids kg</label><input type="number" style={cIS} value={f.weight} onChange={(e) => setF({ ...f, weight: e.target.value })} disabled={!editable} /></div>
              <div><label style={cLB}>Taille cm</label><input type="number" style={cIS} value={f.height} onChange={(e) => setF({ ...f, height: e.target.value })} disabled={!editable} /></div>
              <div><label style={cLB}>Douleur EVA</label><input type="number" style={cIS} value={f.pain} onChange={(e) => setF({ ...f, pain: e.target.value })} disabled={!editable} /></div>
            </div>
            <div style={{ marginTop: 12 }}><label style={cLB}>Examen clinique</label><textarea style={{ ...cIS, minHeight: 60, resize: "vertical" }} value={f.exam} onChange={(e) => setF({ ...f, exam: e.target.value })} disabled={!editable} /></div>
            <div style={{ marginTop: 10 }}><label style={cLB}>Diagnostic</label><textarea style={{ ...cIS, minHeight: 50, resize: "vertical" }} value={f.diagnosis} onChange={(e) => setF({ ...f, diagnosis: e.target.value })} disabled={!editable} /></div>
            <div style={{ marginTop: 10 }}><label style={cLB}>Conduite a tenir</label><textarea style={{ ...cIS, minHeight: 50, resize: "vertical" }} value={f.conduite} onChange={(e) => setF({ ...f, conduite: e.target.value })} disabled={!editable} /></div>
            <div style={{ display: "flex", gap: 8, justifyContent: "space-between", marginTop: 16, flexWrap: "wrap" }}>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>{consultationId && meta && meta.patient_id && can("prescription:write") && <button onClick={() => setRxOpen(true)} style={{ fontSize: 13, padding: "8px 14px", borderRadius: 8, border: "1px solid #185FA5", background: "#fff", color: "#185FA5", cursor: "pointer" }}>💊 Ordonnance</button>}{consultationId && meta && meta.patient_id && can("lab:request") && <button onClick={() => setLabOpen(true)} style={{ fontSize: 13, padding: "8px 14px", borderRadius: 8, border: "1px solid #185FA5", background: "#fff", color: "#185FA5", cursor: "pointer" }}>🧪 Analyses</button>}{consultationId && meta && meta.patient_id && can("imaging:request") && <button onClick={() => setImgOpen(true)} style={{ fontSize: 13, padding: "8px 14px", borderRadius: 8, border: "1px solid #185FA5", background: "#fff", color: "#185FA5", cursor: "pointer" }}>🩻 Imagerie</button>}{consultationId && meta && meta.patient_id && can("care:request") && <button onClick={() => setCareOpen(true)} style={{ fontSize: 13, padding: "8px 14px", borderRadius: 8, border: "1px solid #185FA5", background: "#fff", color: "#185FA5", cursor: "pointer" }}>🩹 Soin</button>}</div>
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={onClose} style={{ fontSize: 13, padding: "8px 16px", borderRadius: 8, border: "1px solid #CBD5E1", background: "#fff", color: "#64748B", cursor: "pointer" }}>Fermer</button>
                {editable && <button onClick={save} disabled={saving} style={{ fontSize: 13, padding: "8px 16px", borderRadius: 8, border: "none", background: "#185FA5", color: "#fff", cursor: "pointer" }}>{saving ? "..." : "Enregistrer"}</button>}
              </div>
            </div>
            {rxOpen && meta && <PrescriptionModal patientId={meta.patient_id} consultationId={consultationId} diagnosis={f.diagnosis} can={can} onClose={() => setRxOpen(false)} onDone={() => setRxOpen(false)} />}
            {labOpen && meta && <LabQuickModal patientId={meta.patient_id} consultationId={consultationId} serviceId={meta.service_id} onClose={() => setLabOpen(false)} onDone={() => setLabOpen(false)} />}
            {imgOpen && meta && <ImagingQuickModal patientId={meta.patient_id} consultationId={consultationId} serviceId={meta.service_id} onClose={() => setImgOpen(false)} onDone={() => setImgOpen(false)} />}
            {careOpen && meta && <CareQuickModal patientId={meta.patient_id} consultationId={consultationId} serviceId={meta.service_id} onClose={() => setCareOpen(false)} onDone={() => setCareOpen(false)} />}
          </>
        )}
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────
// Module Ordonnance : liste, modeles, redaction (modele + IA), PDF.
// ─────────────────────────────────────────────────────────────

const rxIS = { width: "100%", padding: "8px 10px", border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 13, boxSizing: "border-box", background: "#fff" };

async function printPrescription(rx, hospitalName) {
  const w = window.open("", "_blank");
  if (!w) { alert("Autorisez les pop-ups pour imprimer."); return; }
  w.document.write('<p style="font-family:Arial;padding:40px;color:#475569">Génération de l\'ordonnance…</p>');

  const meds = (rx.lines || []).filter((l) => (l.drug || "").trim());
  // Certificat de verification (QR) - best effort (compte medecin requis).
  let token = rx.prescription_number || "";
  let verifyUrl = "";
  let qrDataUrl = "";
  try {
    const res = await axios.post(`${API_URL}/api/prescriptions/issue`, { drugs_count: meds.length }, getAuthConfig());
    token = res.data.token || token;
    verifyUrl = res.data.verify_url || "";
    if (verifyUrl) qrDataUrl = await QRCode.toDataURL(verifyUrl, { width: 140, margin: 1 });
  } catch (e) { /* sans QR si indisponible */ }

  const today = (rx.created_at || new Date().toISOString()).slice(0, 10);
  const todayTime = new Date().toLocaleString("fr-FR");
  const medsHtml = meds.length ? meds.map((l, i) => `
    <div class="medication">
      <div class="med-number">${i + 1}</div>
      <div>
        <div class="med-name">${esc(l.drug || "")}${l.dosage ? " — " + esc(l.dosage) : ""}</div>
        <div class="med-info">${esc([l.posology, l.duration, l.quantity ? "Qté : " + l.quantity : ""].filter(Boolean).join(" · "))}</div>
        ${l.notes ? `<div class="med-info" style="color:#94a3b8;margin-top:2px">${esc(l.notes)}</div>` : ""}
      </div>
    </div>`).join("") : '<p style="color:#999">Aucun médicament</p>';
  const diagnosticHtml = "";  // Le diagnostic n'apparait pas sur l'ordonnance (secret medical) ; il reste dans le dossier.
  const notesHtml = rx.notes ? `<div class="notes-section"><p><strong>Recommandations :</strong></p><p>${esc(rx.notes)}</p></div>` : "";
  const qrSection = qrDataUrl ? `<div class="qr-section"><img src="${qrDataUrl}" alt="QR"/><div class="qr-info"><p><strong>N° ${esc(token)}</strong></p><p>Vérifier l'authenticité : ${esc(verifyUrl)}</p><p style="color:#94a3b8;margin-top:4px">Scannez ce QR code pour vérifier l'ordonnance</p></div></div>` : "";

  const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Ordonnance ${esc(token)}</title><style>
    * { margin:0; padding:0; box-sizing:border-box; }
    body { font-family:Arial,sans-serif; padding:40px; color:#1a1a1a; font-size:14px; }
    .header { display:flex; justify-content:space-between; border-bottom:3px solid #0596DE; padding-bottom:20px; margin-bottom:24px; }
    .estab h2 { color:#0596DE; font-size:20px; } .estab p { color:#555; font-size:13px; line-height:1.6; }
    .header-right { text-align:right; } .logo { color:#0596DE; font-size:18px; font-weight:bold; }
    .ord-num { font-size:11px; color:#555; margin-top:6px; font-family:monospace; }
    .title { text-align:center; font-size:20px; font-weight:bold; letter-spacing:3px; color:#0596DE; margin-bottom:24px; border:2px solid #0596DE; padding:10px; }
    .patient-section { display:flex; justify-content:space-between; margin-bottom:24px; background:#f8fafc; padding:16px; border-radius:8px; }
    .diagnostic { background:#fff3cd; border-left:4px solid #f59e0b; padding:12px; margin-bottom:24px; }
    .medications h3 { font-size:16px; margin-bottom:12px; color:#0596DE; border-bottom:1px solid #e2e8f0; padding-bottom:8px; }
    .medication { display:flex; gap:12px; padding:10px; border:1px solid #e2e8f0; border-radius:8px; margin-bottom:8px; }
    .med-number { width:26px; height:26px; background:#0596DE; color:#fff; border-radius:50%; display:flex; align-items:center; justify-content:center; font-weight:bold; font-size:12px; flex-shrink:0; }
    .med-name { font-weight:bold; margin-bottom:4px; } .med-info { color:#555; font-size:12px; }
    .notes-section { background:#f0f9ff; border:1px solid #bae6fd; padding:12px; border-radius:8px; margin-bottom:16px; }
    .qr-section { display:flex; gap:14px; align-items:center; border:1px solid #e2e8f0; border-radius:8px; padding:14px; margin-bottom:16px; background:#f8fafc; }
    .qr-section img { width:90px; height:90px; flex-shrink:0; }
    .qr-info { font-size:11px; color:#555; line-height:1.6; word-break:break-all; } .qr-info strong { color:#0596DE; font-family:monospace; font-size:13px; }
    .footer { display:flex; justify-content:space-between; margin-top:24px; border-top:1px solid #e2e8f0; padding-top:24px; }
    .line { width:200px; border-bottom:2px solid #1a1a1a; margin-bottom:8px; } .tabbibak { font-size:11px; color:#94a3b8; text-align:right; }
    @media print { body { padding:20px; } }
  </style></head><body>
    <div class="header">
      <div class="estab"><h2>${esc(hospitalName || "Etablissement")}</h2>
        ${rx.service_name ? `<p>${esc(rx.service_name)}</p>` : ""}
        <p>Dr. ${esc(rx.doctor_name || "")}</p></div>
      <div class="header-right"><div class="logo">Tabbibak.dz</div><div class="ord-num">N° ${esc(token)}</div></div>
    </div>
    <div class="title">ORDONNANCE MÉDICALE</div>
    <div class="patient-section">
      <div><p><strong>Patient :</strong> ${esc(rx.patient_name || "")}</p></div>
      <div><p><strong>Date :</strong> ${esc(today)}</p></div>
    </div>
    ${diagnosticHtml}
    <div class="medications"><h3>Prescription</h3>${medsHtml}</div>
    ${notesHtml}
    ${qrSection}
    <div class="footer">
      <div><div class="line"></div><p>Signature et cachet du médecin</p><p style="font-weight:bold;margin-top:4px">Dr. ${esc(rx.doctor_name || "")}</p></div>
      <div class="tabbibak"><p>Généré via Tabbibak.dz</p><p>${todayTime}</p></div>
    </div>
  </body></html>`;
  w.document.open(); w.document.write(html); w.document.close(); w.focus();
  setTimeout(() => { w.print(); }, 400);
}

async function _resolveHospitalName(name) {
  if (name) return name;
  try { const me = await axios.get(`${API_URL}/api/sih/me`, getAuthConfig()); return me.data.establishment?.name || ""; } catch (e) { return ""; }
}

const _docHead = (title, hospitalName, sub) => `<div class="header">
  <div class="estab"><h2>${esc(hospitalName || "Etablissement")}</h2>${sub ? `<p>${esc(sub)}</p>` : ""}</div>
  <div class="header-right"><div class="logo">Tabbibak.dz</div></div></div>
  <div class="title">${esc(title)}</div>`;

const _docStyle = `* { margin:0; padding:0; box-sizing:border-box; }
  body { font-family:Arial,sans-serif; padding:40px; color:#1a1a1a; font-size:14px; }
  .header { display:flex; justify-content:space-between; border-bottom:3px solid #0596DE; padding-bottom:20px; margin-bottom:24px; }
  .estab h2 { color:#0596DE; font-size:20px; } .estab p { color:#555; font-size:13px; }
  .logo { color:#0596DE; font-size:18px; font-weight:bold; }
  .title { text-align:center; font-size:18px; font-weight:bold; letter-spacing:2px; color:#0596DE; margin-bottom:22px; border:2px solid #0596DE; padding:10px; }
  .info { background:#f8fafc; padding:14px; border-radius:8px; margin-bottom:18px; display:grid; grid-template-columns:1fr 1fr; gap:6px 24px; font-size:13px; }
  .sec { margin-bottom:16px; } .sec h3 { font-size:14px; color:#0596DE; border-bottom:1px solid #e2e8f0; padding-bottom:6px; margin-bottom:8px; }
  .sec p { font-size:13px; line-height:1.6; white-space:pre-wrap; }
  ul { margin:6px 0 0 18px; font-size:13px; line-height:1.7; }
  .footer { display:flex; justify-content:space-between; margin-top:30px; border-top:1px solid #e2e8f0; padding-top:20px; }
  .line { width:200px; border-bottom:2px solid #1a1a1a; margin-bottom:8px; } .tabbibak { font-size:11px; color:#94a3b8; text-align:right; }
  @media print { body { padding:20px; } }`;

async function printDischarge(adm, hospitalName, patientFile) {
  const w = window.open("", "_blank");
  if (!w) { alert("Autorisez les pop-ups pour imprimer."); return; }
  w.document.write('<p style="font-family:Arial;padding:40px;color:#475569">Génération…</p>');
  hospitalName = await _resolveHospitalName(hospitalName);
  const d = (x) => x ? new Date(x).toLocaleDateString("fr-FR") : "—";
  const todayTime = new Date().toLocaleString("fr-FR");
  const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Compte-rendu d'hospitalisation</title><style>${_docStyle}</style></head><body>
    ${_docHead("COMPTE-RENDU D'HOSPITALISATION", hospitalName, adm.service_name || "")}
    <div class="info">
      <div><strong>Patient :</strong> ${esc(adm.patient_name || "")}</div>
      <div><strong>Service :</strong> ${esc(adm.service_name || "—")}</div>
      <div><strong>Lit :</strong> ${esc(adm.bed_number || "—")}</div>
      <div><strong>Médecin :</strong> ${esc(adm.discharge_by || adm.doctor_name || "—")}</div>
      <div><strong>Entrée :</strong> ${d(adm.admitted_at)}</div>
      <div><strong>Sortie :</strong> ${d(adm.discharged_at)}</div>
    </div>
    ${patientFile ? `<div class="sec"><h3>Antécédents & terrain</h3>
      ${patientFile.medical_history ? `<div><b>Antécédents médicaux :</b> ${esc(patientFile.medical_history)}</div>` : ""}
      ${patientFile.surgical_history ? `<div><b>Antécédents chirurgicaux :</b> ${esc(patientFile.surgical_history)}</div>` : ""}
      ${patientFile.family_history ? `<div><b>Antécédents familiaux :</b> ${esc(patientFile.family_history)}</div>` : ""}
      ${patientFile.allergies ? `<div><b>Allergies :</b> ${esc(patientFile.allergies)}</div>` : ""}
      ${patientFile.current_treatments ? `<div><b>Traitement habituel :</b> ${esc(patientFile.current_treatments)}</div>` : ""}
      ${patientFile.blood_group ? `<div><b>Groupe sanguin :</b> ${esc(patientFile.blood_group)}</div>` : ""}
      ${patientFile.treating_doctor ? `<div><b>Médecin traitant :</b> ${esc(patientFile.treating_doctor)}</div>` : ""}
    </div>` : ""}
    ${patientFile && (patientFile.profession || patientFile.marital_status || patientFile.insurance) ? `<div class="sec"><h3>Contexte</h3>
      ${patientFile.profession ? `<div><b>Profession :</b> ${esc(patientFile.profession)}</div>` : ""}
      ${patientFile.marital_status ? `<div><b>Situation familiale :</b> ${esc(patientFile.marital_status)}</div>` : ""}
      ${patientFile.insurance ? `<div><b>Assurance / couverture :</b> ${esc(patientFile.insurance)}</div>` : ""}
    </div>` : ""}
    ${adm.reason ? `<div class="sec"><h3>Motif d'hospitalisation</h3><p>${esc(adm.reason)}</p></div>` : ""}
    ${adm.entry_mode ? `<div class="sec"><h3>Mode d'entrée</h3><p>${esc(adm.entry_mode)}</p></div>` : ""}
    ${adm.history ? `<div class="sec"><h3>Histoire de la maladie</h3><p>${esc(adm.history)}</p></div>` : ""}
    ${adm.discharge_diagnosis ? `<div class="sec"><h3>Diagnostic de sortie</h3><p>${esc(adm.discharge_diagnosis)}</p></div>` : ""}
    ${adm.stay_summary ? `<div class="sec"><h3>Résumé du séjour</h3><p>${esc(adm.stay_summary)}</p></div>` : ""}
    ${adm.stay_snapshot ? `<div class="sec"><h3>Détail du séjour</h3>
      <p style="color:#64748B;font-size:12px"><b>${adm.stay_snapshot.counts.consultations}</b> consultation(s) · <b>${adm.stay_snapshot.counts.cares}</b> soin(s) · <b>${adm.stay_snapshot.counts.labs}</b> analyse(s) · <b>${adm.stay_snapshot.counts.imaging}</b> imagerie(s) · <b>${adm.stay_snapshot.counts.prescriptions}</b> ordonnance(s)</p>
      ${(adm.stay_snapshot.consultations || []).map(c => { const v = c.vitals || {}; const vit = [v.bp?`TA ${v.bp}`:"", v.pulse?`Pouls ${v.pulse}`:"", v.temperature?`T ${v.temperature}°C`:"", v.spo2?`SpO2 ${v.spo2}%`:"", v.weight?`${v.weight}kg`:"", v.height?`${v.height}cm`:"", (v.pain!==null&&v.pain!==undefined&&v.pain!=="")?`EVA ${v.pain}`:""].filter(Boolean).join(" · "); return `<div style="margin:8px 0;padding:8px 10px;background:#F8FAFC;border-radius:6px"><div style="font-weight:600">Consultation${c.doctor?` — Dr ${esc(c.doctor)}`:""}${c.date?` (${String(c.date).slice(0,10)})`:""}</div>${c.motif?`<div><b>Motif :</b> ${esc(c.motif)}</div>`:""}${vit?`<div><b>Constantes :</b> ${esc(vit)}</div>`:""}${c.exam?`<div><b>Examen clinique :</b> ${esc(c.exam)}</div>`:""}${c.diagnostic?`<div><b>Diagnostic :</b> ${esc(c.diagnostic)}</div>`:""}${c.conduite?`<div><b>Conduite à tenir :</b> ${esc(c.conduite)}</div>`:""}${c.notes?`<div><b>Notes :</b> ${esc(c.notes)}</div>`:""}</div>`; }).join("")}
      ${(adm.stay_snapshot.cares || []).map(c => `<p style="margin:2px 0">• Soin : ${esc(c.description || "—")} (${esc(c.status)})${c.done_by?` — fait par ${esc(c.done_by)}`:""}</p>`).join("")}
      ${(adm.stay_snapshot.labs || []).map(l => { const an = (l.analyses || []).map(a => `${esc(a.name || "?")} : <b>${esc(a.result || "—")}</b>${a.unit?` ${esc(a.unit)}`:""}${a.reference?` (réf. ${esc(a.reference)})`:""}`).join(" ; "); return `<div style="margin:8px 0;padding:8px 10px;background:#F8FAFC;border-radius:6px"><div style="font-weight:600">Analyse${l.date?` (${String(l.date).slice(0,10)})`:""} — ${esc(l.status)}</div>${an?`<div>${an}</div>`:""}${l.reason?`<div><b>Motif :</b> ${esc(l.reason)}</div>`:""}</div>`; }).join("")}
      ${(adm.stay_snapshot.imaging || []).map(im => `<div style="margin:8px 0;padding:8px 10px;background:#F8FAFC;border-radius:6px"><div style="font-weight:600">Imagerie : ${esc(im.modality || "—")} (${esc(im.status)})</div>${im.report?`<div><b>Compte-rendu :</b> ${esc(im.report)}</div>`:""}</div>`).join("")}
    </div>` : ""}
    ${(adm.stay_snapshot && adm.stay_snapshot.events && adm.stay_snapshot.events.filter(e => e.type === "observation").length) ? `<div class="sec"><h3>Observation médicale initiale</h3>${adm.stay_snapshot.events.filter(e => e.type === "observation").map(e => `<div style="margin:6px 0"><p style="margin:0">${esc(e.text)}</p><p style="margin:0;font-size:11px;color:#94A3B8">${esc(e.author || "")}${e.date ? " — " + new Date(e.date).toLocaleString("fr-FR") : ""}</p></div>`).join("")}</div>` : ""}
    ${(adm.stay_snapshot && adm.stay_snapshot.events && adm.stay_snapshot.events.filter(e => e.type !== "observation").length) ? `<div class="sec"><h3>Évolution du séjour</h3>${adm.stay_snapshot.events.filter(e => e.type !== "observation").map(e => `<div style="margin:8px 0;padding:8px 10px;background:#F8FAFC;border-radius:6px"><div style="font-weight:600;font-size:12px;text-transform:capitalize">${esc(e.type || "note")}${e.author ? " — " + esc(e.author) : ""}${e.date ? " (" + new Date(e.date).toLocaleString("fr-FR") + ")" : ""}</div><p style="margin:2px 0 0">${esc(e.text)}</p></div>`).join("")}</div>` : ""}
    ${(adm.stay_snapshot && adm.stay_snapshot.operations && adm.stay_snapshot.operations.length) ? `<div class="sec"><h3>Parcours chirurgical</h3>${adm.stay_snapshot.operations.map(o => `<div style="margin:10px 0;padding:10px 12px;background:#F8FAFC;border-radius:6px"><div style="font-weight:600">${esc(o.procedure || "Intervention")}${o.date ? " — " + new Date(o.date).toLocaleString("fr-FR") : ""}</div>${o.surgeon ? `<div><b>Chirurgien :</b> ${esc(o.surgeon)}</div>` : ""}${o.assistant ? `<div><b>Aide opératoire :</b> ${esc(o.assistant)}</div>` : ""}${o.anesthetist ? `<div><b>Anesthésiste :</b> ${esc(o.anesthetist)}</div>` : ""}${o.anesthesia_type ? `<div><b>Type d'anesthésie :</b> ${esc(o.anesthesia_type)}</div>` : ""}${o.asa_score ? `<div><b>Score ASA :</b> ${esc(o.asa_score)}</div>` : ""}
${o.indication ? `<div><b>Indication opératoire :</b> ${esc(o.indication)}</div>` : ""}${o.preop_diagnosis ? `<div><b>Diagnostic préopératoire :</b> ${esc(o.preop_diagnosis)}</div>` : ""}${o.postop_diagnosis ? `<div><b>Diagnostic postopératoire :</b> ${esc(o.postop_diagnosis)}</div>` : ""}${o.operative_report ? `<div><b>Compte-rendu opératoire :</b> ${esc(o.operative_report)}</div>` : ""}${o.complications ? `<div><b>Complications :</b> ${esc(o.complications)}</div>` : ""}${o.postop_course ? `<div><b>Suites opératoires :</b> ${esc(o.postop_course)}</div>` : ""}${o.pathology ? `<div><b>Anatomopathologie :</b> ${esc(o.pathology)}</div>` : ""}</div>`).join("")}</div>` : ""}
    ${adm.evolution ? `<div class="sec"><h3>Évolution durant le séjour</h3><p>${esc(adm.evolution)}</p></div>` : ""}
    ${adm.secondary_diagnoses ? `<div class="sec"><h3>Diagnostics associés</h3><p>${esc(adm.secondary_diagnoses)}</p></div>` : ""}
    ${adm.discharge_treatment ? `<div class="sec"><h3>Traitement de sortie</h3><p>${esc(adm.discharge_treatment)}</p></div>` : ""}
    ${adm.exams_to_plan ? `<div class="sec"><h3>Examens à prévoir</h3><p>${esc(adm.exams_to_plan)}</p></div>` : ""}
    ${adm.followup ? `<div class="sec"><h3>Rendez-vous de suivi</h3><p>${esc(adm.followup)}</p></div>` : ""}
    ${adm.discharge_mode ? `<div class="sec"><h3>Mode de sortie</h3><p>${esc(adm.discharge_mode)}</p></div>` : ""}
    ${adm.discharge_instructions ? `<div class="sec"><h3>Consignes & traitement de sortie</h3><p>${esc(adm.discharge_instructions)}</p></div>` : ""}
    <div class="footer"><div><div class="line"></div><p>Signature et cachet</p><p style="font-weight:bold;margin-top:4px">${esc(adm.discharge_by || adm.doctor_name || "")}</p></div>
      <div class="tabbibak"><p>Généré via Tabbibak.dz</p><p>${todayTime}</p></div></div>
  </body></html>`;
  w.document.open(); w.document.write(html); w.document.close(); w.focus();
  setTimeout(() => { w.print(); }, 400);
}

async function printExamRequest(req, kind, hospitalName) {
  const w = window.open("", "_blank");
  if (!w) { alert("Autorisez les pop-ups pour imprimer."); return; }
  w.document.write('<p style="font-family:Arial;padding:40px;color:#475569">Génération…</p>');
  hospitalName = await _resolveHospitalName(hospitalName);
  const isLab = kind === "labo";
  if (isLab && req.request_id) { try { const _x = await axios.get(`${API_URL}/api/sih/lab/requests/${req.request_id}`, getAuthConfig()); if (_x.data.request) req = _x.data.request; } catch (e) {} }
  const title = isLab ? "BON D'ANALYSES — LABORATOIRE" : "BON D'EXAMEN — IMAGERIE";
  const d = (x) => x ? new Date(x).toLocaleDateString("fr-FR") : new Date().toLocaleDateString("fr-FR");
  const todayTime = new Date().toLocaleString("fr-FR");
  let body = "";
  if (isLab) {
    const items = (req.analyses || []).map((a) => `<li>${esc(a.name || a.label || a.code || a)}</li>`).join("");
    body = `<div class="sec"><h3>Analyses demandées</h3>${items ? `<ul>${items}</ul>` : '<p>—</p>'}</div>`;
  } else {
    body = `<div class="sec"><h3>Examen demandé</h3><p>${esc(req.exam_type || "—")}${req.region ? " · " + esc(req.region) : ""}</p></div>`;
  }
  const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>${title}</title><style>${_docStyle}</style></head><body>
    ${_docHead(title, hospitalName, req.service_name || "")}
    <div class="info">
      <div><strong>Patient :</strong> ${esc(req.patient_name || "")}</div>
      <div><strong>Date :</strong> ${d(req.created_at || req.requested_at)}</div>
      <div><strong>Prescripteur :</strong> ${esc(req.requested_by || "—")}</div>
      <div><strong>Priorité :</strong> ${req.priority === "urgent" ? "Urgent" : "Normal"}</div>
    </div>
    ${body}
    ${req.reason ? `<div class="sec"><h3>Renseignements cliniques</h3><p>${esc(req.reason)}</p></div>` : ""}
    <div class="footer"><div><div class="line"></div><p>Signature et cachet du médecin</p></div>
      <div class="tabbibak"><p>Généré via Tabbibak.dz</p><p>${todayTime}</p></div></div>
  </body></html>`;
  w.document.open(); w.document.write(html); w.document.close(); w.focus();
  setTimeout(() => { w.print(); }, 400);
}

function OrdonnancesModule({ can }) {
  const [tab, setTab] = useState("list");
  const [rxs, setRxs] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hospitalName, setHospitalName] = useState("");
  const [editRx, setEditRx] = useState(null);      // {prescriptionId} pour voir/modifier
  const [newRx, setNewRx] = useState(null);        // {patientId, patientName} pour creer
  const [editTpl, setEditTpl] = useState(null);
  const [patients, setPatients] = useState([]);
  const [pickPatient, setPickPatient] = useState(false);
  const canTemplate = can("prescription:template");
  const canWrite = can("prescription:write");

  const loadAll = () => {
    setLoading(true);
    Promise.all([
      axios.get(`${API_URL}/api/sih/prescriptions`, getAuthConfig()).then((r) => setRxs(r.data.prescriptions || [])).catch(() => {}),
      axios.get(`${API_URL}/api/sih/prescription-templates`, getAuthConfig()).then((r) => setTemplates(r.data.templates || [])).catch(() => {}),
    ]).finally(() => setLoading(false));
  };
  useEffect(() => {
    loadAll();
    axios.get(`${API_URL}/api/sih/me`, getAuthConfig()).then((r) => setHospitalName(r.data.establishment?.name || "")).catch(() => {});
  }, []);
  useEffect(() => {
    const openFor = (pid, pname) => { if (pid) { setTab("list"); setNewRx({ patientId: pid, patientName: pname || "" }); } };
    const onGoRx = (e) => { if (e.detail) openFor(e.detail.patientId, e.detail.patientName); };
    window.addEventListener("tabbibak:goToPrescription", onGoRx);
    if (window.__rxPending) { openFor(window.__rxPending.patientId, window.__rxPending.patientName); window.__rxPending = null; }
    return () => window.removeEventListener("tabbibak:goToPrescription", onGoRx);
  }, []);

  const openNew = () => {
    axios.get(`${API_URL}/api/sih/patients`, getAuthConfig()).then((r) => { setPatients(r.data.patients || []); setPickPatient(true); }).catch(() => {});
  };
  const delRx = async (rx) => {
    if (!window.confirm(`Supprimer l'ordonnance de ${rx.patient_name || "ce patient"} ?`)) return;
    try { await axios.delete(`${API_URL}/api/sih/prescriptions/${rx.prescription_id}`, getAuthConfig()); loadAll(); }
    catch (e) { alert(e.response?.data?.detail || "Erreur"); }
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: "#0F172A", margin: 0 }}>Ordonnances</h1>
        {tab === "list" && canWrite && <button onClick={openNew} style={{ padding: "9px 16px", background: "#185FA5", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, cursor: "pointer" }}>+ Nouvelle ordonnance</button>}
        {tab === "templates" && canTemplate && <button onClick={() => setEditTpl({})} style={{ padding: "9px 16px", background: "#185FA5", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, cursor: "pointer" }}>+ Nouveau modele</button>}
      </div>

      <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
        {[["list", "Ordonnances"], ["templates", "Modeles"]].map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)} style={{ fontSize: 14, padding: "7px 16px", borderRadius: 8, cursor: "pointer", border: tab === id ? "1px solid #185FA5" : "1px solid #CBD5E1", background: tab === id ? "#185FA5" : "#fff", color: tab === id ? "#fff" : "#475569" }}>{label}</button>
        ))}
      </div>

      {loading ? <div style={{ padding: 30, textAlign: "center", color: "#94A3B8" }}>Chargement...</div> : (
        <div style={{ marginTop: 16, background: "#fff", border: "1px solid #E2E8F0", borderRadius: 12, overflow: "auto" }}>
          {tab === "list" ? (
            rxs.length === 0 ? <div style={{ padding: 30, textAlign: "center", color: "#94A3B8", fontSize: 14 }}>Aucune ordonnance.</div> : (
              <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 720 }}>
                <thead><tr style={{ background: "#F8FAFC", textAlign: "left" }}>{["Date", "N°", "Patient", "Medecin", "Diagnostic", "Action"].map((h) => <th key={h} style={{ padding: "10px 14px", fontSize: 12, color: "#64748B", fontWeight: 600 }}>{h}</th>)}</tr></thead>
                <tbody>
                  {rxs.map((rx) => (
                    <tr key={rx.prescription_id} style={{ borderTop: "1px solid #F1F5F9" }}>
                      <td style={{ padding: "10px 14px", fontSize: 13, color: "#475569" }}>{(rx.created_at || "").slice(0, 10)}</td>
                      <td style={{ padding: "10px 14px", fontSize: 12, color: "#94A3B8" }}>{rx.prescription_number}</td>
                      <td style={{ padding: "10px 14px", fontSize: 14, fontWeight: 500 }}>{rx.patient_name}</td>
                      <td style={{ padding: "10px 14px", fontSize: 13, color: "#475569" }}>{formatDoctorName(rx.doctor_name)}</td>
                      <td style={{ padding: "10px 14px", fontSize: 13, color: "#475569", maxWidth: 220 }}>{rx.diagnosis || "—"}</td>
                      <td style={{ padding: "10px 14px" }}>
                        <span style={{ display: "flex", gap: 10 }}>
                          <button onClick={() => setEditRx({ prescriptionId: rx.prescription_id })} style={{ fontSize: 13, color: "#185FA5", background: "none", border: "none", cursor: "pointer" }}>Voir</button>
                          <button onClick={() => printPrescription(rx, hospitalName)} style={{ fontSize: 13, color: "#185FA5", background: "none", border: "none", cursor: "pointer" }}>PDF</button>
                          {canWrite && <button onClick={() => delRx(rx)} style={{ fontSize: 13, color: "#A32D2D", background: "none", border: "none", cursor: "pointer" }}>Supprimer</button>}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )
          ) : (
            templates.length === 0 ? <div style={{ padding: 30, textAlign: "center", color: "#94A3B8", fontSize: 14 }}>Aucun modele. {canTemplate ? "Cliquez sur « Nouveau modele »." : ""}</div> : (
              <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 560 }}>
                <thead><tr style={{ background: "#F8FAFC", textAlign: "left" }}>{["Nom", "Pathologie", "Medicaments", "Action"].map((h) => <th key={h} style={{ padding: "10px 14px", fontSize: 12, color: "#64748B", fontWeight: 600 }}>{h}</th>)}</tr></thead>
                <tbody>
                  {templates.map((t) => (
                    <tr key={t.template_id} style={{ borderTop: "1px solid #F1F5F9" }}>
                      <td style={{ padding: "10px 14px", fontSize: 14, fontWeight: 500 }}>{t.name}</td>
                      <td style={{ padding: "10px 14px", fontSize: 13, color: "#475569" }}>{t.pathology || "—"}</td>
                      <td style={{ padding: "10px 14px", fontSize: 13, color: "#475569" }}>{(t.lines || []).length} ligne(s)</td>
                      <td style={{ padding: "10px 14px" }}>
                        {canTemplate && <span style={{ display: "flex", gap: 10 }}>
                          <button onClick={() => setEditTpl(t)} style={{ fontSize: 13, color: "#185FA5", background: "none", border: "none", cursor: "pointer" }}>Modifier</button>
                        </span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )
          )}
        </div>
      )}

      {pickPatient && (
        <div onClick={() => setPickPatient(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: 24, overflow: "auto" }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 12, width: "92%", maxWidth: 460, padding: 20, marginTop: 30 }}>
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 10 }}>Choisir un patient</div>
            <div style={{ maxHeight: 360, overflow: "auto" }}>
              {patients.length === 0 ? <div style={{ color: "#94A3B8", fontSize: 13, padding: 10 }}>Aucun patient dans votre perimetre.</div> :
                patients.map((p) => (
                  <button key={p.patient_id} onClick={() => { setPickPatient(false); setNewRx({ patientId: p.patient_id, patientName: `${p.first_name || ""} ${p.last_name || ""}`.trim() }); }} style={{ display: "block", width: "100%", textAlign: "left", padding: "9px 12px", border: "1px solid #E2E8F0", borderRadius: 8, marginBottom: 6, background: "#fff", cursor: "pointer", fontSize: 14 }}>
                    {(p.first_name || "")} {(p.last_name || "")} <span style={{ color: "#94A3B8", fontSize: 12 }}>{p.patient_number}</span>
                  </button>
                ))}
            </div>
          </div>
        </div>
      )}

      {editRx && <PrescriptionModal prescriptionId={editRx.prescriptionId} hospitalName={hospitalName} can={can} onClose={() => setEditRx(null)} onDone={() => { setEditRx(null); loadAll(); }} />}
      {newRx && <PrescriptionModal patientId={newRx.patientId} patientName={newRx.patientName} hospitalName={hospitalName} can={can} onClose={() => setNewRx(null)} onDone={() => { setNewRx(null); loadAll(); }} />}
      {editTpl && <RxTemplateModal template={editTpl.template_id ? editTpl : null} onClose={() => setEditTpl(null)} onDone={() => { setEditTpl(null); loadAll(); }} />}
    </div>
  );
}

const emptyLine = () => ({ drug: "", dosage: "", posology: "", duration: "", quantity: "", notes: "" });

function LineEditor({ lines, setLines, meds }) {
  const upd = (i, k, v) => { const n = [...lines]; n[i] = { ...n[i], [k]: v }; setLines(n); };
  return (
    <div>
      {lines.map((l, i) => (
        <div key={i} style={{ border: "1px solid #E2E8F0", borderRadius: 8, padding: 10, marginBottom: 8 }}>
          <div style={{ display: "flex", gap: 8, marginBottom: 6 }}>
            <input list="meds-dci" style={{ ...rxIS, flex: 2 }} placeholder="Medicament (DCI)" value={l.drug} onChange={(e) => upd(i, "drug", e.target.value)} />
            <input style={{ ...rxIS, flex: 1 }} placeholder="Dosage" value={l.dosage} onChange={(e) => upd(i, "dosage", e.target.value)} />
            <button onClick={() => setLines(lines.filter((_, j) => j !== i))} style={{ background: "none", border: "none", color: "#A32D2D", cursor: "pointer", fontSize: 18 }}>×</button>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <input style={{ ...rxIS, flex: 2 }} placeholder="Posologie (1 cp x3/j)" value={l.posology} onChange={(e) => upd(i, "posology", e.target.value)} />
            <input style={{ ...rxIS, flex: 1 }} placeholder="Duree (7 j)" value={l.duration} onChange={(e) => upd(i, "duration", e.target.value)} />
            <input style={{ ...rxIS, flex: 1 }} placeholder="Qte" value={l.quantity} onChange={(e) => upd(i, "quantity", e.target.value)} />
          </div>
        </div>
      ))}
      <datalist id="meds-dci">{(meds || []).map((m, i) => <option key={i} value={m.dci || m.name} />)}</datalist>
      <button onClick={() => setLines([...lines, emptyLine()])} style={{ fontSize: 13, color: "#185FA5", background: "none", border: "1px dashed #CBD5E1", borderRadius: 8, padding: "8px 12px", cursor: "pointer", width: "100%" }}>+ Ajouter un medicament</button>
    </div>
  );
}

function PrescriptionModal({ patientId, patientName, consultationId, diagnosis, prescriptionId, hospitalName, can, onClose, onDone }) {
  const [diag, setDiag] = useState(diagnosis || "");
  const [notes, setNotes] = useState("");
  const [lines, setLines] = useState([emptyLine()]);
  const [templates, setTemplates] = useState([]);
  const [meds, setMeds] = useState([]);
  const [pName, setPName] = useState(patientName || "");
  const [rxData, setRxData] = useState(null);
  const [loading, setLoading] = useState(!!prescriptionId);
  const [saving, setSaving] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiWarn, setAiWarn] = useState("");
  const [err, setErr] = useState("");
  const canWrite = can("prescription:write");
  const editable = canWrite;
  const [hName, setHName] = useState(hospitalName || "");

  useEffect(() => {
    if (!hospitalName) axios.get(`${API_URL}/api/sih/me`, getAuthConfig()).then((r) => setHName(r.data.establishment?.name || "")).catch(() => {});
    axios.get(`${API_URL}/api/sih/prescription-templates`, getAuthConfig()).then((r) => setTemplates(r.data.templates || [])).catch(() => {});
    axios.get(`${API_URL}/api/sih/pharmacy/medications`, getAuthConfig()).then((r) => setMeds(r.data.medications || [])).catch(() => {});
    if (prescriptionId) {
      axios.get(`${API_URL}/api/sih/prescriptions/${prescriptionId}`, getAuthConfig()).then((r) => {
        const rx = r.data; setRxData(rx); setDiag(rx.diagnosis || ""); setNotes(rx.notes || "");
        setLines((rx.lines && rx.lines.length ? rx.lines : [emptyLine()])); setPName(rx.patient_name || "");
      }).catch(() => setErr("Chargement impossible")).finally(() => setLoading(false));
    }
  }, [prescriptionId]);

  const applyTemplate = (id) => {
    const t = templates.find((x) => x.template_id === id);
    if (t) { setLines((t.lines && t.lines.length ? t.lines.map((l) => ({ ...emptyLine(), ...l })) : [emptyLine()])); if (t.pathology && !diag) setDiag(t.pathology); }
  };

  const aiSuggest = async () => {
    if (!diag.trim()) { setErr("Renseignez le diagnostic pour la suggestion IA."); return; }
    setAiLoading(true); setErr(""); setAiWarn("");
    try {
      const r = await axios.post(`${API_URL}/api/sih/prescriptions/ai-suggest`, { diagnosis: diag, motif: notes || null }, getAuthConfig());
      const sug = (r.data.lines || []).map((l) => ({ ...emptyLine(), ...l }));
      if (sug.length) setLines(sug);
      setAiWarn(r.data.warning || "Proposition TabbibAI — verifiez et ajustez avant de valider.");
    } catch (e) { setErr(e.response?.data?.detail || "Suggestion IA indisponible"); }
    finally { setAiLoading(false); }
  };

  const save = async () => {
    setSaving(true); setErr("");
    const payload = { diagnosis: diag || null, notes: notes || null, lines: lines.filter((l) => (l.drug || "").trim()) };
    try {
      if (prescriptionId) await axios.put(`${API_URL}/api/sih/prescriptions/${prescriptionId}`, payload, getAuthConfig());
      else await axios.post(`${API_URL}/api/sih/prescriptions`, { patient_id: patientId, consultation_id: consultationId || null, ...payload }, getAuthConfig());
      onDone();
    } catch (e) { setErr(e.response?.data?.detail || "Erreur"); setSaving(false); }
  };

  const doPrint = () => {
    const rx = rxData || { prescription_number: "", created_at: new Date().toISOString(), patient_name: pName, doctor_name: "", service_name: "", diagnosis: diag, notes, lines };
    printPrescription(rx, hName || hospitalName);
  };

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1100, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: 24, overflow: "auto" }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 12, width: "94%", maxWidth: 680, padding: 22, marginTop: 12 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ fontSize: 17, fontWeight: 700 }}>{prescriptionId ? "Ordonnance" : "Nouvelle ordonnance"}</div>
          <button onClick={onClose} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#94A3B8" }}>×</button>
        </div>
        <div style={{ fontSize: 13, color: "#64748B", marginBottom: 12 }}>{pName}</div>
        {err && <div style={{ background: "#FCEBEB", border: "1px solid #F7C1C1", color: "#A32D2D", borderRadius: 8, padding: "8px 12px", marginBottom: 12, fontSize: 12 }}>{err}</div>}
        {loading ? <div style={{ padding: 20, color: "#94A3B8" }}>Chargement...</div> : (
          <>
            <div style={{ marginBottom: 10 }}><label style={{ fontSize: 12, color: "#64748B", display: "block", marginBottom: 4 }}>Diagnostic</label><input style={rxIS} value={diag} onChange={(e) => setDiag(e.target.value)} disabled={!editable} /></div>

            {editable && (
              <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap", alignItems: "center" }}>
                <select onChange={(e) => { applyTemplate(e.target.value); e.target.value = ""; }} defaultValue="" style={{ ...rxIS, width: "auto", flex: 1, minWidth: 180 }}>
                  <option value="" disabled>Charger un modele…</option>
                  {templates.map((t) => <option key={t.template_id} value={t.template_id}>{t.name}{t.pathology ? ` (${t.pathology})` : ""}</option>)}
                </select>
                <button onClick={aiSuggest} disabled={aiLoading} style={{ fontSize: 13, padding: "8px 14px", borderRadius: 8, border: "1px solid #7C3AED", background: aiLoading ? "#EDE9FE" : "#F5F3FF", color: "#6D28D9", cursor: "pointer", fontWeight: 600 }}>{aiLoading ? "TabbibAI réfléchit…" : "✨ Suggestion TabbibAI"}</button>
              </div>
            )}
            {aiWarn && <div style={{ background: "#FEF3C7", border: "1px solid #FCD34D", color: "#92400E", borderRadius: 8, padding: "8px 12px", marginBottom: 12, fontSize: 12 }}>⚠️ {aiWarn}</div>}

            <LineEditor lines={lines} setLines={editable ? setLines : () => {}} meds={meds} />

            <div style={{ marginTop: 12 }}><label style={{ fontSize: 12, color: "#64748B", display: "block", marginBottom: 4 }}>Remarques</label><textarea style={{ ...rxIS, minHeight: 50, resize: "vertical" }} value={notes} onChange={(e) => setNotes(e.target.value)} disabled={!editable} /></div>

            <div style={{ display: "flex", gap: 8, justifyContent: "space-between", marginTop: 16, flexWrap: "wrap" }}>
              <button onClick={doPrint} style={{ fontSize: 13, padding: "9px 16px", borderRadius: 8, border: "1px solid #185FA5", background: "#fff", color: "#185FA5", cursor: "pointer" }}>🖨 PDF / Imprimer</button>
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={onClose} style={{ fontSize: 13, padding: "9px 16px", borderRadius: 8, border: "1px solid #CBD5E1", background: "#fff", color: "#64748B", cursor: "pointer" }}>Fermer</button>
                {editable && <button onClick={save} disabled={saving} style={{ fontSize: 13, padding: "9px 16px", borderRadius: 8, border: "none", background: "#185FA5", color: "#fff", cursor: "pointer" }}>{saving ? "..." : "Enregistrer"}</button>}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function RxTemplateModal({ template, onClose, onDone }) {
  const [name, setName] = useState(template?.name || "");
  const [pathology, setPathology] = useState(template?.pathology || "");
  const [lines, setLines] = useState(template?.lines?.length ? template.lines.map((l) => ({ ...emptyLine(), ...l })) : [emptyLine()]);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const save = async () => {
    if (!name.trim()) { setErr("Nom requis."); return; }
    setSaving(true); setErr("");
    const payload = { name, pathology: pathology || null, lines: lines.filter((l) => (l.drug || "").trim()) };
    try {
      if (template?.template_id) await axios.put(`${API_URL}/api/sih/prescription-templates/${template.template_id}`, payload, getAuthConfig());
      else await axios.post(`${API_URL}/api/sih/prescription-templates`, payload, getAuthConfig());
      onDone();
    } catch (e) { setErr(e.response?.data?.detail || "Erreur"); setSaving(false); }
  };
  const del = async () => {
    if (!template?.template_id) return;
    if (!window.confirm("Supprimer ce modele ?")) return;
    try { await axios.delete(`${API_URL}/api/sih/prescription-templates/${template.template_id}`, getAuthConfig()); onDone(); }
    catch (e) { setErr("Erreur"); }
  };

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1100, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: 24, overflow: "auto" }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 12, width: "94%", maxWidth: 640, padding: 22, marginTop: 12 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <div style={{ fontSize: 17, fontWeight: 700 }}>{template?.template_id ? "Modifier le modele" : "Nouveau modele"}</div>
          <button onClick={onClose} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#94A3B8" }}>×</button>
        </div>
        {err && <div style={{ background: "#FCEBEB", border: "1px solid #F7C1C1", color: "#A32D2D", borderRadius: 8, padding: "8px 12px", marginBottom: 12, fontSize: 12 }}>{err}</div>}
        <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
          <input style={{ ...rxIS, flex: 1 }} placeholder="Nom du modele" value={name} onChange={(e) => setName(e.target.value)} />
          <input style={{ ...rxIS, flex: 1 }} placeholder="Pathologie (ex. Angine)" value={pathology} onChange={(e) => setPathology(e.target.value)} />
        </div>
        <LineEditor lines={lines} setLines={setLines} meds={[]} />
        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 16 }}>
          {template?.template_id ? <button onClick={del} style={{ fontSize: 13, color: "#A32D2D", background: "none", border: "none", cursor: "pointer" }}>Supprimer</button> : <span />}
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={onClose} style={{ fontSize: 13, padding: "9px 16px", borderRadius: 8, border: "1px solid #CBD5E1", background: "#fff", color: "#64748B", cursor: "pointer" }}>Annuler</button>
            <button onClick={save} disabled={saving} style={{ fontSize: 13, padding: "9px 16px", borderRadius: 8, border: "none", background: "#185FA5", color: "#fff", cursor: "pointer" }}>{saving ? "..." : "Enregistrer"}</button>
          </div>
        </div>
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────
// Module Facturation : factures, encaissements, tarifs, PDF.
// ─────────────────────────────────────────────────────────────

const dzd = (n) => `${Number(n || 0).toLocaleString("fr-FR")} DZD`;
const INV_STATUS = { draft: ["Brouillon", "#64748B", "#F1F5F9"], issued: ["Émise", "#185FA5", "#E6F0FA"], partial: ["Part. payée", "#B45309", "#FEF3C7"], paid: ["Payée", "#3B6D11", "#EAF3DE"], cancelled: ["Annulée", "#A32D2D", "#FCEBEB"] };
const PAY_METHODS = { espece: "Espèces", carte: "Carte", cheque: "Chèque", virement: "Virement" };
const biS = { width: "100%", padding: "8px 10px", border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 13, boxSizing: "border-box", background: "#fff" };

async function printOperativeReport(op, hospitalName) {
  const w = window.open("", "_blank");
  if (!w) { alert("Autorisez les pop-ups pour imprimer."); return; }
  w.document.write('<p style="font-family:Arial;padding:40px;color:#475569">Génération…</p>');
  hospitalName = await _resolveHospitalName(hospitalName);
  const d = (x) => x ? new Date(x).toLocaleString("fr-FR") : "—";
  const todayTime = new Date().toLocaleString("fr-FR");
  const row = (label, val) => val ? `<div><strong>${label} :</strong> ${esc(val)}</div>` : "";
  const sec = (title, val) => val ? `<div class="sec"><h3>${title}</h3><p>${esc(val)}</p></div>` : "";
  const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Compte-rendu opératoire</title><style>${_docStyle}</style></head><body>
    ${_docHead("COMPTE-RENDU OPERATOIRE", hospitalName, "")}
    <div class="info">
      <div><strong>Patient :</strong> ${esc(op.patient_name || "—")}</div>
      <div><strong>Intervention :</strong> ${esc(op.procedure || "—")}</div>
      <div><strong>Date :</strong> ${d(op.start_at)}</div>
      ${row("Chirurgien", op.surgeon)}
      ${row("Aide opératoire", op.assistant)}
      ${row("Anesthésiste", op.anesthetist)}
      ${row("Type d'anesthésie", op.anesthesia_type)}
      ${row("Score ASA", op.asa_score)}
    </div>
    ${sec("Indication opératoire", op.indication)}
    ${sec("Diagnostic pré-opératoire", op.preop_diagnosis)}
    ${sec("Diagnostic post-opératoire", op.postop_diagnosis)}
    ${sec("Compte-rendu du geste opératoire", op.operative_report)}
    ${sec("Complications per-opératoires", op.complications)}
    ${sec("Suites opératoires immédiates", op.postop_course)}
    ${sec("Anatomopathologie / prélèvements", op.pathology)}
    <div class="footer"><div><div class="line"></div>Signature et cachet du chirurgien</div>
      <div class="tabbibak">Généré via Tabbibak.dz<br>${todayTime}</div></div>
  </body></html>`;
  w.document.open(); w.document.write(html); w.document.close();
  setTimeout(() => { w.print(); }, 400);
}
async function printPathologyReport(spec, hospitalName) {
  const w = window.open("", "_blank");
  if (!w) { alert("Autorisez les pop-ups pour imprimer."); return; }
  w.document.write('<p style="font-family:Arial;padding:40px;color:#475569">Génération…</p>');
  hospitalName = await _resolveHospitalName(hospitalName);
  const d = (x) => x ? new Date(x).toLocaleString("fr-FR") : "—";
  const todayTime = new Date().toLocaleString("fr-FR");
  const sec = (title, val) => val ? `<div class="sec"><h3>${title}</h3><p>${esc(val)}</p></div>` : "";
  const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Compte-rendu anatomopathologique</title><style>${_docStyle}</style></head><body>
    ${_docHead("COMPTE-RENDU ANATOMOPATHOLOGIQUE", hospitalName, "")}
    <div class="info">
      <div><strong>N° d'enregistrement :</strong> ${esc(spec.accession || "—")}</div>
      <div><strong>Patient :</strong> ${esc(spec.patient_name || "—")}</div>
      <div><strong>Nature :</strong> ${esc(spec.specimen_nature || "—")}</div>
      <div><strong>Origine :</strong> ${esc(spec.origin || "—")}</div>
      <div><strong>Reçu le :</strong> ${d(spec.created_at)}</div>
      ${spec.validated_at ? `<div><strong>Validé le :</strong> ${d(spec.validated_at)}</div>` : ""}
    </div>
    ${sec("Renseignements cliniques", spec.clinical_info)}
    ${sec("Examen macroscopique", spec.macroscopy)}
    ${sec("Examen microscopique", spec.microscopy)}
    ${spec.conclusion ? `<div class="sec"><h3>Conclusion diagnostique</h3><p style="font-weight:600">${esc(spec.conclusion)}</p></div>` : ""}
    ${spec.icd_o ? `<div class="sec"><h3>Code CIM-O</h3><p>${esc(spec.icd_o)}</p></div>` : ""}
    <div class="footer"><div><div class="line"></div>Signature du pathologiste${spec.validated_by ? `<br><span style="font-size:11px;color:#94a3b8">${esc(spec.validated_by)}</span>` : ""}</div>
      <div class="tabbibak">Généré via Tabbibak.dz<br>${todayTime}</div></div>
  </body></html>`;
  w.document.open(); w.document.write(html); w.document.close();
  setTimeout(() => { w.print(); }, 400);
}
async function printImagingReport(req, hospitalName) {
  const w = window.open("", "_blank");
  if (!w) { alert("Autorisez les pop-ups pour imprimer."); return; }
  w.document.write('<p style="font-family:Arial;padding:40px;color:#475569">Génération…</p>');
  hospitalName = await _resolveHospitalName(hospitalName);
  if (req.request_id) { try { const _x = await axios.get(`${API_URL}/api/sih/imaging/requests/${req.request_id}`, getAuthConfig()); if (_x.data.request) req = _x.data.request; } catch (e) {} }
  const d = (x) => x ? new Date(x).toLocaleString("fr-FR") : "—";
  const todayTime = new Date().toLocaleString("fr-FR");
  const sec = (title, val) => val ? `<div class="sec"><h3>${title}</h3><p>${esc(val)}</p></div>` : "";
  const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Compte-rendu d'imagerie</title><style>${_docStyle}</style></head><body>
    ${_docHead("COMPTE-RENDU D'IMAGERIE", hospitalName, req.service_name || "")}
    <div class="info">
      <div><strong>Patient :</strong> ${esc(req.patient_name || "—")}</div>
      <div><strong>Examen :</strong> ${esc(req.exam_type || "—")}${req.region ? " · " + esc(req.region) : ""}</div>
      <div><strong>Date :</strong> ${d(req.created_at || req.requested_at)}</div>
      <div><strong>Prescripteur :</strong> ${esc(req.requested_by || "—")}</div>
    </div>
    ${sec("Observations", req.findings)}
    ${req.conclusion ? `<div class="sec"><h3>Conclusion</h3><p style="font-weight:600">${esc(req.conclusion)}</p></div>` : ""}
    ${!req.findings && !req.conclusion ? '<div class="sec"><p style="color:#94a3b8">Compte-rendu non encore rédigé.</p></div>' : ""}
    <div class="footer"><div><div class="line"></div>Signature du radiologue</div>
      <div class="tabbibak">Généré via Tabbibak.dz<br>${todayTime}</div></div>
  </body></html>`;
  w.document.open(); w.document.write(html); w.document.close();
  setTimeout(() => { w.print(); }, 400);
}
async function printLabResults(req, hospitalName) {
  const w = window.open("", "_blank");
  if (!w) { alert("Autorisez les pop-ups pour imprimer."); return; }
  w.document.write('<p style="font-family:Arial;padding:40px;color:#475569">Génération…</p>');
  hospitalName = await _resolveHospitalName(hospitalName);
  const d = (x) => x ? new Date(x).toLocaleString("fr-FR") : "—";
  const todayTime = new Date().toLocaleString("fr-FR");
  try { const _x = await axios.get(`${API_URL}/api/sih/lab/requests/${req.request_id}`, getAuthConfig()); if (_x.data.request) req = _x.data.request; } catch (e) {}
  const rows = (req.analyses || []).map((a) => {
    const val = a.result != null && a.result !== "" ? a.result : "—";
    const abnormal = labFlag(a.name, a.result) !== null;
    return `<tr><td>${esc(a.name || "")}</td><td style="text-align:center;font-weight:600;color:${abnormal ? "#A32D2D" : "#1a1a1a"}">${esc(val)}${abnormal ? " ⚠" : ""}</td><td style="text-align:center;color:#555">${esc(a.unit || "")}</td><td style="color:#555">${esc(a.reference || "—")}</td></tr>`;
  }).join("");
  const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Résultats de laboratoire</title><style>${_docStyle}
    table { width:100%; border-collapse:collapse; margin-top:8px; font-size:13px; }
    th { background:#f8fafc; text-align:left; padding:8px 10px; color:#555; border-bottom:2px solid #e2e8f0; }
    td { padding:8px 10px; border-bottom:1px solid #f1f5f9; }</style></head><body>
    ${_docHead("RESULTATS D'ANALYSES", hospitalName, "")}
    <div class="info">
      <div><strong>Patient :</strong> ${esc(req.patient_name || "—")}</div>
      <div><strong>Prescripteur :</strong> ${esc(req.prescriber || "—")}</div>
      <div><strong>Prélevé le :</strong> ${d(req.created_at)}</div>
      <div><strong>Statut :</strong> ${esc(req.status || "—")}</div>
    </div>
    <table><thead><tr><th>Analyse</th><th style="text-align:center">Résultat</th><th style="text-align:center">Unité</th><th>Valeurs de référence</th></tr></thead>
    <tbody>${rows || '<tr><td colspan="4" style="text-align:center;color:#94a3b8">Aucun résultat</td></tr>'}</tbody></table>
    <p style="font-size:11px;color:#94a3b8;margin-top:10px">⚠ = valeur hors des normes de référence</p>
    <div class="footer"><div><div class="line"></div>Signature du biologiste</div>
      <div class="tabbibak">Généré via Tabbibak.dz<br>${todayTime}</div></div>
  </body></html>`;
  w.document.open(); w.document.write(html); w.document.close();
  setTimeout(() => { w.print(); }, 400);
}
async function printInvoice(inv, hospitalName) {
  const w = window.open("", "_blank");
  if (!w) { alert("Autorisez les pop-ups pour imprimer."); return; }
  w.document.write('<p style="font-family:Arial;padding:40px;color:#475569">Génération…</p>');
  // Correctif L-PDF-STALE : ce PDF partait des donnees deja en memoire dans
  // le navigateur (l'etat local du composant), qui peuvent etre perimees
  // (un autre paiement enregistre entre-temps dans un autre onglet/par un
  // autre membre du personnel) — rien ne garantissait que le document
  // imprime corresponde a l'enregistrement reellement en base. Meme
  // correctif deja applique aux bulletins labo/imagerie (printLabResults,
  // printImagingReport) : on relit l'enregistrement officiel juste avant
  // de generer le document.
  if (inv.invoice_id) {
    try { const _x = await axios.get(`${API_URL}/api/sih/billing/invoices/${inv.invoice_id}`, getAuthConfig()); inv = _x.data; } catch (e) {}
  }
  hospitalName = await _resolveHospitalName(hospitalName);
  const rows = (inv.lines || []).map((l) => `<tr><td style="padding:7px 8px;border-bottom:1px solid #eee">${esc(l.label)}</td><td style="padding:7px 8px;border-bottom:1px solid #eee;text-align:center">${esc(l.quantity)}</td><td style="padding:7px 8px;border-bottom:1px solid #eee;text-align:right">${dzd(l.unit_price)}</td><td style="padding:7px 8px;border-bottom:1px solid #eee;text-align:right">${dzd(l.total)}</td></tr>`).join("");
  const paid = inv.paid_amount || 0; const total = inv.total_amount || 0; const bal = total - paid;
  const todayTime = new Date().toLocaleString("fr-FR");
  const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Facture ${esc(inv.invoice_number)}</title><style>${_docStyle}
    table{width:100%;border-collapse:collapse;font-size:13px;margin-top:6px}
    th{background:#F1F5F9;text-align:left;padding:7px 8px;font-size:12px}
    .totals{margin-top:14px;margin-left:auto;width:260px;font-size:13px}
    .totals div{display:flex;justify-content:space-between;padding:4px 0}
    .totals .grand{border-top:2px solid #0596DE;margin-top:4px;padding-top:6px;font-weight:bold;font-size:15px;color:#0596DE}
  </style></head><body>
    ${_docHead("FACTURE", hospitalName, "")}
    <div class="info"><div><strong>Patient :</strong> ${esc(inv.patient_name || "")}</div>
      <div><strong>Facture N° :</strong> ${esc(inv.invoice_number || "")}</div>
      <div><strong>Date :</strong> ${(inv.created_at || "").slice(0, 10)}</div>
      <div><strong>Statut :</strong> ${esc((INV_STATUS[inv.status] || [inv.status])[0])}</div></div>
    <table><thead><tr><th>Désignation</th><th style="text-align:center">Qté</th><th style="text-align:right">P.U.</th><th style="text-align:right">Total</th></tr></thead>
      <tbody>${rows || '<tr><td colspan="4" style="padding:10px;color:#999">Aucune ligne</td></tr>'}</tbody></table>
    <div class="totals">
      <div><span>Total</span><span>${dzd(total)}</span></div>
      <div><span>Payé</span><span>${dzd(paid)}</span></div>
      <div class="grand"><span>Reste à payer</span><span>${dzd(bal)}</span></div>
    </div>
    ${inv.notes ? `<div class="sec" style="margin-top:18px"><h3>Remarques</h3><p>${esc(inv.notes)}</p></div>` : ""}
    <div class="footer"><div><div class="line"></div><p>Signature et cachet</p></div>
      <div class="tabbibak"><p>Généré via Tabbibak.dz</p><p>${todayTime}</p></div></div>
  </body></html>`;
  w.document.open(); w.document.write(html); w.document.close(); w.focus();
  setTimeout(() => { w.print(); }, 400);
}

async function printReceipt(inv, payment, hospitalName) {
  const w = window.open("", "_blank");
  if (!w) { alert("Autorisez les pop-ups."); return; }
  w.document.write('<p style="font-family:Arial;padding:40px;color:#475569">Génération…</p>');
  // Correctif L-PDF-STALE (voir printInvoice) : relire la facture pour que
  // le total/reste-a-payer affiches sur le recu soient ceux reellement en
  // base au moment de l'impression, pas un etat local potentiellement
  // perime (le recu genere juste apres un encaissement, lui, part deja
  // d'une donnee fraichement relue - voir addPayment - mais une
  // reimpression ulterieure depuis l'historique des paiements ne l'etait
  // pas).
  if (inv.invoice_id) {
    try {
      const _x = await axios.get(`${API_URL}/api/sih/billing/invoices/${inv.invoice_id}`, getAuthConfig());
      inv = _x.data;
      const freshPayment = (inv.payments || []).find((p) => p.payment_id === payment.payment_id);
      if (freshPayment) payment = freshPayment;
    } catch (e) {}
  }
  hospitalName = await _resolveHospitalName(hospitalName);
  const bal = (inv.total_amount || 0) - (inv.paid_amount || 0);
  const todayTime = new Date().toLocaleString("fr-FR");
  const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Reçu</title><style>${_docStyle}</style></head><body>
    ${_docHead("REÇU DE PAIEMENT", hospitalName, "")}
    <div class="info"><div><strong>Patient :</strong> ${esc(inv.patient_name || "")}</div>
      <div><strong>Facture :</strong> ${esc(inv.invoice_number || "")}</div>
      <div><strong>Date :</strong> ${(payment.date || "").slice(0, 10)}</div>
      <div><strong>Mode :</strong> ${esc(PAY_METHODS[payment.method] || payment.method)}</div></div>
    <div class="sec"><h3>Montant encaissé</h3><p style="font-size:22px;font-weight:bold;color:#0596DE">${dzd(payment.amount)}</p></div>
    <div class="info"><div><strong>Total facture :</strong> ${dzd(inv.total_amount)}</div>
      <div><strong>Reste à payer :</strong> ${dzd(bal)}</div></div>
    <div class="footer"><div><div class="line"></div><p>Signature et cachet</p></div>
      <div class="tabbibak"><p>Généré via Tabbibak.dz</p><p>${todayTime}</p></div></div>
  </body></html>`;
  w.document.open(); w.document.write(html); w.document.close(); w.focus();
  setTimeout(() => { w.print(); }, 400);
}

function BillingModule({ can }) {
  const [tab, setTab] = useState("invoices");
  const [invoices, setInvoices] = useState([]);
  const [tarifs, setTarifs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hospitalName, setHospitalName] = useState("");
  const [openInv, setOpenInv] = useState(null);   // {invoiceId} ou {new:true}
  const [editTarif, setEditTarif] = useState(null);
  const canTarif = can("billing:tarif");
  const canWrite = can("billing:write");

  const load = () => {
    setLoading(true);
    Promise.all([
      axios.get(`${API_URL}/api/sih/billing/invoices`, getAuthConfig()).then((r) => setInvoices(r.data.invoices || [])).catch(() => {}),
      axios.get(`${API_URL}/api/sih/billing/tarifs`, getAuthConfig()).then((r) => setTarifs(r.data.tarifs || [])).catch(() => {}),
    ]).finally(() => setLoading(false));
  };
  useEffect(() => { load(); axios.get(`${API_URL}/api/sih/me`, getAuthConfig()).then((r) => setHospitalName(r.data.establishment?.name || "")).catch(() => {}); }, []);

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: "#0F172A", margin: 0 }}>Facturation</h1>
        {tab === "invoices" && canWrite && <button onClick={() => setOpenInv({ new: true })} style={{ padding: "9px 16px", background: "#185FA5", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, cursor: "pointer" }}>+ Nouvelle facture</button>}
        {tab === "tarifs" && canTarif && <button onClick={() => setEditTarif({})} style={{ padding: "9px 16px", background: "#185FA5", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, cursor: "pointer" }}>+ Nouveau tarif</button>}
      </div>

      <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
        {[["invoices", "Factures"], ["tarifs", "Catalogue de tarifs"]].map(([id, lbl]) => (
          <button key={id} onClick={() => setTab(id)} style={{ fontSize: 14, padding: "7px 16px", borderRadius: 8, cursor: "pointer", border: tab === id ? "1px solid #185FA5" : "1px solid #CBD5E1", background: tab === id ? "#185FA5" : "#fff", color: tab === id ? "#fff" : "#475569" }}>{lbl}</button>
        ))}
      </div>

      {loading ? <div style={{ padding: 30, textAlign: "center", color: "#94A3B8" }}>Chargement...</div> : (
        <div style={{ marginTop: 16, background: "#fff", border: "1px solid #E2E8F0", borderRadius: 12, overflow: "auto" }}>
          {tab === "invoices" ? (
            invoices.length === 0 ? <div style={{ padding: 30, textAlign: "center", color: "#94A3B8", fontSize: 14 }}>Aucune facture.</div> : (
              <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 780 }}>
                <thead><tr style={{ background: "#F8FAFC", textAlign: "left" }}>{["Date", "N°", "Patient", "Total", "Payé", "Reste", "Statut", ""].map((h) => <th key={h} style={{ padding: "10px 14px", fontSize: 12, color: "#64748B", fontWeight: 600 }}>{h}</th>)}</tr></thead>
                <tbody>
                  {invoices.map((inv) => { const st = INV_STATUS[inv.status] || [inv.status, "#64748B", "#F1F5F9"]; const bal = (inv.total_amount || 0) - (inv.paid_amount || 0); return (
                    <tr key={inv.invoice_id} style={{ borderTop: "1px solid #F1F5F9" }}>
                      <td style={{ padding: "10px 14px", fontSize: 13, color: "#475569" }}>{(inv.created_at || "").slice(0, 10)}</td>
                      <td style={{ padding: "10px 14px", fontSize: 12, color: "#94A3B8" }}>{inv.invoice_number}</td>
                      <td style={{ padding: "10px 14px", fontSize: 14, fontWeight: 500 }}>{inv.patient_name}</td>
                      <td style={{ padding: "10px 14px", fontSize: 13 }}>{dzd(inv.total_amount)}</td>
                      <td style={{ padding: "10px 14px", fontSize: 13, color: "#3B6D11" }}>{dzd(inv.paid_amount)}</td>
                      <td style={{ padding: "10px 14px", fontSize: 13, color: bal > 0 ? "#B45309" : "#64748B" }}>{dzd(bal)}</td>
                      <td style={{ padding: "10px 14px" }}><span style={{ fontSize: 12, padding: "3px 10px", borderRadius: 12, background: st[2], color: st[1] }}>{st[0]}</span></td>
                      <td style={{ padding: "10px 14px" }}><button onClick={() => setOpenInv({ invoiceId: inv.invoice_id })} style={{ fontSize: 13, color: "#185FA5", background: "none", border: "none", cursor: "pointer" }}>Ouvrir</button></td>
                    </tr>
                  ); })}
                </tbody>
              </table>
            )
          ) : (
            tarifs.length === 0 ? <div style={{ padding: 30, textAlign: "center", color: "#94A3B8", fontSize: 14 }}>Aucun tarif. {canTarif ? "Cliquez sur « Nouveau tarif »." : ""}</div> : (
              <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 520 }}>
                <thead><tr style={{ background: "#F8FAFC", textAlign: "left" }}>{["Catégorie", "Prestation", "Prix", ""].map((h) => <th key={h} style={{ padding: "10px 14px", fontSize: 12, color: "#64748B", fontWeight: 600 }}>{h}</th>)}</tr></thead>
                <tbody>
                  {tarifs.map((t) => (
                    <tr key={t.tarif_id} style={{ borderTop: "1px solid #F1F5F9" }}>
                      <td style={{ padding: "10px 14px", fontSize: 13, color: "#64748B" }}>{t.category || "—"}</td>
                      <td style={{ padding: "10px 14px", fontSize: 14, fontWeight: 500 }}>{t.name}{t.code ? <span style={{ fontSize: 12, color: "#94A3B8" }}> · {t.code}</span> : null}</td>
                      <td style={{ padding: "10px 14px", fontSize: 13 }}>{dzd(t.price)}</td>
                      <td style={{ padding: "10px 14px" }}>{canTarif && <button onClick={() => setEditTarif(t)} style={{ fontSize: 13, color: "#185FA5", background: "none", border: "none", cursor: "pointer" }}>Modifier</button>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )
          )}
        </div>
      )}

      {openInv && <InvoiceModal invoiceId={openInv.invoiceId} tarifs={tarifs} hospitalName={hospitalName} can={can} onClose={() => setOpenInv(null)} onDone={() => { setOpenInv(null); load(); }} />}
      {editTarif && <TarifModal tarif={editTarif.tarif_id ? editTarif : null} onClose={() => setEditTarif(null)} onDone={() => { setEditTarif(null); load(); }} />}
    </div>
  );
}

const emptyInvLine = () => ({ label: "", quantity: 1, unit_price: 0 });

function InvoiceModal({ invoiceId, tarifs, hospitalName, can, onClose, onDone }) {
  const [inv, setInv] = useState(null);
  const [patients, setPatients] = useState([]);
  const [patientId, setPatientId] = useState("");
  const [lines, setLines] = useState([emptyInvLine()]);
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(!!invoiceId);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [pay, setPay] = useState({ amount: "", method: "espece", note: "" });
  const canWrite = can("billing:write");

  useEffect(() => {
    if (invoiceId) {
      axios.get(`${API_URL}/api/sih/billing/invoices/${invoiceId}`, getAuthConfig()).then((r) => { setInv(r.data); setNotes(r.data.notes || ""); setLines(r.data.lines?.length ? r.data.lines : [emptyInvLine()]); }).catch(() => setErr("Chargement impossible")).finally(() => setLoading(false));
    } else {
      axios.get(`${API_URL}/api/sih/patients`, getAuthConfig()).then((r) => setPatients(r.data.patients || [])).catch(() => {});
    }
  }, [invoiceId]);

  const total = lines.reduce((s, l) => s + (Number(l.quantity) || 0) * (Number(l.unit_price) || 0), 0);
  const editable = !invoiceId || (inv && ["draft", "issued", "partial"].includes(inv.status));
  const updLine = (i, k, v) => { const n = [...lines]; n[i] = { ...n[i], [k]: v }; setLines(n); };
  const addTarif = (id) => { const t = tarifs.find((x) => x.tarif_id === id); if (t) setLines([...lines.filter((l) => l.label), { label: t.name, quantity: 1, unit_price: t.price }]); };

  const saveLines = async () => {
    setSaving(true); setErr("");
    const payload = { lines: lines.filter((l) => (l.label || "").trim()).map((l) => ({ label: l.label, quantity: Number(l.quantity) || 0, unit_price: Number(l.unit_price) || 0 })), notes: notes || null };
    try {
      if (invoiceId) { await axios.put(`${API_URL}/api/sih/billing/invoices/${invoiceId}`, payload, getAuthConfig()); const r = await axios.get(`${API_URL}/api/sih/billing/invoices/${invoiceId}`, getAuthConfig()); setInv(r.data); }
      else { if (!patientId) { setErr("Choisissez un patient"); setSaving(false); return; } await axios.post(`${API_URL}/api/sih/billing/invoices`, { patient_id: patientId, ...payload }, getAuthConfig()); onDone(); }
    } catch (e) {
      // Correctif L-BILLING-BOUNDS : une quantite/un prix hors bornes
      // declenche desormais une erreur de validation FastAPI dont "detail"
      // est un TABLEAU (pas une chaine) — l'affichage direct produisait
      // "[object Object]" ou plantait le rendu. Meme filtre typeof deja
      // utilise partout ailleurs dans ce fichier.
      setErr(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Erreur (verifiez les quantites et prix saisis)");
    }
    finally { setSaving(false); }
  };
  const issue = async () => { try { await axios.post(`${API_URL}/api/sih/billing/invoices/${invoiceId}/issue`, {}, getAuthConfig()); const r = await axios.get(`${API_URL}/api/sih/billing/invoices/${invoiceId}`, getAuthConfig()); setInv(r.data); } catch (e) { setErr(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Erreur"); } };
  const cancel = async () => { if (!window.confirm("Annuler cette facture ?")) return; try { await axios.post(`${API_URL}/api/sih/billing/invoices/${invoiceId}/cancel`, {}, getAuthConfig()); onDone(); } catch (e) { setErr(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Erreur"); } };
  const del = async () => { if (!window.confirm("Supprimer définitivement cette facture ?")) return; try { await axios.delete(`${API_URL}/api/sih/billing/invoices/${invoiceId}`, getAuthConfig()); onDone(); } catch (e) { setErr(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Erreur"); } };
  const addPayment = async () => {
    const amt = Number(pay.amount);
    if (!amt || amt <= 0) { setErr("Montant invalide"); return; }
    setErr("");
    try {
      const r = await axios.post(`${API_URL}/api/sih/billing/invoices/${invoiceId}/payments`, { amount: amt, method: pay.method, note: pay.note || null }, getAuthConfig());
      const fresh = await axios.get(`${API_URL}/api/sih/billing/invoices/${invoiceId}`, getAuthConfig());
      setInv(fresh.data); setPay({ amount: "", method: "espece", note: "" });
      printReceipt(fresh.data, r.data.payment, hospitalName);
    } catch (e) { setErr(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Erreur"); }
  };

  const st = inv ? (INV_STATUS[inv.status] || [inv.status, "#64748B", "#F1F5F9"]) : null;
  const balance = inv ? (inv.total_amount || 0) - (inv.paid_amount || 0) : total;

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1100, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: 24, overflow: "auto" }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 12, width: "94%", maxWidth: 680, padding: 22, marginTop: 12 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ fontSize: 17, fontWeight: 700 }}>{invoiceId ? `Facture ${inv?.invoice_number || ""}` : "Nouvelle facture"}{st && <span style={{ fontSize: 12, padding: "3px 10px", borderRadius: 12, background: st[2], color: st[1], marginLeft: 10 }}>{st[0]}</span>}</div>
          <button onClick={onClose} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#94A3B8" }}>×</button>
        </div>
        <div style={{ fontSize: 13, color: "#64748B", marginBottom: 14 }}>{inv?.patient_name || ""}</div>
        {err && <div style={{ background: "#FCEBEB", border: "1px solid #F7C1C1", color: "#A32D2D", borderRadius: 8, padding: "8px 12px", marginBottom: 12, fontSize: 12 }}>{err}</div>}
        {loading ? <div style={{ padding: 20, color: "#94A3B8" }}>Chargement...</div> : (
          <>
            {!invoiceId && (
              <div style={{ marginBottom: 12 }}><label style={{ fontSize: 12, color: "#64748B", display: "block", marginBottom: 4 }}>Patient</label>
                <select style={biS} value={patientId} onChange={(e) => setPatientId(e.target.value)}><option value="">Choisir…</option>{patients.map((p) => <option key={p.patient_id} value={p.patient_id}>{p.first_name} {p.last_name}</option>)}</select></div>
            )}

            {editable && (
              <div style={{ marginBottom: 10 }}>
                <select onChange={(e) => { addTarif(e.target.value); e.target.value = ""; }} defaultValue="" style={biS}>
                  <option value="" disabled>+ Ajouter depuis le catalogue…</option>
                  {tarifs.map((t) => <option key={t.tarif_id} value={t.tarif_id}>{t.name} — {dzd(t.price)}</option>)}
                </select>
              </div>
            )}

            <div style={{ border: "1px solid #E2E8F0", borderRadius: 8, overflow: "hidden", marginBottom: 12 }}>
              <div style={{ display: "flex", background: "#F8FAFC", fontSize: 11, color: "#64748B", fontWeight: 600, padding: "6px 8px" }}>
                <div style={{ flex: 3 }}>Désignation</div><div style={{ flex: 1, textAlign: "center" }}>Qté</div><div style={{ flex: 1.4, textAlign: "right" }}>P.U.</div><div style={{ flex: 1.4, textAlign: "right" }}>Total</div><div style={{ width: 24 }} />
              </div>
              {lines.map((l, i) => (
                <div key={i} style={{ display: "flex", gap: 6, padding: "6px 8px", borderTop: "1px solid #F1F5F9", alignItems: "center" }}>
                  <input style={{ ...biS, flex: 3 }} placeholder="Prestation" value={l.label} onChange={(e) => updLine(i, "label", e.target.value)} disabled={!editable} />
                  <input type="number" style={{ ...biS, flex: 1, textAlign: "center" }} value={l.quantity} onChange={(e) => updLine(i, "quantity", e.target.value)} disabled={!editable} />
                  <input type="number" style={{ ...biS, flex: 1.4, textAlign: "right" }} value={l.unit_price} onChange={(e) => updLine(i, "unit_price", e.target.value)} disabled={!editable} />
                  <div style={{ flex: 1.4, textAlign: "right", fontSize: 13, color: "#0F172A" }}>{dzd((Number(l.quantity) || 0) * (Number(l.unit_price) || 0))}</div>
                  <div style={{ width: 24, textAlign: "center" }}>{editable && <button onClick={() => setLines(lines.filter((_, j) => j !== i))} style={{ background: "none", border: "none", color: "#A32D2D", cursor: "pointer", fontSize: 16 }}>×</button>}</div>
                </div>
              ))}
              {editable && <div style={{ padding: 8, borderTop: "1px solid #F1F5F9" }}><button onClick={() => setLines([...lines, emptyInvLine()])} style={{ fontSize: 13, color: "#185FA5", background: "none", border: "none", cursor: "pointer" }}>+ Ligne libre</button></div>}
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 24, fontSize: 14, marginBottom: 12 }}>
              <div style={{ textAlign: "right" }}>
                <div style={{ color: "#64748B", fontSize: 12 }}>Total</div>
                <div style={{ fontWeight: 700, fontSize: 18, color: "#0596DE" }}>{dzd(invoiceId ? inv.total_amount : total)}</div>
              </div>
              {invoiceId && <div style={{ textAlign: "right" }}><div style={{ color: "#64748B", fontSize: 12 }}>Payé</div><div style={{ fontWeight: 600, color: "#3B6D11" }}>{dzd(inv.paid_amount)}</div></div>}
              {invoiceId && <div style={{ textAlign: "right" }}><div style={{ color: "#64748B", fontSize: 12 }}>Reste</div><div style={{ fontWeight: 700, color: balance > 0 ? "#B45309" : "#64748B" }}>{dzd(balance)}</div></div>}
            </div>

            {editable && <div style={{ marginBottom: 12 }}><textarea style={{ ...biS, minHeight: 44, resize: "vertical" }} placeholder="Remarques" value={notes} onChange={(e) => setNotes(e.target.value)} /></div>}

            {/* Encaissement */}
            {invoiceId && inv.status !== "cancelled" && inv.status !== "draft" && balance > 0 && canWrite && (
              <div style={{ background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 8, padding: 12, marginBottom: 12 }}>
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Encaisser un paiement</div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <input type="number" style={{ ...biS, flex: 1, minWidth: 120 }} placeholder={`Montant (max ${balance})`} value={pay.amount} onChange={(e) => setPay({ ...pay, amount: e.target.value })} />
                  <select style={{ ...biS, width: "auto" }} value={pay.method} onChange={(e) => setPay({ ...pay, method: e.target.value })}>{Object.entries(PAY_METHODS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select>
                  <button onClick={addPayment} style={{ fontSize: 13, padding: "8px 16px", borderRadius: 8, border: "none", background: "#3B6D11", color: "#fff", cursor: "pointer" }}>Encaisser</button>
                </div>
              </div>
            )}

            {/* Historique des paiements */}
            {invoiceId && inv.payments?.length > 0 && (
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 12, color: "#64748B", marginBottom: 6 }}>Paiements</div>
                {inv.payments.map((p) => (
                  <div key={p.payment_id} style={{ display: "flex", justifyContent: "space-between", fontSize: 13, padding: "5px 0", borderBottom: "1px solid #F1F5F9" }}>
                    <span>{(p.date || "").slice(0, 10)} · {PAY_METHODS[p.method] || p.method}</span>
                    <span style={{ display: "flex", gap: 10, alignItems: "center" }}>{dzd(p.amount)}<button onClick={() => printReceipt(inv, p, hospitalName)} style={{ fontSize: 12, color: "#185FA5", background: "none", border: "none", cursor: "pointer" }}>Reçu</button></span>
                  </div>
                ))}
              </div>
            )}

            <div style={{ display: "flex", gap: 8, justifyContent: "space-between", flexWrap: "wrap", marginTop: 4 }}>
              <div style={{ display: "flex", gap: 8 }}>
                {invoiceId && <button onClick={() => printInvoice(inv, hospitalName)} style={{ fontSize: 13, padding: "9px 16px", borderRadius: 8, border: "1px solid #185FA5", background: "#fff", color: "#185FA5", cursor: "pointer" }}>🖨 PDF facture</button>}
                {invoiceId && inv.status === "draft" && canWrite && <button onClick={issue} style={{ fontSize: 13, padding: "9px 16px", borderRadius: 8, border: "none", background: "#185FA5", color: "#fff", cursor: "pointer" }}>Émettre</button>}
                {invoiceId && inv.status !== "cancelled" && (inv.paid_amount || 0) === 0 && canWrite && <button onClick={cancel} style={{ fontSize: 13, padding: "9px 16px", borderRadius: 8, border: "1px solid #F7C1C1", background: "#fff", color: "#A32D2D", cursor: "pointer" }}>Annuler la facture</button>}
                {invoiceId && (inv.paid_amount || 0) === 0 && canWrite && <button onClick={del} style={{ fontSize: 13, padding: "9px 16px", borderRadius: 8, border: "none", background: "#A32D2D", color: "#fff", cursor: "pointer" }}>Supprimer</button>}
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={onClose} style={{ fontSize: 13, padding: "9px 16px", borderRadius: 8, border: "1px solid #CBD5E1", background: "#fff", color: "#64748B", cursor: "pointer" }}>Fermer</button>
                {editable && canWrite && <button onClick={saveLines} disabled={saving} style={{ fontSize: 13, padding: "9px 16px", borderRadius: 8, border: "none", background: "#185FA5", color: "#fff", cursor: "pointer" }}>{saving ? "..." : (invoiceId ? "Enregistrer" : "Créer la facture")}</button>}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function TarifModal({ tarif, onClose, onDone }) {
  const [f, setF] = useState({ name: tarif?.name || "", category: tarif?.category || "", price: tarif?.price ?? "", code: tarif?.code || "" });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const save = async () => {
    if (!f.name.trim()) { setErr("Nom requis"); return; }
    setSaving(true); setErr("");
    const payload = { name: f.name, category: f.category || null, price: Number(f.price) || 0, code: f.code || null };
    try {
      if (tarif?.tarif_id) await axios.put(`${API_URL}/api/sih/billing/tarifs/${tarif.tarif_id}`, payload, getAuthConfig());
      else await axios.post(`${API_URL}/api/sih/billing/tarifs`, payload, getAuthConfig());
      onDone();
    } catch (e) { setErr(e.response?.data?.detail || "Erreur"); setSaving(false); }
  };
  const del = async () => { if (!tarif?.tarif_id || !window.confirm("Supprimer ce tarif ?")) return; try { await axios.delete(`${API_URL}/api/sih/billing/tarifs/${tarif.tarif_id}`, getAuthConfig()); onDone(); } catch (e) { setErr("Erreur"); } };
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1100, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: 24, overflow: "auto" }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 12, width: "92%", maxWidth: 460, padding: 22, marginTop: 30 }}>
        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 12 }}>{tarif?.tarif_id ? "Modifier le tarif" : "Nouveau tarif"}</div>
        {err && <div style={{ background: "#FCEBEB", border: "1px solid #F7C1C1", color: "#A32D2D", borderRadius: 8, padding: "8px 12px", marginBottom: 12, fontSize: 12 }}>{err}</div>}
        <div style={{ marginBottom: 10 }}><label style={{ fontSize: 12, color: "#64748B", display: "block", marginBottom: 4 }}>Prestation</label><input style={biS} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></div>
        <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
          <div style={{ flex: 1 }}><label style={{ fontSize: 12, color: "#64748B", display: "block", marginBottom: 4 }}>Catégorie</label><input style={biS} placeholder="Consultation, Acte…" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} /></div>
          <div style={{ flex: 1 }}><label style={{ fontSize: 12, color: "#64748B", display: "block", marginBottom: 4 }}>Prix (DZD)</label><input type="number" style={biS} value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} /></div>
        </div>
        <div style={{ marginBottom: 4 }}><label style={{ fontSize: 12, color: "#64748B", display: "block", marginBottom: 4 }}>Code (optionnel)</label><input style={biS} value={f.code} onChange={(e) => setF({ ...f, code: e.target.value })} /></div>
        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 16 }}>
          {tarif?.tarif_id ? <button onClick={del} style={{ fontSize: 13, color: "#A32D2D", background: "none", border: "none", cursor: "pointer" }}>Supprimer</button> : <span />}
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={onClose} style={{ fontSize: 13, padding: "9px 16px", borderRadius: 8, border: "1px solid #CBD5E1", background: "#fff", color: "#64748B", cursor: "pointer" }}>Annuler</button>
            <button onClick={save} disabled={saving} style={{ fontSize: 13, padding: "9px 16px", borderRadius: 8, border: "none", background: "#185FA5", color: "#fff", cursor: "pointer" }}>{saving ? "..." : "Enregistrer"}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default HopitalDashboard;
